/* web/js/app-config.js */
/* Trusted deployment config + the common URL/state contract (never URL query). */
(function(global){
    const defaults={
        environment:'production',publicBasePath:'',apiPrefix:'/api',assetsPrefix:'/dist',
        mediaPrefix:'',releaseId:'',browserNamespace:''
    };
    function prefix(value,label,allowEmpty=false){
        const raw=String(value??'');
        if((raw===''||raw==='/')&&allowEmpty)return '';
        const out=raw.replace(/\/$/,'');
        if(!/^\/(?:[A-Za-z0-9_-]+\/)*[A-Za-z0-9_-]+$/.test(out)){
            throw new Error(`Invalid application ${label}`);
        }
        return out;
    }
    function normalizeConfig(input={}){
        if(!input||typeof input!=='object'||Array.isArray(input))throw new Error('Invalid application config');
        const cfg={...defaults,...input};
        cfg.publicBasePath=prefix(cfg.publicBasePath,'publicBasePath',true);
        cfg.apiPrefix=prefix(input.apiPrefix??`${cfg.publicBasePath}/api`,'apiPrefix');
        cfg.assetsPrefix=prefix(input.assetsPrefix??`${cfg.publicBasePath}/dist`,'assetsPrefix');
        cfg.mediaPrefix=prefix(input.mediaPrefix??cfg.publicBasePath,'mediaPrefix',true);
        cfg.environment=String(cfg.environment);
        cfg.browserNamespace=String(cfg.browserNamespace);
        cfg.releaseId=String(cfg.releaseId);
        if(!/^[a-z][a-z0-9_-]*$/.test(cfg.environment)
            ||(cfg.browserNamespace&&!/^[a-zA-Z0-9_-]+$/.test(cfg.browserNamespace))){
            throw new Error('Invalid application environment/namespace');
        }
        if(cfg.publicBasePath){
            if(!cfg.browserNamespace||cfg.environment==='production')throw new Error('Missing isolated application environment');
            for(const key of ['apiPrefix','assetsPrefix','mediaPrefix']){
                if(cfg[key]!==cfg.publicBasePath&&!cfg[key].startsWith(cfg.publicBasePath+'/')){
                    throw new Error(`Application ${key} escapes publicBasePath`);
                }
            }
        }else if(cfg.environment!=='production'){
            throw new Error('Non-production application requires publicBasePath');
        }
        const result=Object.fromEntries(Object.keys(defaults).map(key=>[key,cfg[key]]));
        if(input.workboardV2Enabled!==undefined){
            if(typeof input.workboardV2Enabled!=='boolean')throw new Error('Invalid workboardV2Enabled config');
            if(input.workboardV2Enabled&&(cfg.environment!=='new'||cfg.publicBasePath!=='/new')){
                throw new Error('Workboard v2 requires the trusted /new environment');
            }
            result.workboardV2Enabled=input.workboardV2Enabled;
        }
        return Object.freeze(result);
    }
    const node=global.document?.getElementById('app-config');
    const config=normalizeConfig(global.__APP_CONFIG__??(node?JSON.parse(node.textContent):defaults));
    // This is a consistency guard, not a backend selector. A root/missing config
    // served at /new must fail before any request can reach the production API.
    if(/^\/new(?:\/|$)/.test(global.location.pathname)&&config.publicBasePath!=='/new'){
        throw new Error('Missing trusted /new application config');
    }
    const origin=global.location.origin;
    const inPrefix=(pathname,base)=>pathname===base||pathname.startsWith(base+'/');
    function parse(value){
        try{
            const url=new URL(String(value),global.location.href);
            return /^(https?:)$/.test(url.protocol)&&url.origin===origin?url:null;
        }catch(_e){return null;}
    }
    function assertSafePath(pathname){
        if(!config.publicBasePath)return;
        // Browsers preserve encoded '/' while ingress commonly decodes it before
        // location routing. Never hand a disguised '..' escape to the root edge.
        let structural=pathname;
        for(let round=0;round<8;round++){
            const next=structural.replace(/%(25|2e|2f|5c)/gi,(_match,hex)=>String.fromCharCode(parseInt(hex,16)));
            if(next===structural)break;
            structural=next;
        }
        structural=structural.replace(/\\/g,'/');
        if(/(?:^|\/)\.{1,2}(?:\/|$)/.test(structural))throw new Error('Unsafe internal application URL');
    }
    function resolve(value){
        const raw=String(value??'');
        if(!raw||raw.startsWith('#')||raw.startsWith('?'))return raw;
        const url=parse(raw);
        if(!url)return raw; // External/provider/S3/data/blob URLs are not rewritten.
        assertSafePath(url.pathname);
        const pathname=url.pathname;
        let next=pathname;
        if(inPrefix(pathname,config.apiPrefix)||inPrefix(pathname,config.assetsPrefix)
            ||(config.publicBasePath&&inPrefix(pathname,config.publicBasePath)))return raw;
        if(pathname==='/site.webmanifest'&&config.publicBasePath)next=config.assetsPrefix+'/site.webmanifest';
        else if(inPrefix(pathname,'/api'))next=config.apiPrefix+pathname.slice(4);
        else if(inPrefix(pathname,'/dist'))next=config.assetsPrefix+pathname.slice(5);
        else if(/^\/(uploads|images|videos|f|runs)(?:\/|$)/.test(pathname))next=config.mediaPrefix+pathname;
        else next=config.publicBasePath+pathname;
        if(next===pathname)return raw;
        url.pathname=next;
        return /^(?:https?:)?\/\//i.test(raw)?url.href:url.pathname+url.search+url.hash;
    }
    function internalPath(value){
        let url;
        try{url=parse(resolve(value));}catch(_e){return '';}
        if(!url)return '';
        const pathname=url.pathname;
        if(inPrefix(pathname,config.apiPrefix))return '/api'+pathname.slice(config.apiPrefix.length);
        if(inPrefix(pathname,config.assetsPrefix))return '/dist'+pathname.slice(config.assetsPrefix.length);
        if(config.mediaPrefix&&inPrefix(pathname,config.mediaPrefix))return pathname.slice(config.mediaPrefix.length)||'/';
        if(config.publicBasePath&&inPrefix(pathname,config.publicBasePath))return pathname.slice(config.publicBasePath.length)||'/';
        return pathname;
    }
    function internalApiRoute(value){
        let url;
        try{url=parse(resolve(value));}catch(_e){return '';}
        return url&&inPrefix(url.pathname,config.apiPrefix)?'/api'+url.pathname.slice(config.apiPrefix.length):'';
    }
    function request(input){
        if(typeof global.Request!=='undefined'&&input instanceof global.Request){
            const url=resolve(input.url);
            return url===input.url?input:new global.Request(url,input);
        }
        return resolve(input);
    }
    function stateKey(key){
        const raw=String(key);
        const namespace=config.browserNamespace?`cg2:${config.browserNamespace}:`:'';
        return namespace&&!raw.startsWith(namespace)?namespace+raw:raw;
    }
    function css(value){
        return String(value).replace(/url\(\s*(?:(['"])(.*?)\1|([^'"\s][^)]*))\s*\)/gi,(_match,quote,quoted,bare)=>{
            const raw=(quoted??bare).trim();
            const next=resolve(raw);
            return `url(${quote||''}${next}${quote||''})`;
        });
    }
    function srcset(value){
        const text=String(value);
        let out='',index=0;
        while(index<text.length){
            const separator=index;
            while(index<text.length&&/[\s,]/.test(text[index]))index++;
            out+=text.slice(separator,index);
            if(index===text.length)break;
            const start=index;
            while(index<text.length&&!/\s/.test(text[index]))index++;
            const end=index;
            while(index>start&&text[index-1]===',')index--;
            out+=resolve(text.slice(start,index));
            if(index!==end)continue; // Trailing commas delimit candidates.
            const descriptor=index;
            while(index<text.length&&text[index]!==',')index++;
            out+=text.slice(descriptor,index);
        }
        return out;
    }
    function redirect(value,{replace=false}={}){
        global.location[replace?'replace':'assign'](resolve(value));
    }
    global.AppConfig=config;
    global.__APP_CONFIG__=config;
    global.AppURL=Object.freeze({resolve,request,internalPath,internalApiRoute,stateKey,css,srcset,redirect,normalizeConfig});
})(window);

/* web/js/app-environment.js */
/* Legacy vanilla consumers share these URL/storage boundaries in prefixed builds.
 * Keep logical graph/file references unchanged; rewrite at browser IO sinks only.
 * Root installs no DOM/storage shims and keeps its historical keys and URLs.
 */
(function(global){
    const urls=global.AppURL;
    const cfg=global.AppConfig;
    if(!urls||!cfg)throw new Error('Trusted application config must load first');
    function installStorage(){
        if(!cfg.browserNamespace||!global.Storage)return;
        const proto=global.Storage.prototype;
        const get=proto.getItem,set=proto.setItem,remove=proto.removeItem,key=proto.key;
        const length=Object.getOwnPropertyDescriptor(proto,'length');
        const prefix=urls.stateKey('');
        function keys(storage){
            const result=[];
            for(let index=0;index<length.get.call(storage);index++){
                const value=key.call(storage,index);
                if(value?.startsWith(prefix))result.push(value);
            }
            return result;
        }
        proto.getItem=function(name){return get.call(this,urls.stateKey(name));};
        proto.setItem=function(name,value){return set.call(this,urls.stateKey(name),value);};
        proto.removeItem=function(name){return remove.call(this,urls.stateKey(name));};
        proto.clear=function(){keys(this).forEach(name=>remove.call(this,name));};
        proto.key=function(index){return keys(this)[Number(index)]?.slice(prefix.length)??null;};
        Object.defineProperty(proto,'length',{...length,get(){return keys(this).length;}});
    }
    function installUrls(){
        if(!cfg.publicBasePath)return;
        if(global.XMLHttpRequest){
            const open=global.XMLHttpRequest.prototype.open;
            global.XMLHttpRequest.prototype.open=function(method,url,...args){
                return open.call(this,method,urls.resolve(url),...args);
            };
        }
        if(global.EventSource){
            const Native=global.EventSource;
            global.EventSource=new Proxy(Native,{construct(Target,args,newTarget){
                return Reflect.construct(Target,[urls.resolve(args[0]),...args.slice(1)],newTarget);
            }});
        }
        if(global.Audio){
            const Native=global.Audio;
            global.Audio=new Proxy(Native,{construct(Target,args,newTarget){
                return Reflect.construct(Target,args.length?[urls.resolve(args[0]),...args.slice(1)]:args,newTarget);
            }});
        }
        if(global.open){
            const open=global.open;
            global.open=function(url,...args){return open.call(this,url==null?url:urls.resolve(url),...args);};
        }
        if(global.navigator?.sendBeacon){
            const beacon=global.navigator.sendBeacon.bind(global.navigator);
            global.navigator.sendBeacon=(url,data)=>beacon(urls.resolve(url),data);
        }
        for(const method of ['pushState','replaceState']){
            const native=global.history[method];
            global.history[method]=function(state,title,url){
                return native.call(this,state,title,url==null?url:urls.resolve(url));
            };
        }
        installDom();
    }
    function installDom(){
        const proto=global.Element.prototype;
        const inner=Object.getOwnPropertyDescriptor(proto,'innerHTML');
        const set=proto.setAttribute;
        const urlAttrs=new Set(['src','href','action','formaction','poster','data','xlink:href']);
        const srcset=urls.srcset;
        function attr(name,value){
            const lower=String(name).toLowerCase();
            if(urlAttrs.has(lower))return urls.resolve(value);
            if(lower==='srcset')return srcset(value);
            if(lower==='style'||lower==='data-style')return urls.css(value);
            return value;
        }
        function tree(root){
            const elements=[...(root.nodeType===1?[root]:[]),...root.querySelectorAll('*')];
            for(const element of elements){
                for(const name of [...element.getAttributeNames()]){
                    const value=element.getAttribute(name);
                    const next=attr(name,value);
                    if(next!==value)set.call(element,name,next);
                }
                if(element.tagName==='TEMPLATE')tree(element.content);
                if(element.tagName==='STYLE')element.textContent=urls.css(element.textContent);
            }
        }
        function html(value){
            const text=String(value);
            if(!/\s(?:src|href|action|formaction|poster|srcset|data|xlink:href|style|data-style)\s*=|url\(/i.test(text))return value;
            const template=global.document.createElement('template');
            inner.set.call(template,text); // Inert: no request to root while parsing.
            tree(template.content);
            return inner.get.call(template);
        }
        proto.setAttribute=function(name,value){return set.call(this,name,attr(name,value));};
        const setNS=proto.setAttributeNS;
        proto.setAttributeNS=function(namespace,name,value){return setNS.call(this,namespace,name,attr(name,value));};
        function setter(target,name,transform){
            const descriptor=target&&Object.getOwnPropertyDescriptor(target,name);
            if(!descriptor?.set||!descriptor.configurable)return;
            Object.defineProperty(target,name,{...descriptor,set(value){descriptor.set.call(this,transform(value));}});
        }
        setter(proto,'innerHTML',html);
        setter(proto,'outerHTML',html);
        const adjacent=proto.insertAdjacentHTML;
        proto.insertAdjacentHTML=function(position,value){return adjacent.call(this,position,html(value));};
        if(global.Range){
            const fragment=global.Range.prototype.createContextualFragment;
            global.Range.prototype.createContextualFragment=function(value){return fragment.call(this,html(value));};
        }
        for(const [constructor,names] of [
            ['HTMLImageElement',['src','srcset']],['HTMLMediaElement',['src']],
            ['HTMLVideoElement',['poster']],['HTMLSourceElement',['src','srcset']],
            ['HTMLAnchorElement',['href']],['HTMLAreaElement',['href']],['HTMLLinkElement',['href']],
            ['HTMLScriptElement',['src']],['HTMLIFrameElement',['src']],['HTMLEmbedElement',['src']],
            ['HTMLObjectElement',['data']],['HTMLFormElement',['action']],
            ['HTMLInputElement',['src','formAction']],['HTMLButtonElement',['formAction']]
        ]){
            for(const name of names)setter(global[constructor]?.prototype,name,name==='srcset'?srcset:urls.resolve);
        }
        const style=global.CSSStyleDeclaration?.prototype;
        if(style){
            const property=style.setProperty;
            style.setProperty=function(name,value,priority){return property.call(this,name,urls.css(value),priority);};
            for(const name of ['cssText','background','backgroundImage','borderImage','listStyle','listStyleImage','content','cursor','maskImage']){
                setter(style,name,urls.css);
            }
        }
        const sheet=global.CSSStyleSheet?.prototype;
        for(const name of ['insertRule','replace','replaceSync']){
            if(!sheet?.[name])continue;
            const native=sheet[name];
            sheet[name]=function(value,...args){return native.call(this,urls.css(value),...args);};
        }
        // Covers the static shell and inert landing template too. Build rewriting
        // handles eager assets before this script; these are runtime backstops.
        tree(global.document);
        global.document.addEventListener('submit',event=>{
            const form=event.target;
            if(form instanceof global.HTMLFormElement){
                if(form.hasAttribute('action'))form.action=urls.resolve(form.getAttribute('action'));
                const button=event.submitter;
                if(button?.hasAttribute('formaction'))button.formAction=urls.resolve(button.getAttribute('formaction'));
            }
        },true);
    }
    function badge(){
        if(cfg.environment==='production'||!global.document.body)return;
        if(global.document.getElementById('app-environment-badge'))return;
        const element=global.document.createElement('div');
        element.id='app-environment-badge';
        element.className='app-environment-badge';
        element.setAttribute('role','status');
        element.textContent='Тестовая версия'+(cfg.releaseId?' · '+cfg.releaseId:'');
        global.document.body.appendChild(element);
    }
    installStorage();
    installUrls();
    if(global.document.readyState==='loading')global.document.addEventListener('DOMContentLoaded',badge,{once:true});
    else badge();
    global.AppEnvironment=Object.freeze({badge});
})(window);

/* web/js/ux.js */
/* ═══════════════════════════════════════════
   omoibot — UX Module
   Toast · Confirm · Hotkeys · ActionRunner · AutoSave
   Загружать ПЕРЕД core.js
   ═══════════════════════════════════════════ */
/* ═══ Toast ═══ */
function escHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, ch => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
    }[ch]));
}
const Toast = {
    _container: null,
    _ensureContainer() {
        if (!this._container) {
            this._container = document.createElement('div');
            this._container.className = 'toast-container';
            this._container.setAttribute('role', 'status');
            this._container.setAttribute('aria-live', 'polite');
            this._container.setAttribute('aria-atomic', 'false');
            document.body.appendChild(this._container);
        }
        return this._container;
    },
    /**
     * Show toast notification
     * @param {string} message
     * @param {'success'|'error'|'warning'|'info'} type
     * @param {number} duration ms, default 3000
     */
    show(message, type = 'info', duration = 3000) {
        const container = this._ensureContainer();
        const icons = { success: 'OK', error: 'ERR', warning: 'WARN', info: 'INFO' };
        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;
        toast.setAttribute('role', type === 'error' || type === 'warning' ? 'alert' : 'status');
        toast.innerHTML = `
            <span class="toast-icon">${icons[type] || 'INFO'}</span>
            <span class="toast-text">${this._esc(message)}</span>
            <button class="toast-close" aria-label="Закрыть уведомление" data-toast-close><span class="ui-code-icon" aria-hidden="true">DEL</span></button>
        `;
        container.appendChild(toast);
        // Auto-dismiss
        if (duration > 0) {
            toast._timer = setTimeout(() => this.dismiss(toast), duration);
        }
        return toast;
    },
    dismiss(el) {
        if (!el || el._dismissing) return;
        el._dismissing = true;
        clearTimeout(el._timer);
        el.classList.add('toast-leaving');
        el.addEventListener('animationend', () => el.remove(), { once: true });
        // Fallback if animation doesn't fire
        setTimeout(() => { if (el.parentElement) el.remove(); }, 300);
    },
    success(msg, dur) { return this.show(msg, 'success', dur); },
    error(msg, dur)   { return this.show(msg, 'error', dur || 5000); },
    warning(msg, dur) { return this.show(msg, 'warning', dur); },
    info(msg, dur)    { return this.show(msg, 'info', dur); },
    _esc(s) { return escHtml(s || ''); }
};
window.Toast = Toast;
document.addEventListener('click', (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-toast-close]') : null;
    if (!btn) return;
    event.preventDefault();
    Toast.dismiss(btn.parentElement);
});
const FocusTrap = {
    selector: [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(','),
    _isVisible(el) {
        return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    },
    focusables(root) {
        return [...root.querySelectorAll(this.selector)].filter(el => this._isVisible(el));
    },
    activate(root, options = {}) {
        if (!root) return;
        this.deactivate(root, false);
        const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const onEscape = options.onEscape || null;
        const onKeydown = (event) => {
            if (event.key === 'Escape' && onEscape) {
                event.preventDefault();
                event.stopPropagation();
                onEscape();
                return;
            }
            if (event.key !== 'Tab') return;
            const items = this.focusables(root);
            if (!items.length) {
                event.preventDefault();
                root.focus?.();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        root.__focusTrap = { previous, onKeydown };
        document.addEventListener('keydown', onKeydown, true);
        setTimeout(() => {
            const target = options.initialFocus
                ? root.querySelector(options.initialFocus)
                : root.querySelector('[autofocus]');
            const fallback = target || this.focusables(root)[0] || root;
            fallback?.focus?.();
        }, 0);
    },
    deactivate(root, returnFocus = true) {
        const trap = root?.__focusTrap;
        if (!trap) return;
        document.removeEventListener('keydown', trap.onKeydown, true);
        delete root.__focusTrap;
        if (returnFocus && trap.previous && document.contains(trap.previous)) {
            setTimeout(() => trap.previous.focus?.(), 0);
        }
    }
};
const A11yActions = {
    clickableSelector: [
        '[data-route]',
        '[data-dashboard-action]',
        '[data-job-action]',
        '[data-project-action]',
        '[data-project-view-action]',
        '[data-chat-action]',
        '[data-module-action]',
        '[data-bot-action]',
        '[data-task-editor-action]',
        '[data-task-editor-menu-action]',
        '[data-task-render-action]',
        '[data-debugger-action]',
        '[data-node-ctx-action]',
        '[data-jdbg-canvas-action]',
        '.tile[onclick]',
        '.tile[data-task-id]',
        '.task-tile[onclick]',
        '.task-tile[data-task-id]',
        '.job-card[onclick]',
        '.ticker-tile[onclick]',
        '.modal-tile[onclick]',
        '.chat-item[onclick]',
        '.ctx-menu-item[onclick]',
        '.ar-opt[onclick]',
        '.color-opt[onclick]',
        '.icon-opt[onclick]',
        '.bot-platform-tile[onclick]',
        '.folder-card [onclick]'
    ].join(','),
    nativeSelector: 'a,button,input,textarea,select,summary',
    labelFor(el) {
        const direct = el.getAttribute('aria-label') || el.getAttribute('title') || '';
        if (direct.trim()) return direct.trim();
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        if (text) return text;
        const data = el.dataset || {};
        return data.label || data.action || data.jobAction || data.projectAction ||
            data.projectViewAction || data.chatAction || data.moduleAction ||
            data.botAction || data.taskEditorAction || data.debuggerAction ||
            data.nodeCtxAction || data.jdbgCanvasAction || data.color ||
            data.value || data.icon || '';
    },
    enhance(root = document) {
        root.querySelectorAll?.(this.clickableSelector).forEach(el => {
            if (el.matches(this.nativeSelector)) return;
            if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
            if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
            if (!el.hasAttribute('aria-label')) {
                const label = this.labelFor(el);
                if (label) el.setAttribute('aria-label', label);
            }
        });
        if (root === document || root.nodeType === 1) this.labelize(root);
    },
    _labelIdSeq: 0,
    /**
     * Ensure every visible form control has a programmatic label.
     * Idempotent — safe to re-run on MutationObserver-added subtrees.
     * Skips input[type=hidden]/buttons, controls already named
     * (aria-label/-labelledby, wrapping <label>, or label[for]).
     */
    labelize(root = document) {
        if (!root || typeof root.querySelectorAll !== 'function') return;
        const controls = [...root.querySelectorAll('input,select,textarea')];
        if (root.nodeType === 1 && root.matches?.('input,select,textarea')) controls.push(root);
        controls.forEach(ctrl => {
            const type = (ctrl.getAttribute('type') || '').toLowerCase();
            if (ctrl.tagName === 'INPUT' && ['hidden', 'submit', 'button', 'reset', 'image'].includes(type)) return;
            if (ctrl.getAttribute('aria-label') || ctrl.getAttribute('aria-labelledby')) return;
            if (ctrl.closest('label')) return; // wrapped-label pattern — already associated
            const doc = ctrl.ownerDocument || document;
            if (ctrl.id && [...doc.querySelectorAll('label[for]')].some(l => l.getAttribute('for') === ctrl.id)) return;
            const group = ctrl.closest('.form-group, .profile-field, .form-row') || ctrl.parentElement;
            const labelEl = group && group.querySelector('.form-label');
            if (labelEl) {
                if (labelEl.tagName === 'LABEL') {
                    if (!ctrl.id) ctrl.id = '__lf' + (++this._labelIdSeq);
                    if (!labelEl.getAttribute('for')) labelEl.setAttribute('for', ctrl.id);
                    return;
                }
                if (!labelEl.id) labelEl.id = '__ll' + (++this._labelIdSeq);
                ctrl.setAttribute('aria-labelledby', labelEl.id);
                return;
            }
            const ph = ctrl.getAttribute('placeholder');
            if (ph && ph.trim()) ctrl.setAttribute('aria-label', ph.trim());
        });
    },

    init() {
        this.enhance(document);
        document.addEventListener('keydown', event => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            const el = event.target.closest?.(this.clickableSelector);
            if (!el || el.matches(this.nativeSelector)) return;
            event.preventDefault();
            el.click();
        });
        const observer = new MutationObserver(records => {
            records.forEach(record => {
                record.addedNodes.forEach(node => {
                    if (node.nodeType !== Node.ELEMENT_NODE) return;
                    if (node.matches?.(this.clickableSelector)) this.enhance({ querySelectorAll: () => [node] });
                    this.enhance(node);
                });
            });
        });
        if (document.body) observer.observe(document.body, { childList: true, subtree: true });
    }
};

A11yActions.init();

/* ═══ ActionRunner — double-submit guard for action-button delegates ═══
   Modals here are Modal.show(htmlString) with data-*-action buttons, not
   <form>s, so there is no submit event to debounce. run() wraps a delegate
   handler: re-entry on an in-flight button is swallowed; async/mutating
   handlers (those returning a Promise) get disabled + aria-busy until they
   settle; sync handlers (navigation/pickers) pass straight through. */
const ActionRunner = {
    _isButtonish(el) {
        return el instanceof Element &&
            (el.tagName === 'BUTTON' || el.getAttribute('role') === 'button' ||
             el.classList.contains('btn'));
    },

    _lock(target) {
        if (!this._isButtonish(target)) return () => {};
        if ('disabled' in target) target.disabled = true;
        target.setAttribute('aria-busy', 'true');
        let released = false;
        return () => {
            if (released) return;
            released = true;
            if ('disabled' in target) target.disabled = false;
            target.removeAttribute('aria-busy');
        };
    },

    /** Run an async fn with the button busy-locked; re-entry returns undefined. */
    withBusy(btn, asyncFn) {
        if (this._isButtonish(btn) &&
            (btn.disabled || btn.getAttribute('aria-busy') === 'true')) return undefined;
        const release = this._lock(btn);
        let ret;
        try { ret = asyncFn(); } catch (e) { release(); throw e; }
        if (ret && typeof ret.then === 'function') return ret.finally(release);
        release();
        return ret;
    },

    /** Delegate wrapper: guards re-entry, busies async handlers only. */
    run(target, handler) {
        if (this._isButtonish(target) &&
            (target.disabled || target.getAttribute('aria-busy') === 'true')) return undefined;
        return this.withBusy(target, handler);
    }
};
window.ActionRunner = ActionRunner;

/* ═══ Confirm / Prompt (replaces native) ═══ */
const Dialogs = {
    /**
     * Styled confirm dialog (returns Promise<boolean>)
     */
    confirm(message, title = 'подтверждение') {
        return new Promise(resolve => {
            const overlay = document.createElement('div');
            overlay.className = 'confirm-overlay';
            const titleId = 'confirm-title-' + Date.now();
            let resolved = false;

            const _close = (result) => {
                if (resolved) return;
                resolved = true;
                const box = overlay.querySelector('.confirm-box');
                FocusTrap.deactivate(box);
                overlay.remove();
                resolve(result);
            };

            overlay.innerHTML = `
                <div class="confirm-box" role="dialog" aria-modal="true" aria-labelledby="${titleId}" tabindex="-1">
                    <div class="confirm-title" id="${titleId}">${this._esc(title)}</div>
                    <div class="confirm-message">${this._esc(message)}</div>
                    <div class="confirm-buttons">
                        <button class="btn btn-ghost" id="confirm-no">отмена</button>
                        <button class="btn btn-primary" id="confirm-yes">OK</button>
                    </div>
                </div>
            `;
            document.body.appendChild(overlay);

            overlay.querySelector('#confirm-yes').addEventListener('click', () => _close(true));
            overlay.querySelector('#confirm-no').addEventListener('click', () => _close(false));
            overlay.addEventListener('click', e => { if (e.target === overlay) _close(false); });
            FocusTrap.activate(overlay.querySelector('.confirm-box'), {
                initialFocus: '#confirm-yes',
                onEscape: () => _close(false)
            });
        });
    },

    /**
     * Styled prompt dialog (returns Promise<string|null>)
     */
    prompt(message, defaultValue = '', title = '', options = {}) {
        return new Promise(resolve => {
            const overlay = document.createElement('div');
            overlay.className = 'confirm-overlay';
            const titleId = 'prompt-title-' + Date.now();
            let resolved = false;
            const type = options?.type === 'password' ? 'password' : 'text';
            const autocomplete = options?.autocomplete ? ` autocomplete="${this._esc(options.autocomplete)}"` : '';
            const inputmode = options?.inputmode ? ` inputmode="${this._esc(options.inputmode)}"` : '';

            const _close = (result) => {
                if (resolved) return;
                resolved = true;
                const box = overlay.querySelector('.confirm-box');
                const input = overlay.querySelector('#prompt-input');
                if (input) input.value = '';
                FocusTrap.deactivate(box);
                overlay.remove();
                resolve(result);
            };

            overlay.innerHTML = `
                <div class="confirm-box" role="dialog" aria-modal="true" aria-labelledby="${titleId}" tabindex="-1">
                    <div class="confirm-title" id="${titleId}">${this._esc(title || message)}</div>
                    <div class="confirm-message">${this._esc(message)}</div>
                    <input class="confirm-input" id="prompt-input" type="${type}" value="${this._esc(defaultValue)}"${autocomplete}${inputmode}>
                    <div class="confirm-buttons">
                        <button class="btn btn-ghost" id="prompt-cancel">отмена</button>
                        <button class="btn btn-primary" id="prompt-ok">OK</button>
                    </div>
                </div>
            `;
            document.body.appendChild(overlay);

            const input = overlay.querySelector('#prompt-input');
            overlay.querySelector('#prompt-ok').addEventListener('click', () => _close(input.value));
            overlay.querySelector('#prompt-cancel').addEventListener('click', () => _close(null));
            overlay.addEventListener('click', e => { if (e.target === overlay) _close(null); });

            input.addEventListener('keydown', e => { if (e.key === 'Enter') _close(input.value); });
            FocusTrap.activate(overlay.querySelector('.confirm-box'), {
                initialFocus: '#prompt-input',
                onEscape: () => _close(null)
            });
            setTimeout(() => input.select(), 50);
        });
    },

    _esc(s) { return escHtml(s || ''); }
};

/* ═══ Undo/Redo stack for node editor ═══ */
const UndoStack = {
    _stack: [],
    _index: -1,
    MAX: 50,

    /** Save a snapshot. Call after every meaningful change. */
    push(nodes, edges) {
        const snap = JSON.stringify({ nodes, edges });
        // If we're in the middle of the stack (after undo), trim future
        if (this._index < this._stack.length - 1) {
            this._stack = this._stack.slice(0, this._index + 1);
        }
        // Don't save if identical to current
        if (this._stack.length && this._stack[this._stack.length - 1] === snap) return;
        this._stack.push(snap);
        if (this._stack.length > this.MAX) this._stack.shift();
        this._index = this._stack.length - 1;
    },

    undo() {
        if (this._index <= 0) return null;
        this._index--;
        return JSON.parse(this._stack[this._index]);
    },

    redo() {
        if (this._index >= this._stack.length - 1) return null;
        this._index++;
        return JSON.parse(this._stack[this._index]);
    },

    canUndo() { return this._index > 0; },
    canRedo() { return this._index < this._stack.length - 1; },

    clear() { this._stack = []; this._index = -1; }
};

/* ═══ AutoSave ═══ */
const AutoSave = {
    _timer: null,
    _dirty: false,
    _manager: null,
    _mutationSeq: 0,
    DELAY: 2000, // ms after last change

    _persistenceEnabled() {
        const flags = (typeof WorkboardFlags !== 'undefined') ? WorkboardFlags : (typeof window !== 'undefined' ? window.WorkboardFlags : null);
        if (!flags) return false;
        if (typeof flags.isEnabled === 'function') return !!flags.isEnabled('persistenceTask');
        if (typeof flags.enabled === 'function') return !!flags.enabled('persistenceTask');
        return !!flags.persistenceTask;
    },

    _ensureManager() {
        if (!this._persistenceEnabled()) return null;
        const Manager = (typeof WorkboardPersistenceManager !== 'undefined')
            ? WorkboardPersistenceManager
            : (typeof window !== 'undefined' ? window.WorkboardPersistenceManager : null);
        if (!Manager) return null;
        if (!this._manager) this._manager = new Manager();
        return this._manager;
    },

    _payload() {
        if (typeof TaskEditor === 'undefined') return null;
        if (typeof TaskEditor.persistencePayload === 'function') return TaskEditor.persistencePayload();
        return null;
    },

    markDirty() {
        this._dirty = true;
        this._mutationSeq = (this._mutationSeq || 0) + 1;
        const manager = this._ensureManager();
        const payload = manager ? this._payload() : null;
        if (manager && payload) manager.markDirty(payload);
        this._updateBadge(manager ? 'dirty' : 'saving');
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this._doSave(), this.DELAY);
    },

    async _doSave() {
        if (!this._dirty) return;
        if (typeof TaskEditor === 'undefined' || !TaskEditor.taskId) return;
        const manager = this._ensureManager();
        const startedSeq = this._mutationSeq || 0;
        const inFlight = TaskEditor._saveInFlight || null;
        const inFlightSeq = inFlight ? TaskEditor._saveInFlightSeq : null;
        const reusedStaleInFlight = Number.isFinite(inFlightSeq) && inFlightSeq < startedSeq;
        try {
            this._updateBadge('saving');
            if (manager && reusedStaleInFlight) {
                try { await inFlight; } catch (_) {}
                this._dirty = true;
                this._updateBadge('saving');
                clearTimeout(this._timer);
                this._timer = setTimeout(() => this._doSave(), 0);
                return;
            }
            const ok = manager
                ? await manager.save(() => TaskEditor.save(true, { startedSeq }))
                : await TaskEditor.save(true, { startedSeq }); // silent = true
            if (ok === false) throw new Error('TaskEditor.save returned false');
            if (ok !== false && !manager && (reusedStaleInFlight || (this._mutationSeq || 0) !== startedSeq)) {
                this._dirty = true;
                this._updateBadge('saving');
                clearTimeout(this._timer);
                this._timer = setTimeout(() => this._doSave(), 0);
                return;
            }
            if (!manager || !manager.isDirty()) {
                this._dirty = false;
                this._updateBadge('saved');
                setTimeout(() => this._updateBadge(''), 2000);
            } else {
                this._dirty = true;
                this._updateBadge('saving');
            }
        } catch (e) {
            console.error('AutoSave error:', e);
            this._updateBadge('error');
        }
    },

    async flush() {
        if (!this._dirty && !(typeof TaskEditor !== 'undefined' && TaskEditor.isNew)) return true;
        try { clearTimeout(this._timer); } catch (_) {}
        const manager = this._ensureManager();
        let ok;
        if (manager) {
            const payload = this._payload();
            if (payload && !manager.isDirty()) manager.markDirty(payload);
            for (let attempt = 0; attempt < 3; attempt++) {
                const startedSeq = this._mutationSeq || 0;
                const inFlight = TaskEditor._saveInFlight || null;
                const inFlightSeq = inFlight ? TaskEditor._saveInFlightSeq : null;
                const reusedStaleInFlight = Number.isFinite(inFlightSeq) && inFlightSeq < startedSeq;
                if (reusedStaleInFlight) {
                    try { await inFlight; } catch (_) {}
                    this._dirty = true;
                    this._updateBadge('saving');
                    continue;
                }
                ok = await manager.save(() => TaskEditor.save(true, { startedSeq }));
                if (ok === false) {
                    this._updateBadge('error');
                    return false;
                }
                if ((this._mutationSeq || 0) === startedSeq && !manager.isDirty()) {
                    this._dirty = false;
                    this._updateBadge('saved');
                    return true;
                }
                this._dirty = true;
                this._updateBadge('saving');
            }
            return false;
        }
        for (let attempt = 0; attempt < 3; attempt++) {
            const startedSeq = this._mutationSeq || 0;
            const inFlightSeq = TaskEditor._saveInFlight ? TaskEditor._saveInFlightSeq : null;
            const reusedStaleInFlight = Number.isFinite(inFlightSeq) && inFlightSeq < startedSeq;
            ok = await TaskEditor.save(true, { startedSeq });
            if (ok === false) {
                this._updateBadge('error');
                return false;
            }
            if (!reusedStaleInFlight && (this._mutationSeq || 0) === startedSeq) {
                this._dirty = false;
                this._updateBadge('saved');
                return true;
            }
            this._dirty = true;
        }
        this._updateBadge('saving');
        return false;
    },

    _ackSaved(payload, response, startedSeq) {
        const manager = this._ensureManager();
        if (!manager) {
            if (startedSeq == null || (this._mutationSeq || 0) === startedSeq) {
                this._dirty = false;
                return true;
            }
            this._dirty = true;
            return false;
        }
        if (!manager.isDirty() && payload) manager.markDirty(payload);
        const acknowledged = manager.acknowledge(payload, response);
        this._dirty = manager.isDirty();
        return acknowledged;
    },

    _setStatusText(text) {
        const status = document.getElementById('task-save-status');
        if (status) status.textContent = text;
    },

    _updateBadge(state) {
        if (state === 'dirty') this._setStatusText('Есть несохранённые изменения');
        else if (state === 'saving') this._setStatusText('Сохраняем...');
        else if (state === 'saved') this._setStatusText('Сохранено · только что');
        else if (state === 'error') this._setStatusText('Ошибка сохранения');
        else if (state === 'conflict') this._setStatusText('Конфликт сохранения');
        else this._setStatusText('Сохранено');
    }
};

/* ═══ Hotkeys ═══ */
document.addEventListener('keydown', e => {
    // Don't capture when typing in inputs
    const tag = (e.target.tagName || '').toLowerCase();
    const isInput = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable;

    // Escape — close modal
    if (e.key === 'Escape') {
        const modalOverlay = document.getElementById('modal-overlay');
        if (modalOverlay && !modalOverlay.hidden && modalOverlay.style.display !== 'none') {
            Modal.hide();
            e.preventDefault();
            return;
        }
    }

    // Ctrl/Cmd + S — save
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (typeof Router !== 'undefined' && Router.current === 'task') {
            if (typeof TaskEditor !== 'undefined') {
                TaskEditor.save().then?.((ok)=>{
                    if (ok !== false && typeof AutoSave !== 'undefined') {
                        const manager=typeof AutoSave._ensureManager==='function'?AutoSave._ensureManager():null;
                        const stillDirty=manager
                            ?(typeof manager.isDirty==='function'?manager.isDirty():!!manager.hasUnsavedChanges?.())
                            :!!AutoSave._dirty;
                        AutoSave._dirty=stillDirty;
                        AutoSave._updateBadge(stillDirty?'dirty':'saved');
                    }
                });
            }
        }
        return;
    }

    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !isInput) {
        if (typeof Router !== 'undefined' && Router.current === 'task') {
            e.preventDefault();
            if (typeof TaskView !== 'undefined') TaskView.run();
        }
        return;
    }

    // Ctrl/Cmd + Z — undo (only in editor, not in inputs)
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey && !isInput) {
        if (typeof Router !== 'undefined' && Router.current === 'task') {
            e.preventDefault();
            const snap = UndoStack.undo();
            if (snap && typeof TaskEditor !== 'undefined') {
                TaskEditor.nodes = snap.nodes;
                TaskEditor.edges = snap.edges;
                TaskEditor.render();
                Toast.info('Отменено');
                AutoSave.markDirty();
            }
        }
        return;
    }

    // Ctrl/Cmd + Shift + Z — redo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey && !isInput) {
        if (typeof Router !== 'undefined' && Router.current === 'task') {
            e.preventDefault();
            const snap = UndoStack.redo();
            if (snap && typeof TaskEditor !== 'undefined') {
                TaskEditor.nodes = snap.nodes;
                TaskEditor.edges = snap.edges;
                TaskEditor.render();
                Toast.info('Повторено');
                AutoSave.markDirty();
            }
        }
        return;
    }

    // Ctrl/Cmd + Y — redo (Windows style)
    if ((e.ctrlKey || e.metaKey) && e.key === 'y' && !isInput) {
        if (typeof Router !== 'undefined' && Router.current === 'task') {
            e.preventDefault();
            const snap = UndoStack.redo();
            if (snap && typeof TaskEditor !== 'undefined') {
                TaskEditor.nodes = snap.nodes;
                TaskEditor.edges = snap.edges;
                TaskEditor.render();
                Toast.info('Повторено');
                AutoSave.markDirty();
            }
        }
        return;
    }

    // Delete / Backspace — remove selected node(s) (if any node is focused/last-clicked)
    if ((e.key === 'Delete' || e.key === 'Backspace') && !isInput) {
        if (typeof TaskEditor !== 'undefined' && typeof Router !== 'undefined' && Router.current === 'task') {
            if (TaskEditor._selectedIds && TaskEditor._selectedIds.size > 0) {
                e.preventDefault();
                TaskEditor.deleteSelected();
                Toast.info(TaskEditor._selectedIds.size > 1 ? 'Ноды удалены' : 'Нода удалена');
            } else if (TaskEditor._selectedNodeId) {
                e.preventDefault();
                TaskEditor.removeNode(TaskEditor._selectedNodeId);
                TaskEditor._selectedNodeId = null;
                Toast.info('Нода удалена');
            } else if (TaskEditor._selectedEdgeIndex != null) {
                e.preventDefault();
                const edgeIndex = TaskEditor._selectedEdgeIndex;
                TaskEditor._selectedEdgeIndex = null;
                TaskEditor.removeEdge(edgeIndex);
            }
        }
        return;
    }

    // A — quick add node dialog (only on canvas, not in inputs)
    if (e.key === 'a' && !isInput && !(e.ctrlKey || e.metaKey)) {
        if (typeof Router !== 'undefined' && Router.current === 'task') {
            if (typeof TaskEditor !== 'undefined') {
                e.preventDefault();
                TaskEditor.addNodeDialog();
            }
        }
        return;
    }

    // Ctrl/Cmd + C — copy selected nodes
    if ((e.ctrlKey || e.metaKey) && e.key === 'c' && !isInput) {
        if (typeof Router !== 'undefined' && Router.current === 'task') {
            if (typeof TaskEditor !== 'undefined' && TaskEditor._selectedIds && TaskEditor._selectedIds.size > 0) {
                e.preventDefault();
                TaskEditor.copySelected();
            }
        }
        return;
    }

    // Ctrl/Cmd + V — paste nodes
    if ((e.ctrlKey || e.metaKey) && e.key === 'v' && !isInput) {
        if (typeof Router !== 'undefined' && Router.current === 'task') {
            if (typeof TaskEditor !== 'undefined' && TaskEditor._clipboard) {
                e.preventDefault();
                TaskEditor.pasteClipboard();
            }
        }
        return;
    }

    // Ctrl/Cmd + A — select all nodes
    if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !isInput) {
        if (typeof Router !== 'undefined' && Router.current === 'task') {
            if (typeof TaskEditor !== 'undefined') {
                e.preventDefault();
                TaskEditor.selectAll();
            }
        }
        return;
    }

    // F — zoom to fit
    if (e.key === 'f' && !isInput && !(e.ctrlKey || e.metaKey)) {
        if (typeof Router !== 'undefined' && Router.current === 'task') {
            if (typeof TaskEditor !== 'undefined') {
                e.preventDefault();
                TaskEditor.zoomToFit();
            }
        }
        return;
    }
});

window.ToolsDock = {
    STORAGE_KEY: 'omoibot.taskTools.dockState',
    _bound: false,

    init() {
        const dock = document.getElementById('tools-dock');
        if (!dock) return;
        if (!this._bound) {
            this._bound = true;
            document.addEventListener('click', (event) => {
                const target = event.target instanceof Element ? event.target : null;
                if (target && target.closest('[data-action="tools-dock-toggle"]')) {
                    this.toggle();
                    return;
                }
                const current = document.getElementById('tools-dock');
                if (current && current.dataset.state === 'expanded' && target && !target.closest('#tools-dock')) {
                    this.set(false);
                }
            });
            document.addEventListener('keydown', (event) => {
                if (event.key !== 'Escape') return;
                const current = document.getElementById('tools-dock');
                if (current && current.dataset.state === 'expanded') this.set(false);
            });
            const observeText = (element) => {
                if (!element || typeof MutationObserver === 'undefined') return;
                new MutationObserver(() => this._refreshSummary()).observe(element, {
                    childList: true,
                    characterData: true,
                    subtree: true,
                    attributes: true,
                    attributeFilter: ['hidden', 'data-state'],
                });
            };
            observeText(document.getElementById('canvas-info'));
            observeText(document.getElementById('canvas-cost-summary'));
            observeText(document.getElementById('editor-zoom-label'));
            observeText(document.getElementById('balance'));
            try {
                if (typeof CostEstimator !== 'undefined' && CostEstimator.on) {
                    CostEstimator.on(() => this._refreshSummary());
                }
            } catch (_) {}
        }
        let saved = 'collapsed';
        try {
            saved = localStorage.getItem(this.STORAGE_KEY) || 'collapsed';
        } catch (_) {}
        this.set(saved === 'expanded');
    },

    toggle() {
        const dock = document.getElementById('tools-dock');
        if (!dock) return;
        if ((document.body.dataset.taskTab || 'tv-editor') === 'tv-jobs') {
            this.set(false);
            return;
        }
        this.set(dock.dataset.state !== 'expanded');
    },

    set(open) {
        const dock = document.getElementById('tools-dock');
        if (!dock) return;
        dock.dataset.state = open ? 'expanded' : 'collapsed';
        const body = document.getElementById('tools-dock-body');
        if (body) body.hidden = !open;
        const handle = dock.querySelector('[data-action="tools-dock-toggle"]');
        if (handle) handle.setAttribute('aria-expanded', String(open));
        try {
            localStorage.setItem(this.STORAGE_KEY, open ? 'expanded' : 'collapsed');
        } catch (_) {}
        this._refreshSummary();
    },

    _refreshSummary() {
        const dock = document.getElementById('tools-dock');
        const tabs = document.getElementById('tools-dock-handle-tabs');
        const info = document.getElementById('tools-dock-handle-info');
        const compact = document.getElementById('tools-dock-collapsed-summary');
        if (dock && dock.dataset.state === 'expanded') {
            if (tabs) tabs.textContent = '';
            if (info) info.textContent = '';
            if (compact) compact.textContent = '';
            return;
        }
        const active = document.body.dataset.taskTab || 'tv-editor';
        if (tabs) {
            tabs.textContent = '';
        }
        if (info) {
            info.textContent = '';
        }
        if (compact) {
            if (active === 'tv-jobs') {
                compact.textContent = '';
                return;
            }
            compact.textContent = this._collapsedEditorSummary();
            compact.title = compact.textContent;
        }
    },

    _collapsedEditorSummary() {
        const zoom = (document.getElementById('editor-zoom-label')?.textContent || '').trim();
        const snap = this._costSnapshot();
        const costSummary = document.getElementById('canvas-cost-summary');
        const costText = costSummary && !costSummary.hidden ? (costSummary.textContent || '') : '';
        const perRun = snap?.loading
            ? null
            : (snap ? Number(snap.perRun || snap.listPerRun || 0) : null)
                ?? this._extractCredits(costText, /1\s*запуск\s+(-?\d+(?:[.,]\d+)?)/i)
                ?? this._extractCredits(costText, /запуск:\s*(-?\d+(?:[.,]\d+)?)/i);
        const parts = [];
        if (snap?.loading || /считаем/i.test(costText)) {
            parts.push('запуск: расчет');
        } else if (Number.isFinite(perRun)) {
            parts.push(`запуск ${Math.round(perRun)} тк`);
        } else if (/бесплат/i.test(costText)) {
            parts.push('запуск 0 тк');
        }
        if (zoom) parts.push(`зум ${zoom}`);
        return parts.join(' · ');
    },

    _costSnapshot() {
        try {
            if (typeof CostEstimator !== 'undefined' && CostEstimator.snapshot) {
                return CostEstimator.snapshot();
            }
        } catch (_) {}
        return null;
    },

    _extractCredits(text, pattern) {
        const match = String(text || '').match(pattern);
        if (!match) return null;
        const value = Number(String(match[1]).replace(',', '.'));
        return Number.isFinite(value) ? value : null;
    },
};

window.DebuggerToolsDock = {
    STORAGE_KEY: 'omoibot.debuggerTools.dockState',
    _bound: false,

    init() {
        const dock = document.getElementById('debugger-tools-dock');
        if (!dock) return;
        if (!this._bound) {
            this._bound = true;
            document.addEventListener('click', (event) => {
                const target = event.target instanceof Element ? event.target : null;
                if (target && target.closest('[data-action="debugger-tools-dock-toggle"]')) {
                    this.toggle();
                    return;
                }
                const current = document.getElementById('debugger-tools-dock');
                if (current && current.dataset.state === 'expanded' && target && !target.closest('#debugger-tools-dock')) {
                    this.set(false);
                }
            });
            document.addEventListener('keydown', (event) => {
                if (event.key !== 'Escape') return;
                const current = document.getElementById('debugger-tools-dock');
                if (current && current.dataset.state === 'expanded') this.set(false);
            });
            const observeText = (element) => {
                if (!element || typeof MutationObserver === 'undefined') return;
                new MutationObserver(() => this._refreshSummary()).observe(element, {
                    childList: true,
                    characterData: true,
                    subtree: true,
                });
            };
            observeText(document.getElementById('debugger-info'));
            observeText(document.getElementById('debugger-zoom-label'));
        }
        let saved = 'collapsed';
        try {
            saved = localStorage.getItem(this.STORAGE_KEY) || 'collapsed';
        } catch (_) {}
        this.set(saved === 'expanded');
    },

    toggle() {
        const dock = document.getElementById('debugger-tools-dock');
        if (!dock) return;
        this.set(dock.dataset.state !== 'expanded');
    },

    set(open) {
        const dock = document.getElementById('debugger-tools-dock');
        if (!dock) return;
        dock.dataset.state = open ? 'expanded' : 'collapsed';
        const body = document.getElementById('debugger-tools-dock-body');
        if (body) body.hidden = !open;
        const handle = dock.querySelector('[data-action="debugger-tools-dock-toggle"]');
        if (handle) handle.setAttribute('aria-expanded', String(open));
        try {
            localStorage.setItem(this.STORAGE_KEY, open ? 'expanded' : 'collapsed');
        } catch (_) {}
        this._refreshSummary();
    },

    _refreshSummary() {
        const dock = document.getElementById('debugger-tools-dock');
        const tabs = document.getElementById('debugger-tools-dock-handle-tabs');
        const info = document.getElementById('debugger-tools-dock-handle-info');
        if (dock && dock.dataset.state === 'expanded') {
            if (tabs) tabs.textContent = '';
            if (info) info.textContent = '';
            return;
        }
        if (tabs) tabs.textContent = 'Доска';
        if (info) {
            const count = document.getElementById('debugger-info')?.textContent || '';
            const zoom = document.getElementById('debugger-zoom-label')?.textContent || '';
            info.textContent = [count, zoom].filter(Boolean).join(' · ');
        }
    },
};

/* web/js/chunk-loader.js */
/* ═══ Lazy frontend chunk loader ═══ */
const ChunkLoader={
    _manifestPromise:null,
    _loads:new Map(),

    _manifest(){
        if(!this._manifestPromise){
            const manifestUrl=window.AppURL?window.AppURL.resolve('/dist/manifest.json'):'/dist/manifest.json';
            const request=window.fetch(manifestUrl,{credentials:'same-origin'})
                .then(response=>{
                    if(!response.ok)throw new Error('Failed to load frontend manifest');
                    return response.json();
                })
                .then(manifest=>{
                    if(manifest?.version!==2)throw new Error('Unsupported frontend manifest version');
                    if(!manifest.assets?.js||typeof manifest.assets.js!=='object'){
                        throw new Error('Invalid frontend manifest');
                    }
                    return manifest;
                });
            this._manifestPromise=request;
            request.catch(()=>{
                if(this._manifestPromise===request)this._manifestPromise=null;
            });
        }
        return this._manifestPromise;
    },

    _url(name,manifest){
        const chunks=manifest.assets.js;
        if(!Object.prototype.hasOwnProperty.call(chunks,name)){
            throw new Error(`Unknown frontend chunk: ${name}`);
        }
        const raw=chunks[name];
        let url;
        try{url=new URL(raw,window.location.href)}
        catch(_error){throw new Error(`Unsafe frontend chunk URL: ${name}`)}
        const escapedName=String(name).replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
        const assetBase=String(window.AppConfig?.assetsPrefix||'/dist').replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
        const hashedPath=new RegExp(`^${assetBase}/assets/${escapedName}-[a-f0-9]{12}\\.js$`);
        if(
            url.origin!==window.location.origin
            ||url.username||url.password
            ||url.search
            ||url.hash
            ||!hashedPath.test(url.pathname)
        )throw new Error(`Unsafe frontend chunk URL: ${name}`);
        return url.href;
    },

    load(name){
        const key=String(name||'');
        const cached=this._loads.get(key);
        if(cached)return cached;

        const request=this._manifest().then(manifest=>{
            const src=this._url(key,manifest);
            return new Promise((resolve,reject)=>{
                const script=document.createElement('script');
                script.src=src;
                script.async=true;
                script.dataset.chunk=key;
                script.addEventListener('load',()=>resolve(),{once:true});
                script.addEventListener('error',()=>{
                    script.remove();
                    reject(new Error(`Failed to load frontend chunk: ${key}`));
                },{once:true});
                document.head.appendChild(script);
            });
        });
        this._loads.set(key,request);
        request.catch(()=>{
            if(this._loads.get(key)===request)this._loads.delete(key);
        });
        return request;
    }
};

window.ChunkLoader=ChunkLoader;

/* web/js/cookie-consent.js */
const CookieConsent = {
    STORAGE_KEY: 'omoibot_cookie_consent_v1',
    ACCEPTED_VALUE: 'accepted',
    NOTICE_ID: 'cookie-consent',

    _storageGet(){
        try{return window.localStorage.getItem(this.STORAGE_KEY);}catch(_e){return '';}
    },

    _storageSet(){
        try{window.localStorage.setItem(this.STORAGE_KEY,this.ACCEPTED_VALUE);}catch(_e){}
    },

    hasAccepted(){
        return this._storageGet()===this.ACCEPTED_VALUE;
    },

    init(){
        if(!document.body||this.hasAccepted()||document.getElementById(this.NOTICE_ID))return;
        const notice=document.createElement('section');
        notice.id=this.NOTICE_ID;
        notice.className='cookie-consent';
        notice.setAttribute('role','region');
        notice.setAttribute('aria-label','Уведомление о cookie');
        notice.innerHTML=`
            <div class="cookie-consent-text">
                <strong>Cookie</strong>
                <span>Мы используем технические cookie для входа, безопасности и реферальной программы.</span>
                <a href="/legal/privacy">Политика</a>
            </div>
            <button class="btn btn-primary cookie-consent-btn" type="button" data-action="cookie-consent-accept">Согласен</button>
        `;
        document.body.appendChild(notice);
    },

    accept(){
        this._storageSet();
        document.getElementById(this.NOTICE_ID)?.remove();
    }
};

document.addEventListener('click',(event)=>{
    const target=event.target instanceof Element ? event.target.closest('[data-action="cookie-consent-accept"]') : null;
    if(!target)return;
    event.preventDefault();
    CookieConsent.accept();
});

if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',()=>CookieConsent.init(),{once:true});
}else{
    CookieConsent.init();
}

window.CookieConsent=CookieConsent;

/* web/js/style-hydrator.js */
/* CSP-safe style attribute hydration. */
const StyleHydrator=(()=>{
    const BLOCKED_VALUE=/url\s*\(|expression\s*\(|@import|javascript:|behavior\s*:/i;
    const MAX_UI_RADIUS_PX=12;
    const SAFE_STYLE_TOKENS=new Set([
        '--accent','--accent-light','--bg','--bg-card','--bg-input','--bg-secondary','--border',
        '--danger','--green','--omb-bg','--omb-bg-soft','--omb-chalk','--omb-cloud',
        '--omb-electric-cobalt','--omb-err','--omb-err-04','--omb-error','--omb-fog',
        '--omb-graphite','--omb-grid-line','--omb-ink','--omb-ink-dim','--omb-ink-mute',
        '--omb-line','--omb-line-soft','--omb-mist','--omb-paper','--omb-pebble',
        '--omb-selection-fill','--omb-signal-blue','--omb-steel','--omb-stone',
        '--red','--text','--text-muted','--text-primary','--text-secondary',
    ]);
    const ALLOWED_PROPS=new Set([
        '--nc','accent-color','align-items','align-self','background','border','border-color','border-radius',
        'color','cursor','display','flex','flex-direction','flex-shrink','flex-wrap',
        'font-family','font-size','font-weight','gap','grid-template-columns','height',
        'justify-content','left','line-height','margin','margin-bottom','margin-left',
        'margin-right','margin-top','max-height','max-width','min-width','object-fit',
        'opacity','overflow','overflow-y','padding','padding-left','padding-right',
        'pointer-events','position','text-align','text-overflow','top','transform',
        'transition','user-select','white-space','width','word-break'
    ]);
    const LENGTH='(?:0|auto|[-]?[0-9]{1,4}(?:px|%|vh|vw|fr|em|rem)|var\\(--[a-z0-9_-]+\\))';
    const COORD_LENGTH='(?:0|auto|[-]?[0-9]{1,6}(?:\\.[0-9]{1,16})?(?:px|%|vh|vw|em|rem)|var\\(--[a-z0-9_-]+\\))';
    const TOKEN_VALUE='var\\(--[a-z0-9_-]+\\)';
    const COLOR='(?:transparent|currentColor|inherit|#[0-9a-f]{3,8}|rgb\\([0-9,.\\s/]+\\)|rgba\\([0-9,.\\s/]+\\)|'+TOKEN_VALUE+')';
    const FONT_TOKEN='(?:var\\(--font\\)|var\\(--font-mono\\)|var\\(--omb-font-display\\)|var\\(--omb-font-mono\\))';
    function usesSafeToken(value){
        const matches=String(value||'').match(/var\(--[a-z0-9_-]+\)/gi)||[];
        return matches.every(match=>SAFE_STYLE_TOKENS.has(match.slice(4,-1)));
    }
    function validateRadius(value){
        const raw=String(value||'').trim();
        if(raw==='50%'||raw==='999px')return true;
        const match=raw.match(/^([0-9]{1,2})px$/);
        if(!match)return /^var\(--omb-r-(?:pixel|sm|md|lg|xl)\)$/.test(raw);
        return Number(match[1])<=MAX_UI_RADIUS_PX;
    }
    const VALUE_OK={
        display:/^(flex|grid|block|inline-block|none)$/i,
        color:new RegExp(`^${COLOR}$`,'i'),
        background:new RegExp(`^${COLOR}$`,'i'),
        'border-color':new RegExp(`^${COLOR}$`,'i'),
        'accent-color':new RegExp(`^${COLOR}$`,'i'),
        gap:/^\d{1,3}px$/,
        margin:new RegExp(`^${LENGTH}(\\s+${LENGTH}){0,3}$`),
        'margin-bottom':new RegExp(`^${LENGTH}$`),
        'margin-left':new RegExp(`^${LENGTH}$`),
        'margin-right':new RegExp(`^${LENGTH}$`),
        'margin-top':new RegExp(`^${LENGTH}$`),
        padding:new RegExp(`^${LENGTH}(\\s+${LENGTH}){0,3}$`),
        'padding-left':new RegExp(`^${LENGTH}$`),
        'padding-right':new RegExp(`^${LENGTH}$`),
        width:new RegExp(`^${LENGTH}$`),
        height:new RegExp(`^(?:100%|${LENGTH}|calc\\([0-9vhvwpx%\\s+\\-]+\\))$`),
        'min-width':new RegExp(`^${LENGTH}$`),
        'max-width':new RegExp(`^(?:none|${LENGTH})$`),
        'max-height':new RegExp(`^(?:none|${LENGTH})$`),
        flex:new RegExp(`^[0-9](\\s+[0-9])?(\\s+${LENGTH})?$`),
        'flex-direction':/^(row|column|row-reverse|column-reverse)$/,
        'flex-wrap':/^(wrap|nowrap|wrap-reverse)$/,
        'flex-shrink':/^[0-9]$/,
        'align-items':/^(stretch|center|flex-start|flex-end|baseline)$/,
        'align-self':/^(auto|stretch|center|flex-start|flex-end|baseline)$/,
        'justify-content':/^(center|flex-start|flex-end|space-between|space-around|space-evenly)$/,
        'grid-template-columns':/^(repeat\(\d,\s*minmax\(0,\s*1fr\)\)|[0-9fr\s]+)$/,
        'text-align':/^(left|center|right)$/,
        'font-size':/^[0-9]{1,3}px$/,
        'font-weight':/^(normal|bold|[1-9]00)$/,
        'font-family':new RegExp(`^${FONT_TOKEN}$`),
        'line-height':/^[0-9.]{1,4}$/,
        opacity:/^(0(\.\d{1,2})?|1(\.0{1,2})?)$/,
        'border-radius':validateRadius,
        border:new RegExp(`^[0-9]{1,2}px\\s+(solid|dashed|dotted)\\s+${COLOR}$`,'i'),
        cursor:/^(pointer|grab|default|text)$/,
        overflow:/^(auto|hidden|visible|scroll)$/,
        'overflow-y':/^(auto|hidden|visible|scroll)$/,
        'pointer-events':/^(none|auto)$/,
        position:/^(absolute|relative|static|fixed)$/,
        left:new RegExp(`^${COORD_LENGTH}$`),
        top:new RegExp(`^${COORD_LENGTH}$`),
        transform:/^translate\(-50%,\s*-50%\)$/,
        transition:/^[a-z-]+\s+[0-9.]+s$/,
        'user-select':/^(none|auto|text)$/,
        'white-space':/^(nowrap|normal|pre-wrap)$/,
        'word-break':/^(break-all|break-word|normal)$/,
        'text-overflow':/^(ellipsis|clip)$/,
        'object-fit':/^(contain|cover|fill|none)$/,
        '--nc':new RegExp(`^${COLOR}$`,'i')
    };

    function apply(el){
        if(!(el instanceof Element))return;
        const raw=el.getAttribute('data-style');
        if(!raw)return;
        raw.split(';').forEach(part=>{
            const idx=part.indexOf(':');
            if(idx<=0)return;
            const prop=part.slice(0,idx).trim().toLowerCase();
            const value=part.slice(idx+1).trim();
            if(!prop||!value||!ALLOWED_PROPS.has(prop)||BLOCKED_VALUE.test(value))return;
            const validator=VALUE_OK[prop];
            if(!usesSafeToken(value))return;
            if(validator){
                const ok=typeof validator==='function'?validator(value):validator.test(value);
                if(!ok)return;
            }
            el.style.setProperty(prop,value);
        });
        el.removeAttribute('data-style');
    }

    function hydrate(root=document){
        if(root instanceof Element&&root.hasAttribute('data-style'))apply(root);
        root.querySelectorAll?.('[data-style]').forEach(apply);
    }

    function init(){
        hydrate(document);
        const observer=new MutationObserver(records=>{
            records.forEach(record=>{
                record.addedNodes.forEach(node=>{
                    if(node instanceof Element)hydrate(node);
                });
            });
        });
        observer.observe(document.documentElement,{childList:true,subtree:true});
    }

    return {init,hydrate};
})();

StyleHydrator.init();

/* web/js/theme.js */
(function(){
    const THEME_STORAGE_KEY='omoibot.theme';
    const THEME_META_COLORS={light:'#ffffff',dark:'#0f1117'};
    const BRAND_MARK_LIGHT='/assets/omoibot-mark.svg';
    const BRAND_MARK_DARK='/assets/omoibot-mark-dark.svg';
    const THEME_LABELS={
        light:{label:'светлая',title:'Темная тема',aria:'Включить темную тему'},
        dark:{label:'темная',title:'Светлая тема',aria:'Включить светлую тему'}
    };

    function normalizeTheme(value){
        return value==='dark'?'dark':'light';
    }

    function readStoredTheme(){
        try{
            const stored=localStorage.getItem(THEME_STORAGE_KEY)||'light';
            return normalizeTheme(stored);
        }catch(e){
            return 'light';
        }
    }

    function writeStoredTheme(theme){
        try{
            localStorage.setItem(THEME_STORAGE_KEY,theme);
        }catch(e){}
    }

    function updateMeta(theme){
        const meta=document.querySelector('meta[name="theme-color"]');
        if(meta)meta.setAttribute('content',THEME_META_COLORS[theme]||THEME_META_COLORS.light);
    }

    function syncBrandMarks(theme=currentTheme()){
        const next=normalizeTheme(theme);
        const src=next==='dark'?BRAND_MARK_DARK:BRAND_MARK_LIGHT;
        document.querySelectorAll('[data-brand-mark]').forEach((img)=>{
            if(img.getAttribute('src')!==src)img.setAttribute('src',src);
        });
    }

    function syncButtons(theme=currentTheme()){
        const next=normalizeTheme(theme);
        const labels=THEME_LABELS[next]||THEME_LABELS.light;
        document.querySelectorAll('[data-action="theme-toggle"]').forEach((button)=>{
            button.setAttribute('aria-pressed',next==='dark'?'true':'false');
            button.setAttribute('aria-label',labels.aria);
            button.setAttribute('title',labels.title);
            const label=button.querySelector('[data-theme-toggle-label]');
            if(label)label.textContent=labels.label;
            const icon=button.querySelector('[data-theme-toggle-icon]');
            if(icon)icon.dataset.themeIcon=next;
        });
        syncBrandMarks(next);
    }

    function applyTheme(value,{persist=false}={}){
        const theme=normalizeTheme(value);
        if(document.body)document.body.dataset.theme=theme;
        document.documentElement.dataset.theme=theme;
        document.documentElement.style.colorScheme=theme;
        updateMeta(theme);
        syncButtons(theme);
        if(persist)writeStoredTheme(theme);
        return theme;
    }

    function currentTheme(){
        return normalizeTheme(document.body?.dataset?.theme||document.documentElement.dataset.theme||'light');
    }

    function toggleTheme(){
        const next=currentTheme()==='dark'?'light':'dark';
        return applyTheme(next,{persist:true});
    }

    function initTheme(){
        applyTheme(readStoredTheme());
    }

    window.Theme={
        apply:applyTheme,
        current:currentTheme,
        sync:syncButtons,
        toggle:toggleTheme
    };

    if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',initTheme,{once:true});
    else initTheme();
})();

/* web/js/zoom.js */
/* ═══ Canvas Zoom Controller ═══
   Smooth zoom for MacBook trackpad (pinch) and mouse wheel.
   Supports two canvases: 'editor' and 'debugger'.
*/

const CanvasZoom = {
    canvases: {},
    MIN: 0.01,
    MAX: 3.0,
    MAX_CANVAS_RESERVE: 12000,
    MAX_CANVAS_VISIBLE_EXTENT: 24000,
    _globalWheelBound: false,
    _globalWheelHandler: null,

    _unbindCanvasHandlers(s) {
        if (!s?.scroller) return;
        if (s._wheelHandler) {
            s.scroller.removeEventListener('wheel', s._wheelHandler);
            s._wheelHandler = null;
        }
        if (s._pointerHandlers) {
            const { onPointerDown, onPointerMove, forget } = s._pointerHandlers;
            s.scroller.removeEventListener('pointerdown', onPointerDown);
            s.scroller.removeEventListener('pointermove', onPointerMove);
            s.scroller.removeEventListener('pointerup', forget);
            s.scroller.removeEventListener('pointercancel', forget);
            s.scroller.removeEventListener('lostpointercapture', forget);
            s._pointerHandlers = null;
        }
        s.scroller._zoomBound = false;
        s.scroller._pinchBound = false;
    },

    _unbindViewportHandler(s) {
        if (!s?.scroller) return;
        const handler = s._scrollHandler || s.scroller._viewportChangeHandler;
        if (handler) {
            s.scroller.removeEventListener('scroll', handler);
        }
        if (s.scroller._viewportChangeHandler === handler) {
            s.scroller._viewportChangeHandler = null;
        }
        s._scrollHandler = null;
        s.scroller._viewportChangeBound = false;
    },

    init(id) {
        // Re-init allowed (tab may have been hidden)
        const container = document.getElementById(id + '-canvas-container');
        const scroller = document.getElementById(id + '-scroller');
        const transform = document.getElementById(id + '-transform');
        const label = document.getElementById(id + '-zoom-label');
        if (!container || !scroller || !transform) return;

        const existing = this.canvases[id] || {};
        if (existing?.rafId) cancelAnimationFrame(existing.rafId);
        if (existing?.viewportRaf) cancelAnimationFrame(existing.viewportRaf);
        if (this.canvases[id]) {
            this._unbindCanvasHandlers(existing);
            this._unbindViewportHandler(existing);
        }
        const state = {
            container, scroller, transform, label,
            scale: Number.isFinite(existing.scale) ? existing.scale : 1,
            targetScale: Number.isFinite(existing.targetScale) ? existing.targetScale : 1,
            animating: false,
            scrollTargetX: 0,
            scrollTargetY: 0,
            rafId: null,
            viewportListeners: existing.viewportListeners || new Set(),
            viewportRaf: 0,
        };
        this.canvases[id] = state;
        this._syncVisualScale(state);

        if (!scroller._viewportChangeBound) {
            scroller._viewportChangeBound = true;
            const scrollHandler = () => this._scheduleViewportChange(id);
            scroller.addEventListener('scroll', scrollHandler, { passive: true });
            state._scrollHandler = scrollHandler;
            scroller._viewportChangeHandler = scrollHandler;
        }

        if (!scroller._zoomBound) {
            scroller._zoomBound = true;
            state._wheelHandler = (e) => {
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    this._zoomFromWheel(id, e);
                }
            };
            scroller.addEventListener('wheel', state._wheelHandler, { passive: false });
        }
        this._bindPointerGestures(id);
        this._bindGlobalWheel();
    },

    _bindPointerGestures(id) {
        const s = this.canvases[id];
        if (!s || s.scroller._pinchBound || typeof PointerEvent === 'undefined') return;
        s.scroller._pinchBound = true;
        const pointers = new Map();
        let pinchStartDistance = 0;
        let pinchStartScale = 1;
        let pan = null;

        const isCanvasChrome = (target) => {
            const isMobile = window.matchMedia?.('(max-width:760px)').matches;
            if(isMobile && target?.closest?.('.nd,.node,[data-debugger-drag-node]')){
                const interactive = target.closest(
                    '[data-mobile-node-drag],.port,[data-task-node-port],[data-debugger-port],button,a,input,textarea,select,video,audio'
                );
                return !!interactive;
            }
            return target?.closest?.('.nd,.node,.port,button,a,input,textarea,select,[data-debugger-drag-node],[data-task-node-port]');
        };
        const center = () => {
            const points = [...pointers.values()];
            return {
                x: (points[0].x + points[1].x) / 2,
                y: (points[0].y + points[1].y) / 2
            };
        };
        const distance = () => {
            const points = [...pointers.values()];
            return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        };
        const forget = (event) => {
            pointers.delete(event.pointerId);
            if (pan?.id === event.pointerId) pan = null;
            if (pointers.size < 2) {
                pinchStartDistance = 0;
            }
        };

        const onPointerDown = (event) => {
            if (event.pointerType === 'mouse') return;
            pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            try { s.scroller.setPointerCapture(event.pointerId); } catch (_e) {}
            if (pointers.size === 1 && !isCanvasChrome(event.target)) {
                pan = {
                    id: event.pointerId,
                    x: event.clientX,
                    y: event.clientY,
                    scrollLeft: s.scroller.scrollLeft,
                    scrollTop: s.scroller.scrollTop
                };
            }
            if (pointers.size === 2) {
                pan = null;
                pinchStartDistance = distance();
                pinchStartScale = s.targetScale || s.scale || 1;
                event.preventDefault();
            }
        };
        const onPointerMove = (event) => {
            if (!pointers.has(event.pointerId)) return;
            pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            if (pointers.size >= 2 && pinchStartDistance > 0) {
                const nextScale = Math.max(this.MIN, Math.min(this.MAX, pinchStartScale * distance() / pinchStartDistance));
                const c = center();
                event.preventDefault();
                this._smoothZoom(id, nextScale, c.x, c.y);
                return;
            }
            if (pan?.id === event.pointerId) {
                event.preventDefault();
                s.scroller.scrollLeft = pan.scrollLeft - (event.clientX - pan.x);
                s.scroller.scrollTop = pan.scrollTop - (event.clientY - pan.y);
            }
        };
        s._pointerHandlers = { onPointerDown, onPointerMove, forget };

        s.scroller.addEventListener('pointerdown', onPointerDown, { passive: false });
        s.scroller.addEventListener('pointermove', onPointerMove, { passive: false });
        s.scroller.addEventListener('pointerup', forget);
        s.scroller.addEventListener('pointercancel', forget);
        s.scroller.addEventListener('lostpointercapture', forget);
    },

    _bindGlobalWheel() {
        if (this._globalWheelBound) return;
        this._globalWheelBound = true;
        this._globalWheelHandler = (e) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            const activeId = (typeof Router !== 'undefined' && Router.current === 'debugger')
                ? 'debugger'
                : (typeof Router !== 'undefined' && Router.current === 'task')
                    ? 'editor'
                    : null;
            if (!activeId || !this.canvases[activeId]) return;
            const ownsGesture = e.target.closest(
                `#${activeId}-canvas-container,#${activeId}-scroller,.${activeId}-topbar,.canvas-toolbar,.tools-dock`
            );
            if (!ownsGesture || e.target.closest(`#${activeId}-scroller`)) return;
            e.preventDefault();
            this._zoomFromWheel(activeId, e);
        };
        document.addEventListener('wheel', this._globalWheelHandler, { passive: false });
    },

    _unbindGlobalWheelIfIdle() {
        if (Object.keys(this.canvases || {}).length) return;
        if (this._globalWheelHandler) {
            document.removeEventListener('wheel', this._globalWheelHandler);
        }
        this._globalWheelHandler = null;
        this._globalWheelBound = false;
    },

    _zoomFromWheel(id, e) {
        const s = this.canvases[id];
        if (!s) return;
        // Trackpad pinch: deltaY is small & fractional (±1..±10)
        // Mouse ctrl+wheel: deltaY is larger (±100)
        const intensity = Math.abs(e.deltaY) > 50 ? 0.002 : 0.008;
        const factor = 1 - e.deltaY * intensity;
        const newScale = Math.max(this.MIN, Math.min(this.MAX, s.targetScale * factor));
        this._smoothZoom(id, newScale, e.clientX, e.clientY);
    },

    reset(id) {
        const s = this.canvases[id];
        if (!s) return;
        s.scale = 1;
        s.targetScale = 1;
        s.animating = false;
        s.scrollTargetX = 0;
        s.scrollTargetY = 0;
        s.transform.style.transform = 'scale(1)';
        this._syncVisualScale(s);
        s.scroller.scrollLeft = 0;
        s.scroller.scrollTop = 0;
        this._updateLabel(s);
    },

    // Programmatic zoom to a viewport coordinate (cx, cy in client space).
    // Used by mobile double-tap and other automated zoom targets. Clamps to
    // [MIN, MAX] like other paths.
    zoomToPoint(id, cx, cy, scale) {
        const s = this.canvases[id];
        if (!s) return;
        const target = Math.max(this.MIN, Math.min(this.MAX, Number(scale) || 1));
        this._smoothZoom(id, target, cx, cy);
    },

    _smoothZoom(id, newScale, cx, cy) {
        const s = this.canvases[id];
        if (!s) return;

        s.targetScale = Math.round(newScale * 1000) / 1000;
        if (s.targetScale < this.MIN) s.targetScale = this.MIN;
        if (s.targetScale > this.MAX) s.targetScale = this.MAX;

        // Cursor anchor point in content coordinates
        const rect = s.scroller.getBoundingClientRect();
        const rawAnchorX = cx == null ? NaN : Number(cx);
        const rawAnchorY = cy == null ? NaN : Number(cy);
        const anchorX = Number.isFinite(rawAnchorX) ? rawAnchorX : rect.left + rect.width / 2;
        const anchorY = Number.isFinite(rawAnchorY) ? rawAnchorY : rect.top + rect.height / 2;
        const cursorX = anchorX - rect.left;
        const cursorY = anchorY - rect.top;

        // Point in content space under cursor (before zoom)
        const contentX = (s.scroller.scrollLeft + cursorX) / s.scale;
        const contentY = (s.scroller.scrollTop + cursorY) / s.scale;

        // Where that point should be after zoom
        s.scrollTargetX = contentX * s.targetScale - cursorX;
        s.scrollTargetY = contentY * s.targetScale - cursorY;

        if (!s.animating) {
            s.animating = true;
            this._animate(id);
        }
    },

    _animate(id) {
        const s = this.canvases[id];
        if (!s) return;

        const lerp = 0.25;
        const diff = Math.abs(s.scale - s.targetScale);

        if (diff < 0.001) {
            s.scale = s.targetScale;
            this._syncVisualScale(s);
            s.transform.style.transform = `scale(${s.scale})`;
            s.scroller.scrollLeft = s.scrollTargetX;
            s.scroller.scrollTop = s.scrollTargetY;
            this._ensureCanvasExtent(s);
            s.animating = false;
            s.rafId = null;
            this._updateLabel(s);
            this._scheduleViewportChange(id);
            return;
        }

        s.scale += (s.targetScale - s.scale) * lerp;

        const curScrollLeft = s.scroller.scrollLeft;
        const curScrollTop = s.scroller.scrollTop;
        const nextScrollLeft = curScrollLeft + (s.scrollTargetX - curScrollLeft) * lerp;
        const nextScrollTop = curScrollTop + (s.scrollTargetY - curScrollTop) * lerp;

        s.transform.style.transform = `scale(${s.scale})`;
        s.scroller.scrollLeft = nextScrollLeft;
        s.scroller.scrollTop = nextScrollTop;

        this._updateLabel(s);
        this._scheduleViewportChange(id);
        s.rafId = requestAnimationFrame(() => {
            s.rafId = null;
            this._animate(id);
        });
    },

    destroy(id) {
        const s = this.canvases[id];
        if (!s) return;
        if (s.rafId) cancelAnimationFrame(s.rafId);
        if (s.viewportRaf) cancelAnimationFrame(s.viewportRaf);
        this._unbindCanvasHandlers(s);
        this._unbindViewportHandler(s);
        s.viewportListeners?.clear();
        delete this.canvases[id];
        this._unbindGlobalWheelIfIdle();
    },

    _updateLabel(s) {
        if (s.label) s.label.textContent = Math.round(s.scale * 100) + '%';
    },

    _syncVisualScale(s) {
        if (!s?.transform?.style) return;
        const safeScale = Math.max(0.1, Number(s.scale) || 1);
        s.transform.style.setProperty('--workboard-crt-line-step', `${(3 / safeScale).toFixed(3)}px`);
        s.transform.style.setProperty('--workboard-crt-line-thickness', `${(1 / safeScale).toFixed(3)}px`);
        const gridScale = Math.max(this.MIN, Number(s.scale) || 1);
        const gridBaseStep = 40;
        const minGridScreenStep = 8;
        const gridMultiplier = Math.max(1, Math.ceil(minGridScreenStep / (gridBaseStep * gridScale)));
        s.transform.style.setProperty('--workboard-grid-step', `${gridBaseStep * gridMultiplier}px`);
        s.transform.style.setProperty('--workboard-grid-line-thickness', `${(1 / gridScale).toFixed(3)}px`);
        if (!s.animating) this._ensureCanvasExtent(s);
    },

    _ensureCanvasExtent(s) {
        const canvas = s?.transform?.querySelector?.('.node-canvas');
        const scroller = s?.scroller;
        if (!canvas || !scroller) return;

        const scale = Math.max(this.MIN, Number(s.scale) || 1);
        const reserveRight = Math.min(this.MAX_CANVAS_RESERVE, Math.max(900, Math.ceil((scroller.clientWidth || 0) / scale)));
        const reserveBottom = Math.min(this.MAX_CANVAS_RESERVE, Math.max(600, Math.ceil((scroller.clientHeight || 0) / scale)));
        const visibleRight = Math.min(this.MAX_CANVAS_VISIBLE_EXTENT, Math.ceil(((scroller.scrollLeft || 0) + (scroller.clientWidth || 0)) / scale));
        const visibleBottom = Math.min(this.MAX_CANVAS_VISIBLE_EXTENT, Math.ceil(((scroller.scrollTop || 0) + (scroller.clientHeight || 0)) / scale));

        let maxX = 0;
        let maxY = 0;
        canvas.querySelectorAll?.('.nd').forEach((node) => {
            const left = parseInt(node.style.left, 10) || node.offsetLeft || 0;
            const top = parseInt(node.style.top, 10) || node.offsetTop || 0;
            maxX = Math.max(maxX, left + 340);
            maxY = Math.max(maxY, top + 220);
        });

        const nextWidth = Math.max(4000, visibleRight, maxX + reserveRight);
        const nextHeight = Math.max(3000, visibleBottom, maxY + reserveBottom);
        const currentWidth = parseInt(canvas.style.minWidth || 0, 10) || 0;
        const currentHeight = parseInt(canvas.style.minHeight || 0, 10) || 0;
        if (nextWidth > currentWidth) canvas.style.minWidth = nextWidth + 'px';
        if (nextHeight > currentHeight) canvas.style.minHeight = nextHeight + 'px';
    },

    zoomIn(id) {
        const s = this.canvases[id];
        if (!s) return;
        this._smoothZoom(id, Math.min(this.MAX, s.targetScale * 1.2));
    },

    zoomOut(id) {
        const s = this.canvases[id];
        if (!s) return;
        this._smoothZoom(id, Math.max(this.MIN, s.targetScale / 1.2));
    },

    zoomReset(id) {
        const s = this.canvases[id];
        if (!s) return;
        this._smoothZoom(id, 1);
    },

    clientPointToCanvas(id, clientX, clientY, fallbackCanvas = null) {
        const state = this.canvases[id] || {};
        const scroller = state.scroller || document.getElementById(id + '-scroller');
        const canvas = fallbackCanvas || (
            id === 'debugger'
                ? document.getElementById('debug-node-canvas')
                : document.getElementById('node-canvas')
        );
        const host = scroller || canvas;
        const rect = host?.getBoundingClientRect?.() || { left: 0, top: 0 };
        const rawScale = Number(state.scale || this.getScale?.(id) || 1);
        const scale = Number.isFinite(rawScale) && rawScale > 0 ? rawScale : 1;
        const scrollLeft = scroller ? (scroller.scrollLeft || 0) : 0;
        const scrollTop = scroller ? (scroller.scrollTop || 0) : 0;
        return {
            x: (Number(clientX || 0) - rect.left + scrollLeft) / scale,
            y: (Number(clientY || 0) - rect.top + scrollTop) / scale
        };
    },

    canvasViewport(id, margin = 800) {
        const state = this.canvases[id] || {};
        const scroller = state.scroller || document.getElementById(id + '-scroller');
        const rawScale = Number(state.scale || this.getScale?.(id) || 1);
        const scale = Number.isFinite(rawScale) && rawScale > 0 ? rawScale : 1;
        const left = ((scroller?.scrollLeft || 0) / scale) - margin;
        const top = ((scroller?.scrollTop || 0) / scale) - margin;
        const right = (((scroller?.scrollLeft || 0) + (scroller?.clientWidth || 0)) / scale) + margin;
        const bottom = (((scroller?.scrollTop || 0) + (scroller?.clientHeight || 0)) / scale) + margin;
        return { left, top, right, bottom };
    },

    onViewportChange(id, callback) {
        if(typeof callback!=='function')return ()=>{};
        const state=this.canvases[id]||(this.canvases[id]={});
        if(!state.viewportListeners)state.viewportListeners=new Set();
        state.viewportListeners.add(callback);
        return ()=>state.viewportListeners?.delete(callback);
    },

    _scheduleViewportChange(id) {
        const state=this.canvases[id];
        if(!state||state.viewportRaf)return;
        state.viewportRaf=requestAnimationFrame(()=>{
            state.viewportRaf=0;
            state.viewportListeners?.forEach(callback=>{
                try{callback(this.canvasViewport(id,900));}catch(_e){}
            });
        });
    },

    getScale(id) {
        return this.canvases[id] ? this.canvases[id].scale : 1;
    }
};

// Shared modules and lexical callers must use one viewport controller.
window.CanvasZoom = CanvasZoom;

/* web/js/auth.js */
/* ═══ Auth and session lifecycle ═══ */
const REF_CODE_RE = /^[A-HJ-NP-Z2-9]{1,32}$/;
const REF_COOKIE_NAME = window.AppConfig?.browserNamespace?`cg_${window.AppConfig.browserNamespace}_ref`:'cg_ref';
const REF_COOKIE_PATH = window.AppConfig?.publicBasePath?window.AppConfig.publicBasePath+'/':'/';

const Auth = {
    booted:false,
    mode:'login',
    user:null,
    access:null,
    plan:null,
    pendingTotp:null,
    csrfToken:'',
    authDialogOpen:false,
    landingPlansLoaded:false,
    landingPlansLoading:false,
    publicConfig:null,
    publicConfigPromise:null,
    referralCode:'',
    referralPublicConfig:null,
    referralPublicConfigCode:'',
    referralPublicConfigPromise:null,
    referralPublicConfigPromiseCode:'',
    submissionGeneration:0,
    registrationBannerGeneration:0,
    authContextGeneration:0,
    mobileAccountMenuBound:false,
    _authFieldSelector:'#auth-email,#auth-password,#auth-display-name,#auth-invite-code,#auth-totp-code',

    authUrl(url){return window.AppURL?window.AppURL.resolve(url):url},
    _landingAccessPolicy(config){
        if(!config){
            return {label:'Проверить доступ',copy:'Проверим доступные способы входа',mode:'login'};
        }
        if(!config.registration_enabled){
            return {label:'Войти',copy:'Самостоятельная регистрация временно закрыта',mode:'login'};
        }
        if(config.invite_required){
            return {label:'Получить доступ',copy:'Для регистрации понадобится приглашение',mode:'register'};
        }
        return {label:'Создать аккаунт',copy:'Создайте аккаунт и начните с первого проекта',mode:'register'};
    },
    _applyLandingAccessPolicy(config){
        const policy=this._landingAccessPolicy(config);
        document.querySelectorAll('[data-landing-access-cta]').forEach((button)=>{
            button.textContent=policy.label;
            button.dataset.authMode=policy.mode;
        });
        document.querySelectorAll('[data-landing-access-copy]').forEach((node)=>{
            node.textContent=policy.copy;
        });
        if(!this._canRegister()&&this.authDialogOpen&&this.mode==='register'){
            this._invalidateAuthContext();
            this.mode='login';
            this._renderMode();
        }
    },
    _invalidateAuthContext(){
        this.authContextGeneration+=1;
        return this.authContextGeneration;
    },
    _canRegister(){
        return this.publicConfig?.registration_enabled===true;
    },
    loadPublicConfig(){
        if(this.publicConfigPromise)return this.publicConfigPromise;
        this.publicConfigPromise=(async()=>{
            let config=null;
            try{
                if(typeof API!=='undefined'&&API.get){
                    const response=await API.get('/api/auth/public-config',{allowUnauthorized:true,retry:false});
                    const valid=response&&!response.error
                        &&typeof response.invite_required==='boolean'
                        &&typeof response.registration_enabled==='boolean'
                        &&Number.isInteger(Number(response.password_min_length))
                        &&Number(response.password_min_length)>0;
                    if(valid)config={
                        invite_required:response.invite_required,
                        registration_enabled:response.registration_enabled,
                        password_min_length:Number(response.password_min_length),
                    };
                }
            }catch(_e){
                config=null;
            }
            this.publicConfig=config;
            this._applyLandingAccessPolicy(config);
            return config;
        })();
        return this.publicConfigPromise;
    },
    _authErrorMessage(response,fallback='Не удалось выполнить запрос'){
        const code=String(response?.code||'');
        if(code==='invalid_email')return 'Введите корректный email';
        if(code==='password_too_short'){
            const min=Number(response?.password_min_length||this.publicConfig?.password_min_length||0);
            return min?`Пароль должен быть не короче ${min} символов`:'Пароль слишком короткий';
        }
        if(code==='invite_required')return 'Введите код приглашения';
        if(code==='invalid_invite')return 'Код приглашения недействителен';
        if(code==='registration_disabled'||code==='registration_closed')return 'Самостоятельная регистрация временно закрыта';
        return response?.error||fallback;
    },
    _authErrorFieldId(code){
        return ({
            invalid_email:'auth-email',
            password_too_short:'auth-password',
            invite_required:'auth-invite-code',
            invalid_invite:'auth-invite-code',
        })[String(code||'')]||'';
    },
    _esc(value){
        return String(value??'').replace(/[&<>"']/g,(ch)=>({
            '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
        }[ch]));
    },
    _initials(name,email=''){
        const source=String(name||email||'Пользователь').trim();
        const parts=source.replace(/@.*/,'').split(/\s+/).filter(Boolean);
        const first=parts[0]?.[0]||'П';
        const second=parts.length>1?parts[1][0]:'';
        return (first+second).toUpperCase();
    },
    _safeAvatarUrl(url){
        const raw=String(url||'').trim();
        if(!raw)return '';
        try{
            if(raw.startsWith('/'))return this.authUrl(raw);
            const parsed=new URL(raw,window.location.origin);
            if(parsed.protocol==='http:'||parsed.protocol==='https:')return this.authUrl(parsed.href);
        }catch(_e){}
        return '';
    },
    _setHidden(el,hidden){
        if(!el)return;
        el.hidden=!!hidden;
        el.setAttribute('aria-hidden',hidden?'true':'false');
    },
    _isMobileAccountMenuEnabled(){
        if(typeof window==='undefined')return false;
        if(window.matchMedia)return window.matchMedia('(max-width: 760px)').matches;
        return Number(window.innerWidth||0)<=760;
    },
    _setMobileAccountMenuOpen(open){
        const menu=document.getElementById('mobile-account-menu');
        const trigger=document.querySelector('[data-action="mobile-account-toggle"]');
        if(!menu)return;
        const wasActive=!menu.hidden;
        const active=!!open && !!this.user && this._isMobileAccountMenuEnabled();
        menu.hidden=!active;
        menu.setAttribute('aria-hidden',active?'false':'true');
        document.body?.classList.toggle('mobile-account-menu-open',active);
        if(trigger)trigger.setAttribute('aria-expanded',active?'true':'false');
        if(active){
            this._syncMobileSecondaryNavState();
            if(!wasActive&&typeof FocusTrap!=='undefined'){
                FocusTrap.activate(menu,{
                    initialFocus:'[data-mobile-context-route]:not([hidden]),[data-mobile-route]',
                    onEscape:()=>this.closeMobileAccountMenu()
                });
            }
        }else if(wasActive){
            if(typeof FocusTrap!=='undefined')FocusTrap.deactivate(menu,true);
            else setTimeout(()=>trigger?.focus?.(),0);
        }
    },
    closeMobileAccountMenu(){
        this._setMobileAccountMenuOpen(false);
    },
    toggleMobileAccountMenu(){
        const menu=document.getElementById('mobile-account-menu');
        if(!menu||!this.user||!this._isMobileAccountMenuEnabled()){
            this.closeMobileAccountMenu();
            return;
        }
        this._setMobileAccountMenuOpen(menu.hidden);
    },
    _syncMobileSecondaryNavState(){
        const menu=document.getElementById('mobile-account-menu');
        if(!menu)return;
        const current=document.body?.dataset?.currentPage||'';
        menu.querySelectorAll('[data-mobile-route]').forEach((item)=>{
            const active=item.dataset.mobileRoute===current;
            item.classList.toggle('active',active);
            if(active)item.setAttribute('aria-current','page');
            else item.removeAttribute('aria-current');
        });
        menu.querySelectorAll('[data-mobile-context-route]').forEach((item)=>{
            item.hidden=item.dataset.mobileContextRoute!==current;
        });
    },
    _bindMobileAccountMenuChrome(){
        if(this.mobileAccountMenuBound)return;
        this.mobileAccountMenuBound=true;
        document.addEventListener('click',(event)=>{
            const menu=document.getElementById('mobile-account-menu');
            if(!menu||menu.hidden)return;
            const trigger=document.querySelector('[data-action="mobile-account-toggle"]');
            const target=event.target;
            if(target instanceof Element && (menu.contains(target)||trigger?.contains(target)))return;
            this.closeMobileAccountMenu();
        });
        document.addEventListener('keydown',(event)=>{
            if(event.key==='Escape')this.closeMobileAccountMenu();
        });
        window.addEventListener('resize',()=>{
            if(!this._isMobileAccountMenuEnabled())this.closeMobileAccountMenu();
        });
    },
    _landingFeatureLabels:{
        'tasks.create':'Создание задач',
        'jobs.run':'Запуски задач',
        'ai.agent':'AI-агент',
        'llm.chat':'Чаты с моделями',
        'bots.internal':'Внутренний бот для себя',
        'bots.external':'Внешние Telegram-боты',
        'files.upload':'Загрузка файлов',
        'integrations.enabled':'Интеграции',
        'debugger.enabled':'Отладка запусков'
    },
    _landingFeatureOrder:[
        'tasks.create',
        'files.upload',
        'jobs.run',
        'ai.agent',
        'llm.chat',
        'integrations.enabled',
        'debugger.enabled',
        'bots.internal',
        'bots.external'
    ],
    _hiddenLandingFeatures:new Set(['modules.custom']),
    _formatLandingMoney(priceMinor,currency='RUB'){
        const minor=Number(priceMinor||0);
        const amount=Math.max(0,Math.round(minor/100));
        const grouped=String(amount).replace(/\B(?=(\d{3})+(?!\d))/g,' ');
        const code=String(currency||'RUB').toUpperCase();
        if(code==='RUB')return `${grouped} ₽`;
        if(code==='USD')return `$${grouped}`;
        if(code==='EUR')return `${grouped} €`;
        return `${grouped} ${this._esc(code)}`;
    },
    _landingIntervalLabel(interval='month'){
        const value=String(interval||'month');
        if(value==='year')return 'год';
        if(value==='week')return 'неделя';
        if(value==='day')return 'день';
        return 'месяц';
    },
    _landingPlanFeatures(plan){
        const features=plan?.features||{};
        const limits=plan?.limits||{};
        const seen=new Set();
        const ordered=[
            ...this._landingFeatureOrder,
            ...Object.keys(features).filter((key)=>!this._landingFeatureOrder.includes(key))
        ];
        const items=ordered
            .filter((key)=>{
                if(seen.has(key))return false;
                seen.add(key);
                if(this._hiddenLandingFeatures?.has(key))return false;
                return !!features[key];
            })
            .map((key)=>this._landingFeatureLabels[key]||key)
            .filter(Boolean);
        const concurrent=Number(limits.concurrent_jobs||0);
        if(concurrent>0)items.push(`Одновременных задач: ${concurrent}`);
        return items;
    },
    _fallbackLandingPlans(){
        return [
            {slug:'starter',name:'Стартовый',description:'Небольшой пакет для разовых задач. Хватит на ~10 готовых роликов.',credits:5000,price_minor:49900,currency:'RUB',sort_order:10},
            {slug:'standard',name:'Стандартный',description:'Подходит для регулярной работы. Полные batch-прогоны для серий офферов.',credits:20000,price_minor:169900,currency:'RUB',sort_order:20},
            {slug:'mega',name:'Мега',description:'Большой запас для интенсивных проектов. Для агентств и студий.',credits:100000,price_minor:749900,currency:'RUB',sort_order:30}
        ];
    },
    _renderLandingPlans(packages,options={}){
        const grid=document.getElementById('landing-pricing-grid');
        if(!grid)return;
        const active=(packages||[])
            .filter((pkg)=>pkg && pkg.is_active!==false && pkg.is_active!==0)
            .sort((a,b)=>(Number(a.sort_order||0)-Number(b.sort_order||0))||(Number(a.price_minor||0)-Number(b.price_minor||0)));
        const source=active.length?active:this._fallbackLandingPlans();
        grid.classList.toggle('is-fallback',!!options.fallback);
        grid.innerHTML=source.map((pkg)=>{
            const credits=Number(pkg.credits||0);
            const creditsText=credits>0?`${credits.toLocaleString('ru-RU')} кредитов`:'';
            const price=pkg.price_minor==null
                ?(options.fallback?'актуально в кабинете':'—')
                :this._formatLandingMoney(pkg.price_minor,pkg.currency);
            const popular=String(pkg.slug||'').toLowerCase()==='standard';
            const cls=popular?'landing-plan-card is-popular':'landing-plan-card';
            return `<article class="${cls}" data-plan-slug="${this._esc(pkg.slug||'')}">
                <h3>${this._esc(pkg.name||'Пакет')}</h3>
                <div>
                    <div class="landing-plan-price">${this._esc(price)}</div>
                    ${creditsText?`<div class="landing-plan-card-credits">${this._esc(creditsText)}</div>`:''}
                </div>
                <p>${this._esc(pkg.description||'Кредиты для production-потоков omoibot.')}</p>
            </article>`;
        }).join('');
    },
    async loadLandingPlans(){
        const grid=document.getElementById('landing-pricing-grid');
        if(!grid||this.landingPlansLoading)return;
        this.landingPlansLoading=true;
        let resolved=false;
        const fallbackTimer=setTimeout(()=>{
            if(resolved)return;
            this._renderLandingPlans([],{fallback:true});
        },3000);
        try{
            if(typeof API==='undefined'||!API.get){
                resolved=true;
                clearTimeout(fallbackTimer);
                this._renderLandingPlans([], {fallback:true});
                return;
            }
            const resp=await API.get('/api/billing/packages',{allowUnauthorized:true});
            resolved=true;
            clearTimeout(fallbackTimer);
            const packages=Array.isArray(resp)?resp:(Array.isArray(resp?.packages)?resp.packages:[]);
            this._renderLandingPlans(packages,{fallback:!packages.length});
            this.landingPlansLoaded=packages.length>0;
        }catch(_e){
            clearTimeout(fallbackTimer);
            this._renderLandingPlans([], {fallback:true});
        }finally{
            this.landingPlansLoading=false;
        }
    },
    // Materialize the landing from its <template> (audit WS-9). Called
    // before setState reads #landing-page: cloning keeps the markup inert
    // in the authenticated DOM, so its hero image never loads until the
    // public state actually needs it.
    _ensureLandingMounted(){
        if(document.getElementById('landing-page'))return;
        const tpl=document.getElementById('landing-page-template');
        const app=document.getElementById('app');
        if(!tpl||!app)return;
        app.before(tpl.content.cloneNode(true));
        // The public config may have resolved BEFORE materialization — the
        // access policy looked for the CTAs in a document that did not have
        // them yet. Re-apply so buttons carry the real texts.
        this._applyLandingAccessPolicy(this.publicConfig||null);
    },
    setState(state,options={}){
        const validStates=['checking','authenticated','unauthenticated','auth-error'];
        const next=validStates.includes(state)?state:'checking';
        const body=document.body;
        if(next==='unauthenticated')this._ensureLandingMounted();
        const landing=document.getElementById('landing-page');
        if(next!=='unauthenticated'&&landing){
            // Detach the landing when leaving the public state — an
            // authenticated user must not keep its 62 hidden nodes (and
            // reveal observer targets) in the live DOM.
            if(typeof LandingEffects!=='undefined'&&typeof LandingEffects.destroy==='function'){
                LandingEffects.destroy();
            }
            landing.remove();
        }
        const overlay=document.getElementById('auth-overlay');
        const app=document.getElementById('app');
        const card=overlay?.querySelector('.auth-card');
        const boot=document.getElementById('auth-boot');
        const bootMessage=document.getElementById('auth-boot-message');
        const retry=document.getElementById('auth-boot-retry');
        const showLanding=next==='unauthenticated';
        const showAuth=next==='unauthenticated'&&this.authDialogOpen;
        const showBoot=next==='checking'||next==='auth-error';

        if(body){
            body.dataset.authState=next;
            body.classList.toggle('auth-dialog-open',showAuth);
            body.classList.toggle('landing-visible',showLanding);
        }

        if(boot)boot.setAttribute('aria-hidden',showBoot?'false':'true');
        if(bootMessage){
            bootMessage.textContent=options.message || (next==='auth-error'
                ?'Не удалось проверить сессию'
                :'Проверяем сессию...');
        }
        if(retry){
            retry.hidden=next!=='auth-error';
            retry.disabled=false;
        }

        this._setHidden(landing,!showLanding);
        if(showLanding){
            if(typeof LandingEffects!=='undefined' && typeof LandingEffects.init==='function'){
                LandingEffects.init();
            }
        }

        if(overlay){
            this._setHidden(overlay,!showAuth);
            overlay.style.display=showAuth?'flex':'';
        }

        if(app){
            const blocked=next!=='authenticated';
            app.toggleAttribute('inert',blocked);
            if(showAuth||showLanding){
                app.style.filter='';
                app.setAttribute('aria-hidden','true');
            }else{
                app.style.filter='';
                if(next==='authenticated')app.removeAttribute('aria-hidden');
                else app.setAttribute('aria-hidden','true');
            }
        }

        body?.classList.toggle('auth-required',next!=='authenticated');

        if(typeof FocusTrap!=='undefined'&&card){
            if(showAuth){
                card.setAttribute('tabindex','-1');
                FocusTrap.activate(card,{
                    initialFocus:this.pendingTotp?'#auth-totp-code':'#auth-email',
                    onEscape:()=>this.closeFromLanding()
                });
            }else{
                FocusTrap.deactivate(card,false);
            }
        }
    },
    show(error='',options={}){
        this._invalidateAuthContext();
        this.authDialogOpen=options.openAuth!==false;
        this._setError(error);
        this._renderMode();
        this.setState('unauthenticated');
    },
    closeFromLanding(){
        if(!this.authDialogOpen)return;
        const card=document.getElementById('auth-overlay')?.querySelector('.auth-card');
        if(typeof FocusTrap!=='undefined'&&card)FocusTrap.deactivate(card,true);
        this.show('',{openAuth:false});
    },
    hide(){
        this._invalidateAuthContext();
        this.authDialogOpen=false;
        this.setState('authenticated');
    },
    openFromLanding(mode='register'){
        this._invalidateAuthContext();
        this.pendingTotp=null;
        this.mode=mode==='register'&&this._canRegister()?'register':'login';
        this.authDialogOpen=true;
        this._setError('');
        this._renderMode();
        this.setState('unauthenticated');
        setTimeout(()=>document.getElementById('auth-email')?.focus(),0);
    },
    startBackground(){
        if(!this.user)return;
        if(typeof SSE!=='undefined'){
            if(SSE.init)SSE.init();
            else if(SSE.connect)SSE.connect();
        }
    },
    stopBackground(){
        if(typeof SSE!=='undefined'&&SSE.disconnect)SSE.disconnect();
        if(typeof Jobs!=='undefined'&&Jobs.pollTimer){
            clearInterval(Jobs.pollTimer);
            Jobs.pollTimer=null;
        }
    },
    setUser(user){
        const prevId=this.user?.id??null;
        const nextId=user?.id??null;
        this.user=user||null;
        this.access=this.user?.access||null;
        this.plan=this.access?.plan||null;
        // Session-scoped GET cache must not survive a real user change —
        // flush it (and abort in-flight requests) only when the identity
        // actually changed, so re-verification of the same user keeps data.
        if(prevId!==nextId&&typeof API!=='undefined'&&typeof API.resetSessionCache==='function'){
            API.resetSessionCache();
        }
    },
    isAdmin(){
        const role=this.user?.role||'';
        return role==='admin'||role==='super_admin';
    },
    isSuperAdmin(){
        return !!(this.user?.is_system_owner||this.user?.role==='super_admin');
    },
    hasFeature(key){
        if(this.isSuperAdmin())return true;
        const features=this.access?.features||{};
        return !!features[key];
    },
    hasAnyFeature(keys){
        return (keys||[]).some(key=>this.hasFeature(key));
    },
    canOpenSettings(){
        if(this.isSuperAdmin())return true;
        return String(this.plan?.slug||'')==='tester' && this.hasFeature('integrations.enabled');
    },
    canOpenBots(){
        return this.isAdmin();
    },
    applyAccessUI(){
        const navFeatures={
            chats:'llm.chat',
            agent:'ai.agent'
        };
        Object.entries(navFeatures).forEach(([page,feature])=>{
            const el=document.querySelector(`.nav-item[data-page="${page}"]`);
            const allowed=Array.isArray(feature)?this.hasAnyFeature(feature):this.hasFeature(feature);
            if(el)el.hidden=!!this.user&&!allowed;
        });
        document.querySelectorAll('[data-admin-bots-nav]').forEach((el)=>{
            el.hidden=!this.canOpenBots();
        });
        document.querySelectorAll('[data-admin-nav]').forEach((el)=>{
            el.hidden=!this.isSuperAdmin();
        });
        const settingsNav=document.querySelector('.nav-item[data-page="settings"]');
        if(settingsNav)settingsNav.hidden=!!this.user&&!this.canOpenSettings();
        const actions={
            'task-run':'jobs.run',
            'debugger-restart-selected':'jobs.run',
            'canvas-agent-send':'ai.agent',
            'chats-send':'llm.chat',
            'chats-attach-file':'files.upload'
        };
        Object.entries(actions).forEach(([action,feature])=>{
            document.querySelectorAll(`[data-action="${action}"]`).forEach((el)=>{
                const blocked=!!this.user&&!this.hasFeature(feature);
                if('disabled' in el)el.disabled=blocked;
                el.title=blocked?'Функция недоступна':(el.getAttribute('title')||'');
            });
        });
        document.querySelectorAll('[data-action="modules-add"]').forEach((el)=>{
            const blocked=!!this.user&&!this.canOpenSettings();
            if('disabled' in el)el.disabled=blocked;
            el.title=blocked?'Настройки API доступны только владельцу и тестерам':(el.getAttribute('title')||'');
        });
    },
    requireLogin(message='Сессия истекла. Войдите снова.'){
        this._invalidateAuthContext();
        this.stopBackground();
        this.setUser(null);
        this.pendingTotp=null;
        this.csrfToken='';
        this.renderUser();
        this.authDialogOpen=true;
        this.setState('unauthenticated');
        this._setError(message);
        this._renderMode();
    },
    _readRefCookie(){
        const m=(document.cookie||'').split('; ').find((c)=>c.startsWith(REF_COOKIE_NAME+'='));
        return m?decodeURIComponent(m.split('=',2)[1]||''):'';
    },
    _normalizeRefCode(value){
        const code=String(value||'').trim().toUpperCase();
        return REF_CODE_RE.test(code)?code:'';
    },
    _currentReferralCode(){
        const stored=this.referralCode||this._readRefCookie()||(()=>{try{return localStorage.getItem(REF_COOKIE_NAME)||'';}catch(_e){return '';}})();
        return this._normalizeRefCode(stored);
    },
    _setReferralIdentity(value){
        const normalized=this._normalizeRefCode(value);
        const previous=this._currentReferralCode();
        this.referralCode=normalized;
        if(previous!==normalized)this._invalidateAuthContext();
        return normalized;
    },
    _writeRefCookie(code,ttlDays){
        const ttl=Math.max(86400,Math.floor(Number(ttlDays||30)*86400));
        const secure=location.protocol==='https:'?'; Secure':'';
        document.cookie=`${REF_COOKIE_NAME}=${code}; Max-Age=${ttl}; Path=${REF_COOKIE_PATH}; SameSite=Lax${secure}`;
    },
    _clearRefStorage(){
        this._setReferralIdentity('');
        document.cookie=`${REF_COOKIE_NAME}=; Max-Age=0; Path=${REF_COOKIE_PATH}; SameSite=Lax`;
        try{localStorage.removeItem(REF_COOKIE_NAME);}catch(_e){}
        this.referralPublicConfig=null;
        this.referralPublicConfigCode='';
        this.referralPublicConfigPromise=null;
        this.referralPublicConfigPromiseCode='';
    },
    async captureReferral(){
        const params=new URLSearchParams(location.search||'');
        const raw=this._normalizeRefCode(params.get('ref')||'');
        if(raw){
            const cfg=await this._ensureReferralPublicConfig(raw);
            if(!cfg || !cfg.enabled || !cfg.referral_valid)return;
            const code=this._normalizeRefCode(cfg.ref_code||raw);
            if(!code)return;
            const hasCookie=this._readRefCookie();
            this._setReferralIdentity(this._normalizeRefCode(hasCookie)||code);
            if(!hasCookie){
                this._writeRefCookie(code,cfg.ttl_days);
            }
            try{localStorage.setItem(REF_COOKIE_NAME,code);}catch(_e){}
            return;
        }
        const cookie=this._readRefCookie();
        const stored=cookie||(()=>{try{return localStorage.getItem(REF_COOKIE_NAME)||'';}catch(_e){return '';}})();
        const normalized=this._normalizeRefCode(stored);
        if(normalized)this._setReferralIdentity(normalized);
    },
    async _ensureReferralPublicConfig(code=''){
        const normalized=this._normalizeRefCode(code);
        if(this.referralPublicConfig && this.referralPublicConfigCode===normalized)return this.referralPublicConfig;
        if(this.referralPublicConfigPromise&&this.referralPublicConfigPromiseCode===normalized){
            return this.referralPublicConfigPromise;
        }
        const promise=(async()=>{
            try{
                const suffix=normalized?`?ref_code=${encodeURIComponent(normalized)}`:'';
                const resp=await fetch(`/api/referral/public-config${suffix}`);
                if(!resp.ok)return null;
                const config=await resp.json();
                this.referralPublicConfig=config;
                this.referralPublicConfigCode=normalized;
                return config;
            }catch(_e){
                return null;
            }
        })();
        this.referralPublicConfigPromise=promise;
        this.referralPublicConfigPromiseCode=normalized;
        try{
            return await promise;
        }finally{
            if(this.referralPublicConfigPromise===promise){
                this.referralPublicConfigPromise=null;
                this.referralPublicConfigPromiseCode='';
            }
        }
    },
    _removeRegistrationBanner(){
        const banner=document.getElementById('auth-referral-banner');
        if(banner && banner.parentNode)banner.parentNode.removeChild(banner);
    },
    async _renderRegistrationBanner(){
        const renderGeneration=++this.registrationBannerGeneration;
        if(this.mode!=='register' || this.pendingTotp){
            this._removeRegistrationBanner();
            return;
        }
        const inviteWrap=document.getElementById('auth-invite-wrap');
        const code=this._currentReferralCode();
        if(!code){
            if(inviteWrap)inviteWrap.style.display='block';
            this._removeRegistrationBanner();
            return;
        }
        const cfg=await this._ensureReferralPublicConfig(code);
        if(this.registrationBannerGeneration!==renderGeneration)return;
        if(this.mode!=='register'||this.pendingTotp||this._currentReferralCode()!==code){
            this._removeRegistrationBanner();
            if(this.mode==='register'&&!this.pendingTotp&&inviteWrap)inviteWrap.style.display='block';
            return;
        }
        if(!cfg || !cfg.enabled || !cfg.referral_valid){
            if(inviteWrap)inviteWrap.style.display='block';
            this._removeRegistrationBanner();
            return;
        }
        if(inviteWrap)inviteWrap.style.display='none';
        const welcomePercent=Number(cfg.welcome_percent||0);
        if(!welcomePercent){
            this._removeRegistrationBanner();
            return;
        }
        const anchor=document.getElementById('auth-email-wrap');
        if(!anchor)return;
        let banner=document.getElementById('auth-referral-banner');
        if(!banner){
            anchor.insertAdjacentHTML('beforebegin','<div id="auth-referral-banner" class="auth-referral-banner"></div>');
            banner=document.getElementById('auth-referral-banner');
        }
        if(banner){
            banner.textContent=`Получите +${welcomePercent}% токенами к первому пополнению.`;
        }
    },
    toggleMode(){
        if(this.pendingTotp){
            this._invalidateAuthContext();
            this.pendingTotp=null;
            this.mode='login';
            this._setError('');
            this._renderMode();
            return;
        }
        if(this.mode==='login'&&!this._canRegister()){
            this._setError('');
            this._renderMode();
            return;
        }
        this._invalidateAuthContext();
        this.mode=this.mode==='login'?'register':'login';
        this._setError('');
        this._renderMode();
    },
    _renderMode(){
        const isRegister=this.mode==='register';
        const isTotp=!!this.pendingTotp;
        const title=document.getElementById('auth-title');
        const subtitle=document.getElementById('auth-subtitle');
        const emailWrap=document.getElementById('auth-email-wrap');
        const passwordWrap=document.getElementById('auth-password-wrap');
        const displayWrap=document.getElementById('auth-display-wrap');
        const inviteWrap=document.getElementById('auth-invite-wrap');
        const setupWrap=document.getElementById('auth-totp-setup');
        const qrImg=document.getElementById('auth-totp-qr');
        const secretBox=document.getElementById('auth-totp-secret');
        const uriBox=document.getElementById('auth-totp-uri');
        const expiresBox=document.getElementById('auth-totp-expires');
        const codeWrap=document.getElementById('auth-totp-wrap');
        const passwordInput=document.getElementById('auth-password');
        const submit=document.getElementById('auth-submit-btn');
        const switchBtn=document.getElementById('auth-switch-btn');
        if(title)title.textContent=isTotp
            ?(this.pendingTotp.setup?'Подключите 2FA':'Код 2FA')
            :(isRegister?'Регистрация':'Вход');
        if(subtitle)subtitle.textContent=isTotp
            ?(this.pendingTotp.setup
                ?'Добавьте ключ в Google Authenticator и введите одноразовый код.'
                :'Введите одноразовый код из Google Authenticator.')
            :(isRegister
            ?'Создайте аккаунт, чтобы у каждого пользователя был свой профиль и свои проекты.'
            :'Войдите в свой профиль, чтобы работать с проектами и задачами.');
        if(emailWrap)emailWrap.style.display=isTotp?'none':'block';
        if(passwordWrap)passwordWrap.style.display=isTotp?'none':'block';
        if(displayWrap)displayWrap.style.display=!isTotp&&isRegister?'block':'none';
        if(inviteWrap)inviteWrap.style.display=!isTotp&&isRegister?'block':'none';
        if(setupWrap)setupWrap.style.display=isTotp&&this.pendingTotp.setup?'block':'none';
        if(qrImg){
            const qr=this.pendingTotp?.totpQr||'';
            qrImg.style.display=isTotp&&this.pendingTotp.setup&&qr?'block':'none';
            if(qr)qrImg.src=qr;
            else qrImg.removeAttribute('src');
        }
        if(secretBox)secretBox.textContent=this.pendingTotp?.secret?`Ключ: ${this.pendingTotp.secret}`:'';
        if(uriBox)uriBox.textContent=this.pendingTotp?.otpauthUri||'';
        if(expiresBox){
            const seconds=Number(this.pendingTotp?.expiresIn||0);
            expiresBox.textContent=isTotp&&seconds?`Окно подтверждения: ${Math.max(1,Math.round(seconds/60))} мин.`:'';
        }
        if(codeWrap)codeWrap.style.display=isTotp?'block':'none';
        if(passwordInput){
            passwordInput.autocomplete=isRegister?'new-password':'current-password';
            const minPasswordLength=Number(this.publicConfig?.password_min_length||0);
            if(isRegister&&Number.isInteger(minPasswordLength)&&minPasswordLength>0){
                passwordInput.minLength=minPasswordLength;
            }else{
                passwordInput.removeAttribute('minlength');
            }
        }
        if(submit)submit.textContent=isTotp?'Подтвердить':(isRegister?'Создать аккаунт':'Войти');
        if(switchBtn){
            switchBtn.textContent=isTotp?'Назад':(isRegister?'У меня уже есть аккаунт':'Создать аккаунт');
            switchBtn.hidden=!isTotp&&!isRegister&&!this._canRegister();
        }
        this._renderRegistrationBanner();
    },
    _clearFieldError(field){
        if(!field?.matches?.(this._authFieldSelector))return;
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-errormessage');
        const error=document.getElementById('auth-error');
        if(error?.dataset.fieldId===field.id){
            error.hidden=true;
            error.textContent='';
            delete error.dataset.fieldId;
        }
    },
    _setError(message,{fieldId}={}){
        const el=document.getElementById('auth-error');
        if(!el)return;
        document.querySelectorAll(this._authFieldSelector).forEach((field)=>{
            field.removeAttribute('aria-invalid');
            field.removeAttribute('aria-errormessage');
        });
        const text=String(message||'');
        el.textContent=text;
        el.hidden=!text;
        delete el.dataset.fieldId;
        if(!text)return;
        const field=fieldId?document.getElementById(fieldId):null;
        if(field?.matches?.(this._authFieldSelector)){
            field.setAttribute('aria-invalid','true');
            field.setAttribute('aria-errormessage','auth-error');
            el.dataset.fieldId=field.id;
            field.focus();
        }
    },
    _accountMenuMarkup(){
        if(!this.user)return '';
        const email=this.user.email||'';
        const name=(this.user.display_name||'').trim() || (email ? email.split('@',1)[0] : 'Пользователь');
        const role=this.user.role||'user';
        const totp=this.user.totp_enabled?'2FA включена':'2FA выкл.';
        const footerIcon=(name)=>CGIcons.html(name,{badgeClass:'auth-userbox-action-icon',fallback:'tools'});
        const adminButton=this.isSuperAdmin()
            ?`<button type="button" class="auth-userbox-action" data-auth-admin title="Админка">${footerIcon('admin')}<span>админка</span></button>`
            :'';
        const inviteButton=this.user.can_create_invites
            ?`<button type="button" class="auth-userbox-action" data-auth-invite title="Пригласить">${footerIcon('send')}<span>пригласить</span></button>`
            :'';
        const helpButton=`<button type="button" class="auth-userbox-action" data-auth-help data-action="onboarding-start" title="Справка по интерфейсу"><span class="auth-userbox-action-icon auth-userbox-help-mark" aria-hidden="true">?</span><span>справка</span></button>`;
        const settingsButton=this.canOpenSettings()
            ?`<button type="button" class="auth-userbox-action" data-auth-settings title="Настройки">${footerIcon('tools')}<span>настройки</span></button>`
            :'';
        return `
            <div class="auth-userbox-profile">
                <div class="auth-userbox-info">
                    <div class="auth-userbox-name">${this._esc(name)}</div>
                    <div class="auth-userbox-email">${this._esc(email)}</div>
                    <div class="auth-userbox-meta">${this._esc(role)} · ${this._esc(totp)}</div>
                </div>
            </div>
            <div class="auth-userbox-actions">
                <button type="button" class="auth-userbox-action" data-auth-profile title="Профиль">${footerIcon('avatar')}<span>профиль</span></button>
                ${adminButton}
                ${inviteButton}
                ${helpButton}
                ${settingsButton}
                <button type="button" class="auth-userbox-action" data-auth-logout title="Выйти">${footerIcon('back')}<span>выйти</span></button>
            </div>
        `;
    },
    _mobileSecondaryNavMarkup(){
        if(!this.user)return '';
        const email=this.user.email||'';
        const name=(this.user.display_name||'').trim() || (email ? email.split('@',1)[0] : 'Пользователь');
        const role=this.user.role||'user';
        const totp=this.user.totp_enabled?'2FA включена':'2FA выкл.';
        const botsRoute=this.canOpenBots()
            ?'<a class="mobile-secondary-route" href="#/bots" data-mobile-route="bots"><span aria-hidden="true">BT</span><span>Боты</span></a>'
            :'';
        const adminRoute=this.isSuperAdmin()
            ?'<a class="mobile-secondary-route" href="#/admin" data-mobile-route="admin"><span aria-hidden="true">AD</span><span>Админ</span></a>'
            :'';
        return `
            <nav class="mobile-secondary-nav" aria-label="Дополнительная навигация">
                <button type="button" class="mobile-secondary-route mobile-context-action" data-mobile-context-route="task" data-mobile-close data-action="task-close" hidden><span aria-hidden="true">BACK</span><span>Закрыть задачу</span></button>
                <button type="button" class="mobile-secondary-route mobile-context-action" data-mobile-context-route="debugger" data-mobile-close data-action="debugger-close" hidden><span aria-hidden="true">BACK</span><span>Закрыть рабочую доску</span></button>
                ${botsRoute}
                <a class="mobile-secondary-route" href="#/templates" data-mobile-route="templates"><span aria-hidden="true">TP</span><span>Шаблоны</span></a>
                <a class="mobile-secondary-route" href="/legal" data-mobile-close><span aria-hidden="true">DOC</span><span>Правовые документы</span></a>
                ${adminRoute}
            </nav>
            <div class="mobile-account-status" role="status">
                <span>Статус аккаунта</span>
                <strong>${this._esc(name)} · ${this._esc(role)} · ${this._esc(totp)}</strong>
            </div>
            ${this._accountMenuMarkup()}
        `;
    },
    _bindAccountMenu(root,{closeOnAction=false}={}){
        if(!root)return;
        if(root.id==='mobile-account-menu'){
            const trigger=document.querySelector('[data-action="mobile-account-toggle"]');
            if(trigger)trigger.setAttribute('aria-expanded',root.hidden?'false':'true');
        }
        if(root.__authAccountMenuClickHandler){
            root.removeEventListener('click',root.__authAccountMenuClickHandler);
        }
        const close=()=>{if(closeOnAction)this.closeMobileAccountMenu();};
        const handler=(event)=>{
            const target=event.target instanceof Element?event.target:null;
            if(!target||!root.contains(target))return;
            const mobileRoute=target.closest('[data-mobile-route]');
            if(mobileRoute){
                event.preventDefault();
                close();
                Router.go(mobileRoute.dataset.mobileRoute);
                return;
            }
            if(target.closest('[data-mobile-close]')){
                close();
                return;
            }
            if(target.closest('[data-auth-profile]')){
                close();
                Router.go('profile');
                return;
            }
            if(target.closest('[data-auth-admin]')){
                close();
                Router.go('admin');
                return;
            }
            if(target.closest('[data-auth-invite]')){
                close();
                this.createInvite();
                return;
            }
            if(target.closest('[data-auth-settings]')){
                close();
                Router.go('settings');
                return;
            }
            if(target.closest('[data-auth-help]')){
                close();
                return;
            }
            if(target.closest('[data-auth-logout]')){
                close();
                this.logout();
            }
        };
        root.__authAccountMenuClickHandler=handler;
        root.addEventListener('click',handler);
    },
    _renderMobileAccountMenu(){
        const menu=document.getElementById('mobile-account-menu');
        if(!menu)return;
        if(!menu.hidden)this.closeMobileAccountMenu();
        const markup=this._mobileSecondaryNavMarkup();
        menu.innerHTML=markup||'';
        if(!markup){
            this.closeMobileAccountMenu();
            return;
        }
        if(window.OmoIcons)OmoIcons.hydrate(menu);
        if(window.Theme)Theme.sync();
        this._bindAccountMenu(menu,{closeOnAction:true});
    },
    renderUser(){
        const box=document.getElementById('auth-userbox');
        if(!box)return;
        if(!this.user){
            box.style.display='none';
            box.innerHTML='';
            this._renderMobileAccountMenu();
            return;
        }
        box.style.display='block';
        const markup=this._accountMenuMarkup();
        box.innerHTML=markup;
        if(window.OmoIcons)OmoIcons.hydrate(box);
        if(window.Theme)Theme.sync();
        this._bindAccountMenu(box);
        this._renderMobileAccountMenu();
        this.applyAccessUI();
    },
    async init(){
        this._invalidateAuthContext();
        this._bindMobileAccountMenuChrome();
        this.setState('checking');
        this._setError('');
        await Promise.all([this.captureReferral(),this.loadPublicConfig()]);
        this._renderMode();
        const me=await API.get('/api/me',{allowUnauthorized:true});
        if(me && !me.error && me.ok!==false){
            this._invalidateAuthContext();
            this.csrfToken=me.csrf_token||'';
            this.setUser(me);
            this.renderUser();
            this.setState('authenticated');
            return true;
        }
        if(me && (me.http_status===401 || me.http_status===403)){
            this._invalidateAuthContext();
            this.setUser(null);
            this.pendingTotp=null;
            this.csrfToken='';
            this.renderUser();
            this.authDialogOpen=false;
            this.setState('unauthenticated');
            this._setError('');
            this._renderMode();
            return false;
        }
        this._invalidateAuthContext();
        this.stopBackground();
        this.setUser(null);
        this.pendingTotp=null;
        this.csrfToken='';
        this.renderUser();
        this.setState('auth-error',{message:'Не удалось проверить сессию'});
        if(typeof console!=='undefined')console.error('Session check failed',me||null);
        return false;
    },
    async retrySessionCheck(){
        const retry=document.getElementById('auth-boot-retry');
        if(retry)retry.disabled=true;
        const ready=await this.init();
        if(ready){
            if(!this.booted){
                this.booted=true;
                Router.init();
            }else{
                this.startBackground();
                Router.go(Router.current||'home',Router.params||{},{force:true});
            }
        }else if(retry){
            retry.disabled=false;
        }
    },
    async submit(){
        this._setError('');
        const submissionMode=this.mode;
        const submissionDialogOpen=this.authDialogOpen;
        const submissionTotp=this.pendingTotp;
        const submissionReferralCode=submissionMode==='register'?this._currentReferralCode():'';
        const submissionContextGeneration=this.authContextGeneration;
        const submissionGeneration=++this.submissionGeneration;
        const isCurrentSubmission=()=>this.submissionGeneration===submissionGeneration
            &&this.authContextGeneration===submissionContextGeneration
            &&this.mode===submissionMode
            &&this.authDialogOpen===submissionDialogOpen
            &&this.pendingTotp===submissionTotp
            &&(submissionMode!=='register'||this._currentReferralCode()===submissionReferralCode);
        if(this.pendingTotp){
            const codeInput=document.getElementById('auth-totp-code');
            const code=codeInput?.value?.trim()||'';
            if(code.replace(/\D/g,'').length!==6){
                this._setError('Введите 6-значный код',{fieldId:'auth-totp-code'});
                return;
            }
            const resp=await API.post('/api/auth/totp/confirm',{totp_code:code},{skipAuth:true});
            if(!isCurrentSubmission())return;
            if(!resp||resp.error){
                this._setError(resp?.error||'Не удалось подтвердить код');
                return;
            }
            this._invalidateAuthContext();
            this.pendingTotp=null;
            if(codeInput)codeInput.value='';
            this._finishAuth(resp);
            return;
        }
        const emailInput=document.getElementById('auth-email');
        const email=emailInput?.value?.trim()||'';
        if(emailInput)emailInput.value=email;
        const password=document.getElementById('auth-password')?.value||'';
        const displayName=document.getElementById('auth-display-name')?.value?.trim()||'';
        const inviteCode=document.getElementById('auth-invite-code')?.value?.trim()||'';
        if(!emailInput||!email||!emailInput.validity.valid){
            this._setError('Введите корректный email',{fieldId:'auth-email'});
            return;
        }
        if(submissionMode==='register'){
            if(!this.publicConfig){
                this._setError('Не удалось проверить доступ к регистрации. Повторите позже.');
                return;
            }
            if(!this._canRegister()){
                this._setError('Самостоятельная регистрация временно закрыта');
                return;
            }
            const minPasswordLength=Number(this.publicConfig?.password_min_length||0);
            if(Number.isInteger(minPasswordLength)&&minPasswordLength>0&&password.length<minPasswordLength){
                this._setError(`Пароль должен быть не короче ${minPasswordLength} символов`,{fieldId:'auth-password'});
                return;
            }
            let referralConfig=submissionReferralCode&&this.referralPublicConfigCode===submissionReferralCode
                ?this.referralPublicConfig
                :null;
            if(this.publicConfig.invite_required&&!inviteCode&&submissionReferralCode&&!referralConfig){
                referralConfig=await this._ensureReferralPublicConfig(submissionReferralCode);
                if(!isCurrentSubmission())return;
                if(!referralConfig){
                    this._setError('Не удалось проверить реферальную ссылку. Введите код приглашения или повторите позже.',{fieldId:'auth-invite-code'});
                    return;
                }
            }
            const referralAllowsRegistration=!!(
                submissionReferralCode
                &&referralConfig?.enabled
                &&referralConfig?.referral_valid
            );
            if(this.publicConfig?.invite_required&&!inviteCode&&!referralAllowsRegistration){
                this._setError(submissionReferralCode
                    ?'Введите действительный код приглашения или используйте корректную реферальную ссылку'
                    :'Введите код приглашения',{fieldId:'auth-invite-code'});
                return;
            }
        }
        if(!isCurrentSubmission())return;
        const endpoint=submissionMode==='register'?'/api/auth/register':'/api/auth/login';
        const payload={email,password,invite_code:inviteCode};
        if(submissionMode==='register'){
            payload.display_name=displayName;
            if(submissionReferralCode){
                payload.ref_code=submissionReferralCode;
                payload.ref_source='web';
            }
        }
        const resp=await API.post(endpoint,payload,{skipAuth:true});
        if(!isCurrentSubmission())return;
        if(!resp||resp.error){
            const message=this._authErrorMessage(resp);
            const fieldId=this._authErrorFieldId(resp?.code);
            if(fieldId)this._setError(message,{fieldId});
            else this._setError(message);
            return;
        }
        if(resp.requires_totp||resp.requires_totp_setup){
            this._invalidateAuthContext();
            this.pendingTotp={
                email,
                setup:!!resp.requires_totp_setup,
                secret:resp.totp_secret||'',
                otpauthUri:resp.otpauth_uri||'',
                totpQr:resp.totp_qr||'',
                expiresIn:resp.challenge_expires_in||0
            };
            const passwordInput=document.getElementById('auth-password');
            const codeInput=document.getElementById('auth-totp-code');
            if(passwordInput)passwordInput.value='';
            if(codeInput)codeInput.value='';
            this._setError('');
            this._renderMode();
            setTimeout(()=>document.getElementById('auth-totp-code')?.focus(),0);
            return;
        }
        this._finishAuth(resp);
    },
    _finishAuth(resp){
        const passwordInput=document.getElementById('auth-password');
        const displayInput=document.getElementById('auth-display-name');
        if(passwordInput)passwordInput.value='';
        if(displayInput)displayInput.value='';
        const inviteInput=document.getElementById('auth-invite-code');
        if(inviteInput)inviteInput.value='';
        this.csrfToken=resp.csrf_token||resp.user?.csrf_token||'';
        this.setUser(resp.user||null);
        this.pendingTotp=null;
        this.renderUser();
        const wasRegister=this.mode==='register';
        this._clearRefStorage();
        this._removeRegistrationBanner();
        this.hide();
        this._setError('');
        if(!this.booted){
            this.booted=true;
            Router.init();
        }else{
            this.startBackground();
            Router.go(Router.current||'home',Router.params||{},{force:true});
        }
        if(wasRegister&&typeof OnboardingTour!=='undefined'){
            setTimeout(()=>OnboardingTour.start('register'),250);
        }
    },
    async logout(){
        const resp=await API.post('/api/auth/logout',{}, {allowUnauthorized:true});
        if(!resp||resp.error||resp.ok===false){
            if(typeof Toast!=='undefined')Toast.error(resp?.error||'Не удалось выйти');
            return;
        }
        this.stopBackground();
        this.setUser(null);
        this.pendingTotp=null;
        this.csrfToken='';
        this.renderUser();
        this.show('',{openAuth:false});
    },
    async createInvite(){
        Modal.show(`<h2>Приглашение</h2>
            <p data-style="color:var(--text-muted);line-height:1.5">Введите email будущего пользователя. Код будет одноразовым и сработает только для этого email.</p>
            <form id="auth-invite-form">
                <div class="form-group">
                    <label class="form-label" for="auth-invite-email">Email</label>
                    <input class="form-input" id="auth-invite-email" type="email" autocomplete="email" placeholder="user@example.com" required autofocus>
                </div>
                <div class="form-group">
                    <label class="form-label" for="auth-invite-ttl">Срок действия, часов</label>
                    <input class="form-input" id="auth-invite-ttl" type="number" min="1" max="720" step="1" value="168">
                </div>
                <div id="auth-invite-error" class="auth-error" data-style="display:none"></div>
                <div data-style="display:flex;gap:8px;margin-top:16px;flex-wrap:wrap">
                    <button class="btn btn-primary" id="auth-invite-submit" type="submit">Создать код</button>
                    <button class="btn btn-ghost" type="button" data-action="modal-hide">Отмена</button>
                </div>
            </form>`);
        setTimeout(()=>document.getElementById('auth-invite-email')?.focus(),0);
        document.getElementById('auth-invite-form')?.addEventListener('submit',(event)=>{
            event.preventDefault();
            this.submitInvite();
        });
    },
    async submitInvite(){
        const emailEl=document.getElementById('auth-invite-email');
        const ttlEl=document.getElementById('auth-invite-ttl');
        const errorEl=document.getElementById('auth-invite-error');
        const submitEl=document.getElementById('auth-invite-submit');
        const email=emailEl?.value?.trim()||'';
        const ttlHours=parseInt(ttlEl?.value||'168',10);
        const showError=(message)=>{
            if(errorEl){
                errorEl.style.display='block';
                errorEl.textContent=message;
            }else if(typeof Toast!=='undefined'){
                Toast.error(message);
            }
        };
        if(!email||!email.includes('@')){
            showError('Введите корректный email');
            emailEl?.focus();
            return;
        }
        if(!Number.isInteger(ttlHours)||ttlHours<1||ttlHours>720){
            showError('Срок действия должен быть от 1 до 720 часов');
            ttlEl?.focus();
            return;
        }
        if(errorEl){
            errorEl.style.display='none';
            errorEl.textContent='';
        }
        if(submitEl){
            submitEl.disabled=true;
            submitEl.textContent='Создаю...';
        }
        const resp=await API.post('/api/auth/invites',{email,ttl_hours:ttlHours});
        if(submitEl){
            submitEl.disabled=false;
            submitEl.textContent='Создать код';
        }
        if(!resp||resp.error){
            showError(resp?.error||'Не удалось создать приглашение');
            return;
        }
        const code=resp.invite_code||'';
        const inviteEmail=resp.email||email;
        const expires=resp.expires_at?new Date(resp.expires_at*1000).toLocaleString():'';
        Modal.show(`<h2>Приглашение</h2>
            <p data-style="color:var(--text-muted);line-height:1.5">Код одноразовый для ${this._esc(inviteEmail)}${expires?`, действует до ${this._esc(expires)}`:''}.</p>
            <div class="form-group">
                <label class="form-label">Код</label>
                <input class="form-input" id="invite-code-value" readonly value="${this._esc(code)}">
            </div>
            <div data-style="display:flex;gap:8px;margin-top:16px;flex-wrap:wrap">
                <button class="btn btn-primary" data-action="auth-copy-invite">Скопировать</button>
                <button class="btn btn-ghost" data-action="modal-hide">OK</button>
            </div>`);
    },
    copyInviteCode(){
        const code=document.getElementById('invite-code-value')?.value||'';
        navigator.clipboard?.writeText(code);
        if(typeof Toast!=='undefined')Toast.success('Код скопирован');
    }
};

window.Auth=Auth;

/* web/js/core.js */
/* ═══════════════════════════════════════════
   omoibot — Core (shared caches + helpers)
   ═══════════════════════════════════════════ */

/* ═══ SSE — stub, full implementation in sse.js ═══ */
var SSE=window.SSE||{connect(){},disconnect(){}};
window.SSE=SSE;

let _stCache=null;
let _stCacheTime=0;
let _stCachePromise=null;
let _stBypassNext=false;
const STEP_TYPES_CACHE_TTL=5*60*1000;
const STEP_TYPES_STORAGE_KEY='cg2.stepTypes.v17';
function _validStepTypesPayload(data){
    return data&&typeof data==='object'&&!Array.isArray(data)&&!data.error;
}
async function getStepTypes(force=false){
    const now=Date.now();
    const bypass=!!force||_stBypassNext;
    if(!bypass&&_stCache&&now-_stCacheTime<STEP_TYPES_CACHE_TTL){
        return _stCache;
    }
    if(!bypass&&!_stCache){
        try{
            const saved=JSON.parse(sessionStorage.getItem(STEP_TYPES_STORAGE_KEY)||'null');
            if(saved&&saved.ts&&_validStepTypesPayload(saved.data)&&now-saved.ts<STEP_TYPES_CACHE_TTL){
                _stCache=saved.data;
                _stCacheTime=saved.ts;
                return _stCache;
            }
        }catch(e){}
    }
    if(_stCachePromise&&!bypass)return _stCachePromise;
    _stBypassNext=false;
    const url=bypass?('/api/step_types?_='+Date.now()):'/api/step_types';
    _stCachePromise=API.get(url).then(data=>{
        if(_validStepTypesPayload(data)){
            _stCache=data;
            _stCacheTime=Date.now();
            try{sessionStorage.setItem(STEP_TYPES_STORAGE_KEY,JSON.stringify({ts:_stCacheTime,data:_stCache}))}catch(e){}
            return _stCache;
        }
        API._contractError('/api/step_types', data?.error||'ожидался объект step types');
        return _stCache||{};
    }).catch(()=>{
        API._contractError('/api/step_types','запрос не выполнен');
        return _stCache||{};
    }).finally(()=>{_stCachePromise=null});
    return _stCachePromise;
}
function invalidateStepTypes(){
    _stCache=null;
    _stCacheTime=0;
    _stCachePromise=null;
    _stBypassNext=true;
    try{sessionStorage.removeItem(STEP_TYPES_STORAGE_KEY)}catch(e){}
}

let _projectsCache=null;
let _projectsCacheTime=0;
let _projectsPromise=null;
const PROJECTS_CACHE_TTL=60*1000;
async function getProjects(force=false){
    const now=Date.now();
    if(!force&&_projectsCache&&now-_projectsCacheTime<PROJECTS_CACHE_TTL)return _projectsCache;
    if(_projectsPromise&&!force)return _projectsPromise;
    _projectsPromise=API.get('/api/projects'+(force?'?_='+Date.now():'')).then(data=>{
        _projectsCache=API.expectArray(data,'/api/projects');
        _projectsCacheTime=Date.now();
        return _projectsCache;
    }).catch(()=>{
        API._contractError('/api/projects','запрос не выполнен');
        return _projectsCache||[];
    }).finally(()=>{_projectsPromise=null});
    return _projectsPromise;
}
function invalidateProjects(){_projectsCache=null;_projectsCacheTime=0;_projectsPromise=null;}

function getCachedProject(projectId){
    if(ProjectView?.project&&ProjectView.project.id===projectId)return ProjectView.project;
    const list=_projectsCache||Projects?.list||Dashboard?._projects||[];
    return list.find(p=>p.id===projectId)||null;
}

function canUploadThroughCurrentHost(file){
    void file;
    return true;
}

const DragDrop=window.DragDrop={
    hasFiles(dataTransfer){
        const items=dataTransfer?.items;
        if(items&&Array.from(items).some(item=>!item?.kind||item.kind==='file'))return true;
        const files=dataTransfer?.files;
        if(files&&files.length>0)return true;
        const types=dataTransfer?.types;
        if(!types)return false;
        if(typeof types.includes==='function')return types.includes('Files');
        if(typeof types.contains==='function')return types.contains('Files');
        try{
            return Array.from(types).includes('Files');
        }catch(e){
            return false;
        }
    }
};

const OmoIcons=window.OmoIcons={
    code:{
        HM:'home',PR:'projects',CH:'chat',AG:'agent',AGT:'agent',BT:'bots',AD:'admin',DC:'doc',TP:'layout',TH:'theme',
        ADD:'add',BACK:'back',PREV:'back',NEXT:'next',EDIT:'edit',DEL:'delete',RUN:'play',LAY:'layout',
        FIT:'fit',GRD:'grid',FILE:'file',SEND:'send',STOP:'stop',STP:'stop',PAUSE:'stop',HIDE:'hide',X:'hide',MORE:'more',
        SYN:'sync',CLP:'clipboard',BAT:'batch',DIR:'folder',CFG:'tools',TMP:'layout',GEN:'generate',DL:'download',
        CHT:'chat',TXT:'text',IMG:'image',VID:'video',AUD:'audio',URL:'link',
        CLD:'cloud',PLG:'agent',MOB:'projects',MIC:'audio',NAT:'image',BST:'star',
        ART:'camera',CAM:'camera',MUS:'audio',BIZ:'chart',PWR:'generate',
        HOT:'generate',VIP:'diamond',HME:'home',COO:'shop',CUT:'cut',TGT:'target',
        SHP:'shop','+':'zoom-in','-':'zoom-out','100':'reset'
    },
    type:{
        dashboard:'home',home:'home',profile:'avatar',user:'avatar',invite:'send',
        logout:'back',settings:'tools',projects:'projects',project:'projects',
        chat:'chat',chats:'chat',agent:'agent',plug:'agent',bot:'bots',bots:'bots',
        admin:'admin',add:'add',plus:'add',back:'back',prev:'back',previous:'back',next:'next',forward:'next',edit:'edit',delete:'delete',
        remove:'delete',trash:'delete',run:'play',play:'play',video_run:'play',
        layout:'layout',template:'layout',templates:'layout',fit:'fit',grid:'grid',snap:'grid',file:'file',doc:'doc',document:'doc',legal:'doc',folder:'folder',
        files:'folder',attach:'file',download:'download',down:'download','arrow-down':'download',
        send:'send',sync:'sync',stop:'stop',hide:'hide',more:'more',ellipsis:'more',
        close:'hide',help:'help',question:'help',tour:'help',theme:'theme',appearance:'theme',tools:'tools',tool:'tools',utility:'tools',
        cursor:'cursor',select:'cursor',hand:'hand',pan:'hand',
        clipboard:'clipboard',copy:'clipboard',batch:'batch',text:'text',image:'image',
        video:'video',audio:'audio',link:'link',url:'link',cloud:'cloud',
        generate:'generate',generator:'generate',avatar:'agent',
        'cat-video':'video','cat-mobile':'projects','cat-mic':'audio','cat-nature':'image',
        'cat-beauty':'star','cat-creative':'camera','cat-camera':'camera','cat-music':'audio',
        'cat-text':'text','cat-business':'chart','cat-rocket':'play','cat-bolt':'generate',
        'cat-fire':'generate','cat-diamond':'diamond','cat-home':'home','cat-cooking':'shop',
        'cat-cut':'cut','cat-target':'target','cat-chart':'chart','cat-shop':'shop'
    },
    esc(value){
        return String(value ?? '')
            .replace(/&/g,'&amp;')
            .replace(/</g,'&lt;')
            .replace(/>/g,'&gt;')
            .replace(/"/g,'&quot;')
            .replace(/'/g,'&#39;');
    },
    key(value,fallback='home'){
        const raw=String(value ?? '').trim();
        if(!raw)return this.key(fallback,'home');
        const upper=raw.toUpperCase();
        if(this.code[upper])return this.code[upper];
        const lower=raw.toLowerCase();
        if(this.type[lower])return this.type[lower];
        if(document.getElementById(`omb-icon-${lower}`))return lower;
        const safeFallback=String(fallback||'home').toLowerCase();
        return this.type[safeFallback]||this.code[String(fallback||'').toUpperCase()]||'home';
    },
    svg(value,opts={}){
        const key=this.key(value,opts.fallback||'home');
        const extra=opts.className?` ${this.esc(opts.className)}`:'';
        return `<svg class="omb-icon${extra}" aria-hidden="true" focusable="false"><use href="#omb-icon-${this.esc(key)}"></use></svg>`;
    },
    badge(value,opts={}){
        const key=this.key(value,opts.fallback||'home');
        const extra=opts.className?` ${this.esc(opts.className)}`:'';
        const title=opts.title?` title="${this.esc(opts.title)}"`:'';
        return `<span class="${this.esc(opts.badgeClass||'cg-code-icon')} is-svg-icon${extra}" data-icon="${this.esc(key)}" aria-hidden="true"${title}>${this.svg(key)}</span>`;
    },
    hydrate(root=document){
        const scope=root?.nodeType===1||root?.nodeType===9?root:document;
        const nodes=[];
        if(scope.matches?.('.ui-code-icon,.cg-code-icon,.nav-icon,.modal-step-code,.modal-cat-code,.modal-step-io-icon'))nodes.push(scope);
        scope.querySelectorAll?.('.ui-code-icon,.cg-code-icon,.nav-icon,.modal-step-code,.modal-cat-code,.modal-step-io-icon').forEach(el=>nodes.push(el));
        nodes.forEach(el=>{
            if(el.dataset.ombHydrated==='1')return;
            const raw=el.dataset.icon||el.textContent.trim();
            const key=this.key(raw,el.classList.contains('nav-icon')?'home':'tools');
            el.dataset.icon=key;
            el.dataset.ombHydrated='1';
            el.classList.add('is-svg-icon');
            el.innerHTML=this.svg(key);
        });
    },
    start(){
        const run=()=>this.hydrate(document);
        if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',run,{once:true});
        else run();
        if(window.MutationObserver&&document.body){
            const observer=new MutationObserver(records=>{
                records.forEach(record=>record.addedNodes.forEach(node=>this.hydrate(node)));
            });
            observer.observe(document.body,{childList:true,subtree:true});
        }
    }
};

const CGIcons=window.CGIcons={
    base:'/assets/ui-icons/',
    keys:new Set([
        'dashboard','home','projects','text','image','video','audio','folder','doc','download','batch','link','cloud','tools','generate','avatar','sync','clipboard','layout','chat',
        'agent','bots','admin','theme','add','grid','fit','send','back','next','star','pin','hide','more','delete','play','stop','cursor','hand',
        'cat-video','cat-mobile','cat-mic','cat-nature','cat-beauty','cat-creative','cat-camera','cat-music','cat-text','cat-business',
        'cat-rocket','cat-bolt','cat-fire','cat-diamond','cat-home','cat-cooking','cat-cut','cat-target','cat-chart','cat-shop'
    ]),
    type:{
        text:'text',image:'image',video:'video',audio:'audio',file:'folder',folder:'folder',doc:'doc',document:'doc',legal:'doc',
        files:'folder',download:'download',down:'download','arrow-down':'download',
        batch:'batch',link:'link',url:'link',cloud:'cloud',utility:'tools',
        tool:'tools',tools:'tools',generator:'generate',avatar:'avatar',sync:'sync',
        clipboard:'clipboard',layout:'layout',chat:'chat',chats:'chat',project:'projects',projects:'projects',dashboard:'home',home:'home',
        agent:'agent',bot:'bots',bots:'bots',theme:'theme',appearance:'theme',
        profile:'avatar',user:'avatar',admin:'admin',settings:'tools',send:'send',
        invite:'send',back:'back',prev:'back',previous:'back',next:'next',forward:'next',logout:'back',star:'star',favorite:'star',
        pin:'pin',pinned:'pin',hide:'hide',hidden:'hide',close:'hide',more:'more',ellipsis:'more',trash:'delete',delete:'delete',remove:'delete',
        add:'add',plus:'add',grid:'grid',snap:'grid',fit:'fit',run:'play',play:'play',stop:'stop',pause:'stop',cursor:'cursor',select:'cursor',hand:'hand',pan:'hand'
    },
    code:{
        dashboard:'HM',home:'HM',projects:'PR',text:'TXT',image:'IMG',video:'VID',audio:'AUD',folder:'DIR',doc:'DC',download:'DL',
        batch:'BAT',link:'URL',cloud:'CLD',tools:'CFG',generate:'RUN',avatar:'PLG',
        sync:'SYN',clipboard:'CLP',layout:'LAY',chat:'CHT',agent:'AGT',bots:'BT',admin:'AD',theme:'TH',cursor:'CUR',hand:'HAND',
        add:'ADD',grid:'GRD',fit:'FIT',send:'SEND',back:'BACK',next:'NEXT',star:'BST',pin:'PIN',hide:'HIDE',more:'MORE',delete:'DEL',play:'RUN',stop:'STOP',
        'cat-video':'VID','cat-mobile':'MOB','cat-mic':'MIC','cat-nature':'NAT',
        'cat-beauty':'BST','cat-creative':'ART','cat-camera':'CAM','cat-music':'MUS',
        'cat-text':'TXT','cat-business':'BIZ','cat-rocket':'RUN','cat-bolt':'PWR',
        'cat-fire':'HOT','cat-diamond':'VIP','cat-home':'HME','cat-cooking':'COO',
        'cat-cut':'CUT','cat-target':'TGT','cat-chart':'CHT','cat-shop':'SHP'
    },
    esc(value){return OmoIcons.esc(value)},
    key(value,fallback='dashboard'){
        const raw=String(value ?? '').trim();
        if(raw&&this.keys.has(raw))return raw;
        const lower=raw.toLowerCase();
        if(lower&&this.keys.has(lower))return lower;
        if(lower&&this.type[lower])return this.type[lower];
        if(lower&&document.getElementById(`omb-icon-${lower}`))return lower;
        return this.keys.has(fallback)?fallback:'dashboard';
    },
    src(key){
        return `${this.base}${this.key(key)}.png`;
    },
    codeFor(value,opts={}){
        const key=this.key(value,opts.fallback||'dashboard');
        return this.code[key]||this.code[this.key(opts.fallback||'dashboard')]||'UI';
    },
    isKnown(value){
        const raw=String(value ?? '').trim();
        if(!raw)return false;
        const lower=raw.toLowerCase();
        return this.keys.has(raw)||this.keys.has(lower)||!!this.type[lower]||!!document.getElementById(`omb-icon-${lower}`);
    },
    stepIcon(meta={},type='',fallback='tools'){
        const data=meta&&typeof meta==='object'?meta:{};
        const known=(value)=>{
            if(!this.isKnown(value))return '';
            return this.key(value,fallback);
        };
        const direct=[data.icon,data.output_type,data._output_type,data.category,type]
            .map(known)
            .find(key=>key&&key!=='dashboard'&&key!=='home');
        if(direct)return direct;
        const text=String([
            type,
            data.name,
            data.description,
            data.provider,
            data.model,
            data.category
        ].filter(Boolean).join(' ')).toLowerCase();
        const params=Array.isArray(data.params)?data.params.map(p=>String(p?.key||p?.name||'').toLowerCase()):[];
        const hasParam=(pattern)=>params.some(key=>pattern.test(key));
        if(hasParam(/video|frame|clip/)||/\b(video|vid|kling|runway|pika|wan|veo|reels?)\b/.test(text))return 'video';
        if(hasParam(/audio|voice|sound|music/)||/\b(audio|voice|tts|eleven|music|sound)\b/.test(text))return 'audio';
        if(hasParam(/image|img|photo|mask|reference/)||/\b(image|img|photo|picture|seedream|gpt image|midjourney|ideogram)\b/.test(text))return 'image';
        if(hasParam(/prompt|text|caption|script|system/)||/\b(text|prompt|caption|script|chat|gpt|claude|gemini|llm)\b/.test(text))return 'text';
        if(/\b(split|merge|route|foreach|for_each|generator|blueprint|utility|util)\b/.test(text))return 'tools';
        return this.key(fallback,'tools');
    },
    html(value,opts={}){
        const key=this.key(value,opts.fallback||'dashboard');
        return OmoIcons.badge(key,{...opts,badgeClass:opts.badgeClass||'cg-code-icon'});
    },
    maybe(value,opts={}){
        const raw=String(value ?? '').trim();
        const known=raw&&(this.keys.has(raw)||this.type[raw.toLowerCase()]);
        if(!known)return this.html(opts.fallback||'dashboard',opts);
        return this.html(raw||opts.fallback||'dashboard',opts);
    },
    inline(value,opts={}){
        return `<span class="cg-icon-inline">${this.html(value,opts)}</span>`;
    }
};
OmoIcons.start();

/* web/js/api-client.js */
/* ═══ API client ═══ */
const __nativeFetch=window.fetch.bind(window);
window.fetch=function(input,init={}){
    const resolved=window.AppURL?window.AppURL.request(input):input;
    return __nativeFetch(resolved,{credentials:'same-origin',...init});
};
function __internalApiRoute(url){
    if(window.AppURL)return window.AppURL.internalApiRoute(url);
    try{
        const parsed=new URL(String(url||''),window.location.href);
        return parsed.origin===window.location.origin&&/^\/api(?:\/|$)/.test(parsed.pathname)?parsed.pathname:'';
    }catch(_e){return '';}
}

function __plainErrorText(text){
    const withoutTags = String(text || '').replace(/<script[\s\S]*?<\/script>/gi, ' ').replace(/<[^>]+>/g, ' ');
    return withoutTags.replace(/\s+/g, ' ').trim().slice(0, 140);
}

const API = {
    _retryableStatus(status){
        return [408,429,500,502,503,504].includes(Number(status||0));
    },
    _retryDelayMs(attempt,meta={}){
        const base=Number(meta.retryBaseDelayMs ?? 250);
        const cap=Number(meta.retryMaxDelayMs ?? 2000);
        const raw=Math.min(cap, Math.max(0, base) * Math.pow(2, Math.max(0, attempt-1)));
        return raw;
    },
    _canRetryRequest(method,meta={}){
        if(meta.retry===false)return false;
        if(meta.signal?.aborted)return false;
        const upper=String(method||'GET').toUpperCase();
        if(upper==='GET'||upper==='HEAD')return true;
        return meta.retryUnsafe===true;
    },
    _shouldRetryResponse(data){
        if(!data||typeof data!=='object')return false;
        if(data.aborted)return false;
        if(data.network_error)return true;
        return this._retryableStatus(data.http_status);
    },
    async request(u,opts={},meta={}){
        const method=String((opts&&opts.method)||'GET').toUpperCase();
        const attempts=Math.max(1,Number(meta.retryAttempts ?? (this._canRetryRequest(method,meta)?3:1)));
        let last=null;
        for(let attempt=1;attempt<=attempts;attempt++){
            last=await this._requestOnce(u,opts,meta);
            if(attempt>=attempts)break;
            if(!this._canRetryRequest(method,meta))break;
            if(!this._shouldRetryResponse(last))break;
            await this._sleep(this._retryDelayMs(attempt,meta));
        }
        return last;
    },
    async _requestOnce(u,opts={},meta={}){
        const requestOpts={...opts};
        if(meta.signal && !requestOpts.signal)requestOpts.signal=meta.signal;
        const method=String(requestOpts.method||'GET').toUpperCase();
        const unsafeMethod=['POST','PUT','PATCH','DELETE'].includes(method);
        if(__internalApiRoute(u)&&unsafeMethod&&!meta.skipAuth&&!meta.skipCsrf&&typeof Auth!=='undefined'&&Auth.csrfToken){
            const headers=new Headers(requestOpts.headers||{});
            headers.set('X-CSRF-Token',Auth.csrfToken);
            requestOpts.headers=headers;
        }
        let timeoutId=null;
        if(!requestOpts.signal && typeof AbortController!=='undefined'){
            const controller=new AbortController();
            const isUpload=typeof FormData!=='undefined'&&requestOpts.body instanceof FormData;
            const timeoutMs=Number(meta.timeoutMs||0)||(isUpload?120000:45000);
            requestOpts.signal=controller.signal;
            timeoutId=setTimeout(()=>controller.abort(),timeoutMs);
        }
        let r;
        try{
            r=await fetch(u,requestOpts);
        }catch(e){
            if(timeoutId)clearTimeout(timeoutId);
            const timedOut=e&&e.name==='AbortError';
            return {
                ok:false,
                error:timedOut?'Запрос занял слишком много времени':'Не удалось подключиться к серверу',
                http_status:0,
                network_error:true,
                timeout:timedOut
            };
        }
        if(timeoutId)clearTimeout(timeoutId);
        let data={};
        try{
            if(typeof r.text==='function'){
                const rawText=await r.text();
                if(!rawText&&typeof r.json==='function'&&!('bodyUsed' in r)){
                    data=await r.json();
                }else{
                    try{
                        data=rawText?JSON.parse(rawText):{};
                    }catch(e){
                        const title=__plainErrorText((rawText.match(/<title>([\s\S]*?)<\/title>/i)||[])[1]||'');
                        const brief=title||__plainErrorText(rawText);
                        data={ok:r.ok,error:r.ok?'':(brief||`HTTP ${r.status} ${r.statusText||'Request failed'}`)};
                    }
                }
            }else if(typeof r.json==='function'){
                data=await r.json();
            }
        }catch(e){
            data={ok:r.ok,error:r.ok?'':`HTTP ${r.status} ${r.statusText||'Request failed'}`};
        }
        if(data && typeof data==='object' && !Array.isArray(data)){
            if(!r.ok && typeof data.ok==='undefined')data.ok=false;
            if(typeof data.http_status==='undefined')data.http_status=r.status;
        }
        if(r.status===401 && !meta.allowUnauthorized && !meta.skipAuth && typeof Auth!=='undefined'){
            if(Auth.pendingTotp)return data;
            Auth.requireLogin();
        }
        if(r.status===402 && data && data.code==='insufficient_wallet_funds' && !meta.skipInsufficientFundsModal){
            try{
                if(typeof InsufficientFunds!=='undefined' && InsufficientFunds.show){
                    InsufficientFunds.show({
                        needed:Number(data.needed_credits||0),
                        balance:Number(data.balance_credits||0),
                        message:data.error||'',
                    });
                }
            }catch(_e){/* never let UI plumbing block the response */}
        }
        return data;
    },
    // Task/job mutations invalidate the cached listing endpoints so pages
    // re-render with fresh counts without manual invalidation at each site.
    _invalidateForMutation(u){
        const s=__internalApiRoute(u);
        if(s.startsWith('/api/tasks')||s.startsWith('/api/jobs/run')){
            this.invalidatePrefix('/api/tasks?summary=1');
            this.invalidatePrefix('/api/jobs/stats?group_by=project');
        }
    },
    async get(u,meta={}){return this.request(u,{},meta)},
    async post(u,d,meta={}){this._invalidateForMutation(u);return this.request(u,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(d||{})},meta)},
    async patch(u,d,meta={}){this._invalidateForMutation(u);return this.request(u,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify(d||{})},meta)},
    async put(u,d,meta={}){this._invalidateForMutation(u);return this.request(u,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(d||{})},meta)},
    async del(u,meta={}){this._invalidateForMutation(u);return this.request(u,{method:'DELETE'},meta)},
    // ── Session-scoped GET cache (UI-audit WS-7.2) ──
    // Auth is cookie-based and the user can change without a page reload,
    // so every cache entry and in-flight promise is keyed by the owner id
    // and flushed via resetSessionCache() from Auth.setUser().
    _cache:new Map(),
    _inFlight:new Map(),
    _cacheOwnerId(){
        return (typeof Auth!=='undefined'&&Auth.user&&Auth.user.id)?String(Auth.user.id):'anon';
    },
    _cacheKey(url){
        const publicUrl=window.AppURL?window.AppURL.resolve(url):url;
        const namespace=window.AppConfig?.browserNamespace;
        return `${namespace?namespace+'|':''}${this._cacheOwnerId()}|${publicUrl}`;
    },
    async getCached(url,opts={}){
        const ttlMs=Number(opts.ttlMs||60000);
        const meta=opts.meta||{};
        const owner=this._cacheOwnerId();
        const key=this._cacheKey(url);
        const entry=this._cache.get(key);
        if(entry&&entry.owner===owner&&Date.now()-entry.time<entry.ttlMs){
            return JSON.parse(JSON.stringify(entry.data));
        }
        const inflight=this._inFlight.get(key);
        if(inflight&&inflight.owner===owner){
            const shared=await inflight.promise;
            return JSON.parse(JSON.stringify(shared));
        }
        const controller=(typeof AbortController!=='undefined')?new AbortController():null;
        const promise=this.get(url,{...meta,signal:controller?controller.signal:undefined}).then((data)=>{
            this._inFlight.delete(key);
            const looksLikeError=data&&typeof data==='object'&&!Array.isArray(data)
                &&(data.error||(data.ok===false));
            if(!looksLikeError){
                this._cache.set(key,{owner:owner,time:Date.now(),ttlMs,data});
            }
            return data;
        });
        this._inFlight.set(key,{promise,controller,owner});
        const data=await promise;
        return JSON.parse(JSON.stringify(data));
    },
    invalidate(url){
        this._cache.delete(this._cacheKey(url));
    },
    invalidatePrefix(prefix){
        const suffix=`|${window.AppURL?window.AppURL.resolve(prefix):prefix}`;
        for(const key of Array.from(this._cache.keys())){
            if(key.endsWith(suffix))this._cache.delete(key);
        }
    },
    // Drop every cached entry and abort in-flight GETs — a response from the
    // previous session must never land in the new one.
    resetSessionCache(){
        this._cache.clear();
        for(const entry of this._inFlight.values()){
            try{entry.controller?.abort();}catch(_e){}
        }
        this._inFlight.clear();
    },
    _normalizeErrorMessage(message){
        const text=String(message||'').trim();
        if(!text)return text;
        const lower=text.toLowerCase();
        if(lower.includes('csrf origin')){
            return 'Не удалось подтвердить источник запроса. Обновите страницу и попробуйте снова.';
        }
        if(lower.includes('csrf token')){
            return 'Сессия устарела. Обновите страницу и войдите снова.';
        }
        return text;
    },
    _errorMessage(resp,fallback='Request failed'){
        if(resp&&typeof resp==='object'){
            if(resp.error||resp.message)return this._normalizeErrorMessage(resp.error||resp.message);
            if(resp.ok===false&&resp.http_status)return `HTTP ${resp.http_status}`;
        }
        return this._normalizeErrorMessage(fallback);
    },
    _throwIfError(resp,fallback='Request failed'){
        if(!resp||resp.ok===false||resp.error){
            const err=new Error(this._errorMessage(resp,fallback));
            err.response=resp||{};
            throw err;
        }
        return resp;
    },
    _contractError(label, detail='неожиданный ответ сервера'){
        const message=`${label}: ${detail}`;
        console.error('API contract mismatch:', message);
        if(typeof Toast!=='undefined')Toast.error(message);
        return message;
    },
    // A plan-gated endpoint answers `{error, feature}` with HTTP 403 (see
    // api/access.py:feature_error_response). That is an expected, gracefully
    // handled state — not a contract mismatch — so callers must not log/toast.
    featureGate(resp){
        if(resp&&typeof resp==='object'&&!Array.isArray(resp)
            &&resp.http_status===403
            &&typeof resp.feature==='string'&&resp.feature){
            return {feature:resp.feature,message:this._normalizeErrorMessage(resp.error||'')};
        }
        return null;
    },
    expectArray(data,label='API'){
        if(Array.isArray(data))return data;
        if(this.featureGate(data))return [];
        this._contractError(label, data?.error||'ожидался список');
        return [];
    },
    expectObject(data,label='API'){
        if(data&&typeof data==='object'&&!Array.isArray(data)&&!data.error)return data;
        if(this.featureGate(data))return {};
        this._contractError(label, data?.error||'ожидался объект');
        return {};
    },
    _sleep(ms){
        return new Promise((resolve)=>setTimeout(resolve,Math.max(0,Number(ms||0))));
    },
    _uploadProgressComplete(progress){
        if(!progress||typeof progress!=='object')return false;
        const loaded=Number(progress.loaded||0);
        const total=Number(progress.total||0);
        if(progress.lengthComputable&&total>0&&loaded>=total)return true;
        return Number(progress.percent||0)>=100;
    },
    _uploadRecoveryPayload(item,file,startedAt){
        if(!item||typeof item!=='object')return null;
        const name=String(file?.name||'');
        const size=Number(file?.size||0);
        if(!name||!Number.isFinite(size)||size<=0)return null;
        if(String(item.original_name||'')!==name)return null;
        if(Number(item.size||0)!==size)return null;
        const createdAt=Number(item.created_at||0);
        if(startedAt&&createdAt&&createdAt<startedAt-300)return null;
        const localUrl=item.virtual_path||item.public_url||'';
        if(!localUrl)return null;
        return {
            local_url:localUrl,
            filename:localUrl.split('/').pop()||name,
            file_id:item.id||'',
            recovered:true
        };
    },
    async _recoverCompletedUpload(file,meta={}){
        const attempts=Math.max(1,Number(meta.uploadRecoveryAttempts||6));
        const delayMs=Number(meta.uploadRecoveryDelayMs ?? 3000);
        const startedAt=Number(meta.uploadStartedAt||0);
        for(let i=0;i<attempts;i++){
            const files=await this.get('/api/me/files?kind=upload&limit=50',{
                timeoutMs:Number(meta.uploadRecoveryRequestTimeoutMs||15000),
                skipInsufficientFundsModal:true
            }).catch(()=>null);
            if(Array.isArray(files)){
                for(const item of files){
                    const recovered=this._uploadRecoveryPayload(item,file,startedAt);
                    if(recovered)return recovered;
                }
            }
            if(i<attempts-1)await this._sleep(delayMs);
        }
        return null;
    },
    _uploadWithProgress(url,formData,meta,onProgress){
        return new Promise((resolve)=>{
            const xhr=new XMLHttpRequest();
            let settled=false;
            xhr.open('POST',window.AppURL?window.AppURL.resolve(url):url,true);
            xhr.withCredentials=true;
            xhr.timeout=Number(meta.timeoutMs||0)||1200000;
            if(__internalApiRoute(url)&&!meta.skipAuth&&!meta.skipCsrf&&typeof Auth!=='undefined'&&Auth.csrfToken){
                xhr.setRequestHeader('X-CSRF-Token',Auth.csrfToken);
            }
            if(xhr.upload&&typeof xhr.upload.addEventListener==='function'){
                xhr.upload.addEventListener('progress',(event)=>{
                    const loaded=Number(event.loaded||0);
                    const total=Number(event.total||0);
                    const percent=event.lengthComputable&&total>0
                        ? Math.max(0,Math.min(100,Math.round((loaded/total)*100)))
                        : 0;
                    onProgress({loaded,total,percent,lengthComputable:!!event.lengthComputable});
                });
            }
            const finish=(data)=>{
                if(settled)return;
                settled=true;
                resolve(data);
            };
            xhr.onload=()=>{
                let data={};
                try{data=JSON.parse(xhr.responseText||'{}')}catch(_e){
                    data={error:`HTTP ${xhr.status||0}`};
                }
                if(data&&typeof data==='object'&&!Array.isArray(data)){
                    if((xhr.status<200||xhr.status>=300)&&typeof data.ok==='undefined')data.ok=false;
                    if(typeof data.http_status==='undefined')data.http_status=xhr.status||0;
                }
                if(xhr.status===401&&!meta.allowUnauthorized&&!meta.skipAuth&&typeof Auth!=='undefined'){
                    if(!Auth.pendingTotp)Auth.requireLogin();
                }
                finish(data);
            };
            xhr.onerror=()=>finish({
                ok:false,
                error:'Не удалось подключиться к серверу',
                http_status:0,
                network_error:true,
                timeout:false
            });
            xhr.ontimeout=()=>finish({
                ok:false,
                error:'Загрузка заняла слишком много времени',
                http_status:0,
                network_error:true,
                timeout:true
            });
            xhr.onabort=()=>finish({
                ok:false,
                error:'Запрос отменён',
                http_status:0,
                network_error:true,
                aborted:true
            });
            if(meta.signal&&typeof meta.signal.addEventListener==='function'){
                if(meta.signal.aborted){
                    xhr.abort();
                    return;
                }else{
                    meta.signal.addEventListener('abort',()=>xhr.abort(),{once:true});
                }
            }
            xhr.send(formData);
        });
    },
    async upload(file,opts={}){
        if(!file)throw new Error('No file selected');
        const fd=new FormData();
        fd.append(opts.fieldName||'file',file);
        const meta={...(opts.meta||{})};
        if(!meta.timeoutMs)meta.timeoutMs=1200000;
        const url=opts.url||'/api/upload';
        const uploadStartedAt=Date.now()/1000;
        let lastProgress=null;
        const onProgress=typeof opts.onProgress==='function'
            ? (progress)=>{
                lastProgress=progress;
                opts.onProgress(progress);
            }
            : null;
        const data=(onProgress&&typeof XMLHttpRequest!=='undefined')
            ? await this._uploadWithProgress(url,fd,meta,onProgress)
            : await this.request(url,{method:'POST',body:fd},meta);
        try{
            this._throwIfError(data,'Upload failed');
        }catch(err){
            const canRecover=(data?.network_error||Number(data?.http_status||0)===0)
                && this._uploadProgressComplete(lastProgress);
            if(canRecover){
                const recovered=await this._recoverCompletedUpload(file,{...meta,uploadStartedAt});
                if(recovered)return recovered;
            }
            throw err;
        }
        if(!data.local_url){
            const err=new Error(this._errorMessage(data,'Upload response missing local_url'));
            err.response=data||{};
            throw err;
        }
        return data;
    },
    async urlPreview(url){
        const data=await this.post('/api/url_preview',{url});
        return this._throwIfError(data,'Preview failed');
    }
};

window.API=API;

/* web/js/profile.js */
/* ═══ Profile screen ═══ */
const Profile={
    async load(force=false){
        let user=Auth.user;
        if(force||!user){
            const resp=await API.get('/api/me');
            if(!resp||resp.error){
                this._setStatus(resp?.error||'Не удалось загрузить профиль','error');
                return;
            }
            Auth.setUser(resp);
            user=resp;
            Auth.renderUser();
        }
        this.render(user);
        this._setStatus('');
        if(typeof Wallet!=='undefined')Wallet.load();
        this.referral();
        if(document.getElementById('mcp-connections-list')||document.getElementById('mcp-server-url')){
            this.loadMcpConnections().catch(()=>this._setMcpStatus('Не удалось загрузить MCP подключения','error'));
        }
    },
    async referral(){
        const section=document.getElementById('profile-referral-section');
        if(!section)return;
        const resp=await API.get('/api/me/referral');
        if(!resp||resp.error||!resp.enabled){
            section.hidden=true;
            return;
        }
        section.hidden=false;
        const params=resp.params||{};
        const stats=resp.stats||{};
        const balance=resp.balance||{};
        const hint=document.getElementById('profile-referral-hint');
        if(hint){
            const pct=Number(params.percent||0);
            const welcomePct=Number(params.welcome_percent||0);
            const parts=[];
            if(pct>0)parts.push(`Получайте ${pct}% рублями с каждой оплаты приглашённого`);
            if(welcomePct>0)parts.push(`друг получает +${welcomePct}% токенами к первому пополнению`);
            hint.textContent=parts.length?parts.join(', ')+'.':'';
        }
        const urlInput=document.getElementById('referral-share-url');
        if(urlInput)urlInput.value=resp.share_url||'';
        this._text('referral-stats-invited',Number(stats.invited_total||0).toLocaleString('ru-RU'));
        this._text('referral-stats-paid',Number(stats.invited_paid||0).toLocaleString('ru-RU'));
        this._text('referral-stats-earned',this._fmtMinor(stats.earned_minor));
        this._renderReferralWithdraw(balance,resp.withdrawals||[]);
    },
    _fmtMinor(minor){
        const r=Number(minor||0)/100;
        return r.toLocaleString('ru-RU',{minimumFractionDigits:0,maximumFractionDigits:2})+' ₽';
    },
    _renderReferralWithdraw(balance,withdrawals){
        const box=document.getElementById('profile-referral-withdraw');
        if(!box)return;
        box.hidden=false;
        this._text('referral-balance-available',this._fmtMinor(balance.available_minor));
        this._text('referral-balance-pending',this._fmtMinor(balance.pending_minor));
        const minMinor=Number(balance.min_withdrawal_minor||0);
        const holdDays=Number(balance.hold_days||0);
        this._text('referral-withdraw-hint',
            `Минимум для вывода ${this._fmtMinor(minMinor)}. Каждое начисление становится доступным через ${holdDays} дн.`);
        const hasPending=(withdrawals||[]).some(w=>w&&w.status==='pending');
        const available=Number(balance.available_minor||0);
        const btn=document.getElementById('referral-withdraw-btn');
        if(btn)btn.disabled=hasPending||available<minMinor||available<=0;
        const list=document.getElementById('referral-withdrawals-list');
        if(!list)return;
        list.textContent='';
        const labels={pending:'на рассмотрении',paid:'выплачено',rejected:'отклонено'};
        (withdrawals||[]).forEach(w=>{
            if(!w)return;
            const row=document.createElement('div');
            row.className='profile-referral-wd-item';
            const left=document.createElement('span');
            let amt=this._fmtMinor(w.amount_minor);
            if(w.payout_destination_masked)amt+=` · ${w.payout_destination_masked}`;
            left.textContent=amt;
            const right=document.createElement('span');
            let st=labels[w.status]||w.status||'';
            if(w.status==='rejected'&&w.reject_reason)st+=` · ${w.reject_reason}`;
            right.textContent=st;
            row.appendChild(left);
            row.appendChild(right);
            list.appendChild(row);
        });
    },
    async withdraw(){
        const methodEl=document.getElementById('referral-payout-method');
        const dstEl=document.getElementById('referral-payout-destination');
        const payout_method=(methodEl?.value||'').trim();
        const payout_destination=(dstEl?.value||'').trim();
        if(!payout_destination){
            this._setReferralStatus('Укажите реквизиты для вывода','error');
            return;
        }
        const btn=document.getElementById('referral-withdraw-btn');
        if(btn)btn.disabled=true;
        const resp=await API.post('/api/me/referral/withdraw',{payout_method,payout_destination});
        if(!resp||resp.error){
            this._setReferralStatus(resp?.error||'Не удалось создать заявку','error');
            if(btn)btn.disabled=false;
            return;
        }
        if(dstEl)dstEl.value='';
        this._setReferralStatus('Заявка на вывод создана','ok');
        this.referral();
    },
    async copyReferralLink(){
        const input=document.getElementById('referral-share-url');
        const url=(input?.value||'').trim();
        if(!url){
            this._setReferralStatus('Ссылка ещё не готова','error');
            return;
        }
        try{
            if(navigator.clipboard&&navigator.clipboard.writeText){
                await navigator.clipboard.writeText(url);
            }else if(input){
                input.select();
                document.execCommand('copy');
            }
            this._setReferralStatus('Ссылка скопирована','ok');
        }catch(_e){
            this._setReferralStatus('Не удалось скопировать','error');
        }
    },
    _setReferralStatus(message,type=''){
        const el=document.getElementById('referral-status');
        if(!el)return;
        el.textContent=message||'';
        el.className='profile-status';
        if(type)el.classList.add(`profile-status-${type}`);
    },
    render(user=Auth.user){
        if(!user)return;
        const email=user.email||'';
        const name=(user.display_name||'').trim() || (email ? email.split('@',1)[0] : 'Пользователь');
        this._value('profile-email',email);
        this._value('profile-display-name',user.display_name||name);
        this._value('profile-timezone',user.timezone||'Europe/Moscow');
        this._value('profile-locale',user.locale||'ru');
        this._text('profile-summary-name',name);
        this._text('profile-summary-email',email);
        this._text('profile-role-badge',user.role||'user');
        this._text('profile-2fa-badge',user.totp_enabled?'2FA включена':'2FA выкл.');
        this.preview();
    },
    preview(){
        const email=document.getElementById('profile-email')?.value||Auth.user?.email||'';
        const name=document.getElementById('profile-display-name')?.value?.trim()||email.split('@',1)[0]||'Пользователь';
        const avatarUrl=Auth._safeAvatarUrl(Auth.user?.avatar_url||'');
        const preview=document.getElementById('profile-avatar-preview');
        const initials=document.getElementById('profile-avatar-initials');
        const img=document.getElementById('profile-avatar-img');
        this._text('profile-summary-name',name);
        if(initials)initials.textContent=Auth._initials(name,email);
        if(!preview||!img)return;
        if(avatarUrl){
            img.src=avatarUrl;
            preview.classList.add('has-image');
        }else{
            img.removeAttribute('src');
            preview.classList.remove('has-image');
        }
        if(!img._profilePreviewErrorBound){
            img._profilePreviewErrorBound=true;
            img.addEventListener('error',()=>preview.classList.remove('has-image'));
        }
    },
    async save(){
        const btn=document.getElementById('profile-save-btn');
        if(btn)btn.disabled=true;
        this._setStatus('Сохраняю...');
        const payload={
            display_name:document.getElementById('profile-display-name')?.value?.trim()||'',
            timezone:document.getElementById('profile-timezone')?.value?.trim()||'Europe/Moscow',
            locale:document.getElementById('profile-locale')?.value||'ru'
        };
        const resp=await API.patch('/api/me',payload);
        if(btn)btn.disabled=false;
        if(!resp||resp.error){
            this._setStatus(resp?.error||'Не удалось сохранить','error');
            return;
        }
        Auth.setUser(resp.user||{...Auth.user,...payload});
        Auth.renderUser();
        this.render(Auth.user);
        this._setStatus('Сохранено','ok');
    },
    async changePassword(){
        const btn=document.getElementById('profile-password-btn');
        const current=document.getElementById('profile-current-password')?.value||'';
        const next=document.getElementById('profile-new-password')?.value||'';
        const confirm=document.getElementById('profile-confirm-password')?.value||'';
        if(!current||!next||!confirm){
            this._setPasswordStatus('Заполните все поля','error');
            return;
        }
        if(next.length<10){
            this._setPasswordStatus('Минимум 10 символов','error');
            return;
        }
        if(next!==confirm){
            this._setPasswordStatus('Пароли не совпадают','error');
            return;
        }
        if(btn)btn.disabled=true;
        this._setPasswordStatus('Сохраняю...');
        const resp=await API.post('/api/me/password',{
            current_password:current,
            new_password:next,
            confirm_password:confirm
        });
        if(btn)btn.disabled=false;
        if(!resp||resp.error){
            this._setPasswordStatus(resp?.error||'Не удалось сменить пароль','error');
            return;
        }
        ['profile-current-password','profile-new-password','profile-confirm-password'].forEach((id)=>this._value(id,''));
        this._setPasswordStatus('Пароль изменён','ok');
    },
    _value(id,value){
        const el=document.getElementById(id);
        if(el)el.value=value??'';
    },
    _text(id,value){
        const el=document.getElementById(id);
        if(el)el.textContent=value??'';
    },
    _setStatus(message,type=''){
        const el=document.getElementById('profile-status');
        if(!el)return;
        el.textContent=message||'';
        el.className='profile-status';
        if(type)el.classList.add(`profile-status-${type}`);
    },
    _setPasswordStatus(message,type=''){
        const el=document.getElementById('profile-password-status');
        if(!el)return;
        el.textContent=message||'';
        el.className='profile-status';
        if(type)el.classList.add(`profile-status-${type}`);
    },
    async loadMcpConnections(){
        const urlInput=document.getElementById('mcp-server-url');
        if(urlInput)urlInput.value=window.location.origin+(window.AppURL?window.AppURL.resolve('/mcp'):'/mcp');
        if(window.AppConfig?.environment==='new')this._setMcpStatus('MCP тестовой версии пока недоступен','');
        const resp=await API.get('/api/me/mcp/connections');
        if(!resp||resp.error)return;
        this._renderMcpConnections(resp.connections||[]);
    },
    _renderMcpConnections(connections){
        const list=document.getElementById('mcp-connections-list');
        if(!list)return;
        list.textContent='';
        if(!connections.length){
            list.textContent='Нет активных подключений';
            return;
        }
        connections.forEach(c=>{
            const row=document.createElement('div');
            row.className='profile-mcp-connection';

            const head=document.createElement('div');
            head.className='profile-mcp-connection-head';
            const name=document.createElement('span');
            name.className='profile-mcp-name';
            name.textContent=c.client_name||c.client_id;
            const identity=document.createElement('span');
            identity.className='profile-mcp-identity';
            // Verified vs unverified identity — surfaced plainly, no secrets.
            identity.textContent=c.trust_label_ru||(
                c.trust_label==='metadata-verified'?'подтверждён':'не проверен'
            );
            if(c.trust_label==='metadata-verified'){
                identity.classList.add('profile-mcp-identity-verified');
            }else{
                identity.classList.add('profile-mcp-identity-unverified');
            }
            head.append(name,identity);

            // Hosts — where this client can redirect / is verified.
            const hosts=(c.redirect_hosts||[]).join(', ');
            const meta=document.createElement('div');
            meta.className='profile-mcp-info';
            const created=c.created_at?new Date(c.created_at*1000).toLocaleDateString('ru-RU'):'—';
            const last=c.last_used||c.last_used_at?new Date((c.last_used||c.last_used_at)*1000).toLocaleDateString('ru-RU'):'никогда';
            meta.textContent=`Создано: ${created} · Последний вход: ${last}`+(hosts?` · Хост: ${hosts}`:'');

            // Active/revoked state.
            const state=document.createElement('div');
            state.className='profile-mcp-state';
            const active=(c.state||'active')==='active';
            state.textContent=active?'Активно':'Отозвано';
            state.classList.add(active?'profile-mcp-state-active':'profile-mcp-state-revoked');

            // Grouped Russian scope labels.
            const scopes=document.createElement('ul');
            scopes.className='profile-mcp-scopes';
            (c.scope_groups||[]).forEach(label=>{
                const li=document.createElement('li');
                li.textContent=label;
                scopes.appendChild(li);
            });
            if(!scopes.children.length){
                const li=document.createElement('li');
                li.textContent='Доступы не указаны';
                li.className='profile-mcp-scope-empty';
                scopes.appendChild(li);
            }

            // Policy summary (localized) + migrated-grant badge.
            const policy=document.createElement('div');
            policy.className='profile-mcp-policy';
            const pol=c.policy||{};
            const lim=pol.limits||{};
            const policyParts=[];
            if(pol.profile){policyParts.push(this._policyProfileLabelRu(pol.profile));}
            if(lim.credits_per_day){policyParts.push(`Лимит кредитов/день: ${lim.credits_per_day}`);}
            if(lim.active_operations){policyParts.push(`Активных операций: ${lim.active_operations}`);}
            policy.textContent=policyParts.length?policyParts.join(' · '):'Политика по умолчанию';

            if(c.migrated){
                const badge=document.createElement('span');
                badge.className='profile-mcp-badge-migrated';
                badge.textContent='Совместимый старый режим — проверьте доступы';
                policy.append(badge);
            }

            // Edit-policy control — native button, keyboard-accessible.
            const actions=document.createElement('div');
            actions.className='profile-mcp-actions';
            const editBtn=document.createElement('button');
            editBtn.className='btn btn-ghost btn-small';
            editBtn.type='button';
            editBtn.dataset.action='mcp-edit-policy';
            editBtn.dataset.grantId=c.grant_id||'';
            editBtn.dataset.updatedAt=String(c.updated_at||'');
            editBtn.textContent='Изменить политику';
            if(!c.grant_id){editBtn.disabled=true;}
            actions.append(editBtn);

            // Revoke — native button. Confirmation + optional cancel-active-work
            // is handled by revokeGrant (bound via data-action).
            const revokeBtn=document.createElement('button');
            revokeBtn.className='btn btn-small btn-danger';
            revokeBtn.type='button';
            revokeBtn.dataset.action='mcp-revoke-grant';
            revokeBtn.dataset.grantId=c.grant_id||'';
            revokeBtn.dataset.clientId=c.client_id||'';
            revokeBtn.textContent='Отозвать';
            if(!c.grant_id){revokeBtn.dataset.action='mcp-revoke-connection';}
            actions.append(revokeBtn);

            row.append(head,meta,state,scopes,policy,actions);
            list.appendChild(row);
        });
    },
    _policyProfileLabelRu(profile){
        return {
            'safe-default':'Безопасный режим',
            'legacy-compatible':'Совместимый старый режим',
            'custom':'Своя политика',
            'deny-all':'Всё запрещено',
        }[profile]||profile;
    },
    async copyMcpUrl(){
        const input=document.getElementById('mcp-server-url');
        const url=input?.value?.trim();
        if(!url)return;
        try{
            if(navigator.clipboard?.writeText)await navigator.clipboard.writeText(url);
            else if(input){input.select();document.execCommand('copy');}
            this._setMcpStatus('URL скопирован','ok');
        }catch(_e){this._setMcpStatus('Не удалось скопировать','error');}
    },
    async revokeMcpConnection(clientId){
        const resp=await API.post(`/api/me/mcp/connections/${clientId}/revoke`);
        if(!resp||resp.error){
            this._setMcpStatus(resp?.error||'Не удалось отозвать','error');
            return;
        }
        this._setMcpStatus('Подключение отозвано','ok');
        this.loadMcpConnections();
    },
    async revokeGrant(grantId,opts={}){
        if(!grantId){this._setMcpStatus('Грант не найден','error');return;}
        const msg='Отозвать доступ этого подключения? Текущие токены перестанут работать немедленно.';
        const confirmError='Не удалось подтвердить отзыв. Доступ не отозван';
        if(typeof Dialogs==='undefined'||typeof Dialogs.confirm!=='function'){
            this._setMcpStatus(confirmError,'error');
            return;
        }
        let confirmed;
        try{
            confirmed=await Dialogs.confirm(msg,'отзыв доступа');
        }catch(_e){
            this._setMcpStatus(confirmError,'error');
            return;
        }
        if(typeof confirmed!=='boolean'){
            this._setMcpStatus(confirmError,'error');
            return;
        }
        if(!confirmed){this._setMcpStatus('Отзыв отменён','');return;}
        const cancelActiveWork=opts.cancel_active_work===true;
        const resp=await API.post(
            `/api/me/mcp/grants/${grantId}/revoke`,
            {cancel_active_work:cancelActiveWork},
        );
        if(!resp||resp.error){
            this._setMcpStatus(resp?.error||'Не удалось отозвать','error');
            return;
        }
        const note=resp.cancellation&&resp.cancellation.requested&&!resp.cancellation.ok
            ?' (отмена активных задач не удалась — доступ всё равно отозван)':'';
        this._setMcpStatus('Подключение отозвано'+note,'ok');
        await this.loadMcpConnections();
    },
    async updateGrantPolicy(grantId,expectedUpdatedAt,policy){
        if(!grantId){this._setMcpStatus('Грант не найден','error');return null;}
        const resp=await API.patch(
            `/api/me/mcp/grants/${grantId}/policy`,
            {expected_updated_at:Number(expectedUpdatedAt)||0,policy:policy||{}},
        );
        if(!resp||resp.error||resp.http_status===409){
            // Stale UI write -> conflict. Tell the user to reload.
            this._setMcpStatus('Политика изменена другим устройством — обновите список','error');
            this.loadMcpConnections();
            return resp;
        }
        this._setMcpStatus('Политика обновлена','ok');
        this.loadMcpConnections();
        return resp;
    },
    _setMcpStatus(msg,type=''){
        const el=document.getElementById('mcp-status');
        if(!el)return;
        el.textContent=msg||'';
        el.className='profile-status';
        if(type)el.classList.add(`profile-status-${type}`);
    }
};

window.Profile=Profile;

/* web/js/router.js */
/* ═══ Router ═══ */
const Router={
    current:'home', params:{}, generation:0, _leaveHooks:{},
    _loadedChunks:new Set(['core']), _navigationGeneration:0,
    _routeMap:new Set(['home','projects','project','task','gallery','chats','bots','templates','settings','profile','wallet','debugger','admin']),
    _encode(value){return encodeURIComponent(String(value||''))},
    _decode(value){
        try{return decodeURIComponent(String(value||''))}
        catch(_e){return String(value||'')}
    },
    _routeToHash(page,params={}){
        if(page==='wallet')page='profile';
        if(page==='project')return '#/projects/'+this._encode(params.id);
        if(page==='task'){
            const tab=params.tab&&params.tab!=='tv-editor'?`?tab=${this._encode(params.tab)}`:'';
            return '#/tasks/'+this._encode(params.projectId)+'/'+this._encode(params.taskId)+tab;
        }
        if(page==='debugger'&&params.jobId){
            return '#/debugger/'+this._encode(params.jobId);
        }
        const simple=this._routeMap.has(page)?page:'home';
        return '#/'+simple;
    },
    _parseLocation(){
        const raw=(window.location.hash||'').replace(/^#\/?/,'');
        if(!raw)return {page:'home',params:{}};
        const [path,query='']=raw.split('?',2);
        const parts=path.split('/').filter(Boolean).map((part)=>this._decode(part));
        const qs=new URLSearchParams(query);
        if(parts[0]==='wallet')return {page:'profile',params:{}};
        if(parts[0]==='projects'&&parts[1])return {page:'project',params:{id:parts[1]}};
        if(parts[0]==='projects')return {page:'projects',params:{}};
        if(parts[0]==='tasks'&&parts[1]&&parts[2]){
            return {page:'task',params:{projectId:parts[1],taskId:parts[2],tab:qs.get('tab')||'tv-editor'}};
        }
        if(parts[0]==='debugger'&&parts[1]){
            return {page:'debugger',params:{jobId:parts[1]}};
        }
        const page=this._routeMap.has(parts[0])?parts[0]:'home';
        return {page,params:{}};
    },
    _navPage(page){
        if(page==='project'||page==='task')return 'projects';
        return page;
    },
    _sameRoute(page,params){
        return this.current===page&&JSON.stringify(this.params||{})===JSON.stringify(params||{});
    },
    _hasUnsavedTaskChanges(){
        return this.current==='task'
            && typeof AutoSave!=='undefined'
            && !!AutoSave._dirty;
    },
    _confirmLeaveDirtyTask(nextPage,nextParams={},opts={}){
        if(opts.force||opts.skipUnsavedCheck)return true;
        if(this.current!=='task'||nextPage==='task')return true;
        if(!this._hasUnsavedTaskChanges())return true;
        const message='Есть несохранённые правки. Уйти без сохранения?';
        if(typeof Dialogs!=='undefined'&&Dialogs.confirm){
            Dialogs.confirm(message).then(ok=>{
                if(ok)this.go(nextPage,nextParams,{...opts,skipUnsavedCheck:true});
                else this._writeHistory(this.current,this.params||{},true);
            });
        }
        return false;
    },
    _writeHistory(page,params={},replace=false){
        const hash=this._routeToHash(page,params);
        const local=window.location.pathname+window.location.search+hash;
        const next=window.AppURL?window.AppURL.resolve(local):local;
        const current=window.location.pathname+window.location.search+window.location.hash;
        if(next===current)return;
        if(replace)window.history.replaceState({page,params},'',next);
        else window.history.pushState({page,params},'',next);
    },
    replace(page,params={}){
        this._writeHistory(page,params,true);
    },
    registerLeaveHook(page,fn){
        const key=String(page||'');
        if(!key||typeof fn!=='function')return()=>{};
        if(!this._leaveHooks[key])this._leaveHooks[key]=new Set();
        this._leaveHooks[key].add(fn);
        return()=>this._leaveHooks[key]?.delete(fn);
    },
    closeDebugger(){
        if(typeof JobDebugger!=='undefined')return JobDebugger.close();
        return this.go('home');
    },
    _runLeaveHooks(from,to,fromParams,toParams){
        const hooks=this._leaveHooks[from];
        if(!hooks||!hooks.size)return;
        const payload={from,to,fromParams:{...(fromParams||{})},toParams:{...(toParams||{})}};
        hooks.forEach(fn=>{
            try{fn(payload)}catch(e){console.warn('[Router] leave hook failed',from,e)}
        });
    },
    _chunkForPage(page){
        if(page==='task'||page==='debugger'||page==='workboard')return 'canvas';
        if(page==='bots')return 'bots';
        if(page==='admin')return 'admin';
        return 'core';
    },
    _clearChunkState(page){
        const el=document.getElementById('page-'+page);
        if(!el)return;
        el.removeAttribute('aria-busy');
        el.querySelector('[data-chunk-state]')?.remove();
    },
    _showChunkState(page,state,retry){
        const el=document.getElementById('page-'+page);
        if(!el)return;
        document.querySelectorAll('.page').forEach(candidate=>candidate.classList.remove('active'));
        el.classList.add('active');
        el.setAttribute('aria-busy',state==='loading'?'true':'false');
        let status=el.querySelector('[data-chunk-state]');
        if(!status){
            status=document.createElement('div');
            status.className='empty-state';
            el.prepend(status);
        }
        status.dataset.chunkState=state;
        if(state==='loading'){
            status.setAttribute('role','status');
            status.innerHTML='<div class="empty-state-text">\u0417\u0430\u0433\u0440\u0443\u0437\u043a\u0430 \u0440\u0430\u0437\u0434\u0435\u043b\u0430\u2026</div>';
            return;
        }
        status.setAttribute('role','alert');
        status.innerHTML='<div class="empty-state-text">\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044c \u0440\u0430\u0437\u0434\u0435\u043b</div><div class="empty-state-actions"><button type="button" class="btn btn-primary" data-chunk-retry>\u041f\u043e\u0432\u0442\u043e\u0440\u0438\u0442\u044c</button></div>';
        status.querySelector('[data-chunk-retry]')?.addEventListener('click',retry,{once:true});
    },
    _loadRouteChunk(page,params,opts,chunk,navigationGeneration){
        // Compare the destination itself, not a global counter: a no-op
        // re-entry on the current route must not invalidate the pending
        // chunk, and any real navigation away must invalidate it.
        this._pendingRoute=`${page}|${JSON.stringify(params||{})}`;
        this._showChunkState(page,'loading');
        const request=window.ChunkLoader?.load?.(chunk);
        if(!request||typeof request.then!=='function'){
            this._showChunkState(page,'error',()=>this.go(page,params,{
                ...opts,_chunkNavigationGeneration:navigationGeneration
            }));
            return false;
        }
        request.then(()=>{
            window.AppEvents?.initChunk?.(chunk);
            this._loadedChunks.add(chunk);
            if(this._pendingRoute!==`${page}|${JSON.stringify(params||{})}`)return;
            this.go(page,params,{...opts,_chunkNavigationGeneration:navigationGeneration});
        }).catch(()=>{
            if(this._pendingRoute!==`${page}|${JSON.stringify(params||{})}`)return;
            this._showChunkState(page,'error',()=>this.go(page,params,{
                ...opts,_chunkNavigationGeneration:navigationGeneration
            }));
        });
        return false;
    },
    go(page,params={},opts={}){
        if(!this._routeMap.has(page))page='home';
        if(page==='wallet')page='profile';
        if(page==='settings' && typeof Auth!=='undefined' && Auth.user && Auth.canOpenSettings && !Auth.canOpenSettings()){
            page='profile';
            params={};
        }
        if(page==='bots' && typeof Auth!=='undefined' && Auth.user && Auth.canOpenBots && !Auth.canOpenBots()){
            page='profile';
            params={};
        }
        const el=document.getElementById('page-'+page);
        if(!el){
            page='home';
            params={};
        }
        const previousPage=this.current;
        const previousParams=this.params||{};
        const sameRoute=previousPage===page
            && JSON.stringify(previousParams)===JSON.stringify(params||{});
        if(typeof Auth!=='undefined'&&Auth.closeMobileAccountMenu){
            Auth.closeMobileAccountMenu();
        }
        const boardSession=(previousPage==='task'?window.TaskEditor:previousPage==='debugger'?window.JobDebugger:null)?._workboardSession;
        const boardChanges=!sameRoute&&boardSession?.isAttached?.()&&!(previousPage===page&&String(previousParams.taskId||previousParams.jobId||'')===String(params.taskId||params.jobId||''));
        if(boardChanges&&!opts._workboardFlushed){
            const token=(this._workboardNavigationToken||0)+1;
            this._workboardNavigationToken=token;
            window.WorkboardV2Session.beforeNavigate({from:previousPage,to:page,fromParams:previousParams,toParams:params}).then(ok=>{
                if(token!==this._workboardNavigationToken)return;
                if(ok)this.go(page,params,{...opts,_workboardFlushed:true,skipUnsavedCheck:true});
                else this._writeHistory(this.current,this.params||{},true);
            }).catch(()=>this._writeHistory(this.current,this.params||{},true));
            return false;
        }
        if(!sameRoute&&!this._confirmLeaveDirtyTask(page,params,opts))return false;
        if(!opts._chunkNavigationGeneration&&!sameRoute)++this._navigationGeneration;
        const navigationGeneration=opts._chunkNavigationGeneration||this._navigationGeneration;
        if(opts._chunkNavigationGeneration&&navigationGeneration!==this._navigationGeneration)return false;
        const chunk=this._chunkForPage(page);
        if(chunk!=='core'&&!this._loadedChunks.has(chunk)){
            return this._loadRouteChunk(page,params,opts,chunk,navigationGeneration);
        }
        if(!opts.skipHistory)this._writeHistory(page,params,!!opts.replace);
        if(opts.skipRender&&sameRoute)return;
        if(!sameRoute&&typeof Modal!=='undefined'&&Modal.hide){
            Modal.hide();
        }
        if(!sameRoute){
            this._runLeaveHooks(previousPage,page,previousParams,params||{});
        }
        // Drop job-card multi-selection on real page/params change so it doesn't
        // bleed into a different view; in-view refreshes (fallback polling, post-delete
        // refresh) keep the selection — _refreshSelectionChrome cleans stale ids itself.
        if(typeof Jobs!=='undefined'&&Jobs.clearSelection){
            if(!sameRoute) Jobs.clearSelection();
        }
        this.params=params;
        this._clearChunkState(page);
        this.current=page;
        document.body.dataset.currentPage=page;
        document.querySelectorAll('.page').forEach(p=>p.classList.remove('active'));
        document.querySelectorAll('.nav-item').forEach(n=>{
            n.classList.remove('active');
            n.removeAttribute('aria-current');
        });
        el.classList.add('active');
        const nav=document.querySelector(`[data-page="${this._navPage(page)}"]`);
        if(nav){
            nav.classList.add('active');
            nav.setAttribute('aria-current','page');
        }
        if(typeof CanvasAgent!=='undefined' && typeof CanvasAgent.onRouteChange==='function'){
            CanvasAgent.onRouteChange(page);
        }
        // A no-op re-entry on the same route (clicking the active menu item)
        // must neither bump the generation nor re-run loaders: a pending
        // TaskView.load() discards its result when generation changes, and
        // double loaders mean double requests. Deliberate reloads pass
        // {force:true} (first page load, auth re-entry after login).
        const shouldReload=!sameRoute||opts.force===true;
        const generation=shouldReload?++this.generation:this.generation;
        const titleMap={
            home:'Главная',projects:'Проекты',
            project:params._projectName||'Проект',
            task:params._taskName||'Задача',
            chats:'Чаты',bots:'Боты',templates:'Шаблоны',gallery:'Галерея',
            settings:'Настройки',profile:'Профиль',wallet:'Профиль',
            debugger:'Рабочая доска',admin:'Админка'
        };
        document.title=(titleMap[page]?titleMap[page]+' · ':'')+'OMOIBOT';
        const sidebar=document.getElementById('sidebar');
        const content=document.getElementById('content');
        if(sidebar)sidebar.style.display='';
        if(content){
            content.style.padding='';
            content.style.marginLeft='';
            content.style.overflow='';
        }
        if(shouldReload){
            if(page==='home')Dashboard.load();
            if(page==='projects')Projects.load();
            if(page==='project')ProjectView.load(params.id);
            if(page==='task'&&typeof TaskView!=='undefined')TaskView.load(params.projectId,params.taskId,params.tab,{generation});
            if(page==='gallery'&&typeof Gallery!=='undefined')Gallery.load();
            if(page==='chats')Chats.loadList();
            if(page==='bots' && typeof BotSettings!=='undefined')BotSettings.load();
            if(page==='templates' && typeof TemplatesMarketplace!=='undefined')TemplatesMarketplace.loadFullPage();
            if(page==='settings')Modules.load();
            if(page==='profile')Profile.load();
            if(page==='debugger'&&typeof JobDebugger!=='undefined'&&params.jobId){
                JobDebugger.open(params.jobId,{
                    returnRoute:previousPage==='debugger'?null:{page:previousPage,params:previousParams},
                    routeGeneration:generation,
                    routeJobId:String(params.jobId),
                    initialEntry:previousPage!=='debugger'
                        ||String(previousParams.jobId||'')!==String(params.jobId)
                });
            }
            if(page==='admin' && typeof AdminPanel!=='undefined')AdminPanel.load();
        }
        // The pending chunk (if any) has now fully landed — forget the
        // destination marker so a later-resolving stale chunk can't re-apply.
        this._pendingRoute=null;
    },
    init(){
        if(!this._beforeUnloadBound){
            this._beforeUnloadBound=true;
            window.addEventListener('beforeunload',(event)=>{
                if(!this._hasUnsavedTaskChanges())return;
                event.preventDefault();
                event.returnValue='';
                return '';
            });
        }
        document.querySelectorAll('.nav-item').forEach(el=>{
            el.addEventListener('click',e=>{
                const page=el.dataset.page||'';
                if(!this._routeMap.has(page))return;
                e.preventDefault();
                this.go(page);
            });
        });
        window.addEventListener('popstate',()=>{
            const route=this._parseLocation();
            this.go(route.page,route.params,{skipHistory:true});
        });
        const route=this._parseLocation();
        // force:true — Router.current is initialized to 'home', so the very
        // first navigation to / would look like a sameRoute no-op otherwise.
        this.go(route.page,route.params,{replace:true,force:true});
        this._loadBalance();
        Auth.startBackground();
    },
    async _loadBalance(){
        const el=document.getElementById('balance');
        if(!el)return;
        try{
            const d=await API.get('/api/wallet/summary');
            if(d&&d.balance_credits!=null){
                el.textContent=`${Math.round(d.balance_credits)} тк`;
                el.classList.add('balance-badge-clickable');
                el.setAttribute('role','button');
                el.setAttribute('tabindex','0');
                el.setAttribute('title','Перейти к кошельку');
                if(!el.dataset.walletWired){
                    el.dataset.walletWired='1';
                    const goWallet=()=>this.go('wallet');
                    el.addEventListener('click',goWallet);
                    el.addEventListener('keydown',(ev)=>{
                        if(ev.key==='Enter'||ev.key===' '){ev.preventDefault();goWallet();}
                    });
                }
            }
        }catch(e){/* keep dashed placeholder */}
    },
    refreshBalance(){this._loadBalance();}
};

window.Router=Router;

/* web/js/modal.js */
/* ═══ Modal ═══ */
const Modal={
    _safeHtml(html){
        const raw=String(html||'');
        const template=document.createElement('template');
        template.innerHTML=raw;
        const allowedTags=new Set([
            'A','B','BR','BUTTON','CODE','DETAILS','DIV','EM','FORM','H1','H2','H3','H4',
            'HR','I','IMG','INPUT','LABEL','LI','OPTION','P','PRE','SELECT','SMALL',
            'SOURCE','SPAN','STRONG','SUMMARY','TEXTAREA','UL','OL','VIDEO','AUDIO'
        ]);
        const allowedAttrs=new Set([
            'accept','alt','aria-label','aria-labelledby','aria-modal','autofocus','autoplay','checked','class',
            'cols','controls','controlslist','data-action','data-chat-action','data-chat-id','data-file-index','data-modal-kind','download',
            'data-style','disabled','for','height','hidden','href','id','max','min','multiple','muted','name','placeholder',
            'playsinline','poster','preload','rel','role','rows','selected','src','tabindex','title','type','value','width'
        ]);
        const urlAttrs=new Set(['href','src','poster']);
        const clean=(node)=>{
            [...node.children].forEach(child=>{
                if(!allowedTags.has(child.tagName)){
                    child.remove();
                    return;
                }
                [...child.attributes].forEach(attr=>{
                    const name=attr.name.toLowerCase();
                    if(name.startsWith('on')||name==='style'||name==='srcdoc'||name==='xlink:href'){
                        child.removeAttribute(attr.name);
                        return;
                    }
                    if(!allowedAttrs.has(name)&&!name.startsWith('data-')&&!name.startsWith('aria-')){
                        child.removeAttribute(attr.name);
                        return;
                    }
                    if(urlAttrs.has(name)){
                        const safe=(typeof MediaUtils!=='undefined')
                            ? MediaUtils.safeUrl(attr.value,{allowData:name!=='href',externalPreview:name!=='href'})
                            : '';
                        if(!safe)child.removeAttribute(attr.name);
                        else child.setAttribute(attr.name,safe);
                    }
                });
                if(child.tagName==='A'){
                    child.setAttribute('rel','noopener noreferrer');
                }
                clean(child);
            });
        };
        clean(template.content);
        return template.innerHTML;
    },
    show(html){
        const content=document.getElementById('modal-content');
        const overlay=document.getElementById('modal-overlay');
        if(!content||!overlay)return;
        content.innerHTML=this._safeHtml(html);
        content.setAttribute('role','dialog');
        content.setAttribute('aria-modal','true');
        content.setAttribute('tabindex','-1');
        const title=content.querySelector('h1,h2,h3');
        if(title){
            title.id=title.id||('modal-title-'+Date.now());
            content.setAttribute('aria-labelledby',title.id);
        }else{
            content.removeAttribute('aria-labelledby');
            content.setAttribute('aria-label','Диалог');
        }
        overlay.hidden=false;
        overlay.setAttribute('aria-hidden','false');
        overlay.style.display='flex';
        if(typeof FocusTrap!=='undefined'){
            FocusTrap.activate(content,{onEscape:()=>Modal.hide()});
        }else{
            setTimeout(()=>content.focus(),0);
        }
    },
    hide(options={}){
        // Delegate to MobileSheet ONLY for modal-proxy sheets (form/palette/edit).
        // Peek and canvas-agent run with blocking:false; Modal.hide must not
        // accidentally close them when a regular modal above is dismissed.
        const sheetActive = typeof MobileSheet !== 'undefined'
            && MobileSheet.active
            && ['task-add-node','debugger-add-node','task-edit-node','debugger-edit-node']
                .indexOf(MobileSheet.activeKind || '') !== -1;
        if(sheetActive){
            MobileSheet.close(options || {});
            return;
        }
        const content=document.getElementById('modal-content');
        const overlay=document.getElementById('modal-overlay');
        const modalKind=content?.getAttribute('data-modal-kind')||'';
        const preservePending = !!(options?.preservePendingConnect || options?.preserveTaskAddPending);
        if(modalKind==='task-add-node'&&!preservePending&&typeof TaskEditor!=='undefined'){
            TaskEditor._clearPendingConnect?.();
        }
        if(modalKind==='debugger-add-node'&&!preservePending&&typeof JobDebugger!=='undefined'){
            JobDebugger._clearPendingConnect?.();
        }
        if(content&&typeof FocusTrap!=='undefined')FocusTrap.deactivate(content);
        if(overlay){
            overlay.hidden=true;
            overlay.setAttribute('aria-hidden','true');
            overlay.style.display='';
        }
        if(content){
            content.innerHTML='';
            content.removeAttribute('data-modal-kind');
            content.removeAttribute('aria-labelledby');
            content.removeAttribute('aria-label');
        }
    }
};

window.Modal=Modal;
document.getElementById('modal-overlay')?.addEventListener('click',e=>{if(e.target===e.currentTarget)Modal.hide()});

/* web/js/mobile-sheet.js */
/* ═══ MobileSheet ═══
 * Bottom-sheet host for mobile (≤760px). Singleton — `init()` runs once,
 * shell calls `activate/deactivate/open/close`. Reuses Modal sanitizer and
 * focus-trap contracts. Two flavours via `blocking`: true = modal-proxy
 * (form/palette/action-sheet), false = peek/agent (canvas stays pannable).
 */
const MobileSheet = {
    _initialized: false,
    _activeMode: null,
    active: false,
    activeKind: null,
    _blocking: false,
    _onCloseCb: null,
    _placeholder: null,
    _elementRef: null,
    _prevFocus: null,
    _drag: null,
    _vvHandler: null,
    _persistent: false,
    _expandable: true,

    init(){
        if(this._initialized) return;
        this._initialized = true;
        const host = document.getElementById('mobile-sheet');
        if(!host) return;
        // Backdrop click closes blocking sheet (modal-proxy behaviour).
        host.querySelector('[data-mobile-sheet-backdrop]')?.addEventListener('click', () => {
            if(this.active && this._blocking && !this._persistent) this.close();
        });
        // Handle: tap cycles snap; swipe-down closes.
        const handle = host.querySelector('[data-mobile-sheet-handle]');
        if(handle){
            handle.addEventListener('pointerdown', (e) => this._handlePointerDown(e));
        }
        // Escape closes blocking sheets only — non-blocking peek/agent must not
        // swallow Escape that might belong to a nested element.
        document.addEventListener('keydown', (e) => {
            if(e.key === 'Escape' && this.active && this._blocking && !this._persistent) this.close();
        });
        // Keyboard-aware sizing: visualViewport shrinks when soft keyboard opens.
        if(window.visualViewport){
            this._vvHandler = () => this._applyViewportOffset();
            window.visualViewport.addEventListener('resize', this._vvHandler);
            window.visualViewport.addEventListener('scroll', this._vvHandler);
        }
    },

    activate(mode){
        if(this._activeMode === mode) return;
        this._activeMode = mode;
    },

    deactivate(){
        if(this._activeMode === null) return;
        if(this.active) this.close();
        this._activeMode = null;
    },

    /**
     * Open sheet with either html (sanitized via Modal._safeHtml) or element
     * (reparented in-place; restored to original location on close).
     * Options:
     *  - snap: 'peek' | 'half' | 'full' (default 'half')
     *  - kind: string marker for side-effect hook on close
     *  - blocking: true (modal-proxy) | false (peek/agent — canvas pannable)
     *  - expandable: false locks snap to the requested size (used by peek)
     *  - persistent: true skips Escape/backdrop close (use Modal.hide via api)
     *  - onClose: callback invoked on user-driven close (skipped when silent)
     */
    open(opts = {}){
        const host = document.getElementById('mobile-sheet');
        const content = document.getElementById('mobile-sheet-content');
        const panel = host?.querySelector('.mobile-sheet-panel');
        if(!host || !content || !panel) return;

        // If already open, close first. NOT silent — onClose callbacks must
        // fire (e.g. canvas-agent's _syncClosedFromSheet, otherwise _open
        // stays true and panel keeps hidden=false, then bounces back as the
        // old fixed right-panel on next route enter).
        if(this.active){
            this.close();
        }

        const snap = opts.snap || 'half';
        const kind = opts.kind || '';
        const blocking = opts.blocking !== false; // default true
        this.active = true;
        this.activeKind = kind;
        this._blocking = blocking;
        this._persistent = !!opts.persistent;
        this._expandable = opts.expandable !== false && kind !== 'peek';
        this._onCloseCb = typeof opts.onClose === 'function' ? opts.onClose : null;
        this._prevFocus = blocking ? document.activeElement : null;

        // Mount content: element (reparent with placeholder) OR html (sanitize).
        content.innerHTML = '';
        if(opts.element instanceof Element){
            this._elementRef = opts.element;
            if(opts.element.parentNode){
                this._placeholder = document.createComment('mobile-sheet-anchor');
                opts.element.parentNode.replaceChild(this._placeholder, opts.element);
            }
            content.appendChild(opts.element);
        }else if(typeof opts.html === 'string'){
            const safe = (typeof Modal !== 'undefined' && Modal._safeHtml)
                ? Modal._safeHtml(opts.html)
                : opts.html;
            content.innerHTML = safe;
            if(typeof StyleHydrator !== 'undefined') StyleHydrator.hydrate(content);
        }

        // ARIA / role: blocking is modal dialog; non-blocking is complementary region.
        panel.dataset.snap = snap;
        panel.dataset.kind = kind;
        panel.dataset.blocking = blocking ? 'true' : 'false';
        panel.dataset.persistent = this._persistent ? 'true' : 'false';
        panel.dataset.expandable = this._expandable ? 'true' : 'false';
        if(blocking){
            panel.setAttribute('role', 'dialog');
            panel.setAttribute('aria-modal', 'true');
        }else{
            panel.setAttribute('role', 'complementary');
            panel.removeAttribute('aria-modal');
        }
        panel.setAttribute('tabindex', '-1');

        host.hidden = false;
        host.setAttribute('aria-hidden', 'false');
        host.classList.toggle('mobile-sheet-blocking', blocking);

        // Focus management: only blocking sheets trap focus.
        if(blocking && typeof FocusTrap !== 'undefined'){
            FocusTrap.activate(panel, {onEscape: () => {
                if(!this._persistent) this.close();
            }});
        }else if(blocking){
            setTimeout(() => panel.focus(), 0);
        }

        this._applyViewportOffset();
    },

    close(options = {}){
        if(!this.active) return;
        const host = document.getElementById('mobile-sheet');
        const content = document.getElementById('mobile-sheet-content');
        const panel = host?.querySelector('.mobile-sheet-panel');

        const kind = this.activeKind;
        const silent = !!options.silent;
        const preservePending = !!(options.preservePendingConnect || options.preserveTaskAddPending);

        // Side-effect: pending-connect cleanup by kind (mirrors Modal.hide behaviour).
        if(!preservePending){
            if(kind === 'task-add-node' && typeof TaskEditor !== 'undefined'){
                TaskEditor._clearPendingConnect?.();
            }else if(kind === 'debugger-add-node' && typeof JobDebugger !== 'undefined'){
                JobDebugger._clearPendingConnect?.();
            }
        }

        // Release focus trap for blocking sheets.
        if(this._blocking && panel && typeof FocusTrap !== 'undefined'){
            FocusTrap.deactivate(panel);
        }

        // Restore reparent'ed element to its original DOM slot.
        if(this._elementRef && this._placeholder && this._placeholder.parentNode){
            this._placeholder.parentNode.replaceChild(this._elementRef, this._placeholder);
        }else if(content){
            content.innerHTML = '';
        }
        this._elementRef = null;
        this._placeholder = null;

        if(host){
            host.hidden = true;
            host.setAttribute('aria-hidden', 'true');
            host.classList.remove('mobile-sheet-blocking');
        }
        if(panel){
            panel.removeAttribute('role');
            panel.removeAttribute('aria-modal');
            panel.removeAttribute('tabindex');
            panel.removeAttribute('data-kind');
            panel.removeAttribute('data-expandable');
            panel.removeAttribute('data-persistent');
        }

        const cb = this._onCloseCb;
        this._onCloseCb = null;
        const wasBlocking = this._blocking;
        this.active = false;
        this.activeKind = null;
        this._blocking = false;
        this._persistent = false;
        this._expandable = true;

        if(wasBlocking && this._prevFocus && typeof this._prevFocus.focus === 'function'){
            try{ this._prevFocus.focus(); }catch(_){}
        }
        this._prevFocus = null;

        if(!silent && cb){
            try{ cb(); }catch(_){}
        }
    },

    setSnap(snap){
        const panel = document.querySelector('#mobile-sheet .mobile-sheet-panel');
        if(panel && (snap === 'peek' || snap === 'half' || snap === 'full')){
            panel.dataset.snap = snap;
        }
    },

    _handlePointerDown(event){
        if(event.button != null && event.button !== 0) return;
        const handle = event.currentTarget;
        const panel = document.querySelector('#mobile-sheet .mobile-sheet-panel');
        if(!handle || !panel) return;
        try{ handle.setPointerCapture?.(event.pointerId); }catch(_){}
        const startY = event.clientY;
        const startSnap = panel.dataset.snap || 'half';
        this._drag = {startY, startSnap, moved:false};

        const onMove = (ev) => {
            if(!this._drag) return;
            const dy = ev.clientY - startY;
            if(Math.abs(dy) > 4) this._drag.moved = true;
            // Live translate for tactile drag.
            panel.style.transform = `translateY(${Math.max(0, dy)}px)`;
        };
        const onUp = (ev) => {
            handle.removeEventListener('pointermove', onMove);
            handle.removeEventListener('pointerup', onUp);
            handle.removeEventListener('pointercancel', onUp);
            panel.style.transform = '';
            const dy = (ev.clientY || 0) - startY;
            const drag = this._drag;
            this._drag = null;
            if(!drag) return;
            // Swipe-down > 80px on non-persistent sheet → close.
            if(dy > 80 && panel.dataset.persistent !== 'true'){
                this.close();
                return;
            }
            // Peek cards are compact summaries. Expanding them creates a large
            // blank panel because their content is intentionally one small card.
            if(!this._expandable){
                this.setSnap(startSnap);
                return;
            }
            // Snap-cycle on tap (no significant move): peek → half → full → peek.
            if(!drag.moved){
                const order = ['peek','half','full'];
                const idx = order.indexOf(startSnap);
                this.setSnap(order[(idx + 1) % order.length]);
                return;
            }
            // Drag distance picks snap target.
            if(dy < -40) this.setSnap('full');
            else if(dy > 40) this.setSnap(startSnap === 'full' ? 'half' : 'peek');
        };
        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
        handle.addEventListener('pointercancel', onUp);
    },

    _applyViewportOffset(){
        if(!this.active || !window.visualViewport) return;
        const host = document.getElementById('mobile-sheet');
        if(!host) return;
        // Soft keyboard reduces visualViewport.height. Compensate so panel
        // bottom sits above the keyboard (not under it).
        const offset = Math.max(0, window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop);
        host.style.setProperty('--mobile-sheet-viewport-offset', offset + 'px');
    },
};

window.MobileSheet = MobileSheet;

/* web/js/insufficient-funds.js */
/* ═══ Insufficient funds modal ═══
   Single CTA shown whenever the backend returns 402
   {code:"insufficient_wallet_funds"}. Routes the user to the wallet page
   so they can top up. Wired in api-client.js — every endpoint that does a
   wallet preflight (jobs/run, jobs/batch_run, jobs/resume, ...) hits this
   without each caller needing to handle 402 manually. */
const InsufficientFunds={
    _suppressUntil:0,
    show({needed=0,balance=0,message=''}={}){
        const now=Date.now();
        if(now<this._suppressUntil)return;
        this._suppressUntil=now+1500; // de-dupe rapid double-renders
        const need=Math.max(0,Math.round(Number(needed||0)));
        const have=Math.max(0,Math.round(Number(balance||0)));
        const shortfall=Math.max(0,need-have);
        const safeMsg=String(message||'Недостаточно токенов для запуска.');
        const html=`<h2>Недостаточно токенов</h2>
            <div class="insufficient-funds-body">
                <p class="insufficient-funds-msg">${this._esc(safeMsg)}</p>
                <div class="insufficient-funds-rows">
                    <div class="insufficient-funds-row"><span>Нужно</span><b>${need} тк</b></div>
                    <div class="insufficient-funds-row"><span>На балансе</span><b>${have} тк</b></div>
                    ${shortfall>0?`<div class="insufficient-funds-row insufficient-funds-row-gap"><span>Не хватает</span><b>${shortfall} тк</b></div>`:''}
                </div>
            </div>
            <div class="insufficient-funds-actions">
                <button class="btn btn-primary" data-action="insufficient-funds-topup">Пополнить</button>
                <button class="btn btn-ghost" data-action="insufficient-funds-close">Отмена</button>
            </div>`;
        if(typeof Modal!=='undefined' && Modal.show){
            Modal.show(html);
        }
    },
    hide(){
        if(typeof Modal!=='undefined' && Modal.hide)Modal.hide();
    },
    _esc(s){
        return String(s||'').replace(/[&<>"']/g,c=>({"&":"&amp;","<":"&lt;",">":"&gt;",'"':'&quot;',"'":'&#39;'}[c]));
    }
};

document.addEventListener('click',(event)=>{
    const target=event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if(!target)return;
    if(target.dataset.action==='insufficient-funds-topup'){
        event.preventDefault();
        InsufficientFunds.hide();
        if(typeof Router!=='undefined' && Router.go){
            Router.go('wallet');
        }
    }else if(target.dataset.action==='insufficient-funds-close'){
        event.preventDefault();
        InsufficientFunds.hide();
    }
});

window.InsufficientFunds=InsufficientFunds;

/* web/js/external-step-payload.js */
/* Canonical external payloads exclude server execution authority, preserving canvas layout. */
window.ExternalStepPayload={
    clean(value){
        if(Array.isArray(value))return value.map(item=>this.clean(item));
        if(!value||typeof value!=='object')return value;
        const reserved=new Set(['_module_id','_model_id','_module','_model','secret_source','reservation_id',
            'run_identity','provider_snapshot','provider_identity','invocations','quoted_tokens','payment_mode',
            'secret_reference','identity','snapshot','reservation','_app_path','_command_template',
            'app_path','command_template','handler','options_cmd','api_key','access_token','auth_header',
            'authorization','env','environment','extra_args','password','secret','secret_key','shell','token',
            '__proto__','constructor','prototype']);
        return Object.fromEntries(Object.entries(value).filter(([key])=>!reserved.has(key.toLowerCase())
            &&!/^_(engine|billing|wallet|provider)/i.test(key)).map(([key,item])=>[key,this.clean(item)]));
    },
};

/* web/js/cost-estimator.js */
/* ═══ Cost estimator ═══
   Single client for /api/jobs/estimate. Two access patterns:

   * estimate(steps, repeats?) — fire-and-forget; returns the latest cached
     payload (or the in-flight request) and triggers a debounced refresh.
   * forNode(nodeId) — synchronous lookup against the most recent estimate;
     used by the canvas badge renderer.

   Subscribers register a callback to repaint the toolbar/badges when a new
   estimate arrives. The signature is intentionally minimal — UI is
   responsible for scoping its own listeners.
*/
const CostEstimator={
    _state:{
        signature:'',
        lastResp:null,
        loading:false,
        balance:null,
        markup:1.5,
        chatCredits:0,
        agentCredits:0,
        pricingRevision:'',
        shortfall:0,
        warnings:[],
    },
    // Latest signature scheduled for fetch. Updated in `estimate()` before
    // the network roundtrip starts, so out-of-order responses can be
    // detected even when this is the very first fetch (signature still '').
    _pendingSignature:'',
    _byNode:new Map(),
    _listeners:new Set(),
    _debounceId:0,
    _debounceMs:350,
    _balanceTimer:0,
    _balanceTtl:30000,

    on(handler){if(typeof handler==='function')this._listeners.add(handler);return ()=>this._listeners.delete(handler)},
    off(handler){this._listeners.delete(handler)},
    _emit(){
        const snapshot=this.snapshot();
        this._listeners.forEach(fn=>{
            try{fn(snapshot)}catch(e){console.warn('CostEstimator listener failed',e)}
        });
    },

    _resetTaskEditorCanvasSignature(){
        if(typeof window!=='undefined'&&window.TaskEditor){
            window.TaskEditor._lastCostEstimateCanvasSignature='';
        }
    },

    snapshot(){
        const quote=this._state.lastResp?.quote||this._state.lastResp||{};
        return {
            perRun:Number(quote.per_run_credits??this._state.lastResp?.per_run_credits??0),
            total:Number(quote.total_credits??this._state.lastResp?.total_credits??0),
            listPerRun:Number(quote.list_per_run_credits??this._state.lastResp?.list_per_run_credits??0),
            listTotal:Number(quote.list_total_credits??this._state.lastResp?.list_total_credits??0),
            repeats:Number(quote.repeats??this._state.lastResp?.repeats??1),
            balance:this._state.balance==null?null:Number(this._state.balance),
            byStep:Array.isArray(this._state.lastResp?.by_step)?this._state.lastResp.by_step:[],
            markup:Number(this._state.markup||1.5),
            loading:!!this._state.loading,
            chatCredits:Number(this._state.chatCredits||0),
            agentCredits:Number(this._state.agentCredits||0),
            shortfall:Number(this._state.shortfall||0),
            pricingRevision:String(this._state.pricingRevision||''),
            warnings:Array.isArray(this._state.warnings)?this._state.warnings:[],
            quote:this._state.lastResp?.quote||null,
        };
    },

    forNode(nodeId){
        if(!nodeId)return null;
        return this._byNode.get(String(nodeId))||null;
    },

    /**
     * Schedule an estimate refresh for the given step list. Multiple calls
     * within the debounce window collapse into one network roundtrip; if
     * the signature hasn't changed we short-circuit completely so canvas
     * pan/zoom doesn't re-spam the backend.
     */
    estimate(steps, repeats=1, {immediate=false}={}){
        const payloadSteps=Array.isArray(steps)?steps:[];
        const sig=this._signature(payloadSteps, repeats);
        if(sig===this._state.signature && !immediate){
            return Promise.resolve(this._state.lastResp);
        }
        // Mark this signature as the latest intent — `_fetch()` compares
        // its own captured `signature` against `_pendingSignature` when
        // the response arrives, so out-of-order completions for older
        // requests get dropped even if the state signature is still
        // empty (i.e. the very first response hasn't landed yet).
        this._pendingSignature=sig;
        if(this._debounceId)clearTimeout(this._debounceId);
        return new Promise((resolve)=>{
            const run=()=>{
                this._fetch(payloadSteps, repeats, sig).then(resolve);
            };
            if(immediate)run();
            else this._debounceId=setTimeout(run, this._debounceMs);
        });
    },

    async _fetch(steps, repeats, signature){
        this._state.loading=true;
        this._emit();
        let resp=null;
        try{
            resp=await API.post('/api/jobs/estimate',{steps:window.ExternalStepPayload.clean(steps), repeats},{skipInsufficientFundsModal:true});
        }catch(e){
            resp={error:String(e&&e.message||e||'estimate failed')};
        }
        // Stale response: a newer estimate() call captured a different
        // `_pendingSignature` while this one was in flight. Drop the
        // result without touching state so the UI keeps reflecting the
        // latest steps. Compares against `_pendingSignature` (not
        // `_state.signature`) because the very first request would
        // otherwise win unconditionally — see the suite in
        // `web/tests/cost-estimator.test.mjs`.
        if(signature !== this._pendingSignature){
            // Only clear `loading` when nothing else is pending — a
            // fresher request will manage its own loading flag.
            return resp;
        }
        this._state.signature=signature;
        this._state.loading=false;
        if(resp && !resp.error){
            const quote=resp.quote||resp;
            this._state.lastResp=resp;
            this._state.balance=Number(quote.balance_credits??resp.balance_credits??0);
            this._state.markup=Number(resp.markup||1.5);
            this._state.chatCredits=Number(resp.chat_message_credits||0);
            this._state.agentCredits=Number(resp.agent_message_credits||0);
            this._state.shortfall=Number(quote.shortfall_credits??resp.shortfall_credits??0);
            this._state.pricingRevision=String(quote.pricing_revision||resp.pricing_revision||'');
            this._state.warnings=Array.isArray(quote.warnings)?quote.warnings:(Array.isArray(resp.warnings)?resp.warnings:[]);
            this._byNode.clear();
            (resp.by_step||[]).forEach(entry=>{
                const id=String(entry.node_id||entry.idx||'');
                if(id)this._byNode.set(id, entry);
            });
            // Header balance is updated alongside (single source of truth).
            if(typeof Router!=='undefined' && Router.refreshBalance){
                Router.refreshBalance();
            }
        }
        this._emit();
        return resp;
    },

    /** Lightweight balance fetch for surfaces that don't have a step list. */
    async loadSummary(){
        try{
            const data=await API.get('/api/wallet/summary',{skipInsufficientFundsModal:true});
            if(data && !data.error){
                this._state.balance=Number(data.balance_credits||0);
                this._state.chatCredits=Number(data.chat_message_credits||0);
                this._state.agentCredits=Number(data.agent_message_credits||0);
                this._emit();
            }
            return data;
        }catch(e){return null}
    },

    invalidate(reason=''){
        this._state.signature='';
        this._pendingSignature='';
        this._state.pricingRevision='';
        this._state.lastInvalidationReason=String(reason||'');
        this._resetTaskEditorCanvasSignature();
        this._emit();
    },

    clear(){
        this._state.signature='';
        this._pendingSignature='';
        this._state.lastResp=null;
        this._state.shortfall=0;
        this._state.pricingRevision='';
        this._state.warnings=[];
        this._byNode.clear();
        this._resetTaskEditorCanvasSignature();
        this._emit();
    },

    _pricingSignatureParams(step){
        const ignored=new Set(['type','step_type','_module_id','_model_id','_node_id','id','save_as','label','prompt','negative_prompt','text']);
        const out={};
        Object.entries(step||{}).forEach(([key,value])=>{
            if(ignored.has(key))return;
            if(value===null||value===undefined||value==='')return;
            if(['string','number','boolean'].includes(typeof value))out[key]=String(value);
        });
        return out;
    },

    _signature(steps, repeats){
        try{
            const lite=steps.map(s=>{
                if(!s||typeof s!=='object')return null;
                return {
                    type:s.type||s.step_type||'',
                    _module_id:s._module_id||'',
                    _model_id:s._model_id||'',
                    _node_id:s._node_id||s.id||'',
                    repeat:s.repeat||s.repeat_count||1,
                    params:this._pricingSignatureParams(s),
                };
            });
            return JSON.stringify({s:lite,r:repeats});
        }catch(e){return Math.random().toString(36)}
    }
};

window.CostEstimator=CostEstimator;

/* web/js/run-cost-preview.js */
const RunCostPreview={
    taskRequest(task,{mode='full',targetNodeIds=[],repeats=1,batchInputs}={}){
        const payload={task_id:task.id,run_mode:mode,target_node_ids:[...targetNodeIds],
            expected_graph_revision:task.graph_revision??task.expected_graph_revision,repeats};
        if(batchInputs!==undefined)payload.batch_inputs=batchInputs;
        return payload;
    },
    runIntent(payload,previous){
        const clean=JSON.parse(JSON.stringify(payload));
        const signature=JSON.stringify(clean);
        if(previous?.signature===signature)return previous;
        this._requestSeq=(this._requestSeq||0)+1;
        const token=window.crypto?.randomUUID?.()||`${Date.now().toString(36)}_${this._requestSeq}`;
        return {signature,payload:{...clean,client_request_id:'workboard_'+token}};
    },
    _clampRepeats(input){
        const raw=parseInt(input?.value,10);
        return Math.min(100,Math.max(1,Number.isFinite(raw)?raw:1));
    },
    _labelRepeats(repeats){
        if(repeats===1)return '1 запуск';
        if(repeats>=2 && repeats<=4)return `${repeats} запуска`;
        return `${repeats} запусков`;
    },
    _render(root, button, quote){
        if(!root)return;
        const chargeTotal=Number(quote?.quote?.total_credits??quote?.total_credits??0);
        const chargePerRun=Number(quote?.quote?.per_run_credits??quote?.per_run_credits??0);
        const listTotal=Number(quote?.quote?.list_total_credits??quote?.list_total_credits??0);
        const listPerRun=Number(quote?.quote?.list_per_run_credits??quote?.list_per_run_credits??0);
        const total=listTotal||chargeTotal;
        const perRun=listPerRun||chargePerRun;
        const repeats=Number(quote?.quote?.repeats??quote?.repeats??1);
        const balance=Number(quote?.quote?.balance_credits??quote?.balance_credits??0);
        const shortfall=Number(quote?.quote?.shortfall_credits??quote?.shortfall_credits??0);
        const label=this._labelRepeats(repeats);
        root.hidden=false;
        root.dataset.state=shortfall>0?'insufficient':(total>0&&chargeTotal>0?'ok':'free');
        if(total<=0){
            root.textContent=`${label}: бесплатно · баланс ${Math.round(balance)} тк`;
        }else if(chargeTotal<=0){
            root.textContent=`${label}: ${Math.round(total)} тк · списание 0 тк · баланс ${Math.round(balance)} тк`;
        }else if(shortfall>0){
            const chargeLabel=chargeTotal===total?'':` · списание ${Math.round(chargeTotal)} тк`;
            root.textContent=`${label}: ${Math.round(total)} тк · 1 запуск ${Math.round(perRun)} тк${chargeLabel} · Нужно +${Math.round(shortfall)} тк`;
        }else{
            const chargeLabel=chargeTotal===total?'':` · списание ${Math.round(chargeTotal)} тк`;
            root.textContent=`${label}: ${Math.round(total)} тк · 1 запуск ${Math.round(perRun)} тк${chargeLabel} · баланс ${Math.round(balance)} тк`;
        }
        if(button){
            button.dataset.costNeeded=String(Math.round(chargeTotal));
            button.dataset.costBalance=String(Math.round(balance));
            button.dataset.costShortfall=String(Math.round(shortfall));
            button.classList.toggle('btn-cost-blocked', shortfall>0);
            if(shortfall>0){
                button.setAttribute('aria-disabled','true');
                button.title=`Не хватает ${Math.round(shortfall)} тк до запуска`;
            }else{
                button.removeAttribute('aria-disabled');
                if((button.title||'').startsWith('Не хватает'))button.removeAttribute('title');
            }
        }
    },
    attach({steps,session,estimate,mode='full',targetNodeIds=[],batchInputs,input,root,button}){
        let timer=0, latest=0, destroyed=false;
        const serverEstimate=session?.estimate?.bind(session)||estimate;
        const refresh=async ()=>{
            const request=++latest;
            const repeats=this._clampRepeats(input);
            if(input)input.value=String(repeats);
            if(root){
                root.hidden=false;
                root.dataset.state='loading';
                root.textContent='Считаем стоимость запуска...';
            }
            let quote;
            try{
                quote=serverEstimate
                    ?await serverEstimate({mode,targetNodeIds:[...targetNodeIds],repeats,...(batchInputs!==undefined?{batchInputs}: {})})
                    :await CostEstimator.estimate(steps||[], repeats, {immediate:true});
            }catch(error){quote={error:error?.message||'Не удалось оценить стоимость'};}
            if(destroyed||request!==latest)return quote;
            if(serverEstimate&&(!quote||quote.error||quote.ok===false)){
                if(root){root.dataset.state='error';root.textContent=quote?.error||'Не удалось оценить стоимость';}
                if(button){
                    button.classList.remove('btn-cost-blocked');
                    ['costNeeded','costBalance','costShortfall'].forEach(key=>delete button.dataset[key]);
                    button.setAttribute('aria-disabled','true');
                }
            }else this._render(root, button, quote||(serverEstimate?{}:CostEstimator.snapshot()));
            return quote;
        };
        const schedule=()=>{
            if(timer)clearTimeout(timer);
            timer=setTimeout(refresh, 120);
        };
        input?.addEventListener('input', schedule);
        input?.addEventListener('change', schedule);
        refresh();
        return {
            refresh,
            destroy(){
                destroyed=true;
                latest++;
                if(timer)clearTimeout(timer);
                input?.removeEventListener('input', schedule);
                input?.removeEventListener('change', schedule);
            },
        };
    },
};

window.RunCostPreview=RunCostPreview;

/* web/js/display-labels.js */
/* Display-only labels. Never mutate task/job/node data. */
(function(global){
    function stripTechnicalNoise(value){
        let text=String(value??'').trim();
        text=text.replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,'');
        text=text.replace(/[0-9a-f]{24,}[_-]?/gi,'');
        text=text.replace(/\btelegram_(photo|video|document|audio)_\d+\b/gi,'');
        text=text.replace(/[_\s,;:-]+$/g,'').replace(/^[\s,;:-]+/g,'').replace(/\s{2,}/g,' ').trim();
        return text;
    }

    function saveAsVar(node){
        const raw=String(node?.save_as||node?.id||'').trim();
        if(!raw)return '';
        return raw.replace(/[{}]/g,'');
    }

    // Map raw step type (e.g. "kieai:grok-imagine/image-to-video") to a
    // user-facing label. Never expose provider prefix or technical model id.
    // Priority: meta.name → category fallback by meta.output_type → category
    // by type-suffix heuristic → generic "шаг".
    function stepTypeLabel(type,meta){
        const m=meta||{};
        const name=String(m.name||'').trim();
        if(name)return name;
        const outType=String(m.output_type||m._output_type||'').toLowerCase();
        const outMap={image:'Изображение',video:'Видео',audio:'Аудио',text:'Текст'};
        if(outMap[outType])return outMap[outType];
        const t=String(type||'').toLowerCase();
        if(!t)return 'Шаг';
        if(t.includes('video')||t.includes('veo')||t.includes('kling')||t.includes('seedance')
            ||t.includes('lipsync')||t.includes('heygen')||t.includes('runway')||t.includes('sora')
            ||t.includes('dub')||t.includes('reels')||t.includes('infinitalk')||t.includes('motion'))return 'Видео';
        if(t.includes('music')||t.includes('audio')||t.includes('sfx')||t.includes('tts')||t.includes('voice')
            ||t.includes('elevenlabs')||t.includes('suno')||t.includes('dialogue')||t.includes('lyria'))return 'Аудио';
        if(t.includes('image')||t.includes('seedream')||t.includes('nano-banana')||t.includes('gpt-image')
            ||t.includes('recraft')||t.includes('qwen')||t.includes('flux')||t.includes('topaz'))return 'Изображение';
        if(t.includes('llm')||t.includes('prompt')||t.includes('text'))return 'Текст';
        return 'Шаг';
    }

    function nodeTitle(node,meta={}){
        const typeName=stepTypeLabel(node?.type,meta);
        const variable=saveAsVar(node);
        if(variable)return `${typeName} · {{${variable}}}`;
        const clean=stripTechnicalNoise(node?.label||node?.name||'');
        return clean||typeName;
    }

    function jobTitle(job){
        const raw=String(job?.task_name||job?.name||'Запуск').trim();
        const bracket=raw.match(/^(.*?)\s*\[(.*)\]\s*$/);
        if(!bracket)return stripTechnicalNoise(raw)||'Запуск';
        const base=stripTechnicalNoise(bracket[1])||'Запуск';
        const parts=bracket[2].split(',').map(stripTechnicalNoise).filter(Boolean);
        return parts.length?`${base} · ${parts.join(' · ')}`:base;
    }

    function projectName(project){
        return stripTechnicalNoise(project?.name)||'Проект';
    }

    function taskName(task){
        return stripTechnicalNoise(task?.name)||'Задача';
    }

    global.DisplayLabels={
        stripTechnicalNoise,
        stepTypeLabel,
        nodeTitle,
        jobTitle,
        projectName,
        taskName
    };
})(window);

/* web/js/dashboard.js */
/* ═══ Dashboard ═══ */
const Dashboard={
    _projects:[],
    _tasks:[],
    _jobStatsByProject:{},
    _eventsBound:false,
    _id(value){return encodeURIComponent(String(value??''));},

    initEvents(){
        if(this._eventsBound)return;
        this._eventsBound=true;
        document.addEventListener('click',(event)=>{
            const target=event.target instanceof Element ? event.target.closest('[data-dashboard-action]') : null;
            if(!target)return;
            event.preventDefault();
            event.stopPropagation();
            const taskId=target.dataset.taskId||'';
            const projectId=target.dataset.projectId||'';
            const action=target.dataset.dashboardAction;
            const runner=window.ActionRunner||{run:(_target,fn)=>fn()};
            if(action==='pinned-run')this._pinnedRun(taskId,projectId);
            else if(action==='pinned-batch')this._pinnedBatch(taskId);
            else if(action==='confirm-pinned-run')runner.run(target,()=>this._confirmPinnedRun(taskId,projectId));
            else if(action==='agent-start')this._startAgentFlow();
            else if(action==='new-task')this._newTaskFlow();
            else if(action==='confirm-new-task-project')this._confirmNewTaskProject();
            else if(action==='open-project')Router.go('project',{id:projectId});
            else if(action==='reload')this.load();
        });
    },

    async load(){
        this._showSkeletons();
        try{
            let [projects,jobStatsByProject,allTasks]=await Promise.all([
                getProjects(),
                API.getCached('/api/jobs/stats?group_by=project'),
                API.getCached('/api/tasks?summary=1')
            ]);
            projects=Array.isArray(projects)?projects:[];
            allTasks=Array.isArray(allTasks)?allTasks:[];
            jobStatsByProject=(jobStatsByProject&&typeof jobStatsByProject==='object'&&!Array.isArray(jobStatsByProject))?jobStatsByProject:{};

            this._projects=projects;
            this._tasks=allTasks;
            this._jobStatsByProject=jobStatsByProject;

            const tilesEl=document.getElementById('dash-tiles');
            if(tilesEl){
                tilesEl.removeAttribute('aria-busy');
                tilesEl.innerHTML='';
                this._renderPinnedTasks(allTasks, projects, tilesEl);
            }
            this._renderProjectSummary(projects, allTasks, jobStatsByProject);

            if(typeof TemplatesMarketplace!=='undefined'){
                TemplatesMarketplace.initEvents();
                TemplatesMarketplace.loadSharedWithMe();
            }

        }catch(e){
            console.error('Dashboard load error:',e);
            this._renderLoadError();
        }
    },

    _showSkeletons(){
        const tilesEl=document.getElementById('dash-tiles');
        const projectsEl=document.getElementById('home-projects');
        if(tilesEl){
            tilesEl.removeAttribute('aria-busy');
            tilesEl.innerHTML='';
        }
        if(projectsEl&&!projectsEl.innerHTML.trim()){
            projectsEl.setAttribute('aria-busy','true');
            projectsEl.innerHTML=Array.from({length:3},()=>`
                <div class="home-project-card skeleton skeleton-tile" aria-hidden="true">
                    <div class="skeleton-line skeleton-line-short"></div>
                    <div class="skeleton-line"></div>
                    <div class="skeleton-line skeleton-line-value"></div>
                </div>
            `).join('');
        }
    },

    _renderLoadError(){
        const projectsEl=document.getElementById('home-projects');
        if(projectsEl){
            projectsEl.removeAttribute('aria-busy');
            projectsEl.innerHTML='<div class="empty-state"><div class="empty-state-text">Не удалось загрузить главную</div><div class="empty-state-actions"><button class="btn btn-primary btn-small" data-dashboard-action="reload">Повторить</button></div></div>';
        }
    },

    _renderPinnedTasks(allTasks, projects, tilesEl){
        const pinned=allTasks.filter(t=>t.pinned);
        if(!pinned.length)return;

        const projMap={};
        projects.forEach(p=>{projMap[p.id]=p});

        pinned.forEach(t=>{
            const icon=t.icon||'dashboard';
            const proj=projMap[t.project_id];
            const projName=proj?proj.name:'';
            const nodes=t.nodes||[];
            const nodeBatchCount=nodes.filter(n=>n.type==='batch_input').length;
            const batchCount=Math.max(Number(t.batch_count||0),nodeBatchCount);
            const hasBatch=batchCount>0||!!t.has_batch;
            const batchBtnLabel=CGIcons.html('batch')+(batchCount>1?`<span>x${batchCount}</span>`:'');
            const projectId=t.project_id||'';

            const tile=document.createElement('div');
            tile.className='tile tile-pinned'+(hasBatch?' tile-pinned-batch':'');
            tile.dataset.taskId=t.id;
            tile.addEventListener('click',(event)=>{
                if(event.target instanceof Element && event.target.closest('.tile-pinned-actions'))return;
                Router.go('task',{projectId,taskId:t.id});
            });

            if(hasBatch){
                tile.addEventListener('dragover',(e)=>{
                    if(!window.DragDrop?.hasFiles(e.dataTransfer))return;
                    e.preventDefault();e.stopPropagation();
                    e.dataTransfer.dropEffect='copy';
                    tile.classList.add('tile-batch-hover');
                });
                tile.addEventListener('dragleave',(e)=>{
                    if(tile.contains(e.relatedTarget))return;
                    tile.classList.remove('tile-batch-hover');
                });
                tile.addEventListener('drop',(e)=>{
                    e.preventDefault();e.stopPropagation();
                    tile.classList.remove('tile-batch-hover');
                    Dashboard._onPinnedDrop(e, t.id);
                });
            }

            tile.innerHTML=`
                <div class="tile-icon">${CGIcons.maybe(icon,{fallback:'dashboard'})}</div>
                <div class="tile-title" title="${this._esc(t.name||'Задача')}">${this._esc(t.name||'Задача')}</div>
                <div class="tile-desc">${this._esc(projName)}</div>
                <div class="tile-pinned-actions">
                    <button class="btn btn-primary btn-small" data-dashboard-action="pinned-run" data-task-id="${this._esc(t.id)}" data-project-id="${this._esc(projectId)}" title="Запуск" aria-label="Запустить"><span class="ui-code-icon" aria-hidden="true">RUN</span></button>
                    ${hasBatch?`<button class="btn btn-primary btn-small tile-pinned-batch-btn" data-dashboard-action="pinned-batch" data-task-id="${this._esc(t.id)}" title="Batch${batchCount>1?' x'+batchCount:''}">${batchBtnLabel}</button>`:''}
                </div>
                <div class="tile-pin-badge">PIN</div>
            `;

            tilesEl.appendChild(tile);
        });
    },

    _renderProjectSummary(projects, allTasks, jobStatsByProject){
        const el=document.getElementById('home-projects');
        if(!el)return;
        el.removeAttribute('aria-busy');
        const tasksByProject={};
        (Array.isArray(allTasks)?allTasks:[]).forEach(t=>{
            const pid=t.project_id||'';
            if(pid)(tasksByProject[pid]||(tasksByProject[pid]=[])).push(t);
        });
        const list=Array.isArray(projects)?projects:[];
        if(!list.length){
            el.innerHTML='<div class="empty-state"><div class="empty-state-title">проектов пока нет</div><div class="empty-state-text">Создайте проект, чтобы хранить задачи, запуски и материалы вместе.</div><div class="empty-state-actions"><button class="btn btn-primary" data-action="projects-create">+ новый проект</button></div></div>';
            return;
        }
        el.innerHTML=list.slice(0,6).map(p=>{
            const stats=(jobStatsByProject&&jobStatsByProject[p.id])||{};
            const running=(stats.running||0)+(stats.pending||0);
            const errors=stats.error||0;
            const total=stats.total||0;
            const tasks=(tasksByProject[p.id]||[]).length;
            return `<button class="home-project-card" data-dashboard-action="open-project" data-project-id="${this._esc(p.id)}">
                <span class="home-project-title"><span class="dash-project-icon">${CGIcons.maybe(p.icon||'dashboard',{fallback:'dashboard'})}</span>${this._esc(p.name||'Проект')}</span>
                <span class="home-project-meta">${tasks} задач · ${total} запусков</span>
                <span class="home-project-stats">
                    ${running?`<span class="home-stat-pill home-stat-pill-run">RUN ${running}</span>`:''}
                    ${errors?`<span class="home-stat-pill home-stat-pill-error">ERR ${errors}</span>`:''}
                    ${!running&&!errors?`<span class="home-stat-pill">OK</span>`:''}
                </span>
            </button>`;
        }).join('');
    },

    _startAgentFlow(){
        const projects=Array.isArray(this._projects)?this._projects:[];
        if(!projects.length){
            Projects.create();
            return;
        }
        Router.go('project',{id:projects[0].id});
        if(typeof Toast!=='undefined')Toast.info('Откройте задачу — агент доступен на рабочей доске');
    },

    _newTaskFlow(){
        const projects=(Array.isArray(this._projects)?this._projects:[]).filter(p=>p&&p.id);
        if(!projects.length){
            Projects.create();
            return;
        }
        this._showNewTaskProjectDialog(projects);
    },

    _showNewTaskProjectDialog(projects){
        const options=projects.map(p=>`<option value="${this._esc(p.id)}">${this._esc(p.name||'Проект')}</option>`).join('');
        Modal.show(`<h2>${CGIcons.inline('dashboard')} проект для новой задачи</h2>
            <div class="form-group"><label class="form-label" for="new-task-project">проект</label>
                <select class="form-select" id="new-task-project">${options}</select></div>
            <div class="omb-flex omb-flex-wrap omb-gap-2 omb-mt-4">
                <button class="btn btn-primary" data-dashboard-action="confirm-new-task-project"><span class="ui-code-icon" aria-hidden="true">ADD</span><span>создать задачу</span></button>
                <button class="btn btn-ghost" data-action="projects-create">новый проект</button>
                <button class="btn btn-ghost" data-action="modal-hide">отмена</button>
            </div>`);
    },

    _confirmNewTaskProject(){
        const projectId=document.getElementById('new-task-project')?.value||'';
        if(!projectId){
            if(typeof Toast!=='undefined')Toast.warning('Выберите проект');
            return;
        }
        this._openNewTask(projectId);
    },

    _openNewTask(projectId){
        ProjectView.projectId=projectId;
        Modal.hide();
        Router.go('task',{projectId,taskId:String(Date.now())});
    },

    async _pinnedRun(taskId, projectId){
        const task=await API.get('/api/tasks/'+this._id(taskId));
        if(!task||task.error)return;
        if((task.nodes||[]).some(n=>n.type==='batch_input')){
            ProjectView.projectId=task.project_id||projectId||'';
            ProjectView.tasks=[
                task,
                ...(ProjectView.tasks||[]).filter(t=>t.id!==taskId)
            ];
            ProjectView.batchDialog(taskId);
            return;
        }
        const isV2=window.WorkboardV2Session?.isV2(task);
        let steps;
        if(!isV2){
            const types=await getStepTypes();
            steps=window.TaskGraphActions.nodesToSteps(task.nodes||[],task.edges||[],{types});
            if(!steps.length){Toast.warning('Задача пуста');return}
        }
        const quoteTask=isV2?{id:task.id,graph_revision:task.graph_revision}:null;
        this._pinnedV2Task=quoteTask;
        this._pinnedRunIntent=null;
        this._pinnedRunCostPreview?.destroy();
        Modal.show(`<h2>${CGIcons.inline('video')} запуск: ${this._esc(task.name||'задача')}</h2>
            <div class="form-group"><label class="form-label">повторов</label>
            <input class="form-input" id="pinned-run-reps" type="number" value="1" min="1" max="100"></div>
            <div class="run-cost-preview" data-run-cost-preview hidden></div>
            <div class="run-error" data-run-error role="alert" aria-live="assertive" hidden></div>
            <div class="omb-flex omb-gap-2 omb-mt-4">
                <button class="btn btn-primary" data-dashboard-action="confirm-pinned-run" data-task-id="${this._esc(taskId)}" data-project-id="${this._esc(projectId)}"><span class="ui-code-icon" aria-hidden="true">RUN</span><span>запустить</span></button>
                <button class="btn btn-ghost" data-action="modal-hide">отмена</button></div>`);
        if(typeof RunCostPreview!=='undefined'){
            this._pinnedRunCostPreview=RunCostPreview.attach({
                steps,
                estimate:isV2?options=>API.post('/api/jobs/estimate',RunCostPreview.taskRequest(quoteTask,options),{skipInsufficientFundsModal:true}):null,
                input:document.getElementById('pinned-run-reps'),
                root:document.querySelector('[data-run-cost-preview]'),
                button:document.querySelector('[data-dashboard-action="confirm-pinned-run"]'),
            });
        }
    },

    async _confirmPinnedRun(taskId, projectId){
        const reps=Math.min(100,Math.max(1,parseInt(document.getElementById('pinned-run-reps')?.value)||1));
        const task=await API.get('/api/tasks/'+this._id(taskId));
        if(!task||task.error)return;
        if(window.WorkboardV2Session?.isV2(task)){
            if(document.querySelector('[data-run-cost-preview]')?.dataset.state==='insufficient'){
                Toast.error('Недостаточно токенов для запуска');return;
            }
            const quoteTask=this._pinnedV2Task?.id===taskId?this._pinnedV2Task:task;
            const intent=RunCostPreview.runIntent(RunCostPreview.taskRequest(quoteTask,{repeats:reps}),this._pinnedRunIntent);
            this._pinnedRunIntent=intent;
            const resp=await API.post('/api/jobs/run',intent.payload);
            if(!resp||resp.error||resp.ok===false){
                const message=resp?.error||'Не удалось запустить задачу';
                const box=document.querySelector('[data-run-error]');
                if(box){box.hidden=false;box.textContent=message;}
                Toast.error(message);return resp;
            }
            this._pinnedRunCostPreview?.destroy();
            Modal.hide();
            if(this._pinnedRunIntent===intent)this._pinnedRunIntent=null;
            Toast.success('Запущено: '+(resp.count>1?resp.count+' раз':'1 раз'));
            this._refreshProjectSummary();
            return resp;
        }
        const types=await getStepTypes();
        const steps=window.TaskGraphActions.nodesToSteps(task.nodes||[],task.edges||[],{types});
        const blocked=document.querySelector('[data-run-cost-preview]')?.dataset.state==='insufficient';
        if(blocked){
            Toast.error('Недостаточно токенов для запуска');
            return;
        }
        const runProblem=await window.TaskGraphActions.validateStepsForRun(steps);
        if(!runProblem.ok){
            const box=document.querySelector('[data-run-error]');
            if(box){box.hidden=false;box.textContent=runProblem.message;}
            Toast.warning(runProblem.message);
            return;
        }
        Modal.hide();
        const resp=await API.post('/api/jobs/run',{
            task_id:taskId, project_id:projectId,
            task_name:task.name, steps,
            folder_template:task.folder_template||'',
            repeats:reps
        });
        if(!resp||resp.error||resp.ok===false){Toast.error(resp?.error||'Не удалось запустить задачу');return}
        Toast.success('Запущено: '+(resp.count>1?resp.count+' раз':'1 раз'));
        this._refreshProjectSummary();
    },

    async _pinnedBatch(taskId){
        let task=(this._tasks||[]).find(t=>t.id===taskId);
        if(!task)return;
        if(!task.nodes||!task.nodes.length){
            const fullTask=await API.get('/api/tasks/'+this._id(taskId));
            if(!fullTask||fullTask.error)return;
            task=fullTask;
        }
        ProjectView.projectId=task.project_id||'';
        ProjectView.tasks=[
            task,
            ...(ProjectView.tasks||[]).filter(t=>t.id!==taskId)
        ];
        ProjectView.batchDialog(taskId);
    },

    _onPinnedDrop: async function(e, taskId){
        e.preventDefault();e.stopPropagation();

        let fileList=await ProjectView._filesFromDataTransfer(e.dataTransfer);
        fileList=fileList.filter(f=>!f.name.startsWith('.'));
        if(!fileList.length){Toast.warning('Нет файлов');return}

        let task=(this._tasks||[]).find(t=>t.id===taskId);
        if(!task)return;
        if(!task.nodes||!task.nodes.length){
            const fullTask=await API.get('/api/tasks/'+this._id(taskId));
            if(!fullTask||fullTask.error)return;
            task=fullTask;
        }

        const batchNodes=(task.nodes||[]).filter(n=>n.type==='batch_input');
        if(batchNodes.length>1){
            Toast.info('Откройте batch-меню и заполните входы по отдельности');
            ProjectView.projectId=task.project_id||'';
            ProjectView.tasks=[
                task,
                ...(ProjectView.tasks||[]).filter(t=>t.id!==taskId)
            ];
            ProjectView.batchDialog(taskId);
            return;
        }

        Toast.info(`Загрузка файлов: ${fileList.length}`);
        const uploadedUrls=[];
        const failedUploads=[];
        for(const file of fileList){
            if(typeof canUploadThroughCurrentHost==='function' && !canUploadThroughCurrentHost(file))continue;
            try{
                const data=await API.upload(file);
                if(data.local_url) uploadedUrls.push(data.local_url);
            }catch(err){
                failedUploads.push(file.name);
                console.error('Upload error:',file.name,err);
            }
        }
        if(failedUploads.length){
            const names=failedUploads.slice(0,3).join(', ');
            Toast.warning(`Не загрузились: ${names}${failedUploads.length>3?'...':''}`);
        }
        if(!uploadedUrls.length){Toast.error('Ошибка загрузки');return}
        if(window.WorkboardV2Session?.isV2(task)){
            ProjectView.projectId=task.project_id||'';
            ProjectView.tasks=[task,...(ProjectView.tasks||[]).filter(t=>t.id!==taskId)];
            const resp=await ProjectView._executeBatchRun(taskId,uploadedUrls);
            if(resp?.ok)this._refreshProjectSummary();
            return resp;
        }

        const types=await getStepTypes();
        const steps=window.TaskGraphActions.nodesToSteps(task.nodes||[],task.edges||[],{types});
        const resp=await API.post('/api/jobs/batch_run',{
            task_id:taskId, project_id:task.project_id||'',
            task_name:task.name||'Batch', steps,
            folder_template:task.folder_template||'',
            files:uploadedUrls
        });
        if(resp.ok){
            Toast.success(`Batch: ${resp.count} jobs`);
            this._refreshProjectSummary();
        }else{Toast.error(resp.error||'Ошибка')}
    },

    async _refreshProjectSummary(){
        if(Router.current!=='home')return;
        try{
            const stats=await API.getCached('/api/jobs/stats?group_by=project');
            const jobStatsByProject=(stats&&typeof stats==='object'&&!Array.isArray(stats))?stats:{};
            this._jobStatsByProject=jobStatsByProject;
            this._renderProjectSummary(this._projects||[], this._tasks||[], jobStatsByProject);
        }catch(e){}
    },

    _refreshJobs(){this._refreshProjectSummary();},

    _esc(s){return String(s??'').replace(/[&<>"']/g,ch=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]))},
    _js(value){return this._esc(JSON.stringify(String(value??'')))}
};

window.Dashboard=Dashboard;

/* web/js/gallery.js */
const Gallery={
    items:[],
    total:0,
    nextOffset:null,
    _wired:false,
    _projects:[],
    _tasks:[],
    _filtersLoaded:false,
    _esc(value){
        return String(value??'').replace(/[&<>"']/g,(ch)=>({
            '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
        }[ch]));
    },
    _fmtTime(ts){
        if(!ts)return '';
        const d=new Date(Number(ts)*1000);
        const pad=n=>String(n).padStart(2,'0');
        return `${pad(d.getDate())}.${pad(d.getMonth()+1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    _safeItemUrl(item){
        const raw=(item&&typeof item==='object')?(item.url||item.file_url||''):'';
        return window.MediaUtils ? MediaUtils.safeUrl(raw) : '';
    },
    async _loadFilterOptions(){
        if(this._filtersLoaded)return;
        try{
            const projects=typeof getProjects==='function'?await getProjects():await API.get('/api/projects');
            this._projects=Array.isArray(projects)?projects:[];
        }catch(e){this._projects=[]}
        try{
            const tasks=await API.getCached('/api/tasks?summary=1');
            this._tasks=Array.isArray(tasks)?tasks:[];
        }catch(e){this._tasks=[]}
        this._filtersLoaded=true;
        this._populateProjectFilter();
        this._populateTaskFilter();
    },
    _selected(id){
        return document.getElementById(id)?.value||'';
    },
    _populateProjectFilter(){
        const select=document.getElementById('gallery-project-filter');
        if(!select)return;
        const current=select.value;
        const options=['<option value="">все проекты</option>'].concat(this._projects.map((p)=>(
            `<option value="${this._esc(p.id)}">${this._esc(p.name||p.id)}</option>`
        )));
        select.innerHTML=options.join('');
        select.value=current;
    },
    _populateTaskFilter(projectId=this._selected('gallery-project-filter')){
        const select=document.getElementById('gallery-task-filter');
        if(!select)return;
        const current=select.value;
        const tasks=this._tasks.filter((task)=>!projectId||String(task.project_id||'')===String(projectId));
        const options=['<option value="">все задачи</option>'].concat(tasks.map((task)=>(
            `<option value="${this._esc(task.id)}">${this._esc(task.name||task.id)}</option>`
        )));
        select.innerHTML=options.join('');
        if(tasks.some((task)=>String(task.id||'')===String(current)))select.value=current;
    },
    _query(offset=0){
        const params=new URLSearchParams();
        params.set('limit','60');
        params.set('offset',String(offset||0));
        const search=this._selected('gallery-search').trim();
        const type=this._selected('gallery-type-filter');
        const project=this._selected('gallery-project-filter');
        const task=this._selected('gallery-task-filter');
        const sort=this._selected('gallery-sort')||'newest';
        if(search)params.set('search',search);
        if(type)params.set('type',type);
        if(project)params.set('project_id',project);
        if(task)params.set('task_id',task);
        params.set('sort',sort);
        return params;
    },
    async load(reset=true){
        await this._loadFilterOptions();
        if(reset){
            this.items=[];
            this.nextOffset=null;
            this.renderLoading();
        }
        const offset=reset?0:(this.nextOffset||0);
        try{
            const data=await API.get('/api/results?'+this._query(offset).toString());
            const incoming=Array.isArray(data.items)?data.items:[];
            this.items=reset?incoming:this.items.concat(incoming);
            this.total=Number(data.total||this.items.length||0);
            this.nextOffset=data.next_offset==null?null:Number(data.next_offset);
            this.render();
        }catch(e){
            if(typeof Toast!=='undefined')Toast.error('Не удалось загрузить галерею');
            this.render();
        }
    },
    applyFilters(){
        this._populateTaskFilter();
        this.load(true);
    },
    resetFilters(){
        ['gallery-search','gallery-type-filter','gallery-project-filter','gallery-task-filter'].forEach((id)=>{
            const el=document.getElementById(id);
            if(el)el.value='';
        });
        const sort=document.getElementById('gallery-sort');
        if(sort)sort.value='newest';
        this._populateTaskFilter('');
        this.load(true);
    },
    loadMore(){
        if(this.nextOffset!=null)this.load(false);
    },
    renderLoading(){
        const grid=document.getElementById('gallery-grid');
        if(grid)grid.innerHTML='<div class="gallery-empty">Загрузка...</div>';
    },
    render(){
        const count=document.getElementById('gallery-count');
        const grid=document.getElementById('gallery-grid');
        const more=document.getElementById('gallery-load-more');
        if(count)count.textContent=`${this.total||0} результатов`;
        if(more)more.hidden=this.nextOffset==null;
        if(!grid)return;
        if(!this.items.length){
            grid.innerHTML='<div class="gallery-empty">Ничего не найдено</div>';
            return;
        }
        grid.innerHTML=this.items.map((item)=>this._card(item)).join('');
        if(typeof OmoIcons!=='undefined')OmoIcons.hydrate(grid);
    },
    _card(item){
        const type=this._esc(item.type||'file');
        const title=this._esc(item.name||'result');
        const meta=[item.project_name,item.task_name,this._fmtTime(item.created_at)].filter(Boolean).map(v=>this._esc(v)).join(' · ');
        return `<button type="button" class="gallery-card" data-action="gallery-open" data-gallery-id="${this._esc(item.id)}">
            <div class="gallery-preview" data-type="${type}">${this._preview(item)}</div>
            <div class="gallery-card-body">
                <div class="gallery-card-title">${title}</div>
                <div class="gallery-card-meta">${meta}</div>
                <span class="gallery-type-pill">${type}</span>
            </div>
        </button>`;
    },
    _preview(item){
        const type=String(item.type||'file');
        const url=this._esc(this._safeItemUrl(item));
        const name=this._esc(item.name||'');
        if(type==='image'&&url)return `<img src="${url}" alt="${name}" loading="lazy">`;
        if(type==='video'&&url)return `<video src="${url}" muted preload="metadata"></video>`;
        if(type==='audio')return `<div class="gallery-preview-icon">${CGIcons?.html?.('audio')||'AUD'}</div>`;
        if(type==='text')return `<div class="gallery-preview-text">${this._esc(item.content_preview||name)}</div>`;
        return `<div class="gallery-preview-icon">${CGIcons?.html?.('folder')||'FILE'}</div>`;
    },
    openViewer(id){
        const item=this.items.find((entry)=>String(entry.id)===String(id));
        if(!item||typeof Modal==='undefined')return;
        const title=this._esc(item.name||'result');
        const meta=[item.project_name,item.task_name,this._fmtTime(item.created_at)].filter(Boolean).map(v=>this._esc(v)).join(' · ');
        const url=this._safeItemUrl(item);
        Modal.show(`<div class="gallery-viewer">
            <div class="gallery-viewer-head">
                <h2>${title}</h2>
                <div class="gallery-card-meta">${meta}</div>
            </div>
            ${this._viewerBody(item)}
            <div class="gallery-viewer-actions">
                ${url?`<a class="btn btn-primary" href="${this._esc(url)}" target="_blank" rel="noopener">открыть</a>`:''}
                <button class="btn btn-ghost" data-action="modal-hide">закрыть</button>
            </div>
        </div>`);
        document.getElementById('modal-content')?.setAttribute('data-modal-kind','gallery-viewer');
    },
    _viewerBody(item){
        const type=String(item.type||'file');
        const url=this._esc(this._safeItemUrl(item));
        const name=this._esc(item.name||'result');
        if(type==='image'&&url)return `<div class="gallery-viewer-media"><img src="${url}" alt="${name}"></div>`;
        if(type==='video'&&url)return `<div class="gallery-viewer-media"><video controls src="${url}"></video></div>`;
        if(type==='audio'&&url)return `<div class="gallery-viewer-media"><audio controls src="${url}"></audio></div>`;
        if(type==='text')return `<pre class="gallery-text-preview">${this._esc(item.content_preview||'')}</pre>`;
        if(url)return `<div class="gallery-file-view"><a class="btn btn-primary" href="${url}" target="_blank" rel="noopener">${name}</a></div>`;
        return '<div class="gallery-empty">Нет файла для просмотра</div>';
    },
    initEvents(){
        if(this._wired)return;
        this._wired=true;
        document.addEventListener('click',(event)=>{
            const card=event.target?.closest?.('[data-action="gallery-open"][data-gallery-id]');
            if(!card)return;
            event.preventDefault();
            this.openViewer(card.dataset.galleryId);
        });
        document.addEventListener('change',(event)=>{
            if(event.target?.id==='gallery-project-filter'){
                this._populateTaskFilter(event.target.value);
            }
        });
    }
};

window.Gallery=Gallery;

/* web/js/templates-marketplace.js */
/* ═══ Templates marketplace ═══
 *
 * Drives two surfaces:
 *   - home-page mini-widget   (#dash-templates / #dash-shared-with-me)
 *   - dedicated page         (#page-templates, route #/templates)
 *
 * Backend endpoints (see api/templates.py):
 *   GET  /api/template-library?category_id=&q=&sort=&limit=&offset=&exclude_mine=
 *   GET  /api/template-library/<id>
 *   POST /api/template-library/<id>/clone
 *   POST /api/template-library/<id>/helpful
 *   GET  /api/template-library/shared-with-me
 *   GET  /api/template-categories
 *
 * Object.assign-style not used here — TemplatesMarketplace is a top-level
 * controller (like Dashboard / Projects). Registered after dashboard.js
 * in scripts/web-assets.mjs:jsAssets.
 */
const TemplatesMarketplace = {
    state: {
        categories: [],
        homeItems: [],
        homeFilter: { category_id: '', sort: 'recent', q: '' },
        pageItems: [],
        pageFilter: { category_id: '', sort: 'recent', q: '' },
        pageOffset: 0,
        pageHasMore: false,
        sharedItems: [],
    },
    _eventsBound: false,
    _searchDebounce: null,

    initEvents(){
        if(this._eventsBound)return;
        this._eventsBound = true;
        document.addEventListener('click', (event) => {
            const sortBtn = event.target instanceof Element ? event.target.closest('[data-templates-sort]') : null;
            if(sortBtn){
                event.preventDefault();
                this._handleSort(sortBtn);
                return;
            }
            const chip = event.target instanceof Element ? event.target.closest('[data-templates-category]') : null;
            if(chip){
                event.preventDefault();
                this._handleCategoryChip(chip);
                return;
            }
            const action = event.target instanceof Element ? event.target.closest('[data-marketplace-action]') : null;
            if(!action)return;
            event.preventDefault();
            const id = action.dataset.templateId || '';
            const a = action.dataset.marketplaceAction;
            if(a === 'helpful')this.toggleHelpful(id);
            else if(a === 'preview')this.openPreview(id);
            else if(a === 'clone')this.openClone(id);
            else if(a === 'load-more')this._loadPageMore();
            else if(a === 'confirm-clone')this._confirmClone();
            else if(a === 'open-shared')this.openPreview(id);
            else if(a === 'unpublish-template')this.unpublishTemplate(id);
        });
        document.addEventListener('input', (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if(!target)return;
            if(target.id === 'dash-templates-search'){
                this._debouncedSearch('home', target.value || '');
            }else if(target.id === 'page-templates-search'){
                this._debouncedSearch('page', target.value || '');
            }
        });
    },

    _esc(s){
        return String(s ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
    },

    _iconInline(name){
        if(typeof CGIcons !== 'undefined' && typeof CGIcons.inline === 'function')return CGIcons.inline(name);
        if(typeof CGIcons !== 'undefined' && typeof CGIcons.html === 'function')return CGIcons.html(name);
        return '';
    },

    _toast(kind, msg){
        if(typeof Toast !== 'undefined' && Toast[kind])Toast[kind](msg);
    },

    _hideModal(){
        if(typeof Modal !== 'undefined' && Modal.hide)Modal.hide();
    },

    _formatRelative(ts){
        if(!ts)return '';
        const seconds = Math.max(1, Math.floor((Date.now() / 1000) - Number(ts)));
        if(seconds < 60)return 'только что';
        const minutes = Math.floor(seconds / 60);
        if(minutes < 60)return `${minutes} мин назад`;
        const hours = Math.floor(minutes / 60);
        if(hours < 24)return `${hours} ч назад`;
        const days = Math.floor(hours / 24);
        if(days < 30)return `${days} дн назад`;
        const months = Math.floor(days / 30);
        if(months < 12)return `${months} мес назад`;
        return `${Math.floor(months / 12)} г назад`;
    },

    _ownerLabel(item){
        return item.owner_username || item.owner_display_name || 'аноним';
    },

    _detectMediaKind(url){
        const ext = String(url || '').split('?')[0].split('.').pop().toLowerCase();
        if(['mp4','mov','webm','m4v','avi','mkv'].includes(ext))return 'video';
        if(['jpg','jpeg','png','webp','gif','bmp','svg','avif'].includes(ext))return 'image';
        return '';
    },

    _previewMediaHtml(item, { modal = false } = {}){
        const raw = item && item.preview_url;
        if(!raw)return '';
        const kind = this._detectMediaKind(raw);
        if(!kind)return '';
        // Served cross-tenant (after viewer authz) by /api/template-library/<id>/preview
        const src = `/api/template-library/${encodeURIComponent(item.id || '')}/preview`;
        const cls = modal ? 'template-preview-media' : 'template-card-preview-media';
        if(kind === 'video'){
            const attrs = modal
                ? 'controls preload="metadata" playsinline'
                : 'muted playsinline preload="metadata"';
            return `<video class="${cls}" src="${this._esc(src)}" ${attrs}></video>`;
        }
        return `<img class="${cls}" src="${this._esc(src)}" alt="превью" loading="lazy">`;
    },

    _templateIconHtml(item){
        const raw = String(item?.icon || '').trim();
        if(typeof CGIcons !== 'undefined' && CGIcons.isKnown?.(raw)){
            return CGIcons.html(raw, { fallback: 'dashboard' });
        }
        if(!raw && typeof CGIcons !== 'undefined'){
            return CGIcons.html('dashboard', { fallback: 'dashboard' });
        }
        if(!raw)return '☰';
        if(raw.length <= 3 || /[^\x20-\x7E]/.test(raw)){
            return this._esc(raw.slice(0, 3));
        }
        return typeof CGIcons !== 'undefined'
            ? CGIcons.html('dashboard', { fallback: 'dashboard' })
            : '☰';
    },

    async _ensureCategories(){
        if(this.state.categories.length)return this.state.categories;
        const resp = await API.get('/api/template-categories');
        if(resp && Array.isArray(resp.categories)){
            this.state.categories = resp.categories;
        }
        return this.state.categories;
    },

    _renderCategoryChips(containerId, activeId){
        const el = document.getElementById(containerId);
        if(!el)return;
        const cats = this.state.categories || [];
        const allChip = `<button class="filter-btn ${!activeId ? 'active' : ''}" data-templates-category="" data-templates-target="${containerId === 'dash-templates-categories' ? 'home' : 'page'}">все</button>`;
        const items = cats.map(c => {
            const active = c.id === activeId ? 'active' : '';
            return `<button class="filter-btn ${active}" data-templates-category="${this._esc(c.id)}" data-templates-target="${containerId === 'dash-templates-categories' ? 'home' : 'page'}">${this._esc(c.name || c.slug)}</button>`;
        }).join('');
        el.innerHTML = allChip + items;
    },

    _renderCard(item, opts = {}){
        const compact = !!opts.compact;
        const catChip = item.category_name
            ? `<span class="template-card-chip">${this._esc(item.category_name)}</span>`
            : '';
        const helpfulCls = item.my_helpful ? 'is-active' : '';
        const helpfulDisabled = item.is_owner ? 'disabled aria-disabled="true" title="нельзя голосовать за свой шаблон"' : '';
        const owner = this._esc(this._ownerLabel(item));
        const dateLabel = this._esc(this._formatRelative(item.published_at));
        const iconHtml = this._templateIconHtml(item);
        const canUnpublish = !!item.is_owner
            || (typeof Auth !== 'undefined' && typeof Auth.isAdmin === 'function' && Auth.isAdmin());
        const unpublishBtn = canUnpublish
            ? `<button type="button" class="btn btn-ghost btn-small" data-marketplace-action="unpublish-template" data-template-id="${this._esc(item.id)}">снять с публикации</button>`
            : '';
        const previewHtml = this._previewMediaHtml(item);
        const descHtml = (item.publish_description && !compact)
            ? `<p class="template-card-desc">${this._esc(item.publish_description)}</p>`
            : '';
        return `<article class="template-card ${compact ? 'template-card-compact' : ''}" data-template-id="${this._esc(item.id)}">
            ${previewHtml ? `<div class="template-card-preview">${previewHtml}</div>` : ''}
            <div class="template-card-head">
                <div class="template-card-icon">${iconHtml}</div>
                <div class="template-card-title-block">
                    <div class="template-card-title">${this._esc(item.name || 'без названия')}</div>
                    <div class="template-card-meta"><span class="template-card-author">@${owner}</span><span class="template-card-dot">·</span><span>${dateLabel}</span></div>
                </div>
                <button type="button" class="template-card-helpful ${helpfulCls}" data-marketplace-action="helpful" data-template-id="${this._esc(item.id)}" ${helpfulDisabled} aria-label="отметить как полезный">
                    <span class="template-card-helpful-star">${CGIcons.html('star')}</span>
                    <span class="template-card-helpful-count">${this._esc(item.helpful_count || 0)}</span>
                </button>
            </div>
            <div class="template-card-body">
                ${catChip}
                <span class="template-card-runs">${this._iconInline('play')} ${this._esc(item.community_run_count || 0)}</span>
                ${item.clone_count ? `<span class="template-card-clones">${this._iconInline('clipboard')} ${this._esc(item.clone_count)}</span>` : ''}
            </div>
            ${descHtml}
            <div class="template-card-actions">
                ${unpublishBtn}
                <button type="button" class="btn btn-ghost btn-small" data-marketplace-action="preview" data-template-id="${this._esc(item.id)}">открыть</button>
                <button type="button" class="btn btn-primary btn-small" data-marketplace-action="clone" data-template-id="${this._esc(item.id)}">добавить себе</button>
            </div>
        </article>`;
    },

    _renderEmpty(containerId, message){
        const el = document.getElementById(containerId);
        if(!el)return;
        el.innerHTML = `<div class="empty-state"><div class="empty-state-title">${this._esc(message)}</div></div>`;
    },

    _renderGrid(containerId, items, opts = {}){
        const el = document.getElementById(containerId);
        if(!el)return;
        if(!items.length){
            el.innerHTML = `<div class="empty-state"><div class="empty-state-title">${this._esc(opts.emptyMessage || 'шаблонов пока нет')}</div></div>`;
            return;
        }
        el.innerHTML = items.map(it => this._renderCard(it, opts)).join('');
    },

    async loadHomeWidget(){
        this.initEvents();
        const filter = this.state.homeFilter;
        try{
            await this._ensureCategories();
            this._renderCategoryChips('dash-templates-categories', filter.category_id);
            const params = new URLSearchParams({ sort: filter.sort, limit: '12', offset: '0', exclude_mine: '0' });
            if(filter.category_id)params.set('category_id', filter.category_id);
            if(filter.q)params.set('q', filter.q);
            const resp = await API.get(`/api/template-library?${params.toString()}`);
            if(!resp || resp.error){
                this._renderEmpty('dash-templates-grid', resp?.error || 'не удалось загрузить шаблоны');
                return;
            }
            this.state.homeItems = resp.items || [];
            this._renderGrid('dash-templates-grid', this.state.homeItems, { emptyMessage: 'пока никто не опубликовал шаблон' });
        }catch(e){
            this._renderEmpty('dash-templates-grid', 'ошибка загрузки');
        }
    },

    async loadSharedWithMe(){
        const section = document.getElementById('dash-shared-with-me');
        const list = document.getElementById('dash-shared-list');
        const counter = document.getElementById('dash-shared-count');
        if(!section || !list)return;
        try{
            const resp = await API.get('/api/template-library/shared-with-me?limit=6');
            const items = resp?.items || [];
            this.state.sharedItems = items;
            if(!items.length){
                section.style.display = 'none';
                return;
            }
            section.style.display = '';
            if(counter)counter.textContent = String(items.length);
            list.innerHTML = items.map(item => {
                const owner = this._esc(item.owner_username || item.owner_display_name || 'кто-то');
                const iconHtml = this._templateIconHtml(item);
                return `<article class="template-card template-card-shared" data-marketplace-action="open-shared" data-template-id="${this._esc(item.id)}">
                    <div class="template-card-head">
                        <div class="template-card-icon">${iconHtml}</div>
                        <div class="template-card-title-block">
                            <div class="template-card-title">${this._esc(item.name || 'шаблон')}</div>
                            <div class="template-card-meta">от @${owner} · ${this._esc(this._formatRelative(item.shared_at))}</div>
                        </div>
                    </div>
                </article>`;
            }).join('');
        }catch(e){
            section.style.display = 'none';
        }
    },

    async loadFullPage(){
        this.initEvents();
        this.state.pageOffset = 0;
        this.state.pageItems = [];
        try{
            await this._ensureCategories();
            this._renderCategoryChips('page-templates-categories', this.state.pageFilter.category_id);
            const grid = document.getElementById('page-templates-grid');
            if(grid)grid.innerHTML = '<div class="empty-state"><div class="empty-state-title">загружаю...</div></div>';
            await this._loadPageItems(true);
        }catch(e){
            this._renderEmpty('page-templates-grid', 'ошибка загрузки');
        }
    },

    async _loadPageItems(reset = false){
        if(reset){
            this.state.pageOffset = 0;
            this.state.pageItems = [];
        }
        const filter = this.state.pageFilter;
        const params = new URLSearchParams({
            sort: filter.sort,
            limit: '20',
            offset: String(this.state.pageOffset),
            exclude_mine: '0',
        });
        if(filter.category_id)params.set('category_id', filter.category_id);
        if(filter.q)params.set('q', filter.q);
        const resp = await API.get(`/api/template-library?${params.toString()}`);
        if(!resp || resp.error){
            this._renderEmpty('page-templates-grid', resp?.error || 'не удалось загрузить шаблоны');
            return;
        }
        const items = resp.items || [];
        this.state.pageItems = reset ? items : [...this.state.pageItems, ...items];
        this.state.pageOffset = (this.state.pageOffset || 0) + items.length;
        this.state.pageHasMore = !!resp.has_more;
        this._renderGrid('page-templates-grid', this.state.pageItems);
        const moreBtn = document.getElementById('page-templates-load-more');
        if(moreBtn){
            moreBtn.style.display = this.state.pageHasMore ? '' : 'none';
            moreBtn.dataset.marketplaceAction = 'load-more';
        }
    },

    async _loadPageMore(){
        await this._loadPageItems(false);
    },

    _handleSort(btn){
        const target = btn.parentElement?.id === 'dash-templates-sort' ? 'home' : 'page';
        const sort = btn.dataset.templatesSort || 'recent';
        const filter = target === 'home' ? this.state.homeFilter : this.state.pageFilter;
        filter.sort = sort;
        // Toggle active class
        btn.parentElement?.querySelectorAll('[data-templates-sort]').forEach(b => b.classList.toggle('active', b === btn));
        if(target === 'home')this.loadHomeWidget();
        else this._loadPageItems(true);
    },

    _handleCategoryChip(chip){
        const target = chip.dataset.templatesTarget === 'home' ? 'home' : 'page';
        const filter = target === 'home' ? this.state.homeFilter : this.state.pageFilter;
        filter.category_id = chip.dataset.templatesCategory || '';
        // Toggle active class
        chip.parentElement?.querySelectorAll('[data-templates-category]').forEach(b => b.classList.toggle('active', b === chip));
        if(target === 'home')this.loadHomeWidget();
        else this._loadPageItems(true);
    },

    _debouncedSearch(target, value){
        clearTimeout(this._searchDebounce);
        this._searchDebounce = setTimeout(() => {
            const filter = target === 'home' ? this.state.homeFilter : this.state.pageFilter;
            filter.q = value.trim();
            if(target === 'home')this.loadHomeWidget();
            else this._loadPageItems(true);
        }, 350);
    },

    async toggleHelpful(templateId){
        if(!templateId)return;
        const resp = await API.post(`/api/template-library/${encodeURIComponent(templateId)}/helpful`, {});
        if(!resp || resp.error){
            const messages = {
                self_vote: 'Нельзя голосовать за свой шаблон',
                not_public: 'Шаблон не опубликован',
                template_not_found: 'Шаблон не найден',
            };
            this._toast('error', messages[resp?.error] || resp?.error || 'не удалось проголосовать');
            return;
        }
        // Update card in both surfaces
        const update = item => {
            if(item.id === templateId){
                item.helpful_count = resp.helpful_count;
                item.my_helpful = resp.my_helpful;
            }
        };
        this.state.homeItems.forEach(update);
        this.state.pageItems.forEach(update);
        // Re-render cards in DOM (only the matching ones)
        document.querySelectorAll(`.template-card[data-template-id="${CSS.escape(templateId)}"]`).forEach(card => {
            const btn = card.querySelector('.template-card-helpful');
            if(!btn)return;
            const countEl = btn.querySelector('.template-card-helpful-count');
            if(countEl)countEl.textContent = String(resp.helpful_count);
            btn.classList.toggle('is-active', !!resp.my_helpful);
        });
    },

    async openPreview(templateId){
        if(!templateId)return;
        const resp = await API.get(`/api/template-library/${encodeURIComponent(templateId)}`);
        if(!resp || resp.error){
            this._toast('error', resp?.error || 'не удалось открыть шаблон');
            return;
        }
        const t = resp.template;
        const nodesCount = (t.nodes || []).length;
        const stepsCount = (t.steps || []).length;
        const typeBreakdown = {};
        (t.nodes || []).forEach(n => {
            const type = n.type || 'node';
            typeBreakdown[type] = (typeBreakdown[type] || 0) + 1;
        });
        const stripModulePrefix = (k) => {
            const i = String(k).indexOf(':');
            return i > 0 ? String(k).slice(i + 1) : String(k);
        };
        const breakdownText = Object.entries(typeBreakdown).map(([k, v]) => `${this._esc(stripModulePrefix(k))} × ${v}`).join(', ') || 'граф пустой';
        const owner = this._esc(this._ownerLabel(t));
        const cat = t.category ? `<div class="admin-plan-meta"><span>${this._esc(t.category.name)}</span></div>` : '';
        const cloneBtn = t.is_owner
            ? '<button class="btn btn-ghost" data-action="modal-hide">закрыть</button>'
            : `<button class="btn btn-primary" data-marketplace-action="clone" data-template-id="${this._esc(t.id)}">добавить себе</button>`;
        const canUnpublish = !!t.is_owner
            || (typeof Auth !== 'undefined' && typeof Auth.isAdmin === 'function' && Auth.isAdmin());
        const unpublishBtn = canUnpublish
            ? `<button class="btn btn-ghost" data-marketplace-action="unpublish-template" data-template-id="${this._esc(t.id)}">снять с публикации</button>`
            : '';
        Modal.show(`<div class="template-preview-modal">
            <h2>${this._esc(t.name || 'Шаблон')}</h2>
            <div class="template-preview-meta">от @${owner} · ${this._esc(this._formatRelative(t.published_at))}</div>
            ${cat}
            ${(() => { const m = this._previewMediaHtml(t, { modal: true }); return m ? `<div class="template-preview-cover">${m}</div>` : ''; })()}
            ${t.publish_description ? `<p class="template-preview-description">${this._esc(t.publish_description)}</p>` : ''}
            <div class="template-preview-summary">
                <div><strong>Шаги:</strong> ${stepsCount}</div>
                <div><strong>Узлы:</strong> ${nodesCount}</div>
                <div><strong>Состав:</strong> ${breakdownText}</div>
            </div>
            <div class="template-preview-stats">
                <span>${this._iconInline('star')} ${this._esc(t.helpful_count || 0)}</span>
                <span>${this._iconInline('play')} ${this._esc(t.community_run_count || 0)} запусков</span>
                <span>${this._iconInline('clipboard')} ${this._esc(t.clone_count || 0)} клонов</span>
            </div>
            <div class="admin-modal-actions">
                ${cloneBtn}
                ${unpublishBtn}
                <button class="btn btn-ghost" data-action="modal-hide">отмена</button>
            </div>
        </div>`);
    },

    async unpublishTemplate(templateId){
        if(!templateId)return;
        const ok = (typeof Dialogs !== 'undefined' && Dialogs.confirm)
            ? await Dialogs.confirm('Снять шаблон с публикации? Карточка пропадёт из библиотеки. Существующие копии у других пользователей не затрагиваются.')
            : false;
        if(!ok)return;
        const resp = await API.post(`/api/tasks/${encodeURIComponent(templateId)}/unpublish`, {});
        if(!resp || resp.error){
            const messages = { Forbidden: 'Нет прав снять этот шаблон', 'Not found': 'Шаблон не найден' };
            this._toast('error', messages[resp?.error] || resp?.error || 'не удалось снять с публикации');
            return;
        }
        this._toast('success', 'Шаблон снят с публикации');
        if(typeof Modal !== 'undefined' && Modal.hide)Modal.hide();
        // Optimistic removal, then re-sync whichever grids are mounted.
        document.querySelectorAll(`[data-template-id="${(window.CSS && CSS.escape) ? CSS.escape(templateId) : templateId}"]`).forEach(el => {
            const card = el.closest('.template-card');
            if(card)card.remove();
        });
        if(document.getElementById('page-templates-grid'))this._loadPageItems(true);
        if(document.getElementById('dash-templates-grid'))this.loadHomeWidget();
    },

    async openClone(templateId){
        if(!templateId)return;
        // Fetch the user's projects
        const projects = await getProjects();
        const list = Array.isArray(projects) ? projects : [];
        if(!list.length){
            this._toast('error', 'Сначала создайте проект, чтобы добавить туда шаблон');
            return;
        }
        const options = list.map(p => `<option value="${this._esc(p.id)}">${this._esc(p.name || 'Проект')}</option>`).join('');
        Modal.show(`<div class="template-clone-modal">
            <h2>Добавить шаблон себе</h2>
            <p class="template-preview-description">Шаблон будет скопирован в ваш проект как приватная задача. Изменения автора больше не повлияют на копию.</p>
            <div class="form-hint" data-style="font-size:11px;color:var(--text-muted);background:var(--bg-secondary);padding:8px 10px;border-radius:var(--omb-r-sm);margin:0 0 10px">
                Результаты в нодах «Результат» останутся у автора — у вас они откроются как 403. После клонирования запустите граф заново, чтобы создать свои результаты.
            </div>
            <input type="hidden" id="template-clone-id" value="${this._esc(templateId)}">
            <label class="form-group">
                <span class="form-label">Проект</span>
                <select class="form-select" id="template-clone-project">${options}</select>
            </label>
            <div class="admin-modal-actions">
                <button class="btn btn-primary" data-marketplace-action="confirm-clone">скопировать</button>
                <button class="btn btn-ghost" data-action="modal-hide">отмена</button>
            </div>
        </div>`);
    },

    async _confirmClone(){
        const templateId = document.getElementById('template-clone-id')?.value;
        const projectId = document.getElementById('template-clone-project')?.value;
        if(!templateId || !projectId){
            this._toast('error', 'Не выбран проект');
            return;
        }
        const resp = await API.post(
            `/api/template-library/${encodeURIComponent(templateId)}/clone`,
            { project_id: projectId },
        );
        if(!resp || resp.error){
            const messages = {
                project_id_required: 'Выберите проект',
                template_not_visible: 'Нет доступа к шаблону',
                template_not_found: 'Шаблон не найден',
            };
            this._toast('error', messages[resp?.error] || resp?.error || 'не удалось скопировать');
            return;
        }
        this._toast('success', 'Шаблон добавлен в ваш проект');
        this._hideModal();
        // Bump local clone_count
        const update = item => { if(item.id === templateId)item.clone_count = (item.clone_count || 0) + 1; };
        this.state.homeItems.forEach(update);
        this.state.pageItems.forEach(update);
        // Navigate to the new task
        if(resp.new_task_id && typeof Router !== 'undefined'){
            Router.go('task', { projectId, taskId: resp.new_task_id });
        }
    },
};

window.TemplatesMarketplace = TemplatesMarketplace;

/* web/js/media-utils.js */
(function(global){
    function escapeHtml(value){
        if(!value)return '';
        return String(value)
            .replace(/&/g,'&amp;')
            .replace(/</g,'&lt;')
            .replace(/>/g,'&gt;')
            .replace(/"/g,'&quot;')
            .replace(/'/g,'&#39;');
    }

    function fileNameFromUrl(url){
        const raw=String(url||'');
        const clean=raw.split('?')[0].split('#')[0];
        const name=clean.split('/').pop()||'file';
        try{return decodeURIComponent(name)}catch(e){return name}
    }

    function detectFileType(nameOrUrl){
        const name=String(nameOrUrl||'').split('?')[0].split('#')[0];
        const ext=(name.split('.').pop()||'').toLowerCase();
        const typeMap={
            jpg:'image',jpeg:'image',png:'image',webp:'image',gif:'image',bmp:'image',svg:'image',heic:'image',heif:'image',avif:'image',
            mp4:'video',mov:'video',avi:'video',mkv:'video',webm:'video',m4v:'video',
            mp3:'audio',wav:'audio',ogg:'audio',m4a:'audio',flac:'audio',aac:'audio'
        };
        return typeMap[ext]||'';
    }

    function fileKind(name){
        const ext=(String(name).split('?')[0].split('.').pop()||'').toLowerCase();
        if(['jpg','jpeg','png','webp','gif','bmp','svg','heic','heif','avif'].includes(ext))return{type:'image',label:'изображение',badge:'IMG'};
        if(['mp4','mov','webm','m4v','avi','mkv'].includes(ext))return{type:'video',label:'видео',badge:'VID'};
        if(['mp3','wav','m4a','aac','ogg','flac'].includes(ext))return{type:'audio',label:'аудио',badge:'AUD'};
        if(['txt','json','md','csv','log','srt','vtt'].includes(ext))return{type:'text',label:'текст',badge:(ext||'TXT').toUpperCase().slice(0,4)};
        return{type:'file',label:ext?ext.toUpperCase():'файл',badge:(ext||'FILE').toUpperCase().slice(0,4)};
    }

    function isPrivateHost(hostname){
        const host=String(hostname||'').trim().toLowerCase().replace(/^\[|\]$/g,'');
        if(!host)return false;
        if(host==='localhost'||host.endsWith('.localhost'))return true;
        if(host==='::1'||host==='0:0:0:0:0:0:0:1')return true;
        if(host.startsWith('fe80:')||host.startsWith('fc')||host.startsWith('fd'))return true;
        const parts=host.split('.');
        if(parts.length!==4)return false;
        const nums=parts.map((part)=>Number(part));
        if(nums.some((n)=>!Number.isInteger(n)||n<0||n>255))return false;
        const [a,b]=nums;
        if(a===0||a===10||a===127)return true;
        if(a===169&&b===254)return true;
        if(a===172&&b>=16&&b<=31)return true;
        if(a===192&&b===168)return true;
        if(a===100&&b>=64&&b<=127)return true;
        return false;
    }

    function safeUrl(url, options={}){
        const raw=String(url||'').trim();
        if(!raw)return '';
        const allowData=options.allowData!==false;
        const allowBlob=options.allowBlob!==false;
        const protectedPrefixes=['/uploads/','/images/','/videos/','/f/','/api/jobs/'];
        const logical=global.AppURL?global.AppURL.internalPath(raw):raw;
        if(protectedPrefixes.some(prefix=>logical.startsWith(prefix))){
            return global.AppURL?global.AppURL.resolve(raw):raw;
        }
        if(allowData&&/^data:(image\/(?!svg\+xml)[a-z0-9.+-]+|video\/[a-z0-9.+-]+|audio\/[a-z0-9.+-]+);/i.test(raw)){
            return raw;
        }
        try{
            const parsed=new URL(raw, window.location.origin);
            const allowExternal=options.allowExternal!==false||options.externalPreview===true;
            const sameOrigin=parsed.origin===window.location.origin;
            if(
                (parsed.protocol==='http:'||parsed.protocol==='https:')
                && sameOrigin
                && protectedPrefixes.some(prefix=>(global.AppURL?global.AppURL.internalPath(parsed.href):parsed.pathname).startsWith(prefix))
            ){
                return global.AppURL?global.AppURL.resolve(parsed.href):parsed.href;
            }
            if((parsed.protocol==='http:'||parsed.protocol==='https:')&&!sameOrigin&&isPrivateHost(parsed.hostname)){
                return '';
            }
            if((parsed.protocol==='http:'||parsed.protocol==='https:')&&allowExternal){
                return global.AppURL?global.AppURL.resolve(parsed.href):parsed.href;
            }
            if(allowBlob&&parsed.protocol==='blob:'&&sameOrigin){
                return parsed.href;
            }
        }catch(e){}
        return '';
    }

    function withAuth(url, download=false){
        let out=safeUrl(url,{allowData:false});
        if(!out)return '#';
        if(download&&/^(https?:|\/)/i.test(out))out+=(out.includes('?')?'&':'?')+'download=1';
        return (typeof global.Auth!=='undefined')?global.Auth.authUrl(out):out;
    }

    function mediaUrl(url){
        const out=safeUrl(url,{allowExternal:false});
        if(!out)return '';
        const logical=global.AppURL?global.AppURL.internalPath(out):out;
        let protectedPath=/^\/(uploads|images|videos|f)\//.test(logical);
        if(!protectedPath){
            try{
                const parsed=new URL(out, window.location.origin);
                protectedPath=parsed.origin===window.location.origin
                    && (parsed.pathname.startsWith('/uploads/')
                        || parsed.pathname.startsWith('/images/')
                        || parsed.pathname.startsWith('/videos/'));
            }catch(e){}
        }
        if(protectedPath&&typeof global.Auth!=='undefined')return global.Auth.authUrl(out);
        return out;
    }

    let thumbObserver=null;
    let layoutRaf=0;

    const VIDEO_THUMB_CACHE_CAP=60;
    const videoThumbCache=new Map();

    function getThumbCacheKey(video){
        if(!video)return '';
        const direct=video.dataset?.thumbCacheKey;
        if(direct)return direct;
        const wrap=video.closest?.('[data-thumb-cache-key]');
        if(wrap&&wrap.dataset.thumbCacheKey)return wrap.dataset.thumbCacheKey;
        const fileWrap=video.closest?.('[data-file-url]');
        if(fileWrap&&fileWrap.dataset.fileUrl)return fileWrap.dataset.fileUrl;
        return video.getAttribute?.('src')||'';
    }

    function scheduleEdgesRefresh(){
        if(layoutRaf)return;
        layoutRaf=global.requestAnimationFrame?.(()=>{
            layoutRaf=0;
            if(global.TaskEditor?._scheduleRenderEdges){
                global.TaskEditor._scheduleRenderEdges();
            }else{
                global.TaskEditor?.renderEdges?.();
            }
            if(global.JobDebugger?._scheduleRenderEdges){
                global.JobDebugger._scheduleRenderEdges();
            }else{
                global.JobDebugger?.renderEdges?.();
            }
        })||0;
    }

    function applyAspectRatioToWrap(wrap,width,height){
        if(!wrap||!(width>0)||!(height>0))return false;
        const w=Math.max(1,Number(width)||1);
        const h=Math.max(1,Number(height)||1);
        const ratio=w/h;
        wrap.dataset.mediaFit='ready';
        wrap.dataset.mediaOrientation=ratio<0.82?'portrait':(ratio>1.2?'landscape':'square');
        wrap.style.setProperty('--wb-media-aspect-ratio', `${w} / ${h}`);
        wrap.style.setProperty('--wb-video-ratio', String(ratio));
        return true;
    }

    function applyVideoLayout(video){
        if(!video||!video.videoWidth||!video.videoHeight)return false;
        const wrap=video.closest('[data-video-thumb-wrap]')||video.closest('.wb-media-video')||video.parentElement;
        if(!wrap||!wrap.classList?.contains('wb-media-video'))return false;
        if(!applyAspectRatioToWrap(wrap,video.videoWidth,video.videoHeight))return false;
        if(wrap.closest('.nd'))scheduleEdgesRefresh();
        return true;
    }

    function applyThumbCacheHit(video,entry){
        const wrap=video.closest('[data-video-thumb-wrap]')||video.closest('.wb-media-video')||video.parentElement;
        if(wrap){
            wrap.style.setProperty('--video-thumb', `url("${entry.dataUrl}")`);
            wrap.classList.remove('video-thumb-loading','video-thumb-failed');
            wrap.classList.add('video-thumb-ready');
        }
        try{video.setAttribute('poster',entry.dataUrl)}catch(e){}
        video.dataset.videoThumbInit='1';
        const hasSize=Number(entry.width)>0&&Number(entry.height)>0;
        const isInsideNode=!!(wrap&&wrap.closest('.nd'));
        if(hasSize&&wrap&&wrap.classList?.contains('wb-media-video')){
            applyAspectRatioToWrap(wrap,entry.width,entry.height);
            if(isInsideNode)scheduleEdgesRefresh();
        }else if(video.readyState>=1){
            applyVideoLayout(video);
        }else{
            video.addEventListener('loadedmetadata',()=>applyVideoLayout(video),{once:true});
        }
    }

    function cacheVideoThumbEntry(key,entry){
        if(!key||!entry||!entry.dataUrl)return;
        if(videoThumbCache.has(key))videoThumbCache.delete(key);
        else if(videoThumbCache.size>=VIDEO_THUMB_CACHE_CAP){
            const first=videoThumbCache.keys().next().value;
            if(first!==undefined)videoThumbCache.delete(first);
        }
        videoThumbCache.set(key,entry);
    }

    function hydrateVideoPreviews(root=document){
        const scope=root||document;
        scope.querySelectorAll('video[data-video-thumb]:not([data-video-thumb-init]):not([data-video-thumb-observe])').forEach(queueVideoThumb);
    }

    function queueVideoThumb(video){
        if(!('IntersectionObserver' in global)){
            buildVideoThumb(video);
            return;
        }
        video.dataset.videoThumbObserve='1';
        if(!thumbObserver){
            thumbObserver=new IntersectionObserver((entries)=>{
                entries.forEach(entry=>{
                    if(!entry.isIntersecting)return;
                    thumbObserver.unobserve(entry.target);
                    const item=entry.target.matches('video')?entry.target:entry.target.querySelector('video[data-video-thumb]');
                    if(!item||item.dataset.videoThumbInit)return;
                    delete item.dataset.videoThumbObserve;
                    buildVideoThumb(item);
                });
            },{root:null,rootMargin:'240px 0px',threshold:0.01});
        }
        thumbObserver.observe(video.closest('[data-video-thumb-wrap]')||video);
    }

    function buildVideoThumb(video){
        const cacheKey=getThumbCacheKey(video);
        const cached=cacheKey?videoThumbCache.get(cacheKey):null;
        if(cached&&cached.dataUrl){
            applyThumbCacheHit(video,cached);
            return;
        }
        video.dataset.videoThumbInit='1';
        const wrap=video.closest('[data-video-thumb-wrap]')||video.parentElement;
        if(wrap)wrap.classList.add('video-thumb-loading');
        let points=[];
        let idx=0;

        const fail=()=>{
            if(wrap){
                wrap.classList.remove('video-thumb-loading');
                wrap.classList.add('video-thumb-failed');
            }
        };
        const finish=(dataUrl)=>{
            if(wrap&&dataUrl){
                wrap.style.setProperty('--video-thumb', `url("${dataUrl}")`);
                wrap.classList.remove('video-thumb-loading','video-thumb-failed');
                wrap.classList.add('video-thumb-ready');
            }
            if(dataUrl){
                try{video.setAttribute('poster',dataUrl)}catch(e){}
                if(cacheKey){
                    cacheVideoThumbEntry(cacheKey,{
                        dataUrl,
                        width:Number(video.videoWidth)||0,
                        height:Number(video.videoHeight)||0,
                    });
                }
            }
            try{video.pause()}catch(e){}
        };
        const next=()=>{
            const t=points[idx++];
            if(t===undefined){fail();return}
            try{
                if(Math.abs((video.currentTime||0)-t)<0.04)capture();
                else video.currentTime=t;
            }catch(e){
                if(idx<points.length)next();
                else capture();
            }
        };
        const capture=()=>{
            if(!video.videoWidth||!video.videoHeight){
                if(idx<points.length)next();
                else fail();
                return;
            }
            try{
                const w=Math.min(420,video.videoWidth);
                const h=Math.max(1,Math.round(w*(video.videoHeight/video.videoWidth)));
                const canvas=document.createElement('canvas');
                canvas.width=w;
                canvas.height=h;
                const ctx=canvas.getContext('2d',{willReadFrequently:true});
                ctx.drawImage(video,0,0,w,h);

                const pixels=ctx.getImageData(0,0,w,h).data;
                const stride=Math.max(1,Math.floor((w*h)/900));
                let total=0,max=0,count=0;
                for(let p=0;p<pixels.length;p+=stride*4){
                    const b=(pixels[p]+pixels[p+1]+pixels[p+2])/3;
                    total+=b;
                    if(b>max)max=b;
                    count++;
                }
                const avg=count?total/count:0;
                if(avg>24||max>70){
                    finish(canvas.toDataURL('image/jpeg',0.78));
                }else if(idx<points.length){
                    next();
                }else{
                    fail();
                }
            }catch(e){
                if(idx<points.length)next();
                else fail();
            }
        };

        video.addEventListener('loadedmetadata',()=>{
            applyVideoLayout(video);
            const d=Number.isFinite(video.duration)?video.duration:0;
            const raw=d>0
                ?[0.12,Math.min(Math.max(d*0.12,0.35),2),Math.min(Math.max(d*0.35,0.9),5),Math.min(Math.max(d*0.65,1.5),8)]
                :[0.4,1.2,2.4];
            points=[...new Set(raw.filter(t=>d<=0||t<Math.max(0.15,d-0.04)).map(t=>Number(t.toFixed(2))))];
            if(!points.length)points=[0];
            next();
        },{once:true});
        video.addEventListener('seeked',capture);
        if(video.readyState>=1)applyVideoLayout(video);
        video.addEventListener('loadeddata',()=>{if(!points.length)capture()},{once:true});
        video.addEventListener('error',fail,{once:true});
        try{video.load()}catch(e){fail()}
    }

    function clearVideoThumbCache(){
        videoThumbCache.clear();
    }

    const MediaUtils={
        escapeHtml,
        fileNameFromUrl,
        detectFileType,
        fileKind,
        safeUrl,
        withAuth,
        mediaUrl,
        applyVideoLayout,
        hydrateVideoPreviews,
        buildVideoThumb,
        clearVideoThumbCache,
        _videoThumbCacheForTests:videoThumbCache,
    };

    global.MediaUtils=MediaUtils;
    if(!global.escHtml)global.escHtml=escapeHtml;
})(window);

/* web/js/graph-utils.js */
(function(global){
    const TEMPLATE_REF_RE=/\{\{\s*([A-Za-z_][A-Za-z0-9_]*(?:(?:_\d+)|(?:\.[A-Za-z_][A-Za-z0-9_]*)|(?:\[\d+\]))*)\s*\}\}/g;

    function isSemanticallyEmpty(value){
        if(value===undefined||value===null)return true;
        if(Array.isArray(value))return value.every(item=>isSemanticallyEmpty(item));
        if(typeof value==='string'){
            const text=value.trim();
            if(!text)return true;
            if(text.startsWith('[')&&text.endsWith(']')){
                try{
                    const parsed=JSON.parse(text);
                    if(Array.isArray(parsed))return parsed.every(item=>isSemanticallyEmpty(item));
                }catch(_e){}
            }
        }
        return false;
    }

    function extractTemplateRefs(value,out=new Set(),seen=new WeakSet()){
        if(typeof value==='string'){
            TEMPLATE_REF_RE.lastIndex=0;
            let match;
            while((match=TEMPLATE_REF_RE.exec(value))!==null){
                const name=String(match[1]||'').trim();
                if(name)out.add(name);
            }
        }else if(Array.isArray(value)){
            if(seen.has(value))return out;
            seen.add(value);
            value.forEach(item=>extractTemplateRefs(item,out,seen));
        }else if(value&&typeof value==='object'){
            if(seen.has(value))return out;
            seen.add(value);
            Object.keys(value).forEach(key=>{
                if(key==='parse_rules'||String(key).startsWith('_'))return;
                extractTemplateRefs(value[key],out,seen);
            });
        }
        return out;
    }

    function _looksMultiOutputNode(node,meta={}){
        const type=String((node||{}).type||'');
        const num=(...values)=>{
            for(const value of values){
                if(value===undefined||value===null||value==='')continue;
                const parsed=Number(value);
                if(parsed>0)return parsed;
            }
            return 0;
        };
        const result=(node||{})._result||{};
        const pieces=Array.isArray(result.pieces)?result.pieces.filter(Boolean):[];
        const splitVars=result&&typeof result.split_vars==='object'?result.split_vars:{};
        const saveAs=String((node||{}).save_as||'');
        if(pieces.length)return true;
        if(saveAs&&Number(splitVars[`${saveAs}_count`]||0)>0)return true;
        return (
            type==='split' ||
            type.includes('editor_split') ||
            type.includes('audio_split') ||
            type.includes('scene_split') ||
            type.includes('split_video') ||
            type.includes('url_downloader:') ||
            type.includes('grok-imagine/image-to-image') ||
            (node||{})._multi_output===true ||
            meta.multi_output===true ||
            num((node||{})._expected_outputs,(node||{}).expected_outputs,(node||{}).split_count,(node||{}).count,(node||{}).parts)>1
        );
    }

    // The agent sometimes sends parse_rules as a {name: marker} dict instead
    // of an array — a plain `||[]` fallback would not catch it (objects are
    // truthy) and `for...of` over it throws. Keep dict-shaped rules usable
    // in the UI instead of dropping them.
    function parseRulesOf(node){
        const rules=(node||{}).parse_rules;
        if(Array.isArray(rules))return rules.filter(r=>r&&typeof r==='object');
        if(rules&&typeof rules==='object'){
            return Object.keys(rules).map(name=>({name:String(name),from:String(rules[name]||'')}));
        }
        return [];
    }

    function resolveVariableSource(refName,nodes,opts={}){
        const raw=String(refName||'').trim();
        const name=raw.replace(/^\{\{\s*/,'').replace(/\s*\}\}$/,'').trim();
        if(!name)return null;
        const list=(nodes||[]).filter(n=>{
            if(!n||!n.id||n._disabled)return false;
            // Completed result_node is a materialized source. Pending
            // placeholders have no file_url yet, so they cannot produce refs.
            if(n.type==='result_node')return !!String(n.file_url||'').trim();
            return true;
        });
        const types=opts.types||{};
        const isMulti=typeof opts.isMultiOutputNode==='function'
            ?opts.isMultiOutputNode
            :global.TaskGraphActions?.isMultiOutputNode;
        const multiVars=typeof opts.multiOutputVars==='function'
            ?opts.multiOutputVars
            :global.TaskGraphActions?.multiOutputVars;

        for(const node of list){
            const saveAs=String(node.save_as||'').trim();
            if(saveAs===name)return {id:String(node.id),node,name,kind:'exact'};
        }
        for(const node of list){
            for(const rule of parseRulesOf(node)){
                const ruleName=String((rule||{}).name||'').trim();
                if(ruleName===name)return {id:String(node.id),node,name,kind:'parse_rule'};
            }
        }
        const accessorBase=String(name).split(/[.\[]/,1)[0];
        if(accessorBase&&accessorBase!==name){
            for(const node of list){
                const saveAs=String(node.save_as||'').trim();
                if(saveAs===accessorBase)return {id:String(node.id),node,name:accessorBase,kind:'property'};
            }
            for(const node of list){
                for(const rule of parseRulesOf(node)){
                    const ruleName=String((rule||{}).name||'').trim();
                    if(ruleName===accessorBase)return {id:String(node.id),node,name:accessorBase,kind:'parse_rule_property'};
                }
            }
        }
        const tryDerived=(candidate)=>{
            for(const node of list){
                const saveAs=String(node.save_as||'').trim();
                if(!saveAs||!candidate.startsWith(`${saveAs}_`))continue;
                const suffix=candidate.slice(saveAs.length+1);
            if(!/^(?:\d+|count)$/.test(suffix))continue;
            const meta=types[node.type]||{};
            let supports=false;
            if(multiVars){
                    try{supports=(multiVars(node,meta)||[]).some(v=>v&&v.name===candidate)}catch(_e){supports=false}
            }
            if(!supports){
                try{supports=isMulti?!!isMulti(node,meta):false}catch(_e){supports=false}
            }
                if(supports)return {id:String(node.id),node,name:candidate,kind:'derived'};
            }
            return null;
        };
        const derived=tryDerived(name);
        if(derived)return derived;
        if(accessorBase&&accessorBase!==name)return tryDerived(accessorBase);
        return null;
    }

    function topoSort(nodes, edges){
        const inDeg={};
        const adj={};
        const orderIndex=new Map();
        (nodes||[]).forEach((n,idx)=>{
            const id=String(n.id);
            orderIndex.set(id,idx);
            inDeg[n.id]=0;
            adj[n.id]=[];
        });
        const stableIdCompare=(a,b)=>{
            const ai=orderIndex.has(String(a))?orderIndex.get(String(a)):Number.MAX_SAFE_INTEGER;
            const bi=orderIndex.has(String(b))?orderIndex.get(String(b)):Number.MAX_SAFE_INTEGER;
            if(ai!==bi)return ai-bi;
            return String(a).localeCompare(String(b));
        };
        (edges||[]).forEach(e=>{
            if(inDeg[e.to]!==undefined){
                inDeg[e.to]++;
                if(adj[e.from])adj[e.from].push(e.to);
            }
        });
        const q=Object.keys(inDeg).filter(k=>inDeg[k]===0).sort(stableIdCompare);
        const result=[];
        while(q.length){
            const n=q.shift();
            result.push(n);
            const released=[];
            (adj[n]||[]).forEach(m=>{
                inDeg[m]--;
                if(inDeg[m]===0)released.push(m);
            });
            if(released.length)q.push(...released.sort(stableIdCompare));
        }
        const resultSet=new Set(result.map(String));
        (nodes||[]).forEach(n=>{
            const id=String(n.id);
            if(!resultSet.has(id)){
                result.push(n.id);
                resultSet.add(id);
            }
        });
        return result;
    }

    function rectPortCenter(node,el,port){
        if(!node||!el||!port||typeof port.getBoundingClientRect!=='function'||typeof el.getBoundingClientRect!=='function')return null;
        const pr=port.getBoundingClientRect();
        const nr=el.getBoundingClientRect();
        if(!nr||!pr||nr.width<=0||nr.height<=0||pr.width<=0||pr.height<=0)return null;
        const ew=Number(el.offsetWidth)||0;
        const eh=Number(el.offsetHeight)||0;
        if(ew<=0||eh<=0)return null;
        const scaleX=nr.width/ew;
        const scaleY=nr.height/eh;
        const safeScaleX=Number.isFinite(scaleX)&&scaleX>0?scaleX:1;
        const safeScaleY=Number.isFinite(scaleY)&&scaleY>0?scaleY:1;
        return {
            x:(node.x||0)+(pr.left-nr.left+pr.width/2)/safeScaleX,
            y:(node.y||0)+(pr.top-nr.top+pr.height/2)/safeScaleY
        };
    }

    function offsetPortCenter(node,el,port){
        let ox=0, oy=0, cur=port, reached=false;
        while(cur){
            if(cur===el){reached=true;break;}
            ox+=cur.offsetLeft||0;
            oy+=cur.offsetTop||0;
            const next=cur.offsetParent;
            if(!next||next===cur)break;
            cur=next;
        }
        if(!reached)return null;
        return {
            x:(node.x||0)+ox+(port.offsetWidth||0)/2,
            y:(node.y||0)+oy+(port.offsetHeight||0)/2
        };
    }

    function measurePortCenter(node,el,port){
        return offsetPortCenter(node,el,port)||rectPortCenter(node,el,port);
    }

    // Named ports are exact identities, not hints for the legacy single-port path.
    function portKey(direction,portId,slotIndex=null){
        if((direction!=='input'&&direction!=='output')||typeof portId!=='string'||!portId)return null;
        let slot='';
        if(slotIndex!=null&&slotIndex!==''){
            if(typeof slotIndex!=='number'&&(typeof slotIndex!=='string'||!/^\d+$/.test(slotIndex)))return null;
            slot=Number(slotIndex);
            if(!Number.isSafeInteger(slot)||slot<0)return null;
        }
        return JSON.stringify([direction,portId,slot]);
    }

    function namedPortKey(port,nodeId){
        const data=port?.dataset||{};
        if(!data.portId||(data.direction!=='input'&&data.direction!=='output'))return null;
        if(data.nodeId!=null&&String(data.nodeId)!==String(nodeId))return null;
        // An output selector (e.g. item:3) already identifies its element.
        if(data.direction==='output'&&data.slotIndex)return null;
        return portKey(data.direction,data.portId,data.slotIndex);
    }

    function namedPorts(el,nodeId){
        const ports=new Map();
        el?.querySelectorAll?.('.port[data-port-id]').forEach(port=>{
            // A nested board/card must not lend its ports to the outer node.
            const owner=port.closest?.('[data-node-id]:not(.port)');
            if(owner&&owner!==el)return;
            const key=namedPortKey(port,nodeId);
            if(key!=null)ports.set(key,ports.has(key)?null:port);
        });
        return ports;
    }

    function measureNamedPortCenter(node,el,port){
        if(!port)return null;
        // Relative client rects cancel viewport scroll and pan; dividing by the
        // node scale handles zoom, borders, nested offset parents and scrolling.
        const rect=rectPortCenter(node,el,port);
        if(rect)return rect;
        if(!(port.offsetWidth>0&&port.offsetHeight>0))return null;
        let ox=0,oy=0,cur=port;
        const seen=new Set();
        while(cur&&cur!==el&&!seen.has(cur)){
            seen.add(cur);
            ox+=cur.offsetLeft||0;
            oy+=cur.offsetTop||0;
            cur=cur.offsetParent;
            if(cur){ ox+=cur.clientLeft||0; oy+=cur.clientTop||0; }
        }
        if(cur!==el)return null;
        for(let parent=port.parentElement;parent;parent=parent.parentElement){
            ox-=parent.scrollLeft||0;
            oy-=parent.scrollTop||0;
            if(parent===el)break;
        }
        return {x:(node.x||0)+ox+port.offsetWidth/2,y:(node.y||0)+oy+port.offsetHeight/2};
    }

    const PortGeometryCache=(()=>{
        const _entries=new Map();
        const _getters={};
        const _metadata=new Map();
        const _key=(scope,nodeId)=>`${scope}:${String(nodeId||'')}`;
        const _resolveEl=(scope,nodeId)=>{
            const getter=_getters[scope];
            if(typeof getter!=='function')return null;
            return getter(nodeId)||null;
        };

        function registerNodeGetter(scope,fn){
            if(!scope)return;
            if(typeof fn==='function')_getters[scope]=fn;
            else delete _getters[scope];
        }

        function measure(node,scope,opts={}){
            if(!node)return null;
            const nodeId=node.id;
            const el=opts.el||_resolveEl(scope,nodeId);
            if(!el)return null;
            const measureOnePort=(selector)=>{
                const port=selector&&el.querySelector?el.querySelector(selector):null;
                if(!port)return null;
                const measured=measurePortCenter(node,el,port);
                if(!measured)return null;
                return {
                    centerDx:measured.x-(node.x||0),
                    centerDy:measured.y-(node.y||0)
                };
            };
            const declaredWidth=Number(el?.dataset?.renderWidth||node?._renderWidth||0);
            const declaredHeight=Number(el?.dataset?.renderHeight||node?._renderHeight||0);
            const entry={
                nodeW:declaredWidth>0?declaredWidth:(el.offsetWidth||opts.defaultWidth||300),
                nodeH:declaredHeight>0?declaredHeight:(el.offsetHeight||opts.defaultHeight||180),
                ports:{
                    out:measureOnePort('.port-out'),
                    in:measureOnePort('.port-in')
                }
            };
            const key=_key(scope,nodeId);
            invalidate(scope,nodeId);
            const elements=namedPorts(el,nodeId);
            if(elements.size){
                entry.portsById=new Map();
                elements.forEach((port,identity)=>{
                    const point=measureNamedPortCenter(node,el,port);
                    entry.portsById.set(identity,point?{
                        centerDx:point.x-(node.x||0),centerDy:point.y-(node.y||0)
                    }:null);
                });
                // Only named-port cards need these watchers. Legacy cache reads
                // remain layout-free and retain their existing public shape.
                const cleanup=[];
                const dirty=()=>invalidate(scope,nodeId);
                el.addEventListener?.('scroll',dirty,true);
                cleanup.push(()=>el.removeEventListener?.('scroll',dirty,true));
                if(typeof global.ResizeObserver==='function'){
                    const sizes=new Map();
                    const observer=new global.ResizeObserver(records=>{
                        if(records.some(record=>{
                            const size=sizes.get(record.target);
                            return size&&(size[0]!==record.target.offsetWidth||size[1]!==record.target.offsetHeight);
                        }))dirty();
                    });
                    [el,...elements.values()].forEach(target=>{
                        if(!target)return;
                        sizes.set(target,[target.offsetWidth,target.offsetHeight]);
                        observer.observe(target);
                    });
                    cleanup.push(()=>observer.disconnect());
                }
                if(typeof global.MutationObserver==='function'){
                    const observer=new global.MutationObserver(records=>{
                        if(records.some(record=>record.target!==el||record.attributeName!=='style'))dirty();
                    });
                    observer.observe(el,{childList:true,subtree:true,attributes:true,
                        attributeFilter:['data-node-id','data-port-id','data-direction','data-slot-index','class','style']});
                    cleanup.push(()=>observer.disconnect());
                }
                _metadata.set(key,{el,elements,cleanup});
            }
            _entries.set(key,entry);
            return entry;
        }

        function get(node,scope,opts={}){
            if(!node)return null;
            const key=_key(scope,node.id);
            const entry=_entries.get(key);
            const metadata=_metadata.get(key);
            if(entry){
                if(!metadata)return entry;
                const el=opts.el||_resolveEl(scope,node.id);
                if((!el||el===metadata.el)&&metadata.el.isConnected!==false)return entry;
                invalidate(scope,node.id);
            }
            return measure(node,scope,opts);
        }

        function getPort(node,scope,{direction,portId,slotIndex=null}={},opts={}){
            const identity=portKey(direction,portId,slotIndex);
            if(identity==null)return null;
            let entry=get(node,scope,opts);
            const metadata=_metadata.get(_key(scope,node?.id));
            const port=metadata?.elements.get(identity);
            // Catch synchronous port removals/identity edits before observers run.
            if(metadata&&(!metadata.elements.has(identity)||(port&&(!metadata.el.contains(port)||namedPortKey(port,node.id)!==identity)))){
                entry=measure(node,scope,opts);
            }
            return entry?.portsById?.get?.(identity)||null;
        }

        function invalidate(scope,nodeId){
            const key=_key(scope,nodeId);
            _metadata.get(key)?.cleanup.forEach(dispose=>dispose());
            _metadata.delete(key);
            _entries.delete(key);
        }

        function clear(scope){
            const prefix=`${scope}:`;
            Array.from(_entries.keys()).forEach(key=>{
                if(!scope||String(key).startsWith(prefix)){
                    _metadata.get(key)?.cleanup.forEach(dispose=>dispose());
                    _metadata.delete(key);
                    _entries.delete(key);
                }
            });
        }

        return {registerNodeGetter,measure,get,getPort,invalidate,clear,_entries};
    })();

    function measureNodeAnchors(nodes,idPrefix='',opts={}){
        const out=new Map();
        (nodes||[]).forEach(node=>{
            const id=String(node?.id||'');
            if(!id)return;
            const el=document.getElementById(idPrefix+id);
            const declaredWidth=Number(el?.dataset?.renderWidth||node?._renderWidth||0);
            const declaredHeight=Number(el?.dataset?.renderHeight||node?._renderHeight||0);
            const width=declaredWidth>0?declaredWidth:(el?(el.offsetWidth||opts.defaultWidth||300):(opts.defaultWidth||300));
            const height=declaredHeight>0?declaredHeight:(el?(el.offsetHeight||opts.defaultHeight||180):(opts.defaultHeight||180));
            const portOut=el?.querySelector?.('.port-out')||null;
            const portIn=el?.querySelector?.('.port-in')||null;
            const anchor={
                width,
                height,
                out:portOut?measurePortCenter(node,el,portOut):null,
                in:portIn?measurePortCenter(node,el,portIn):null,
            };
            const elements=namedPorts(el,id);
            if(elements.size){
                anchor.portsById=new Map();
                elements.forEach((port,key)=>anchor.portsById.set(key,measureNamedPortCenter(node,el,port)));
            }
            out.set(id,anchor);
        });
        return out;
    }

    function exactEdgeGeometry(fromNode,toNode,fromEl,toEl,opts){
        const edge=opts.edge;
        if(!fromNode||!toNode||String(edge.from)!==String(fromNode.id)||String(edge.to)!==String(toNode.id)
            ||typeof edge.fromPort!=='string'||!edge.fromPort||typeof edge.toPort!=='string'||!edge.toPort)return null;
        const point=(node,el,direction,portId,slotIndex)=>{
            const identity=portKey(direction,portId,slotIndex);
            if(identity==null)return null;
            const cached=opts.cacheScope&&opts.portCache?.getPort?.(node,opts.cacheScope,
                {direction,portId,slotIndex},{el});
            if(cached)return {x:(node.x||0)+cached.centerDx,y:(node.y||0)+cached.centerDy};
            if(el)return measureNamedPortCenter(node,el,namedPorts(el,node.id).get(identity));
            // Premeasured anchors are safe only when indexed by the exact port.
            return opts.anchorById?.get?.(String(node.id))?.portsById?.get?.(identity)||null;
        };
        const from=point(fromNode,fromEl,'output',edge.fromPort,null);
        const to=point(toNode,toEl,'input',edge.toPort,edge.slotIndex);
        if(!from||!to)return null;
        const x1=from.x,y1=from.y,x2=to.x,y2=to.y;
        const tension=Math.max(opts.minTension||40,Math.min(Math.abs(y2-y1)*0.5,opts.maxTension||120));
        return {x1,y1,x2,y2,tension,path:`M${x1},${y1} C${x1+tension},${y1} ${x2-tension},${y2} ${x2},${y2}`};
    }

    function edgeGeometry(fromNode, toNode, fromEl, toEl, opts={}){
        if(opts.edge&&(Object.prototype.hasOwnProperty.call(opts.edge,'fromPort')
            ||Object.prototype.hasOwnProperty.call(opts.edge,'toPort'))){
            return exactEdgeGeometry(fromNode,toNode,fromEl,toEl,opts);
        }
        let x1,y1,x2,y2;
        const portCache=opts.portCache||null;
        const cacheScope=opts.cacheScope;
        if(portCache&&cacheScope){
            const fromEntry=portCache.get?.(fromNode,cacheScope,{
                el:fromEl,
                defaultWidth:opts.defaultFromWidth,
                defaultHeight:opts.defaultFromHeight
            });
            const toEntry=portCache.get?.(toNode,cacheScope,{
                el:toEl,
                defaultWidth:opts.defaultToWidth,
                defaultHeight:opts.defaultToHeight
            });
            const cachedOut=fromEntry?.ports?.out;
            const cachedIn=toEntry?.ports?.in;
            if(cachedOut&&cachedOut.centerDx!=null&&cachedOut.centerDy!=null){
                x1=(fromNode.x||0)+cachedOut.centerDx;
                y1=(fromNode.y||0)+cachedOut.centerDy;
            }
            if(cachedIn&&cachedIn.centerDx!=null&&cachedIn.centerDy!=null){
                x2=(toNode.x||0)+cachedIn.centerDx;
                y2=(toNode.y||0)+cachedIn.centerDy;
            }
        }
        const anchorById=opts.anchorById||null;
        const fromAnchor=anchorById?.get?.(String(fromNode?.id||''));
        const toAnchor=anchorById?.get?.(String(toNode?.id||''));
        let fromW,fromH,toH,portOut,portIn;
        const getFromW=()=>{
            if(fromW==null)fromW=fromAnchor?.width ?? (fromEl?fromEl.offsetWidth:(opts.defaultFromWidth||280));
            return fromW;
        };
        const getFromH=()=>{
            if(fromH==null)fromH=fromAnchor?.height ?? (fromEl?fromEl.offsetHeight:(opts.defaultFromHeight||140));
            return fromH;
        };
        const getToH=()=>{
            if(toH==null)toH=toAnchor?.height ?? (toEl?toEl.offsetHeight:(opts.defaultToHeight||140));
            return toH;
        };
        const getPortOut=()=>{
            if(portOut===undefined)portOut=fromEl&&fromEl.querySelector?fromEl.querySelector('.port-out'):null;
            return portOut;
        };
        const getPortIn=()=>{
            if(portIn===undefined)portIn=toEl&&toEl.querySelector?toEl.querySelector('.port-in'):null;
            return portIn;
        };
        const outInset=opts.outInset==null?39:opts.outInset;
        const inInset=opts.inInset==null?39:opts.inInset;
        const yInset=opts.yInset==null?25:opts.yInset;
        if(x1==null||y1==null){
            if(fromAnchor?.out){
                x1=fromAnchor.out.x;
                y1=fromAnchor.out.y;
            }else if(getPortOut()&&fromEl){
                const p=measurePortCenter(fromNode,fromEl,getPortOut());
                if(p){
                    x1=p.x;
                    y1=p.y;
                }else{
                    x1=(fromNode.x||0)+getFromW()-outInset;
                    y1=(fromNode.y||0)+getFromH()-yInset;
                }
            }else{
                x1=(fromNode.x||0)+getFromW()-outInset;
                y1=(fromNode.y||0)+getFromH()-yInset;
            }
        }
        if(x2==null||y2==null){
            if(toAnchor?.in){
                x2=toAnchor.in.x;
                y2=toAnchor.in.y;
            }else if(getPortIn()&&toEl){
                const p=measurePortCenter(toNode,toEl,getPortIn());
                if(p){
                    x2=p.x;
                    y2=p.y;
                }else{
                    x2=(toNode.x||0)+inInset;
                    y2=(toNode.y||0)+getToH()-yInset;
                }
            }else{
                x2=(toNode.x||0)+inInset;
                y2=(toNode.y||0)+getToH()-yInset;
            }
        }
        const tension=Math.max(
            opts.minTension||40,
            Math.min(Math.abs(y2-y1)*0.5, opts.maxTension||120)
        );
        return {
            x1,y1,x2,y2,tension,
            path:`M${x1},${y1} C${x1+tension},${y1} ${x2-tension},${y2} ${x2},${y2}`
        };
    }

    function edgePath(fromNode, toNode, fromEl, toEl, opts={}){
        return edgeGeometry(fromNode,toNode,fromEl,toEl,opts)?.path||'';
    }

    function edgePortLeads(fromNode, toNode, fromEl, toEl, opts={}){
        const geom=edgeGeometry(fromNode,toNode,fromEl,toEl,opts);
        if(!geom)return {out:'',in:'',start:null,end:null};
        const requested=opts.leadLength==null?44:Number(opts.leadLength);
        const leadLength=Number.isFinite(requested)&&requested>0?requested:44;
        const outLen=Math.min(leadLength,Math.max(16,geom.tension));
        const inLen=Math.min(leadLength,Math.max(16,geom.tension));
        return {
            out:`M${geom.x1},${geom.y1} C${geom.x1+outLen*0.45},${geom.y1} ${geom.x1+outLen*0.75},${geom.y1} ${geom.x1+outLen},${geom.y1}`,
            in:`M${geom.x2-inLen},${geom.y2} C${geom.x2-inLen*0.75},${geom.y2} ${geom.x2-inLen*0.45},${geom.y2} ${geom.x2},${geom.y2}`,
            start:{x:geom.x1,y:geom.y1},
            end:{x:geom.x2,y:geom.y2}
        };
    }

    function layoutDAG(nodes, edges, opts={}){
        const list=Array.isArray(nodes)?nodes:[];
        const ids=new Set(list.map(n=>n&&n.id).filter(Boolean));
        const incoming={};
        list.forEach(n=>{incoming[n.id]=[]});
        (edges||[]).forEach(e=>{
            if(ids.has(e.from)&&ids.has(e.to))incoming[e.to].push(e.from);
        });

        const order=topoSort(list,edges);
        const orderIndex={};
        order.forEach((id,i)=>{orderIndex[id]=i});
        const depth={};
        order.forEach(id=>{
            const parents=(incoming[id]||[]).filter(pid=>depth[pid]!=null);
            depth[id]=parents.length?Math.max(...parents.map(pid=>depth[pid]))+1:0;
        });
        list.forEach(n=>{if(depth[n.id]==null)depth[n.id]=0});

        const columns={};
        list.forEach(n=>{
            const d=depth[n.id]||0;
            (columns[d]||(columns[d]=[])).push(n);
        });

        const startX=opts.startX==null?40:opts.startX;
        const startY=opts.startY==null?40:opts.startY;
        const nodeWidth=opts.nodeWidth||300;
        const nodeHeight=opts.nodeHeight||180;
        const minNodeHeight=opts.minNodeHeight||nodeHeight;
        const columnGap=opts.columnGap||100;
        const rowGap=opts.rowGap||60;
        const measure=typeof opts.measureNode==='function'?opts.measureNode:null;
        let maxRight=startX;
        let maxBottom=startY;

        Object.keys(columns).map(Number).sort((a,b)=>a-b).forEach(d=>{
            const x=startX+d*(nodeWidth+columnGap);
            let y=startY;
            columns[d].sort((a,b)=>{
                const ay=Number(a.y||0), by=Number(b.y||0);
                if(ay!==by)return ay-by;
                return (orderIndex[a.id]||0)-(orderIndex[b.id]||0);
            }).forEach(node=>{
                let measured=0;
                if(measure){
                    try{measured=Number(measure(node.id,node)||0)}catch(_e){measured=0}
                }
                const h=Math.max(minNodeHeight,measured||nodeHeight);
                node.x=Math.round(x);
                node.y=Math.round(y);
                y+=h+rowGap;
                maxRight=Math.max(maxRight,node.x+nodeWidth);
                maxBottom=Math.max(maxBottom,node.y+h);
            });
        });

        return {
            width:maxRight-startX,
            height:maxBottom-startY,
            columns:Object.keys(columns).length
        };
    }

    global.GraphUtils={
        topoSort,
        edgeGeometry,
        edgePath,
        edgePortLeads,
        layoutDAG,
        isSemanticallyEmpty,
        extractTemplateRefs,
        parseRulesOf,
        resolveVariableSource,
        measureNodeAnchors,
        PortGeometryCache,
        TEMPLATE_REF_RE
    };
})(window);

/* web/js/task-graph-actions.js */
(function(global){
    function acceptTypesOf(param){
        if(Array.isArray(param?.accept_types))return param.accept_types.filter(Boolean);
        return param?.accept_type?[param.accept_type]:[];
    }

    function outputTypeOf(node,meta={}){
        return String(node?.output_type||node?._output_type||meta.output_type||meta._output_type||'text');
    }

    function parseListValue(value){
        if(Array.isArray(value))return value;
        if(typeof value==='string'){
            const text=value.trim();
            if(!text)return [];
            if(text.startsWith('[')&&text.endsWith(']')){
                try{
                    const parsed=JSON.parse(text);
                    return Array.isArray(parsed)?parsed:[value];
                }catch(_error){}
            }
            return [value];
        }
        return [];
    }

    function isEmptyListValue(value){
        if(global.GraphUtils?.isSemanticallyEmpty)return global.GraphUtils.isSemanticallyEmpty(value);
        return parseListValue(value).every(item=>!String(item||'').trim());
    }

    function isBlankParamValue(value){
        if(value===undefined||value===null)return true;
        if(typeof value==='string')return !value.trim();
        if(Array.isArray(value))return value.length===0;
        if(value&&typeof value==='object')return Object.keys(value).length===0;
        return false;
    }

    function resultPieces(node){
        const result=node&&node._result?node._result:{};
        const pieces=Array.isArray(result.pieces)?result.pieces.filter(Boolean):[];
        if(!pieces.length)return pieces;
        const isClientOutput=node?.type==='client_output'||result.client_output||result.step_type==='client_output';
        const primary=isClientOutput?String(result.local_path||result.content||'').trim():'';
        return primary&&!pieces.includes(primary)?[primary,...pieces]:pieces;
    }

    function hasResultPieces(node){
        const result=node&&node._result?node._result:{};
        const pieces=resultPieces(node);
        if(pieces.length)return true;
        const saveAs=(node&&node.save_as)||'';
        const splitVars=(result&&result.split_vars)||{};
        if(!splitVars||typeof splitVars!=='object')return false;
        if(saveAs&&parseInt(splitVars[`${saveAs}_count`]||0)>0)return true;
        if(!saveAs)return Object.keys(splitVars).some(key=>/_\d+$/.test(key));
        const escaped=saveAs.replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
        return Object.keys(splitVars).some(key=>new RegExp(`^${escaped}_\\d+$`).test(key));
    }

    function canHaveMultipleOutputs(node,meta={}){
        const type=node?.type||'';
        const expected=Number(node?._expected_outputs||node?.expected_outputs||0);
        const splitCount=Number(node?.split_count||0);
        const count=Number(node?.count||0);
        const parts=Number(node?.parts||0);
        return (
            type==='split' ||
            type.includes('editor_split') ||
            type.includes('audio_split') ||
            type.includes('scene_split') ||
            type.includes('split_video') ||
            type.includes('url_downloader:') ||
            type.includes('grok-imagine/image-to-image') ||
            node?._multi_output===true ||
            meta.multi_output===true ||
            expected>0 ||
            splitCount>1 ||
            count>1 ||
            parts>1 ||
            hasResultPieces(node)
        );
    }

    function isMultiOutputNode(node,meta={}){
        const type=node?.type||'';
        const expected=Number(node?._expected_outputs||node?.expected_outputs||0);
        const splitCount=Number(node?.split_count||0);
        const count=Number(node?.count||0);
        const parts=Number(node?.parts||0);
        if(hasResultPieces(node))return true;
        if(type.includes('url_downloader:')){
            return expected>1||splitCount>1||count>1||parts>1;
        }
        return canHaveMultipleOutputs(node,meta);
    }

    function multiOutputCount(node,meta={}){
        const type=node?.type||'';
        const saveAs=node?.save_as||'result';
        const result=node?._result||{};
        const paramDefault=(key)=>{
            const param=(meta.params||[]).find(candidate=>candidate.key===key);
            return param&&param.default!==undefined?param.default:undefined;
        };
        const intVal=(...values)=>{
            for(const value of values){
                if(value===undefined||value===null||value==='')continue;
                const parsed=parseInt(value);
                if(parsed>0)return parsed;
            }
            return 0;
        };
        const pieces=resultPieces(node);
        if(pieces.length)return pieces.length;
        const splitVars=(result&&result.split_vars)||{};
        const savedCount=parseInt(splitVars[`${saveAs}_count`]||0);
        if(savedCount>0)return savedCount;
        const escaped=saveAs.replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
        const matching=Object.keys(splitVars).filter(key=>new RegExp(`^${escaped}_\\d+$`).test(key)).length;
        if(matching>0)return matching;
        const expected=intVal(node?._expected_outputs,node?.expected_outputs);
        if(expected>0)return expected;
        if(type==='split'){
            const mode=node?.split_mode||'auto';
            if(mode==='grid'){
                const rows=intVal(node?.rows,paramDefault('rows'),2);
                const cols=intVal(node?.cols,paramDefault('cols'),2);
                return Math.max(1,(rows||2)*(cols||2));
            }
            if(mode==='count')return Math.max(1,intVal(node?.split_count,paramDefault('split_count'),2)||2);
            return 4;
        }
        if(type.includes('editor_split')){
            const rows=intVal(node?.rows,paramDefault('rows'),2);
            const cols=intVal(node?.cols,paramDefault('cols'),2);
            return Math.max(1,(rows||2)*(cols||2));
        }
        if(type.includes('grok-imagine/image-to-image')){
            const refs=node?.image_urls;
            const has=Array.isArray(refs)?refs.some(value=>String(value||'').trim()):Boolean(String(refs||'').trim());
            return has?2:6;
        }
        if(type.includes('audio_split')||type.includes('split_video')){
            return Math.max(1,intVal(node?.parts,node?.count,node?.split_count,paramDefault('parts'),paramDefault('count'),paramDefault('split_count'),3)||3);
        }
        if(type.includes('scene_split')){
            return Math.max(1,intVal(node?.count,node?.parts,node?.split_count,paramDefault('count'),paramDefault('parts'),3)||3);
        }
        if(meta.multi_output===true||node?._multi_output===true){
            const rows=intVal(node?.rows,paramDefault('rows'));
            const cols=intVal(node?.cols,paramDefault('cols'));
            if(rows>0&&cols>0)return Math.max(1,rows*cols);
            return Math.max(1,intVal(node?.parts,node?.count,node?.split_count,paramDefault('parts'),paramDefault('count'),paramDefault('split_count'),node?._preview?.images?.length,3)||3);
        }
        return Math.max(1,intVal(node?._preview?.images?.length,3)||3);
    }

    function multiOutputVars(node,meta={}){
        const type=node?.type||'';
        const saveAs=node?.save_as||'result';
        if(!saveAs||!isMultiOutputNode(node,meta))return [];
        const outType=meta.output_type||meta._output_type||'image';
        const typeIcons={text:'text',image:'image',video:'video',audio:'audio'};
        const outIcon=typeIcons[outType]||'batch';
        const outLabel=node?.label||meta.name||type||saveAs;
        const shownCount=Math.max(1,Math.min(multiOutputCount(node,meta),50));
        const vars=[];
        for(let index=1;index<=shownCount;index++){
            vars.push({name:`${saveAs}_${index}`,type:outType,icon:outIcon,label:`${outLabel} #${index}`});
        }
        vars.push({name:`${saveAs}_count`,type:'text',icon:'clipboard',label:`${outLabel} кол-во`});
        return vars;
    }

    const PARAM_WRAPPER_RESERVED=new Set([
        'id','type','save_as','x','y','z_index','selected','color','icon','label',
        '_disabled','expected_version','version','ui_meta','comments','annotations',
        'module_id','model_id','_node_id','_status','_outputIcon','_price',
        '_wb_edges','_wb_positions','_expected_outputs','_multi_output',
        '_generatedFrom','_generatedIdx','_dynamic','_children_expanded','params'
    ]);

    function flattenNestedParams(step){
        const raw=step&&step.params;
        if(!raw||typeof raw!=='object'||Array.isArray(raw))return;
        Object.keys(raw).forEach(key=>{
            if(PARAM_WRAPPER_RESERVED.has(key))return;
            const nested=raw[key];
            if(
                isBlankParamValue(nested)
                &&Object.prototype.hasOwnProperty.call(step,key)
                &&!isBlankParamValue(step[key])
            )return;
            step[key]=nested;
        });
        delete step.params;
    }

    function lowerText(value){return String(value||'').toLowerCase()}
    function paramText(param){return lowerText(`${param?.key||''} ${param?.label||''}`)}
    function sourceText(node){
        return lowerText(`${node?.save_as||''} ${node?.label||''} ${node?.type||''} ${node?._source_var||''}`);
    }
    function isMusicParam(param){return /music|музык|песн|трек/.test(paramText(param))}
    function isVoiceParam(param){return /voice|озвуч|реч|голос|speech|tts/.test(paramText(param))}
    function isMusicSource(node){return /music|suno|song|track|музык|песн|трек/.test(sourceText(node))}
    function completedResultSource(node){
        return node?.type==='result_node'&&String(node.file_url||'').trim();
    }

    function graphIndexes(nodes,edges){
        const nodeById=new Map(nodes.map(node=>[String(node.id),node]));
        const incomingEdgesByTo=new Map();
        edges.forEach(edge=>{
            const key=String(edge.to);
            if(!incomingEdgesByTo.has(key))incomingEdgesByTo.set(key,[]);
            incomingEdgesByTo.get(key).push(edge);
        });
        return {nodeById,incomingEdgesByTo};
    }

    function inferConnectedVariableListDefaults(step,node,nodes,types,indexes){
        const params=Array.isArray(types[node.type]?.params)?types[node.type].params:[];
        const incoming=indexes.incomingEdgesByTo.get(String(node.id))||[];
        if(!incoming.length)return;
        const listParams=params.filter(param=>param?.type==='variable_list'&&param.key);
        const hasSiblingForAcceptedType=(param,acceptTypes)=>listParams.some(other=>{
            if(other===param)return false;
            const otherAccept=acceptTypesOf(other);
            return acceptTypes.some(kind=>otherAccept.includes(kind));
        });

        listParams.forEach(param=>{
            if(!isEmptyListValue(step[param.key]))return;
            const acceptTypes=acceptTypesOf(param);
            if(!acceptTypes.length)return;
            if(param.required!==true&&hasSiblingForAcceptedType(param,acceptTypes))return;
            const refs=[];
            const seen=new Set();
            incoming.forEach(edge=>{
                const source=indexes.nodeById.get(String(edge.from));
                const saveAs=String(source?.save_as||'').trim();
                if(!source||!saveAs)return;
                const sourceType=outputTypeOf(source,types[source.type]||{});
                if(!acceptTypes.includes(sourceType))return;
                const ref=`{{${saveAs}}}`;
                if(seen.has(ref))return;
                seen.add(ref);
                refs.push(ref);
            });
            if(refs.length)step[param.key]=refs;
        });
    }

    function inferConnectedVariableDefaults(step,node,nodes,types,indexes){
        const params=Array.isArray(types[node.type]?.params)?types[node.type].params:[];
        const incoming=indexes.incomingEdgesByTo.get(String(node.id))||[];
        if(!incoming.length)return;
        const sources=incoming
            .map(edge=>indexes.nodeById.get(String(edge.from)))
            .filter(source=>source&&String(source.save_as||'').trim()&&completedResultSource(source));
        if(!sources.length)return;
        const claimed=new Set();
        params.filter(param=>param?.type==='variable'&&param.key).forEach(param=>{
            if(String(step[param.key]||'').trim())return;
            const acceptTypes=acceptTypesOf(param);
            if(!acceptTypes.length)return;
            const connected=sources.filter(source=>{
                return acceptTypes.includes(outputTypeOf(source,types[source.type]||{}));
            });
            if(!connected.length)return;
            let chosen=null;
            if(isMusicParam(param)){
                const musicMatches=connected.filter(isMusicSource);
                if(musicMatches.length===1)chosen=musicMatches[0];
            }else if(isVoiceParam(param)){
                const voiceMatches=connected.filter(source=>!isMusicSource(source));
                if(voiceMatches.length===1)chosen=voiceMatches[0];
            }else if(connected.length===1){
                chosen=connected[0];
            }
            if(!chosen)return;
            const ref=`{{${String(chosen.save_as).trim()}}}`;
            step[param.key]=ref;
            claimed.add(ref);
        });
        const listParams=params.filter(param=>param?.type==='variable_list'&&param.key);
        const hasSiblingForAcceptedType=(param,acceptTypes)=>listParams.some(other=>{
            if(other===param)return false;
            const otherAccept=acceptTypesOf(other);
            return acceptTypes.some(kind=>otherAccept.includes(kind));
        });
        listParams.forEach(param=>{
            if(!isEmptyListValue(step[param.key]))return;
            const acceptTypes=acceptTypesOf(param);
            if(!acceptTypes.length)return;
            if(param.required!==true&&hasSiblingForAcceptedType(param,acceptTypes))return;
            let connected=sources.filter(source=>{
                const ref=`{{${String(source.save_as).trim()}}}`;
                return !claimed.has(ref)&&acceptTypes.includes(outputTypeOf(source,types[source.type]||{}));
            });
            if(isVoiceParam(param))connected=connected.filter(source=>!isMusicSource(source));
            else if(isMusicParam(param))connected=connected.filter(isMusicSource);
            const refs=[];
            const seen=new Set();
            connected.forEach(source=>{
                const ref=`{{${String(source.save_as).trim()}}}`;
                if(seen.has(ref))return;
                seen.add(ref);
                refs.push(ref);
            });
            if(refs.length)step[param.key]=refs;
        });
    }

    function normalizeEdges(nodes,edges,options={}){
        nodes=(nodes||[]).filter(node=>!node._disabled);
        const enabledIds=new Set(nodes.map(node=>String(node.id)));
        const normalized=[];
        const seen=new Set();
        const outgoingByFrom=new Map();
        const wouldCreateCycle=(from,to)=>{
            const stack=[to];
            const visited=new Set();
            while(stack.length){
                const current=stack.pop();
                if(current===from)return true;
                if(visited.has(current))continue;
                visited.add(current);
                (outgoingByFrom.get(current)||[]).forEach(next=>stack.push(next));
            }
            return false;
        };
        const addEdge=(from,to)=>{
            from=String(from||'').trim();
            to=String(to||'').trim();
            if(!from||!to||from===to||!enabledIds.has(from)||!enabledIds.has(to))return;
            const key=`${from}->${to}`;
            if(seen.has(key)||wouldCreateCycle(from,to))return;
            seen.add(key);
            if(!outgoingByFrom.has(from))outgoingByFrom.set(from,[]);
            outgoingByFrom.get(from).push(to);
            normalized.push({from,to});
        };
        (edges||[]).forEach(edge=>addEdge(edge.from,edge.to));
        nodes.forEach(node=>{
            if(node.type==='result_node')return;
            global.GraphUtils.extractTemplateRefs(node).forEach(ref=>{
                const resolved=global.GraphUtils.resolveVariableSource(ref,nodes,{
                    types:options.types||{},
                    isMultiOutputNode:options.isMultiOutputNode||isMultiOutputNode,
                    multiOutputVars:options.multiOutputVars||multiOutputVars
                });
                if(resolved?.id)addEdge(resolved.id,node.id);
            });
        });
        return normalized;
    }

    function nodesToSteps(nodes,edges,options={}){
        nodes=(nodes||[]).filter(node=>!node._disabled);
        if(!nodes.length)return [];
        const enabledIds=new Set(nodes.map(node=>String(node.id)));
        edges=(edges||[]).filter(edge=>{
            return enabledIds.has(String(edge.from))&&enabledIds.has(String(edge.to));
        });
        edges=typeof options.edgesForSave==='function'
            ?options.edgesForSave(nodes,edges)
            :normalizeEdges(nodes,edges,options);
        const indexes=graphIndexes(nodes,edges);
        const types=options.types||{};
        const sorted=global.GraphUtils.topoSort(nodes,edges);
        const generatedChildren={};
        nodes.forEach(node=>{
            if(!node._generatedFrom)return;
            if(!generatedChildren[node._generatedFrom])generatedChildren[node._generatedFrom]=[];
            generatedChildren[node._generatedFrom].push(node);
        });
        Object.values(generatedChildren).forEach(children=>{
            children.sort((left,right)=>(left._generatedIdx||0)-(right._generatedIdx||0));
        });
        const childNodeIds=new Set(nodes.filter(node=>node._generatedFrom).map(node=>node.id));

        return sorted.map(id=>{
            const node=indexes.nodeById.get(String(id));
            if(!node||childNodeIds.has(id))return null;
            if(node.type==='result_node'&&!String(node.file_url||'').trim())return null;
            const step={...node,_node_id:node.id};
            flattenNestedParams(step);
            inferConnectedVariableDefaults(step,node,nodes,types,indexes);
            inferConnectedVariableListDefaults(step,node,nodes,types,indexes);

            if(node.type==='generator'&&generatedChildren[node.id]?.length){
                const blueprint=generatedChildren[node.id].map(child=>{
                    const output={};
                    Object.keys(child).forEach(key=>{
                        if(![
                            'id','x','y','icon','color','label','_status','_generatedFrom',
                            '_generatedIdx','_dynamic','_outputIcon','_price','_preview','_previewLoading'
                        ].includes(key))output[key]=child[key];
                    });
                    return global.ExternalStepPayload ? global.ExternalStepPayload.clean(output) : output;
                });
                step.blueprint=JSON.stringify(blueprint);
                step._children_expanded=true;
            }

            [
                'x','y','icon','color','label','_status','_outputIcon','_price','_preview',
                '_previewLoading','_clientOutputItemsInferred','_clientOutputValidationError','id'
            ].forEach(key=>delete step[key]);
            return global.ExternalStepPayload ? global.ExternalStepPayload.clean(step) : step;
        }).filter(Boolean);
    }

    async function validateStepsForRun(steps){
        if(!steps?.length)return {ok:false,message:'\u0417\u0430\u0434\u0430\u0447\u0430 \u043f\u0443\u0441\u0442\u0430'};
        const types=await global.getStepTypes();
        const missing=[];
        steps.forEach((step,index)=>{
            const meta=types[step.type]||{};
            (meta.params||[]).forEach(param=>{
                if(!param.required)return;
                const keys=[param.key,...(Array.isArray(param.aliases)?param.aliases:[])];
                // Mirror the server order: first non-empty (after trim) value wins,
                // so a whitespace-only "text" cannot mask a real "prompt" alias.
                const chosen=keys.map(key=>step[key]).find(value=>{
                    if(value===undefined||value===null)return false;
                    if(typeof value==='string')return value.trim()!=='';
                    if(Array.isArray(value))return value.length>0;
                    return Boolean(value);
                });
                if(chosen===undefined||global.GraphUtils.isSemanticallyEmpty(chosen)){
                    missing.push(`\u0448\u0430\u0433 ${index+1}: ${param.label||param.key}`);
                }
            });
        });
        if(missing.length){
            const shown=missing.slice(0,3).join(', ');
            const tail=missing.length>3?` \u0438 \u0435\u0449\u0451 ${missing.length-3}`:'';
            return {ok:false,message:`\u0417\u0430\u043f\u043e\u043b\u043d\u0438\u0442\u0435 \u043e\u0431\u044f\u0437\u0430\u0442\u0435\u043b\u044c\u043d\u044b\u0435 \u043f\u043e\u043b\u044f: ${shown}${tail}`};
        }
        return {ok:true};
    }

    async function ensureStepsCanRun(steps){
        const result=await validateStepsForRun(steps);
        if(result.ok)return true;
        const toast=global.Toast||(typeof Toast!=='undefined'?Toast:null);
        if(toast&&typeof toast.warning==='function')toast.warning(result.message);
        else if(typeof console!=='undefined')console.warn('[run-blocked]',result.message);
        return false;
    }

    global.TaskGraphActions={
        hasResultPieces,
        canHaveMultipleOutputs,
        isMultiOutputNode,
        multiOutputCount,
        multiOutputVars,
        normalizeEdges,
        nodesToSteps,
        validateStepsForRun,
        ensureStepsCanRun
    };
})(window);

/* web/js/workboard/v2-session-data.js */
/* Persisted graph and local recovery helpers; never persist job/runtime as graph. */
(function(global){
    const RUNTIME_FIELDS=new Set(['_status','_partial','_result','_execStatus','_step','_stepIdx','_variants','_variantCurrent','_injected','_isRunning','_activeJobId','_pendingDelete']);
    const clone=value=>value==null?value:JSON.parse(JSON.stringify(value));
    function enabled(){return global.AppConfig?.environment==='new'&&global.AppConfig.publicBasePath==='/new'&&global.AppConfig.workboardV2Enabled===true;}
    function isV2(owner){return enabled()&&owner?.graph_schema_version===2;}
    function persistedNode(node){
        const out=global.WorkboardGraphHash?.persistedNode?global.WorkboardGraphHash.persistedNode(node):(clone(node)||{});
        RUNTIME_FIELDS.forEach(key=>delete out[key]);
        return out;
    }
    function graph(value={}){
        return {graph_schema_version:2,nodes:(value.nodes||[]).map(persistedNode),edges:clone(value.edges||[])};
    }
    function snapshot(job,provided){
        let value=provided||job?.workboard_snapshot||job?.workboard_snapshot_json;
        if(typeof value==='string'){try{value=JSON.parse(value);}catch(_){return null;}}
        const board=value?.editable_graph||value?.graph||value;
        return board?.graph_schema_version===2?graph(board):null;
    }
    function userId(){return String(global.Auth?.user?.id||'');}
    function key(kind,id){const value=`workboard:v2:${userId()}:${kind}:${id}`;return global.AppURL?.stateKey?.(value)||value;}
    function readLocal(storageKey){try{return JSON.parse(global.localStorage?.getItem(storageKey)||'null');}catch(_){return null;}}
    function writeLocal(storageKey,value){try{global.localStorage?.setItem(storageKey,JSON.stringify(value));return true;}catch(_){return false;}}
    function removeLocal(storageKey){try{global.localStorage?.removeItem(storageKey);}catch(_){}}
    function requestId(){return global.crypto?.randomUUID?.()||`wb-${Date.now()}-${Math.random().toString(36).slice(2)}`;}
    function failed(response){return !response||!!response.error||response.ok===false;}
    function report(response,fallback){
        if(response?.http_status!==402)global.Toast?.error?.(response?.error||fallback);
    }
    global.WorkboardV2SessionData={clone,enabled,isV2,persistedNode,graph,snapshot,userId,key,readLocal,writeLocal,removeLocal,requestId,failed,report};
    // Core quick/pinned runs must identify v2 before the canvas chunk is loaded.
    global.WorkboardV2Session={...(global.WorkboardV2Session||{}),isV2,enabled};
})(window);

/* web/js/projects-batch.js */
/* ProjectView batch dialog: drag/drop, batch dialog, file scan, batch run, upload. */

window.ProjectViewBatch={
    _onDragStart(e,id){
        this._dragId=id;
        e.dataTransfer.effectAllowed='move';
        e.target.closest('.task-tile').style.opacity='0.4';
    },
    _onDragOver(e,id){
        e.preventDefault();
        e.dataTransfer.dropEffect='move';
        if(this._dragOverId!==id){
            document.querySelectorAll('.task-tile').forEach(el=>el.classList.remove('drag-over'));
            e.target.closest('.task-tile')?.classList.add('drag-over');
            this._dragOverId=id;
        }
    },
    async _onDrop(e,targetId){
        e.preventDefault();
        if(!this._dragId||this._dragId===targetId)return;
        // Reorder in project.task_ids
        const previousOrder=[...(this.project?.task_ids||this.tasks.map(t=>t.id))];
        let order=[...previousOrder];
        order=order.filter(id=>id!==this._dragId);
        const idx=order.indexOf(targetId);
        order.splice(idx,0,this._dragId);
        this.project.task_ids=order;
        const resp=await API.post('/api/projects',this.project);
        if(!resp||resp.error||resp.ok===false){
            this.project.task_ids=previousOrder;
            Toast.error(resp?.error||'Не удалось сохранить порядок задач');
            return;
        }
        invalidateProjects();
        this._renderTasks();
    },
    _onDragEnd(e){
        this._dragId=null;
        this._dragOverId=null;
        document.querySelectorAll('.task-tile').forEach(el=>{
            el.style.opacity='';
            el.classList.remove('drag-over');
        });
    },

    /* ── Batch file drop on task tile ── */
    _batchDragCounter:{},

    _onBatchDragOver(e, taskId){
        // Only react to file drags, not internal drags
        if(!window.DragDrop?.hasFiles(e.dataTransfer))return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect='copy';
        const overlay=document.getElementById('batch-overlay-'+taskId);
        if(overlay) overlay.classList.add('active');
    },

    _onBatchDragLeave(e, taskId){
        const tile=e.target instanceof Element ? e.target.closest('.task-tile') : null;
        // Only hide if actually leaving the tile
        if(tile&&tile.contains(e.relatedTarget))return;
        const overlay=document.getElementById('batch-overlay-'+taskId);
        if(overlay) overlay.classList.remove('active');
    },

    _batchDropZoneDragOver(e,zone){
        e.preventDefault();
        zone.style.borderColor='var(--accent)';
        zone.style.background='var(--accent-light)';
    },

    _batchDropZoneDragLeave(_e,zone){
        zone.style.borderColor='var(--border)';
        zone.style.background='transparent';
    },

    _batchDropZoneDrop(e,zone){
        e.preventDefault();
        zone.style.borderColor='var(--border)';
        zone.style.background='transparent';
        this._batchDialogDrop(e,zone.dataset.key||'');
    },

    _appendFiles(target, additions){
        for(const file of additions||[]){
            if(!file)continue;
            target.push(file);
        }
    },

    _arrayLikeToArray(list){
        if(!list)return [];
        const length=Number(list.length||0);
        if(length>0){
            const out=[];
            for(let i=0;i<length;i++){
                const item=list[i];
                if(item)out.push(item);
            }
            if(out.length)return out;
        }
        try{
            return Array.from(list||[]).filter(Boolean);
        }catch(_e){
            return [];
        }
    },

    async _filesFromDataTransfer(dataTransfer){
        // Safari/WebKit invalidates DataTransfer after the first await, so we
        // must extract everything we need synchronously before yielding.
        const directFiles=this._arrayLikeToArray(dataTransfer?.files);
        const items=this._arrayLikeToArray(dataTransfer?.items);
        const slots=[];
        for(const item of items){
            if(item&&item.kind&&item.kind!=='file'){slots.push(null);continue}
            const entry=typeof item?.webkitGetAsEntry==='function'?item.webkitGetAsEntry():null;
            const handlePromise=typeof item?.getAsFileSystemHandle==='function'
                ?item.getAsFileSystemHandle().catch(()=>null)
                :null;
            const fallback=typeof item?.getAsFile==='function'?item.getAsFile():null;
            slots.push({entry,handlePromise,fallback});
        }

        const itemFiles=[];
        for(const slot of slots){
            if(!slot)continue;
            const beforeEntry=itemFiles.length;
            if(slot.entry?.isDirectory){
                this._appendFiles(itemFiles,await this._readDirectoryEntry(slot.entry));
            }else if(slot.entry?.isFile){
                const file=await new Promise(resolve=>slot.entry.file(resolve,()=>resolve(null)));
                this._appendFiles(itemFiles,[file]);
            }
            if(itemFiles.length>beforeEntry)continue;
            if(slot.handlePromise){
                const handle=await slot.handlePromise;
                const handleFiles=await this._filesFromFileSystemHandle(handle);
                if(handleFiles.length){
                    this._appendFiles(itemFiles,handleFiles);
                    continue;
                }
            }
            this._appendFiles(itemFiles,[slot.fallback]);
        }

        // A real FileList reflects exactly what the user selected. Do not
        // dedupe it by metadata: different files can share name/size/mtime.
        if(directFiles.length>=itemFiles.length)return directFiles;
        return itemFiles;
    },

    async _filesFromFileSystemHandle(handle){
        if(!handle)return [];
        if(handle.kind==='file'&&typeof handle.getFile==='function'){
            const file=await handle.getFile().catch(()=>null);
            return file?[file]:[];
        }
        if(handle.kind==='directory'&&typeof handle.values==='function'){
            const files=[];
            try{
                for await(const child of handle.values()){
                    files.push(...await this._filesFromFileSystemHandle(child));
                }
            }catch(e){
                console.warn('Failed to read dropped directory handle',e);
            }
            return files;
        }
        return [];
    },

    async _onBatchDrop(e, taskId){
        e.preventDefault();
        e.stopPropagation();
        const overlay=document.getElementById('batch-overlay-'+taskId);
        if(overlay) overlay.classList.remove('active');

        const fileList=await this._filesFromDataTransfer(e.dataTransfer);
        if(!fileList.length){Toast.warning('Нет файлов');return}

        const task=await this._ensureFullTask(taskId);
        const batchNodes=(task?.nodes||[]).filter(n=>n.type==='batch_input');
        if(batchNodes.length>1){
            Toast.info('Откройте batch-меню и заполните входы по отдельности');
            this.batchDialog(taskId);
            return;
        }

        // Upload all files
        Toast.info(`Загрузка файлов: ${fileList.length}`);
        const uploadedUrls=[];
        const failedUploads=[];
        for(const file of fileList){
            if(file.name.startsWith('.'))continue;
            if(typeof canUploadThroughCurrentHost==='function' && !canUploadThroughCurrentHost(file))continue;
            try{
                const data=await API.upload(file);
                if(data.local_url) uploadedUrls.push(data.local_url);
            }catch(err){
                failedUploads.push(file.name);
                console.error('Upload error:',file.name,err);
            }
        }
        if(failedUploads.length){
            const names=failedUploads.slice(0,3).join(', ');
            Toast.warning(`Не загрузились: ${names}${failedUploads.length>3?'...':''}`);
        }

        if(!uploadedUrls.length){Toast.error('Не удалось загрузить файлы');return}

        // Execute batch run
        await this._executeBatchRun(taskId, uploadedUrls);
    },

    async _readDirectoryEntry(dirEntry){
        return new Promise(resolve=>{
            const reader=dirEntry.createReader();
            const allFiles=[];
            const readBatch=()=>{
                reader.readEntries(async entries=>{
                    if(!entries.length){resolve(allFiles);return}
                    for(const entry of entries){
                        if(entry.isFile){
                            const file=await new Promise(r=>entry.file(f=>r(f)));
                            allFiles.push(file);
                        }
                    }
                    readBatch(); // Continue reading (readEntries returns max 100)
                });
            };
            readBatch();
        });
    },

    /* ── Batch Dialog (button click) ── */
    _batchInputKey(node,idx){
        return 'batch_'+idx+'_'+String(node?.id||node?.save_as||idx).replace(/[^\w-]/g,'_');
    },

    _batchLabel(node,idx){
        return (node?.input_label||node?.label||node?.save_as||('Batch '+(idx+1))).trim();
    },

    _batchEsc(value){
        if(typeof escHtml==='function')return escHtml(String(value??''));
        return String(value??'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    },

    _batchJs(value){
        return ProjectSafe.js(value);
    },

    async batchDialog(taskId){
        const task=await this._ensureFullTask(taskId);
        if(!task)return;
        const batchNodes=(task.nodes||[]).filter(n=>n.type==='batch_input');
        if(!batchNodes.length){Toast.warning('В задаче нет ноды Batch Input');return}
        this._batchNodes=batchNodes;
        this._batchInputs={};
        this._batchTaskId=taskId;
        this._batchPreparedInputs=null;

        const cards=batchNodes.map((node,idx)=>{
            const key=this._batchInputKey(node,idx);
            const mode=node.batch_mode||'files';
            this._batchInputs[key]={
                key,
                node_id:node.id||'',
                save_as:node.save_as||'',
                label:this._batchLabel(node,idx),
                mode,
                node,
                files:[],
                serverFiles:null
            };
            return this._renderBatchInputCard(node,idx,key);
        }).join('');

        const singleText=batchNodes.length===1
            &&(batchNodes[0].batch_mode||'files')==='text';
        const intro=batchNodes.length>1
            ? '<div data-style="font-size:13px;color:var(--text-secondary);margin-bottom:14px">Заполните каждый вход. Количество элементов должно совпадать; один элемент можно переиспользовать во всех jobs.</div>'
            : `<div data-style="font-size:13px;color:var(--text-secondary);margin-bottom:14px">${singleText?'Весь текст = один job.':'Каждый элемент = отдельный job.'}</div>`;
        Modal.show(`<h2>${CGIcons.inline('batch')} Batch запуск: ${this._batchEsc(task.name||'задача')}</h2>
            ${intro}
            <div data-style="display:flex;flex-direction:column;gap:12px;max-height:62vh;overflow:auto;padding-right:4px">${cards}</div>
            <div data-style="margin-top:16px;display:flex;gap:8px">
                <button class="btn btn-primary" data-project-view-action="confirm-batch-run" data-task-id="${this._batchEsc(taskId)}"><span class="ui-code-icon" aria-hidden="true">RUN</span><span>Запустить</span></button>
                <button class="btn btn-ghost" data-action="modal-hide">Отмена</button>
            </div>
            <div class="run-cost-preview" data-batch-cost-preview hidden></div>
            <div class="run-error" data-batch-run-error role="alert" aria-live="assertive" hidden></div>`);
    },

    _batchMode:'files',
    _batchFiles:[],
    _batchTaskId:null,
    _batchNodes:[],
    _batchInputs:{},

    _renderBatchInputCard(node,idx,key){
        const mode=node.batch_mode||'files';
        const modeIcons={files:CGIcons.html('folder'),text:CGIcons.html('text'),folder:CGIcons.html('batch')};
        const label=this._batchEsc(this._batchLabel(node,idx));
        const saveAs=this._batchEsc(node.save_as||('batch_'+(idx+1)));
        const keyJs=this._batchJs(key);
        const savedPath=this._batchEsc(node.folder_path||'');
        const savedExt=this._batchEsc(node.extensions||'');
        const header=`<div data-style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:10px">
            <div data-style="min-width:0">
                <div data-style="font-weight:700;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${modeIcons[mode]||CGIcons.html('batch')} ${label}</div>
                <div data-style="font-size:11px;color:var(--text-muted);margin-top:2px">{{${saveAs}}}</div>
            </div>
            <span data-style="font-size:11px;color:var(--text-muted);white-space:nowrap">#${idx+1}</span>
        </div>`;

        let body='';
        if(mode==='text'){
            body=`<div class="form-group" data-style="margin-bottom:0">
                <label class="form-label">Текст / ссылки</label>
                <textarea class="form-textarea" id="batch-text-${key}" rows="6" placeholder="весь текст = один job" data-style="font-family:monospace;font-size:12px"></textarea>
            </div>`;
        }else if(mode==='folder'){
            body=`<div class="form-group">
                <label class="form-label">Папка на Яндекс.Диске или публичная ссылка</label>
                <div data-style="display:flex;gap:8px">
                    <input class="form-input" id="batch-folder-path-${key}" value="${savedPath}" placeholder="yandex:/videos или https://disk.yandex.ru/d/..." data-style="flex:1;min-width:0">
                    <button class="btn btn-primary btn-small" data-project-view-action="scan-folder" data-key="${this._batchEsc(key)}">Сканировать</button>
                </div>
            </div>
            <div class="form-group">
                <label class="form-label">Фильтр файлов</label>
                <input class="form-input" id="batch-ext-filter-${key}" value="${savedExt}" placeholder="mp4,mov,jpg; пусто = все" data-style="max-width:260px">
                <div data-style="font-size:11px;color:var(--text-muted);margin-top:4px">Расширения файлов из папки, без точек.</div>
            </div>
            <div id="batch-file-list-${key}" data-style="margin-top:8px;max-height:180px;overflow-y:auto"></div>`;
        }else{
            body=`<div id="batch-drop-zone-${key}" data-batch-drop-zone data-project-view-action="batch-drop-zone" data-key="${this._batchEsc(key)}" data-style="border:2px dashed var(--border);border-radius:var(--omb-r-md);padding:22px;text-align:center;cursor:pointer;transition:all 0.15s;margin-bottom:12px">
                <div data-style="font-size:28px;opacity:0.55;margin-bottom:6px">${CGIcons.html('batch')}</div>
                <div data-style="font-size:13px;color:var(--text-muted)">Перетащите файлы или папку сюда</div>
                <div data-style="font-size:11px;color:var(--text-muted);margin-top:4px">или нажмите для выбора</div>
            </div>
            <input type="file" id="batch-file-input-${key}" data-batch-file-input data-key="${this._batchEsc(key)}" multiple data-style="display:none">
            <div class="form-group">
                <label class="form-label">Или папка на Яндекс.Диске / публичная ссылка</label>
                <div data-style="display:flex;gap:8px">
                    <input class="form-input" id="batch-folder-path-${key}" value="${savedPath}" placeholder="yandex:/videos или https://disk.yandex.ru/d/..." data-style="flex:1;min-width:0">
                    <button class="btn btn-ghost btn-small" data-project-view-action="scan-folder" data-key="${this._batchEsc(key)}">${CGIcons.html('batch')}<span>Сканировать</span></button>
                </div>
            </div>
            <div class="form-group">
                <label class="form-label">Фильтр файлов</label>
                <input class="form-input" id="batch-ext-filter-${key}" value="${savedExt}" placeholder="mp4,mov,jpg; пусто = все" data-style="max-width:260px">
                <div data-style="font-size:11px;color:var(--text-muted);margin-top:4px">Расширения файлов из папки, без точек.</div>
            </div>
            <div id="batch-file-list-${key}" data-style="margin-top:8px;max-height:180px;overflow-y:auto"></div>`;
        }

        return `<div data-style="border:1px solid var(--border);background:var(--bg-secondary);border-radius:var(--omb-r-sm);padding:14px">${header}${body}</div>`;
    },

    async _batchDialogDrop(e,key){
        const fileList=await this._filesFromDataTransfer(e.dataTransfer);
        this._addBatchFiles(key,fileList);
    },

    _batchDialogFiles(e,key){
        const files=Array.from(e.target?.files||[]);
        this._addBatchFiles(key,files);
    },

    _addBatchFiles(key,files){
        const input=this._batchInputs?.[key];
        if(!input)return;
        files=files.filter(f=>!f.name.startsWith('.'));
        if(!files.length)return;
        input.files.push(...files);
        input.serverFiles=null;
        this._renderBatchFileList(key);
    },

    async _scanFolder(key){
        const input=this._batchInputs?.[key];
        if(!input)return;
        const path=document.getElementById('batch-folder-path-'+key)?.value?.trim();
        if(!path){Toast.warning('Введите путь к папке');return}
        const ext=document.getElementById('batch-ext-filter-'+key)?.value?.trim()||'';
        try{
            const resp=await API.post('/api/scan_folder',{path,extensions:ext});
            if(resp.error){Toast.error(resp.error);return}
            if(!resp.files||!resp.files.length){Toast.warning('Папка пуста');return}
            input.files=[];
            input.serverFiles=resp.files;
            this._renderBatchFileList(key);
        }catch(e){Toast.error('Ошибка: '+e.message)}
    },

    _batchServerFiles:null,

    _renderBatchFileList(key){
        const input=this._batchInputs?.[key];
        const el=document.getElementById('batch-file-list-'+key);
        if(!el)return;

        let items=[];
        if(input?.serverFiles){
            items=input.serverFiles.map(f=>({
                name:f.name,
                size:f.size,
                serverPath:f.path
            }));
        }else{
            items=(input?.files||[]).map(f=>({
                name:f.name,
                size:f.size
            }));
        }

        if(!items.length){
            el.innerHTML='';
            return;
        }

        const fmtSize=(b)=>{
            const size=Number(b||0);
            return size>1048576?(size/1048576).toFixed(1)+' MB':(size/1024).toFixed(0)+' KB';
        };
        el.innerHTML=`<div data-style="font-size:12px;color:var(--text-secondary);margin-bottom:8px;font-weight:600">${items.length} файлов:</div>`+
            items.map((f,i)=>`<div data-style="display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:var(--omb-r-sm);background:var(--bg-secondary);margin-bottom:4px;font-size:12px">
                <span data-style="color:var(--text-muted);min-width:24px">${i+1}.</span>
                <span data-style="flex:1;word-break:break-all">${ProjectSafe.html(f.name)}</span>
                <span data-style="color:var(--text-muted);font-size:11px">${ProjectSafe.html(fmtSize(f.size))}</span>
            </div>`).join('');
    },

    _batchSourceSignature(){
        if(!this._batchFileIds)this._batchFileIds=new WeakMap();
        return JSON.stringify(Object.values(this._batchInputs||{}).map(input=>({
            node_id:input.node_id,save_as:input.save_as,mode:input.mode,
            text:document.getElementById('batch-text-'+input.key)?.value||'',
            folder_path:document.getElementById('batch-folder-path-'+input.key)?.value||'',
            extensions:document.getElementById('batch-ext-filter-'+input.key)?.value||'',
            serverFiles:input.serverFiles,
            files:(input.files||[]).map(file=>{
                if(!this._batchFileIds.has(file))this._batchFileIds.set(file,(this._batchFileSeq=(this._batchFileSeq||0)+1));
                return this._batchFileIds.get(file);
            }),
        })));
    },

    async _confirmBatchRun(taskId){
        const task=await this._ensureFullTask(taskId);
        const isV2=window.WorkboardV2Session?.isV2(task);
        const signature=isV2?this._batchSourceSignature():'';
        const prepared=this._batchPreparedInputs;
        const batchInputs=isV2&&prepared?.taskId===taskId&&prepared.signature===signature
            ?prepared.inputs:await this._collectBatchInputsPayload();
        if(!batchInputs)return;
        if(isV2)this._batchPreparedInputs={taskId,signature,inputs:batchInputs};
        else Modal.hide();
        const resp=await this._executeBatchRun(taskId, null, null, batchInputs);
        if(isV2&&resp?.ok){this._batchPreparedInputs=null;Modal.hide();}
        return resp;
    },

    async _uploadBatchFiles(input){
        const uploadedUrls=[];
        const failedUploads=[];
            Toast.info(`Загрузка файлов: ${input.files.length}: ${input.label}`);
        for(const file of input.files){
            if(typeof canUploadThroughCurrentHost==='function' && !canUploadThroughCurrentHost(file))continue;
            try{
                const data=await API.upload(file);
                if(data.local_url) uploadedUrls.push(data.local_url);
            }catch(err){
                failedUploads.push(file.name);
                console.error('Upload error:',file.name,err);
            }
        }
        if(failedUploads.length){
            const names=failedUploads.slice(0,3).join(', ');
            Toast.warning(`Не загрузились: ${names}${failedUploads.length>3?'...':''}`);
        }
        return uploadedUrls;
    },

    async _collectBatchInputsPayload(){
        const result=[];
        const inputs=Object.values(this._batchInputs||{});
        for(const input of inputs){
            const key=input.key;
            const base={
                node_id:input.node_id,
                save_as:input.save_as,
                label:input.label,
            };
            if(input.mode==='text'){
                const text=(document.getElementById('batch-text-'+key)?.value||'').trim();
                if(!text){Toast.warning(`Введите текст: ${input.label}`);return null}
                result.push({...base,mode:'text',items:[text]});
                continue;
            }

            const serverFiles=input.serverFiles||[];
            if(serverFiles.length){
                result.push({...base,mode:'files',files:serverFiles.map(f=>f.path)});
                continue;
            }

            if(input.files&&input.files.length){
                const uploadedUrls=await this._uploadBatchFiles(input);
                if(!uploadedUrls.length){Toast.error(`Не удалось загрузить файлы: ${input.label}`);return null}
                result.push({...base,mode:'files',files:uploadedUrls});
                continue;
            }

            const folderPath=document.getElementById('batch-folder-path-'+key)?.value?.trim()||'';
            const ext=document.getElementById('batch-ext-filter-'+key)?.value?.trim()||'';
            if(folderPath){
                result.push({...base,mode:'folder',folder_path:folderPath,extensions:ext});
                continue;
            }

            Toast.warning(`Заполните вход: ${input.label}`);
            return null;
        }
        return result;
    },

    async _executeBatchRun(taskId, fileUrls, textItems, batchInputs){
        const task=await this._ensureFullTask(taskId);
        if(!task){Toast.error('Задача не найдена');return}

        const isV2=window.WorkboardV2Session?.isV2(task);
        let steps;
        if(!isV2){
            const types=await getStepTypes();
            steps=window.TaskGraphActions.nodesToSteps(task.nodes||[],task.edges||[],{types});
            if(!steps.length){Toast.warning('Задача пуста');return}
        }
        const session=isV2&&window.TaskEditor?.taskId===taskId?window.TaskEditor._workboardSession:null;
        if(session&&await session.flush()===false)return;
        const payload={
            task_id:taskId,
            project_id:this.projectId,
            task_name:task.name||'Batch',
            ...(isV2?RunCostPreview.taskRequest(task):{steps}),
            folder_template:task.folder_template||'',
        };
        if(session)payload.expected_graph_revision=session.payload().expected_graph_revision;

        if(batchInputs){
            payload.batch_inputs=batchInputs;
        }else if(textItems){
            payload.mode='text';
            payload.items=textItems;
        }else if(fileUrls){
            payload.mode='files';
            payload.files=fileUrls;
        }

        let intent;
        if(isV2){
            if(!(this._v2BatchIntents instanceof Map))this._v2BatchIntents=new Map();
            intent=RunCostPreview.runIntent(payload,this._v2BatchIntents.get(taskId));
            this._v2BatchIntents.set(taskId,intent);
            const quote=await API.post('/api/jobs/estimate',intent.payload,{skipInsufficientFundsModal:true});
            const root=this._batchTaskId===taskId?document.querySelector('[data-batch-cost-preview]'):null;
            if(quote&&!quote.error&&quote.ok!==false)RunCostPreview._render(root,null,quote);
            const insufficient=Number(quote?.quote?.shortfall_credits??quote?.shortfall_credits??0)>0;
            if(!quote||quote.error||quote.ok===false||insufficient){
                const message=quote?.error||(insufficient?'Недостаточно токенов для запуска':'Не удалось оценить стоимость');
                const box=this._batchTaskId===taskId?document.querySelector('[data-batch-run-error]'):null;
                if(box){box.hidden=false;box.textContent=message;}
                Toast.error(message);
                return quote?.error||quote?.ok===false?quote:{ok:false,error:message};
            }
        }
        const resp=await API.post('/api/jobs/batch_run',intent?.payload||payload);

        if(resp?.ok){
            if(isV2&&this._v2BatchIntents.get(taskId)===intent)this._v2BatchIntents.delete(taskId);
            Toast.success(`Batch запущен: ${resp.count} jobs`);
            this._batchFiles=[];
            this._batchServerFiles=null;
            this._batchInputs={};
            this._batchNodes=[];
            this.loadTasks();
            if(session&&window.TaskEditor?._workboardSession===session)await session.loadRuntime();
        }else{
            Toast.error(resp?.error||'Ошибка batch-запуска');
        }
        return resp;
    },
};

/* web/js/projects.js */
/* ═══ Projects ═══ */
const ProjectSafe={
    html(value){return typeof escHtml==='function'?escHtml(value):String(value??'').replace(/[&<>"']/g,ch=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));},
    js(value){
        return this.html(JSON.stringify(String(value??'')));
    },
    id(value){return encodeURIComponent(String(value??''));},
    color(value,fallback){
        const raw=String(value||'').trim();
        return /^#[0-9a-f]{3}([0-9a-f]{3})?([0-9a-f]{2})?$/i.test(raw)?raw:fallback;
    }
};

const ProjectPicker={
    iconLabels:{
        dashboard:'панель',video:'видео','cat-mobile':'мобильные приложения','cat-mic':'микрофон','cat-nature':'природа',
        'cat-beauty':'красота','cat-creative':'творчество','cat-camera':'камера','cat-music':'музыка',
        text:'текст','cat-business':'бизнес','cat-rocket':'ракета',generate:'генерация',
        'cat-fire':'огонь','cat-diamond':'бриллиант','cat-home':'дом','cat-cooking':'кулинария',
        'cat-cut':'монтаж','cat-target':'цель','cat-chart':'график','cat-shop':'магазин'
    },
    colorLabels:{
        '#3e7ff0':'синий','#222222':'графитовый','#334155':'сланцевый','#4b5563':'серый',
        '#969faf':'дымчато-серый','#cfd9de':'светло-серый','#16803c':'зелёный','#9a6700':'золотистый','#b42318':'красный'
    },
    iconButton(icon,{actionAttribute,action,selected=false}){
        const safeIcon=ProjectSafe.html(icon);
        const label=ProjectSafe.html(this.iconLabels[icon]||`текущее значение ${icon}`);
        return `<button type="button" class="icon-opt" ${actionAttribute}="${action}" data-icon="${safeIcon}" aria-label="Иконка: ${label}" aria-pressed="${selected?'true':'false'}" data-style="width:38px;height:38px;display:flex;align-items:center;justify-content:center;padding:5px;border:2px solid ${selected?'var(--accent)':'transparent'};background:${selected?'var(--accent-light)':'transparent'}">${CGIcons.maybe(icon,{fallback:'dashboard'})}</button>`;
    },
    colorButton(rawColor,{actionAttribute,action,selected=false}){
        const color=ProjectSafe.color(rawColor,'#3e7ff0');
        const label=ProjectSafe.html(this.colorLabels[color]||color);
        return `<button type="button" class="color-opt" ${actionAttribute}="${action}" data-color="${ProjectSafe.html(color)}" aria-label="Цвет: ${label}" aria-pressed="${selected?'true':'false'}" data-style="width:28px;height:28px;border-radius:var(--omb-r-md);background:${color};border:2px solid transparent"></button>`;
    },
    select(options,selected,{icon=false}={}){
        options.forEach((option)=>{
            const active=option===selected;
            option.setAttribute('aria-pressed',active?'true':'false');
            option.style.borderColor=icon&&active?'var(--accent)':'transparent';
            if(icon)option.style.background=active?'var(--accent-light)':'transparent';
        });
    }
};

const Projects={
    list:[],
    _eventsBound:false,

    initEvents(){
        if(this._eventsBound)return;
        this._eventsBound=true;
        document.addEventListener('click',(event)=>{
            const target=event.target instanceof Element ? event.target.closest('[data-project-action]') : null;
            if(!target)return;
            event.preventDefault();
            const action=target.dataset.projectAction;
            const runner=window.ActionRunner||{run:(_target,fn)=>fn()};
            if(action==='open')Router.go('project',{id:target.dataset.projectId||''});
            else if(action==='pick-icon')this._pickIcon(target,target.dataset.icon||'');
            else if(action==='pick-color')this._pickColor(target,target.dataset.color||'');
            else if(action==='confirm-create')runner.run(target,()=>this.confirmCreate());
        });
    },

    async load(){
        this._showSkeleton();
        try{
            let [projects,allTasks,jobStatsByProject]=await Promise.all([
                getProjects(),
                API.getCached('/api/tasks?summary=1'),
                API.getCached('/api/jobs/stats?group_by=project')
            ]);
            projects=API.expectArray(projects,'/api/projects');
            allTasks=API.expectArray(allTasks,'/api/tasks?summary=1');
            jobStatsByProject=API.expectObject(jobStatsByProject,'/api/jobs/stats?group_by=project');
            this.list=projects;
            const g=document.getElementById('projects-grid');
            if(g)g.removeAttribute('aria-busy');
            if(!this.list.length){
                g.innerHTML=`<div class="empty-state">
                    <div class="empty-state-icon">${CGIcons.html('dashboard')}</div>
                    <div class="empty-state-title">проектов пока нет</div>
                    <div class="empty-state-text">Создайте первый проект, чтобы собрать задачи, запуски и материалы в одном месте.</div>
                    <div class="empty-state-actions">
                        <button class="btn btn-primary" data-action="projects-create">+ новый проект</button>
                    </div>
                </div>`;
                return;
            }
            const tasksByProject={};
            allTasks.forEach(t=>{
                const pid=t.project_id||'';
                (tasksByProject[pid]||(tasksByProject[pid]=[])).push(t);
            });
            g.innerHTML=this.list.map(p=>{
                const tasks=tasksByProject[p.id]||[];
                const projectStats=(jobStatsByProject&&jobStatsByProject[p.id])||{};
                const running=(projectStats.running||0)+(projectStats.pending||0);
                const done=projectStats.done||0;
                const errors=projectStats.error||0;
                const total=projectStats.total||0;
                let stats='';
                if(total){
                    stats='<div class="tile-stats">';
                    if(running)stats+='<span class="tile-stat tile-stat-run">\u25B6 '+running+'</span>';
                    if(done)stats+='<span class="tile-stat tile-stat-done">\u2713 '+done+'</span>';
                    if(errors)stats+='<span class="tile-stat tile-stat-err">\u2717 '+errors+'</span>';
                    stats+='</div>';
                }
                return '<div class="tile" data-project-action="open" data-project-id="'+ProjectSafe.html(p.id)+'">'+
                    '<div class="tile-icon">'+CGIcons.maybe(p.icon||'dashboard',{fallback:'dashboard'})+'</div>'+
                    '<div class="tile-title">'+ProjectSafe.html(p.name||'\u041F\u0440\u043E\u0435\u043A\u0442')+'</div>'+
                    '<div class="tile-desc">'+tasks.length+' \u0437\u0430\u0434\u0430\u0447 \u00B7 '+total+' \u0437\u0430\u043F\u0443\u0441\u043A\u043E\u0432</div>'+
                    stats+
                '</div>';
            }).join('');
        }catch(e){
            console.error(e);
            const g=document.getElementById('projects-grid');
            if(g){
                g.removeAttribute('aria-busy');
                g.innerHTML='<div class="empty-state"><div class="empty-state-text">не удалось загрузить проекты</div></div>';
            }
        }
    },
    _showSkeleton(){
        const g=document.getElementById('projects-grid');
        if(!g||g.innerHTML.trim())return;
        g.setAttribute('aria-busy','true');
        g.innerHTML=Array.from({length:6},()=>`
            <div class="tile skeleton skeleton-tile" aria-hidden="true">
                <div class="skeleton-line skeleton-line-short"></div>
                <div class="skeleton-line skeleton-line-value"></div>
                <div class="skeleton-line"></div>
            </div>
        `).join('');
    },
    _iconOptions:'video,cat-mobile,cat-mic,cat-nature,cat-beauty,cat-creative,cat-camera,cat-music,text,cat-business,cat-rocket,generate,cat-fire,cat-diamond,cat-home,cat-cooking,cat-cut,cat-target,cat-chart,cat-shop',
    _iconChoices(current='dashboard'){
        const choices=['dashboard',...this._iconOptions.split(',')];
        if(current&&!choices.includes(current))choices.push(current);
        return choices;
    },
    _colorChoices(current='#3e7ff0'){
        const choices=[...TASK_COLORS];
        const normalized=ProjectSafe.color(current,'#3e7ff0');
        if(!choices.includes(normalized))choices.push(normalized);
        return choices;
    },
    create(){
        const icons=this._iconChoices('dashboard');
        const colors=TASK_COLORS;
        const initialIcon='dashboard';
        const initialColor=ProjectSafe.color(colors[0],'#3e7ff0');
        const iconsHtml=icons.map(icon=>ProjectPicker.iconButton(icon,{actionAttribute:'data-project-action',action:'pick-icon',selected:icon===initialIcon})).join('');
        const colorsHtml=colors.map(color=>ProjectPicker.colorButton(color,{actionAttribute:'data-project-action',action:'pick-color',selected:color===initialColor})).join('');
        Modal.show('<h2>новый проект</h2>'
            +'<div class="form-group"><label class="form-label" for="new-proj-name">название</label>'
            +'<input class="form-input" id="new-proj-name" placeholder="мой проект" autofocus></div>'
            +'<div class="form-group"><div class="form-label" id="new-proj-icon-label">иконка</div>'
            +'<div data-style="display:flex;flex-wrap:wrap;gap:4px" id="proj-icon-picker" role="group" aria-labelledby="new-proj-icon-label">'+iconsHtml+'</div>'
            +'<input type="hidden" id="new-proj-icon" value="'+ProjectSafe.html(initialIcon)+'"></div>'
            +'<div class="form-group"><div class="form-label" id="new-proj-color-label">цвет</div>'
            +'<div data-style="display:flex;flex-wrap:wrap;gap:6px" id="proj-color-picker" role="group" aria-labelledby="new-proj-color-label">'+colorsHtml+'</div>'
            +'<input type="hidden" id="new-proj-color" value="'+ProjectSafe.html(initialColor)+'"></div>'
            +'<div class="omb-flex omb-gap-2 omb-mt-4">'
            +'<button type="button" class="btn btn-primary" data-project-action="confirm-create">создать</button>'
            +'<button type="button" class="btn btn-ghost" data-action="modal-hide">отмена</button></div>');
        setTimeout(()=>document.getElementById('new-proj-name')?.focus(),100);
    },
    _pickIcon(el,icon){
        ProjectPicker.select(document.querySelectorAll('#proj-icon-picker .icon-opt'),el,{icon:true});
        document.getElementById('new-proj-icon').value=icon;
    },
    _pickColor(el,color){
        ProjectPicker.select(document.querySelectorAll('#proj-color-picker .color-opt'),el);
        document.getElementById('new-proj-color').value=color;
    },
    async confirmCreate(){
        const name=document.getElementById('new-proj-name')?.value?.trim();
        if(!name){Toast.warning('Введите название');return}
        const icon=document.getElementById('new-proj-icon')?.value||'dashboard';
        const color=ProjectSafe.color(document.getElementById('new-proj-color')?.value,'#3e7ff0');
        const resp=await API.post('/api/projects',{name,icon,color,task_ids:[]});
        if(!resp||resp.error||resp.ok===false){Toast.error(resp?.error||'Не удалось создать проект');return}
        invalidateProjects();
        Modal.hide();
        Toast.success('Проект создан');
        Router.go('project',{id:resp.id});
    }
};

/* ═══ Project View — task tiles with sort ═══ */
const TASK_COLORS=['#3e7ff0','#222222','#334155','#4b5563','#969faf','#cfd9de','#16803c','#9a6700','#b42318'];

const ProjectView={
    projectId:null, project:null, tasks:[], allJobs:[], jobStatsByTask:{},
    runStatusFilter:'', runSearchQuery:'',
    sortMode:'created',
    _dragId:null, _dragOverId:null,
    _eventsBound:false,

    initEvents(){
        if(this._eventsBound)return;
        this._eventsBound=true;
        document.addEventListener('click',(event)=>{
            const target=event.target instanceof Element ? event.target.closest('[data-project-view-action]') : null;
            if(!target)return;
            event.preventDefault();
            event.stopPropagation();
            const action=target.dataset.projectViewAction;
            const taskId=target.dataset.taskId||target.closest('[data-task-id]')?.dataset.taskId||'';
            const key=target.dataset.key||'';
            const runner=window.ActionRunner||{run:(_target,fn)=>fn()};
            const run=(fn)=>runner.run(target,fn);
            if(action==='open-task')Router.go('task',{projectId:target.dataset.projectId||this.projectId,taskId});
            else if(action==='toggle-pin')run(()=>this.togglePin(taskId));
            else if(action==='edit-task')this.editTask(taskId);
            else if(action==='duplicate-task')this.duplicateTask(taskId);
            else if(action==='remove-task')run(()=>this.removeTask(taskId));
            else if(action==='quick-run')this.quickRun(taskId);
            else if(action==='batch-dialog')this.batchDialog(taskId);
            else if(action==='confirm-batch-run')runner.run(target,()=>this._confirmBatchRun(taskId));
            else if(action==='scan-folder')this._scanFolder(key);
            else if(action==='batch-drop-zone')document.getElementById('batch-file-input-'+key)?.click();
            else if(action==='select-task-icon')this._pickTaskIcon(target,target.dataset.icon||'');
            else if(action==='select-task-color')this._pickTaskColor(target,target.dataset.color||'');
            else if(action==='save-task-edit')run(()=>this.saveTaskEdit(taskId));
            else if(action==='confirm-quick-run')runner.run(target,()=>this._confirmQuickRun(taskId));
            else if(action==='confirm-copy')run(()=>this._confirmCopy(taskId));
            else if(action==='pick-edit-icon')this._pickEditIcon(target,target.dataset.icon||'');
            else if(action==='pick-edit-color')this._pickEditColor(target,target.dataset.color||'');
            else if(action==='save-project-edit')run(()=>this._saveEdit());
            else if(action==='publish-task')TaskPublishShare.openPublish(taskId);
            else if(action==='unpublish-task')run(()=>TaskPublishShare.unpublish(taskId));
            else if(action==='share-task')TaskPublishShare.openShare(taskId);
            else if(action==='confirm-publish')run(()=>TaskPublishShare.confirmPublish());
            else if(action==='confirm-share')run(()=>TaskPublishShare.confirmShare());
            else if(action==='revoke-share')run(()=>TaskPublishShare.revokeShare(taskId,target.dataset.recipientId||''));
            else if(action==='set-username-inline')run(()=>TaskPublishShare.setUsernameInline());
            else if(action==='runs-status-filter')this.setRunStatusFilter(target.dataset.status||'',target);
        });
        document.addEventListener('change',(event)=>{
            const target=event.target;
            if(!(target instanceof Element)||!target.matches('[data-batch-file-input]'))return;
            this._batchDialogFiles(event,target.dataset.key||'');
        });
        document.addEventListener('input',(event)=>{
            const target=event.target instanceof Element?event.target:null;
            if(target&&target.id==='project-run-search')this.setRunSearch(target.value||'');
        });
        document.addEventListener('dragstart',(event)=>{
            const tile=event.target instanceof Element ? event.target.closest('.task-tile[draggable="true"]') : null;
            if(tile)this._onDragStart(event,tile.dataset.taskId||'');
        });
        document.addEventListener('dragover',(event)=>{
            const tile=event.target instanceof Element ? event.target.closest('.task-tile') : null;
            const dropZone=event.target instanceof Element ? event.target.closest('[data-batch-drop-zone]') : null;
            if(dropZone){this._batchDropZoneDragOver(event,dropZone);return;}
            if(!tile)return;
            const taskId=tile.dataset.taskId||'';
            const hasFiles=window.DragDrop?.hasFiles(event.dataTransfer);
            if(hasFiles&&tile.classList.contains('task-tile-batch'))this._onBatchDragOver(event,taskId);
            else if(tile.draggable)this._onDragOver(event,taskId);
        });
        document.addEventListener('dragleave',(event)=>{
            const tile=event.target instanceof Element ? event.target.closest('.task-tile-batch') : null;
            const dropZone=event.target instanceof Element ? event.target.closest('[data-batch-drop-zone]') : null;
            if(dropZone){this._batchDropZoneDragLeave(event,dropZone);return;}
            if(tile)this._onBatchDragLeave(event,tile.dataset.taskId||'');
        });
        document.addEventListener('dragend',(event)=>{
            const tile=event.target instanceof Element ? event.target.closest('.task-tile[draggable="true"]') : null;
            if(tile)this._onDragEnd(event);
        });
        document.addEventListener('drop',(event)=>{
            const tile=event.target instanceof Element ? event.target.closest('.task-tile') : null;
            const dropZone=event.target instanceof Element ? event.target.closest('[data-batch-drop-zone]') : null;
            if(dropZone){this._batchDropZoneDrop(event,dropZone);return;}
            if(!tile)return;
            const taskId=tile.dataset.taskId||'';
            const hasFiles=window.DragDrop?.hasFiles(event.dataTransfer);
            if(hasFiles&&tile.classList.contains('task-tile-batch'))this._onBatchDrop(event,taskId);
            else if(tile.draggable)this._onDrop(event,taskId);
        });
    },

    async load(id){
        this.projectId=id;
        const projects=await getProjects();
        this.project=projects.find(p=>p.id===id);
        if(!this.project){Router.go('projects');return}
        document.getElementById('project-title').textContent=this.project.name||'проект';
        // Restore sort button state
        document.querySelectorAll('#page-project .filter-btn').forEach(b=>{
            b.classList.toggle('active',b.dataset.sort===this.sortMode);
        });
        await this.loadTasks();
    },

    setSort(mode){
        this.sortMode=mode;
        document.querySelectorAll('#page-project .filter-btn').forEach(b=>{
            b.classList.toggle('active',b.dataset.sort===mode);
        });
        this._renderTasks();
    },

    async loadTasks(){
        let [allTasks,jobStatsByTask]=await Promise.all([
            API.get('/api/tasks?project_id='+encodeURIComponent(this.projectId)+'&summary=1'),
            API.get('/api/jobs/stats?project_id='+encodeURIComponent(this.projectId)+'&group_by=task')
        ]);
        allTasks=Array.isArray(allTasks)?allTasks:[];
        jobStatsByTask=(jobStatsByTask&&typeof jobStatsByTask==='object'&&!Array.isArray(jobStatsByTask))?jobStatsByTask:{};
        this.tasks=allTasks.filter(t=>t.project_id===this.projectId);
        this.allJobs=[];
        this.jobStatsByTask=jobStatsByTask||{};
        this._renderTasks();
        await this._loadProjectRuns();
    },

    _taskStats(taskId){
        return (this.jobStatsByTask&&this.jobStatsByTask[taskId])||{};
    },

    async _ensureFullTask(taskId){
        const idx=this.tasks.findIndex(t=>t.id===taskId);
        const current=idx>=0?this.tasks[idx]:null;
        if(current&&Array.isArray(current.nodes)&&Array.isArray(current.edges))return current;
        const full=await API.get('/api/tasks/'+ProjectSafe.id(taskId));
        if(!full||full.error)return null;
        if(idx>=0){
            this.tasks[idx]={...current,...full};
            return this.tasks[idx];
        }
        this.tasks.push(full);
        return full;
    },

    _getSorted(){
        let tasks=[...this.tasks];
        switch(this.sortMode){
            case 'name':
                tasks.sort((a,b)=>(a.name||'').localeCompare(b.name||'','ru'));
                break;
            case 'active':
                tasks.sort((a,b)=>{
                    const aStats=this._taskStats(a.id);
                    const bStats=this._taskStats(b.id);
                    const aRun=(aStats.running||0)+(aStats.pending||0);
                    const bRun=(bStats.running||0)+(bStats.pending||0);
                    if(bRun!==aRun)return bRun-aRun;
                    const aLast=aStats.last_updated||0;
                    const bLast=bStats.last_updated||0;
                    return bLast-aLast;
                });
                break;
            case 'manual':
                // Use project.task_ids order
                const order=this.project?.task_ids||[];
                tasks.sort((a,b)=>{
                    const ai=order.indexOf(a.id);
                    const bi=order.indexOf(b.id);
                    return (ai===-1?999:ai)-(bi===-1?999:bi);
                });
                break;
            case 'created':
            default:
                tasks.sort((a,b)=>(b.created_at||0)-(a.created_at||0));
                break;
        }
        return tasks;
    },

    _renderTasks(){
        const el=document.getElementById('project-tasks');
        const sorted=this._getSorted();
        if(!sorted.length){
            el.innerHTML=`<div class="empty-state">
                <div class="empty-state-icon">${CGIcons.html('dashboard')}</div>
                <div class="empty-state-title">задач пока нет</div>
                <div class="empty-state-text">Добавьте первую задачу, настройте шаги и запустите генерацию из этого проекта.</div>
                <div class="empty-state-actions">
                    <button class="btn btn-primary" data-action="project-create-task">+ новая задача</button>
                </div>
            </div>`;
            return;
        }
        const isManual=this.sortMode==='manual';
        el.innerHTML=sorted.map(t=>{
            const nodes=t.nodes||[];
            const taskStats=this._taskStats(t.id);
            const running=(taskStats.running||0)+(taskStats.pending||0);
            const done=taskStats.done||0;
            const errors=taskStats.error||0;
            const total=taskStats.total||0;
            const safeTaskId=ProjectSafe.html(t.id);
            const safeProjectId=ProjectSafe.html(this.projectId);
            const safeTaskName=ProjectSafe.html(t.name||'Задача');
            const nodeBatchCount=nodes.filter(n=>n.type==='batch_input').length;
            const batchCount=Math.max(Number(t.batch_count||0),nodeBatchCount);
            const hasBatch=batchCount>0||!!t.has_batch;
            const batchBtnLabel=CGIcons.html('batch')+`<span>batch${batchCount>1?' x'+batchCount:''}</span>`;
            const batchOverlayText=batchCount>1?`Перетащите файлы для batch-запуска (${batchCount} входа)`:'Перетащите файлы для batch-запуска';

            let statsHtml='';
            if(total>0){
                statsHtml='<div class="task-tile-stats">';
                if(running>0) statsHtml+=`<span class="task-stat task-stat-running">${CGIcons.inline('sync')} ${running}</span>`;
                if(done>0) statsHtml+=`<span class="task-stat task-stat-done">OK ${done}</span>`;
                if(errors>0) statsHtml+=`<span class="task-stat task-stat-error">ERR ${errors}</span>`;
                statsHtml+='</div>';
            }

            const isPublished=t.visibility==='public';
            const publishedBadge=isPublished?`<span class="task-tile-badge task-tile-badge-published" title="Опубликовано в библиотеку">${CGIcons.inline('star')} опубликовано</span>`:'';
            const publishBtn=isPublished
                ?`<button class="btn btn-ghost btn-small task-tile-action" data-project-view-action="unpublish-task" data-task-id="${safeTaskId}" title="Снять с публикации" aria-label="Снять с публикации">${CGIcons.html('hide')}</button>`
                :`<button class="btn btn-ghost btn-small task-tile-action" data-project-view-action="publish-task" data-task-id="${safeTaskId}" title="Опубликовать в библиотеку" aria-label="Опубликовать в библиотеку">${CGIcons.html('star')}</button>`;
            const shareBtn=`<button class="btn btn-ghost btn-small task-tile-action" data-project-view-action="share-task" data-task-id="${safeTaskId}" title="Поделиться с пользователем" aria-label="Поделиться">${CGIcons.html('send')}</button>`;
            return `
            <div class="task-tile ${isManual?'task-tile-draggable':''} ${hasBatch?'task-tile-batch':''}" data-task-id="${safeTaskId}" draggable="${isManual?'true':'false'}" data-project-view-action="open-task" data-project-id="${safeProjectId}">
                ${hasBatch?'<div class="batch-drop-overlay" id="batch-overlay-'+safeTaskId+'"><div class="batch-drop-icon">'+CGIcons.html('batch')+'</div><div class="batch-drop-text">'+ProjectSafe.html(batchOverlayText)+'</div></div>':''}
                <div class="task-tile-header">
                    <div class="task-tile-actions">
                        <button class="btn btn-ghost btn-small task-tile-action task-pin-btn ${t.pinned?'is-pinned':''}" data-project-view-action="toggle-pin" data-task-id="${safeTaskId}" title="${t.pinned?'Убрать с дашборда':'На дашборд'}" aria-label="${t.pinned?'Убрать с дашборда':'На дашборд'}">${CGIcons.html('pin')}</button>
                        ${publishBtn}
                        ${shareBtn}
                        <button class="btn btn-ghost btn-small task-tile-action" data-project-view-action="edit-task" data-task-id="${safeTaskId}" title="Настройки" aria-label="Настройки">${CGIcons.html('tools')}</button>
                        <button class="btn btn-ghost btn-small task-tile-action" data-project-view-action="duplicate-task" data-task-id="${safeTaskId}" title="Копировать" aria-label="Копировать">${CGIcons.html('clipboard')}</button>
                        <button class="btn btn-ghost btn-small task-tile-action btn-danger-text" data-project-view-action="remove-task" data-task-id="${safeTaskId}" title="Удалить" aria-label="Удалить задачу">${CGIcons.html('trash')}</button>
                    </div>
                </div>
                <div class="task-tile-name">${safeTaskName}${publishedBadge}</div>
                <div class="task-tile-meta">${nodes.length||t.node_count||0} шагов · ${total} запусков</div>
                ${statsHtml}
                <div class="task-tile-footer">
                    <button class="btn btn-primary btn-small task-run-btn" data-project-view-action="quick-run" data-task-id="${safeTaskId}"><span class="ui-code-icon" aria-hidden="true">RUN</span><span>запуск</span></button>
                    ${hasBatch?`<button class="btn btn-primary btn-small task-batch-btn" data-project-view-action="batch-dialog" data-task-id="${safeTaskId}">${batchBtnLabel}</button>`:''}
                </div>
            </div>`}).join('');
    },

    _projectRunsUrl(){
        return '/api/jobs?project_id='+encodeURIComponent(this.projectId||'')+'&limit=60&offset=0';
    },

    async _loadProjectRuns(){
        const el=document.getElementById('project-runs');
        if(el&&!el.innerHTML.trim()){
            el.setAttribute('aria-busy','true');
            el.innerHTML='<div class="empty-state"><div class="empty-state-text">загружаем запуски проекта...</div></div>';
        }
        try{
            const resp=await API.get(this._projectRunsUrl());
            this.allJobs=Array.isArray(resp)?resp:[];
            this._syncRunSearchInputValue();
            this._renderProjectRuns();
        }catch(e){
            console.error(e);
            if(el){
                el.removeAttribute('aria-busy');
                el.innerHTML='<div class="empty-state"><div class="empty-state-text">не удалось загрузить запуски проекта</div></div>';
            }
        }
    },

    refreshRuns(){
        if(typeof Router!=='undefined'&&Router.current!=='project')return;
        return this._loadProjectRuns();
    },

    setRunStatusFilter(status,button){
        this.runStatusFilter=status||'';
        button?.parentElement?.querySelectorAll('.filter-btn').forEach(btn=>btn.classList.remove('active'));
        button?.classList.add('active');
        this._renderProjectRuns();
    },

    _normalizeRunSearchQuery(query){
        const value=String(query||'').trim().toLowerCase();
        if(!value)return '';
        const email=String((typeof Auth!=='undefined'&&Auth.user&&Auth.user.email)||'').trim().toLowerCase();
        return email&&value===email?'':value;
    },

    _syncRunSearchInputValue(){
        const input=document.getElementById('project-run-search');
        if(!input)return;
        const normalized=this._normalizeRunSearchQuery(input.value||'');
        this.runSearchQuery=normalized;
        if(!normalized&&String(input.value||'').trim())input.value='';
    },

    setRunSearch(query){
        const normalized=this._normalizeRunSearchQuery(query);
        this.runSearchQuery=normalized;
        if(!normalized&&String(query||'').trim()){
            const input=document.getElementById('project-run-search');
            if(input&&input.value!==normalized)input.value=normalized;
        }
        this._renderProjectRuns();
    },

    _renderProjectRuns(){
        const el=document.getElementById('project-runs');
        if(!el)return;
        el.removeAttribute('aria-busy');
        let jobs=Array.isArray(this.allJobs)?this.allJobs:[];
        if(this.runStatusFilter)jobs=jobs.filter(j=>j.status===this.runStatusFilter);
        if(this.runSearchQuery){
            const query=this.runSearchQuery;
            jobs=jobs.filter(j=>{
                const readable=(window.DisplayLabels&&DisplayLabels.jobTitle)?DisplayLabels.jobTitle(j):String(j.task_name||'');
                return readable.toLowerCase().includes(query);
            });
        }
        if(!jobs.length){
            el.innerHTML='<div class="empty-state"><div class="empty-state-text">запусков пока нет</div></div>';
            if(typeof Jobs!=='undefined'&&Jobs._refreshSelectionChrome)Jobs._refreshSelectionChrome();
            return;
        }
        el.innerHTML=(typeof Jobs!=='undefined'&&Jobs.renderSelectionBar?Jobs.renderSelectionBar():'')+
            jobs.map(j=>Jobs.renderCard(j)).join('');
        if(typeof Jobs!=='undefined'&&Jobs._refreshSelectionChrome)Jobs._refreshSelectionChrome();
    },

    /* ── Drag & drop for manual sort ── */
    _pickTaskIcon(el,icon){
        ProjectPicker.select(document.querySelectorAll('#edit-task-icon-picker .icon-opt'),el,{icon:true});
        document.getElementById('edit-task-icon').value=icon;
    },

    _pickTaskColor(el,color){
        el.closest('.color-picker')?.querySelectorAll('.color-opt').forEach(e=>e.classList.remove('color-sel'));
        el.classList.add('color-sel');
        ProjectPicker.select(el.closest('.color-picker')?.querySelectorAll('.color-opt')||[],el);
        document.getElementById('edit-task-color').value=ProjectSafe.color(color,'#3e7ff0');
    },


    createTask(){
        const id=String(Date.now());
        Router.go('task',{projectId:this.projectId,taskId:id});
    },

    editTask(taskId){
        const task=this.tasks.find(t=>t.id===taskId);
        if(!task)return;
        const taskColor=ProjectSafe.color(task.color,'#3e7ff0');
        const curIcon=task.icon||'dashboard';
        const icons=Projects._iconChoices(curIcon);
        const colors=Projects._colorChoices(taskColor);
        const iconsHtml=icons.map(icon=>ProjectPicker.iconButton(icon,{actionAttribute:'data-project-view-action',action:'select-task-icon',selected:icon===curIcon})).join('');
        const colorsHtml=colors.map(rawColor=>{
            const color=ProjectSafe.color(rawColor,'#3e7ff0');
            return ProjectPicker.colorButton(color,{actionAttribute:'data-project-view-action',action:'select-task-color',selected:color===taskColor});
        }).join('');
        Modal.show(`<h2>${CGIcons.inline('tools')} ${ProjectSafe.html(task.name||'задача')}</h2>
            <div class="form-group"><label class="form-label">название</label>
                <input class="form-input" id="edit-task-name" value="${ProjectSafe.html(task.name||'')}"></div>
            <div class="form-group"><label class="form-label">иконка</label>
                <div data-style="display:flex;flex-wrap:wrap;gap:4px" id="edit-task-icon-picker">${iconsHtml}</div>
                <input type="hidden" id="edit-task-icon" value="${ProjectSafe.html(curIcon)}"></div>
            <div class="form-group"><label class="form-label">цвет</label>
                <div class="color-picker">${colorsHtml}</div>
                <input type="hidden" id="edit-task-color" value="${ProjectSafe.html(taskColor)}">
            </div>
            <div class="omb-flex omb-gap-2 omb-mt-4">
                <button class="btn btn-primary" data-project-view-action="save-task-edit" data-task-id="${ProjectSafe.html(taskId)}">сохранить</button>
                <button class="btn btn-ghost" data-action="modal-hide">отмена</button></div>`);
    },

    async saveTaskEdit(taskId){
        const task=await this._ensureFullTask(taskId);
        if(!task)return;
        task.name=document.getElementById('edit-task-name').value||'Задача';
        task.icon=document.getElementById('edit-task-icon').value||'dashboard';
        task.color=ProjectSafe.color(document.getElementById('edit-task-color').value,'#3e7ff0');
        let payload=task;
        if(window.WorkboardV2Session?.isV2(task)){
            const {steps,node_states,view_state,...graph}=task;
            payload=JSON.parse(JSON.stringify({...graph,expected_graph_revision:task.graph_revision}));
            if(task.visibility==='public')payload.confirm_published_edit=true;
        }
        const resp=await API.post('/api/tasks',payload);
        if(!resp||resp.error||resp.ok===false){Toast.error(resp?.error||'Не удалось сохранить задачу');return}
        if(payload!==task){task.graph_revision=resp.graph_revision;task.graph_hash=resp.graph_hash;}
        Modal.hide();
        this.loadTasks();
    },

    async quickRun(taskId){
        const task=await this._ensureFullTask(taskId);
        if(!task)return;
        if((task.nodes||[]).some(n=>n.type==='batch_input')){
            this.batchDialog(taskId);
            return;
        }
        const isV2=window.WorkboardV2Session?.isV2(task);
        let steps;
        if(!isV2){
            const types=await getStepTypes();
            steps=window.TaskGraphActions.nodesToSteps(task.nodes||[],task.edges||[],{types});
            if(!steps.length){Toast.warning('Задача пуста — откройте редактор и добавьте шаги');return}
        }
        const quoteTask=isV2?{id:task.id,graph_revision:task.graph_revision}:null;
        this._quickV2Task=quoteTask;
        this._quickRunIntent=null;
        this._quickRunCostPreview?.destroy();
        Modal.show(`<h2>${CGIcons.inline('video')} запуск: ${ProjectSafe.html(task.name||'задача')}</h2>
            <div class="form-group"><label class="form-label">повторов</label>
            <input class="form-input" id="quick-run-reps" type="number" value="1" min="1" max="100"></div>
            <div class="run-cost-preview" data-run-cost-preview hidden></div>
            <div class="run-error" data-run-error role="alert" aria-live="assertive" hidden></div>
            <div class="omb-flex omb-gap-2 omb-mt-4">
                <button class="btn btn-primary" data-project-view-action="confirm-quick-run" data-task-id="${ProjectSafe.html(taskId)}"><span class="ui-code-icon" aria-hidden="true">RUN</span><span>запустить</span></button>
                <button class="btn btn-ghost" data-action="modal-hide">отмена</button></div>`);
        if(typeof RunCostPreview!=='undefined'){
            this._quickRunCostPreview=RunCostPreview.attach({
                steps,
                estimate:isV2?options=>API.post('/api/jobs/estimate',RunCostPreview.taskRequest(quoteTask,options),{skipInsufficientFundsModal:true}):null,
                input:document.getElementById('quick-run-reps'),
                root:document.querySelector('[data-run-cost-preview]'),
                button:document.querySelector('[data-project-view-action="confirm-quick-run"]'),
            });
        }
    },

    async _confirmQuickRun(taskId){
        const task=await this._ensureFullTask(taskId);
        if(!task)return;
        const reps=Math.min(100,Math.max(1,parseInt(document.getElementById('quick-run-reps')?.value)||1));
        if(window.WorkboardV2Session?.isV2(task)){
            if(document.querySelector('[data-run-cost-preview]')?.dataset.state==='insufficient'){
                Toast.error('Недостаточно токенов для запуска');return;
            }
            const quoteTask=this._quickV2Task?.id===taskId?this._quickV2Task:task;
            const intent=RunCostPreview.runIntent(RunCostPreview.taskRequest(quoteTask,{repeats:reps}),this._quickRunIntent);
            this._quickRunIntent=intent;
            const resp=await API.post('/api/jobs/run',intent.payload);
            if(!resp||resp.error||resp.ok===false){
                const message=resp?.error||'Не удалось запустить задачу';
                const box=document.querySelector('[data-run-error]');
                if(box){box.hidden=false;box.textContent=message;}
                Toast.error(message);return resp;
            }
            this._quickRunCostPreview?.destroy();
            Modal.hide();
            if(this._quickRunIntent===intent)this._quickRunIntent=null;
            Toast.success('Запущено: '+(resp.count>1?resp.count+' раз':'1 раз'));
            this.loadTasks();
            return resp;
        }
        const types=await getStepTypes();
        const steps=window.TaskGraphActions.nodesToSteps(task.nodes||[],task.edges||[],{types});
        const blocked=document.querySelector('[data-run-cost-preview]')?.dataset.state==='insufficient';
        if(blocked){
            Toast.error('Недостаточно токенов для запуска');
            return;
        }
        const runProblem=await window.TaskGraphActions.validateStepsForRun(steps);
        if(!runProblem.ok){
            const box=document.querySelector('[data-run-error]');
            if(box){box.hidden=false;box.textContent=runProblem.message;}
            Toast.warning(runProblem.message);
            return;
        }
        Modal.hide();
        const resp=await API.post('/api/jobs/run',{
            task_id:taskId, project_id:this.projectId,
            task_name:task.name, steps,
            folder_template:task.folder_template||'',
            repeats:reps
        });
        if(!resp||resp.error||resp.ok===false){Toast.error(resp?.error||'Не удалось запустить задачу');return}
        Toast.success('Запущено: '+(resp.count>1?resp.count+' раз':'1 раз'));
        this.loadTasks();
    },

    async togglePin(taskId){
        const resp=await API.post('/api/tasks/'+ProjectSafe.id(taskId)+'/pin');
        if(resp.ok){
            const task=this.tasks.find(t=>t.id===taskId);
            if(task) task.pinned=resp.pinned;
            Toast.info(resp.pinned?'PIN Добавлено на дашборд':'PIN Убрано с дашборда');
            this._renderTasks();
        }
    },

    async duplicateTask(taskId){
        const task=this.tasks.find(t=>t.id===taskId);
        if(!task)return;
        const projects=await getProjects();
        const opts=projects.map(p=>'<option value="'+ProjectSafe.html(p.id)+'" '+(p.id===this.projectId?'selected':'')+'>'+ProjectSafe.html(p.name||'Проект')+'</option>').join('');
        Modal.show('<h2>\u2A49 копировать задачу</h2>'
            +'<div class="form-group"><label class="form-label">в проект</label>'
            +'<select class="form-select" id="copy-target-project">'+opts+'</select></div>'
            +'<div class="omb-flex omb-gap-2 omb-mt-4">'
            +'<button class="btn btn-primary" data-project-view-action="confirm-copy" data-task-id="'+ProjectSafe.html(taskId)+'">копировать</button>'
            +'<button class="btn btn-ghost" data-action="modal-hide">отмена</button></div>');
    },
    async _confirmCopy(taskId){
        const pid=document.getElementById('copy-target-project').value;
        const resp=await API.post('/api/tasks/'+ProjectSafe.id(taskId)+'/copy',{project_id:pid});
        if(!resp||resp.error||resp.ok===false){Toast.error(resp?.error||'Не удалось скопировать задачу');return}
        Modal.hide();
        Toast.success('Задача скопирована');
        this.loadTasks();
    },

    async removeTask(taskId){
        if(!await Dialogs.confirm('Удалить задачу?'))return;
        await API.del('/api/tasks/'+ProjectSafe.id(taskId));
        this.loadTasks();
    },

    async editName(){
        const proj=this.project;
        const curIcon=proj.icon||'dashboard';
        const curColor=ProjectSafe.color(proj.color,'#3e7ff0');
        const icons=Projects._iconChoices(curIcon);
        const colors=Projects._colorChoices(curColor);
        const iconsHtml=icons.map(icon=>ProjectPicker.iconButton(icon,{actionAttribute:'data-project-view-action',action:'pick-edit-icon',selected:icon===curIcon})).join('');
        const colorsHtml=colors.map(rawColor=>{
            const color=ProjectSafe.color(rawColor,'#3e7ff0');
            return ProjectPicker.colorButton(color,{actionAttribute:'data-project-view-action',action:'pick-edit-color',selected:color===curColor});
        }).join('');
        Modal.show('<h2>настройки проекта</h2>'
            +'<div class="form-group"><label class="form-label">название</label>'
            +'<input class="form-input" id="edit-proj-name" value="'+ProjectSafe.html(proj.name||'')+'"></div>'
            +'<div class="form-group"><label class="form-label">иконка</label>'
            +'<div data-style="display:flex;flex-wrap:wrap;gap:4px" id="edit-proj-icon-picker">'+iconsHtml+'</div>'
            +'<input type="hidden" id="edit-proj-icon" value="'+ProjectSafe.html(curIcon)+'"></div>'
            +'<div class="form-group"><label class="form-label">цвет</label>'
            +'<div data-style="display:flex;flex-wrap:wrap;gap:6px" id="edit-proj-color-picker">'+colorsHtml+'</div>'
            +'<input type="hidden" id="edit-proj-color" value="'+ProjectSafe.html(curColor)+'"></div>'
            +'<div class="omb-flex omb-gap-2 omb-mt-4">'
            +'<button class="btn btn-primary" data-project-view-action="save-project-edit">сохранить</button>'
            +'<button class="btn btn-ghost" data-action="modal-hide">отмена</button></div>');
    },
    _pickEditIcon(el,icon){
        ProjectPicker.select(document.querySelectorAll('#edit-proj-icon-picker .icon-opt'),el,{icon:true});
        document.getElementById('edit-proj-icon').value=icon;
    },
    _pickEditColor(el,color){
        ProjectPicker.select(document.querySelectorAll('#edit-proj-color-picker .color-opt'),el);
        document.getElementById('edit-proj-color').value=color;
    },
    async _saveEdit(){
        const name=document.getElementById('edit-proj-name')?.value?.trim();
        if(!name){Toast.warning('Введите название');return}
        this.project.name=name;
        this.project.icon=document.getElementById('edit-proj-icon')?.value||'dashboard';
        this.project.color=ProjectSafe.color(document.getElementById('edit-proj-color')?.value,'#3e7ff0');
        const resp=await API.post('/api/projects',this.project);
        if(!resp||resp.error||resp.ok===false){Toast.error(resp?.error||'Не удалось сохранить проект');return}
        invalidateProjects();
        Modal.hide();
        document.getElementById('project-title').textContent=name;
        this.loadTasks();
    },

    async remove(){
        if(!await Dialogs.confirm('Удалить проект и все его задачи?'))return;
        await API.del('/api/projects/'+ProjectSafe.id(this.projectId));
        invalidateProjects();
        Router.go('projects');
    }
};
Object.assign(ProjectView, window.ProjectViewBatch||{});

/* web/js/jobs.js */
/* ═══ Jobs ═══ */

const Jobs={
    pollTimer:null,
    _eventsBound:false,
    _selectedJobIds:new Set(),
    _batchInFlight:false,
    _selectionAnchorId:'',

    _js(value){return escHtml(JSON.stringify(String(value??'')));},

    initEvents(){
        if(this._eventsBound)return;
        this._eventsBound=true;
        document.addEventListener('click',(event)=>{
            const projWrap=event.target instanceof Element ? event.target.closest('[data-job-select-wrap-project]') : null;
            if(projWrap){
                event.preventDefault();
                event.stopPropagation();
                this.toggleProjectSelection(projWrap.dataset.projectId||'');
                return;
            }
            const selectWrap=event.target instanceof Element ? event.target.closest('[data-job-select-wrap]') : null;
            if(selectWrap){
                const input=selectWrap.querySelector('[data-job-select]');
                const id=input?.dataset.jobId||selectWrap.closest('[data-job-id]')?.dataset.jobId||'';
                event.preventDefault();
                if(id&&input){
                    const next=!this._selectedJobIds.has(String(id));
                    input.checked=next;
                    this.toggleSelection(id, next);
                    this._selectionAnchorId=String(id);
                }
                event.stopPropagation();
                return;
            }
            // Modifier-aware selection on a card body (must run BEFORE [data-job-action] dispatch,
            // because the card itself has data-job-action="detail").
            const cardEl=event.target instanceof Element ? event.target.closest('.job-card[data-job-id]') : null;
            const innerActionEl=event.target instanceof Element
                ? event.target.closest('[data-job-action]:not(.job-card)')
                : null;
            if(cardEl && !innerActionEl){
                const cardId=String(cardEl.dataset.jobId||'');
                if(event.shiftKey && cardId){
                    event.preventDefault(); event.stopPropagation();
                    this._shiftClickToggle(cardId);
                    return;
                }
                if((event.metaKey||event.ctrlKey) && cardId){
                    event.preventDefault(); event.stopPropagation();
                    const next=!this._selectedJobIds.has(cardId);
                    this.toggleSelection(cardId, next);
                    this._selectionAnchorId=cardId;
                    return;
                }
                if(this._selectedJobIds.size>0){
                    // Plain click while a selection exists — clear and continue to detail open.
                    this.clearSelection();
                }
            }
            const target=event.target instanceof Element ? event.target.closest('[data-job-action]') : null;
            if(!target)return;
            const id=target.dataset.jobId||target.closest('[data-job-id]')?.dataset.jobId||'';
            const action=target.dataset.jobAction||'detail';
            event.preventDefault();
            event.stopPropagation();
            if(action==='download-selected'){this.openDownloadDialog();return;}
            if(action==='confirm-download-selected'){this.confirmDownloadSelected();return;}
            if(action==='select-all'){this.selectAllVisible();return;}
            if(action==='clear-selection'){this.clearSelection();return;}
            if(action==='batch-pause'){this.pauseSelected();return;}
            if(action==='batch-resume'){this.resumeSelected();return;}
            if(action==='batch-stop'){this.cancelSelected();return;}
            if(action==='batch-retry'){this.retrySelected?.();return;}
            if(action==='batch-delete'){this.deleteSelected?.();return;}
            if(action==='batch-open-folder'){this.openFolderSelected?.();return;}
            if(!id)return;
            if(action==='detail')this.showDetail(id);
            else if(action==='pause')this.pause(id);
            else if(action==='cancel')this.cancel(id);
            else if(action==='resume')this.resume(id);
            else if(action==='open-folder')this.openFolder(id);
            else if(action==='retry')this.retry(id);
            else if(action==='debug')Router.go('debugger',{jobId:id});
            else if(action==='delete')this.del(id);
        });
        // Right-click context menu on a card.
        document.addEventListener('contextmenu',(event)=>{
            if(!(event.target instanceof Element))return;
            const card=event.target.closest('.job-card[data-job-id]');
            if(!card)return;
            const route=(typeof Router!=='undefined'&&Router&&Router.current)||'';
            if(route!=='home'&&route!=='task'&&route!=='project')return;
            event.preventDefault();
            this._openCardContextMenu(card, event.clientX, event.clientY);
        });
        // Context-menu item dispatch.
        document.addEventListener('click',(event)=>{
            const item=event.target instanceof Element?event.target.closest('[data-job-card-ctx]'):null;
            if(!item)return;
            event.preventDefault(); event.stopPropagation();
            const action=item.dataset.jobCardCtx||'';
            const id=item.dataset.jobId||'';
            const pid=item.dataset.projectId||'';
            this._closeCardContextMenu();
            this._dispatchContextAction(action, id, pid);
        });
        document.addEventListener('error',(event)=>{
            const target=event.target;
            if(!(target instanceof Element)||!target.matches('[data-folder-preview-image]'))return;
            target.style.display='none';
            target.closest('.folder-preview')?.classList.add('folder-preview-failed');
        },true);
    },

    _shiftClickToggle(id){
        const anchor=this._selectionAnchorId;
        const map=this._jobsByIdMap();
        if(!anchor||!map[String(anchor)]){
            this.toggleSelection(id, !this._selectedJobIds.has(String(id)));
            this._selectionAnchorId=String(id);
            return;
        }
        const scope=this._activeScope()||document;
        const visible=Array.from(scope.querySelectorAll('.job-card[data-job-id]'))
            .map(el=>String(el.dataset.jobId||''))
            .filter(Boolean);
        const a=visible.indexOf(String(anchor));
        const b=visible.indexOf(String(id));
        if(a<0||b<0){
            this.toggleSelection(id, true);
            this._selectionAnchorId=String(id);
            return;
        }
        const [lo,hi]=a<b?[a,b]:[b,a];
        for(let k=lo;k<=hi;k++) this._selectedJobIds.add(visible[k]);
        this._refreshSelectionChrome();
        this._selectionAnchorId=String(id);
    },

    _closeCardContextMenu(){
        document.querySelectorAll('.ctx-menu[data-job-card-menu]').forEach(m=>m.remove());
    },

    _openCardContextMenu(card, x, y){
        this._closeCardContextMenu();
        const id=String(card.dataset.jobId||'');
        const pid=card.closest('[data-project-id]')?.dataset.projectId||'';
        // If clicked card is not in selection — switch to single-selection mode.
        if(!this._selectedJobIds.has(id)){
            this.clearSelection();
            this.toggleSelection(id, true);
            this._selectionAnchorId=id;
        }
        const map=this._jobsByIdMap();
        const job=map[id]||{};
        const status=String(job.status||'');
        const isBatch=this._selectedJobIds.size>1;
        const stats=this._selectionStats();
        const items=[];
        if(!isBatch) items.push(['detail','Открыть']);
        if(!isBatch && status!=='pending') items.push(['open-folder','Открыть папку']);
        if(isBatch && stats.running+stats.pending===0) items.push(['open-folder','Открыть папки выбранных']);
        if(status==='running' || (isBatch && stats.running>0)) items.push(['pause','Пауза']);
        if(status==='paused' || (isBatch && stats.paused>0)) items.push(['resume','Запустить']);
        if(['running','pending','paused'].includes(status) || (isBatch && stats.running+stats.pending+stats.paused>0)) items.push(['cancel','Остановить']);
        if(status==='error' || (isBatch && stats.error>0)) items.push(['retry','Повторить']);
        if(pid && !isBatch) items.push(['select-project','Выделить все в проекте']);
        items.push(['delete','Удалить']);

        const menu=document.createElement('div');
        menu.className='ctx-menu';
        menu.dataset.jobCardMenu='1';
        menu.style.left=x+'px';
        menu.style.top=y+'px';
        menu.innerHTML=items.map(([action,label])=>{
            const cls=action==='delete'?'ctx-menu-item ctx-menu-item-danger':'ctx-menu-item';
            return '<div class="'+cls+'" data-job-card-ctx="'+escHtml(action)+'" data-job-id="'+escHtml(id)+'" data-project-id="'+escHtml(pid)+'">'+escHtml(label)+'</div>';
        }).join('');
        document.body.appendChild(menu);
        // Reposition if off-screen.
        const r=menu.getBoundingClientRect();
        if(r.right>window.innerWidth) menu.style.left=Math.max(0,window.innerWidth-r.width-4)+'px';
        if(r.bottom>window.innerHeight) menu.style.top=Math.max(0,window.innerHeight-r.height-4)+'px';
        // Dismiss on next click outside / Escape.
        const closer=(ev)=>{
            if(ev && ev.target instanceof Element && ev.target.closest('[data-job-card-ctx]'))return;
            this._closeCardContextMenu();
            document.removeEventListener('click',closer,true);
            document.removeEventListener('contextmenu',closer,true);
            document.removeEventListener('keydown',keyCloser,true);
        };
        const keyCloser=(ev)=>{
            if(ev.key==='Escape'){this._closeCardContextMenu();document.removeEventListener('click',closer,true);document.removeEventListener('contextmenu',closer,true);document.removeEventListener('keydown',keyCloser,true);}
        };
        setTimeout(()=>{
            document.addEventListener('click',closer,true);
            document.addEventListener('contextmenu',closer,true);
            document.addEventListener('keydown',keyCloser,true);
        },0);
    },

    _dispatchContextAction(action, id, pid){
        const isBatch=this._selectedJobIds.size>1;
        if(action==='detail' && id){this.showDetail(id);return;}
        if(action==='open-folder'){
            if(isBatch){this.openFolderSelected?.();return;}
            if(id){this.openFolder(id);return;}
        }
        if(action==='select-project' && pid){this.toggleProjectSelection(pid);return;}
        if(isBatch){
            if(action==='pause'){this.pauseSelected();return;}
            if(action==='resume'){this.resumeSelected();return;}
            if(action==='cancel'){this.cancelSelected();return;}
            if(action==='retry'){this.retrySelected?.();return;}
            if(action==='delete'){this.deleteSelected?.();return;}
        }else if(id){
            if(action==='pause'){this.pause(id);return;}
            if(action==='resume'){this.resume(id);return;}
            if(action==='cancel'){this.cancel(id);return;}
            if(action==='retry'){this._batchAction?.('retry',[id],'RETRY');return;}
            if(action==='delete'){this.del(id);return;}
        }
    },

    /* Stage 4: three quantities the card needs.
       - done = nodes completed in THIS enqueue (queue_execution_scope ∩ same-epoch-done)
       - scope = nodes this enqueue will execute (queue_execution_scope size)
       - total = total nodes in the task graph (live, recomputed in update_job)
       Display format: "3 из 5 · всего в задаче 20" (done / scope · total).
       Falls back gracefully when the backend doesn't yet carry the new
       fields (legacy responses) — shows the old "X/Y" via current_step. */
    _progress(j){
        const total=Number(j?.total_steps)||0;
        const done=Number(j?.done_steps);
        const scope=Number(j?.run_scope);
        const status=String(j?.status||'');
        // Modern path: backend supplied done_steps + run_scope.
        if(Number.isFinite(done) && Number.isFinite(scope) && scope>0){
            const clampedDone=Math.max(0,Math.min(done,scope));
            const showCounter=status==='running'||status==='paused'||status==='cancelled'||status==='error';
            const percent=showCounter?Math.min(100,Math.max(0,Math.round((clampedDone/scope)*100))):0;
            const label=showCounter?(clampedDone+'/'+scope):'';
            const totalSuffix=showCounter?((total>scope)?(' · всего в задаче '+total):''):(total>0?('всего в задаче '+total):'');
            return {label, percent, totalSuffix};
        }
        // Legacy fallback (older payloads): current_step+1 of total.
        if(j?.current_step==null){
            return {label:'', percent:0, totalSuffix:total>0?('всего в задаче '+total):''};
        }
        const rawCurrent=Number(j.current_step);
        if(!Number.isFinite(rawCurrent)){
            return {label:'', percent:0, totalSuffix:total>0?('всего в задаче '+total):''};
        }
        const showCounter=status==='running'||status==='paused'||status==='cancelled'||status==='error';
        const displayTotal=total>0?String(total):'?';
        const displayCurrent=total>0
            ? Math.min(total,Math.max(0,rawCurrent+1))
            : Math.max(0,rawCurrent+1);
        const percent=showCounter&&total>0
            ? Math.min(100,Math.max(0,Math.round((displayCurrent/total)*100)))
            : 0;
        const label=showCounter?(displayCurrent+'/'+displayTotal):'';
        const totalSuffix=showCounter?'':(total>0?('всего в задаче '+total):'');
        return {label, percent, totalSuffix};
    },

    _stepTypeLabel(type){
        if(!type)return '';
        const t=String(type).toLowerCase();
        const known={
            'text':'текст','llm':'текст','prompt':'текст',
            'image':'изображение','image_generation':'изображение',
            'audio':'аудио','tts':'озвучка','voice':'озвучка',
            'video':'видео','video_generation':'видео',
            'lipsync':'липсинк','heygen':'видео-аватар',
            'reels_maker':'reels','suno':'музыка',
            'agent':'агент','generator':'генератор',
        };
        if(known[t])return known[t];
        // Provider-prefixed types (e.g. "kieai:grok-imagine/image-to-video")
        // must never expose the prefix. Resolve through cached step_types
        // when possible, otherwise fall back to a neutral category label.
        if(t.includes(':')){
            const meta=(window.TaskEditor&&TaskEditor._cachedTypes&&TaskEditor._cachedTypes[type])
                ||(window.JobDebugger&&JobDebugger.types&&JobDebugger.types[type])
                ||null;
            if(window.DisplayLabels&&DisplayLabels.stepTypeLabel){
                return DisplayLabels.stepTypeLabel(type,meta||{}).toLowerCase();
            }
            if(meta&&meta.name)return String(meta.name).toLowerCase();
            return 'шаг';
        }
        return t.replace(/_/g,' ');
    },

    _statusMeta(status){
        const key=String(status||'pending');
        const map={
            running:{variant:'running', cls:'omb-pill omoi-card__status-indicator omoi-card__status-indicator--running', text:'В работе'},
            done:{variant:'done', cls:'omb-pill omb-pill--ok omoi-card__status-indicator omoi-card__status-indicator--done', text:'Готово'},
            error:{variant:'error', cls:'omb-pill omb-pill--err omoi-card__status-indicator omoi-card__status-indicator--error', text:'Ошибка'},
            pending:{variant:'pending', cls:'omb-pill omoi-card__status-indicator omoi-card__status-indicator--pending', text:'Ожидание'},
            paused:{variant:'paused', cls:'omb-pill omb-pill--warn omoi-card__status-indicator omoi-card__status-indicator--paused', text:'Пауза'},
            cancelled:{variant:'cancelled', cls:'omb-pill omb-pill--mute omoi-card__status-indicator omoi-card__status-indicator--cancelled', text:'Отменён'},
        };
        return map[key]||{variant:'unknown', cls:'omb-pill omb-pill--mute omoi-card__status-indicator omoi-card__status-indicator--unknown', text:key};
    },

    renderCard(j){
        const status=String(j.status||'');
        const statusMeta=this._statusMeta(status);
        const progress=this._progress(j);
        const rep=j.total_repeats>1?' #'+j.repeat_num:'';
        const isErr=status==='error';
        const isRunning=status==='running';
        const isPending=status==='pending';
        const isPaused=status==='paused';
        const dt=j.created_at?Jobs._fmtTime(j.created_at):'';
        const creditsRaw=j&&('list_total_credits' in j)?j.list_total_credits:j?.total_credits;
        const credits=Number(creditsRaw)||0;
        const fmtCredits=(window.Wallet&&window.Wallet._fmtCredits)?window.Wallet._fmtCredits.bind(window.Wallet):(v=>{const n=Number(v||0);return Number.isInteger(n)?String(n):n.toFixed(2);});
        const creditsHtml=credits>0?(' · '+escHtml(fmtCredits(credits))+' ТК'):'';
        const jobId=escHtml(j.id||'');
        const rawTitle=j.task_name||'Job';
        const readableTitle=(window.DisplayLabels&&DisplayLabels.jobTitle)?DisplayLabels.jobTitle(j):rawTitle;
        const technicalHint=readableTitle!==rawTitle?'<span class="job-technical-hint">'+escHtml(rawTitle)+'</span>':'';
        const selected=this._selectedJobIds.has(String(j.id||''));
        const variantClass=' omoi-card--'+statusMeta.variant;
        const selectHtml='<label class="job-select" title="Выбрать запуск" data-job-select-wrap data-debugger-stop>'
            +'<input type="checkbox" data-job-select data-job-id="'+jobId+'" aria-label="Выбрать запуск" '+(selected?'checked':'')+'>'
            +'<span></span></label>';
        const errorText=isErr&&j.error?escHtml(String(j.error).slice(0,180)):'';
        // Stage 4/5: prefer the denormalized current_node_type/label from
        // listing summary (no per-row JSON parse) over the legacy fields
        // that were never populated. Falls back to legacy if not present.
        const showNodeMeta=status==='running'||status==='paused'||status==='cancelled'||status==='error';
        const hasCurrentNodeMeta=!!(String(j.current_node_label||'').trim()||String(j.current_node_type||'').trim());
        const stepTypeLabel=showNodeMeta
            ?this._stepTypeLabel(j.current_node_type||j.last_step_type||j.step_type||'')
            :(hasCurrentNodeMeta?'':this._stepTypeLabel(j.last_step_type||j.step_type||''));
        const nodeLabelText=showNodeMeta?String(j.current_node_label||'').trim():'';
        const cardInfoHtml='<div class="card-info"><div class="card-title">'+escHtml(readableTitle)+rep+'</div>'
            +'<div class="card-subtitle">'+escHtml(dt)+creditsHtml+technicalHint+'</div></div>';
        const statusIndicatorHtml='<span class="'+statusMeta.cls+'" data-status="'+escHtml(status)+'" title="Статус: '+escHtml(statusMeta.text)+'" aria-label="Статус: '+escHtml(statusMeta.text)+'"><span class="led"></span></span>';
        const screenHtml=
            '<div class="omoi-card__screen">'
            +'<div class="omoi-card__screen-head">'
            +cardInfoHtml
            +'</div>'
            +'<div class="omoi-card__progress-track"><div class="omoi-card__progress-fill" data-progress-bar data-style="width:'+progress.percent+'%"></div></div>'
            +'<div class="omoi-card__step" data-step-label>'+(progress.label?(escHtml(progress.label)+' выполнено'+(progress.totalSuffix?escHtml(progress.totalSuffix):'')):(progress.totalSuffix?escHtml(progress.totalSuffix):''))+'</div>'
            +'<div class="omoi-card__step-type" data-step-type'+(stepTypeLabel||nodeLabelText?'':' hidden')+'>'
                +escHtml(nodeLabelText?nodeLabelText:stepTypeLabel)
                +(nodeLabelText&&stepTypeLabel?'<span class="omoi-card__step-type-tag"> · '+escHtml(stepTypeLabel)+'</span>':'')
            +'</div>'
            +'<div class="omoi-card__preview" data-step-preview hidden></div>'
            +'<div class="omoi-card__error" data-error-text'+(errorText?'':' hidden')+'>'+errorText+'</div>'
            +'</div>';
        return '<div class="job-card omoi-card'+variantClass+(isErr?' job-card-error':'')+(isPaused?' job-card-paused':'')+(selected?' job-card-selected':'')+'" data-job-id="'+jobId+'" data-job-action="detail">'
            +'<div class="omoi-card__head">'
            +selectHtml
            +statusIndicatorHtml
            +'</div>'
            +screenHtml
            +'<div class="card-actions">'
            +(isRunning?'<button class="btn btn-ghost btn-small" data-job-action="pause" data-job-id="'+jobId+'" title="Пауза" aria-label="Пауза"><span class="ui-code-icon" aria-hidden="true">PAUSE</span><span>Пауза</span></button>':'')
            +(isRunning||isPending?'<button class="btn btn-ghost btn-small" data-job-action="cancel" data-job-id="'+jobId+'" title="Остановить" aria-label="Остановить"><span class="ui-code-icon" aria-hidden="true">STOP</span><span>Стоп</span></button>':'')
            +(isPaused?'<button class="btn btn-ghost btn-small" data-job-action="resume" data-job-id="'+jobId+'" title="Запустить" aria-label="Запустить"><span class="ui-code-icon" aria-hidden="true">RUN</span><span>Запуск</span></button>':'')
            +(isErr?'<button class="btn btn-ghost btn-small" data-job-action="retry" data-job-id="'+jobId+'" title="Повторить" aria-label="Повторить">'+CGIcons.html('sync')+'<span>Повторить</span></button>':'')
            +(status!=='pending'?'<button class="btn btn-ghost btn-small" data-job-action="open-folder" data-job-id="'+jobId+'" title="Открыть папку" aria-label="Открыть папку">'+CGIcons.html('folder')+'<span>Папка</span></button>':'')
            +(isErr?'<button class="btn btn-ghost btn-small" data-job-action="debug" data-job-id="'+jobId+'" title="Открыть рабочую доску" aria-label="Открыть рабочую доску">'+CGIcons.html('tools')+'<span>Доска</span></button>':'')
            +'<button class="btn btn-ghost btn-small" data-job-action="delete" data-job-id="'+jobId+'" title="Удалить" aria-label="Удалить"><span class="ui-code-icon" aria-hidden="true">DEL</span><span>Удалить</span></button>'
            +'</div></div>';
    },

    applyStatusToCard(cardEl, patch){
        if(!cardEl||!patch)return;
        if(patch.status){
            const variants=['running','done','error','pending','paused','cancelled'];
            variants.forEach(v=>cardEl.classList.toggle('omoi-card--'+v, v===patch.status));
            cardEl.classList.toggle('job-card-error', patch.status==='error');
            cardEl.classList.toggle('job-card-paused', patch.status==='paused');
            const indicator=cardEl.querySelector('.omoi-card__status-indicator');
            if(indicator){
                const meta=this._statusMeta(patch.status);
                indicator.className=meta.cls;
                indicator.dataset.status=patch.status;
                indicator.setAttribute('title','Статус: '+meta.text);
                indicator.setAttribute('aria-label','Статус: '+meta.text);
                indicator.innerHTML='<span class="led"></span>';
            }
        }
    },

    renderSelectionBar(){
        return `<div class="job-download-bar" data-job-download-bar hidden>
            <div class="job-download-summary">
                <strong data-job-download-count>0</strong><span>выбрано</span>
                <span class="job-download-progress" data-batch-progress hidden></span>
            </div>
            <div class="job-download-actions">
                <button class="btn btn-ghost btn-small" data-job-action="batch-pause" hidden title="Поставить на паузу выбранные" aria-label="Пауза выбранных"><span class="ui-code-icon" aria-hidden="true">PAUSE</span><span>Пауза</span></button>
                <button class="btn btn-ghost btn-small" data-job-action="batch-resume" hidden title="Запустить выбранные" aria-label="Запустить выбранные"><span class="ui-code-icon" aria-hidden="true">RUN</span><span>Запустить</span></button>
                <button class="btn btn-ghost btn-small" data-job-action="batch-stop" hidden title="Остановить выбранные" aria-label="Остановить выбранные"><span class="ui-code-icon" aria-hidden="true">STOP</span><span>Остановить</span></button>
                <button class="btn btn-ghost btn-small" data-job-action="batch-retry" hidden title="Повторить ошибочные" aria-label="Повторить ошибочные">${CGIcons.html('sync')}<span>Повторить</span></button>
                <button class="btn btn-ghost btn-small" data-job-action="select-all" title="Выбрать все запуски" aria-label="Выбрать все запуски">${CGIcons.html('check-square')}<span>Выбрать все</span></button>
                <button class="btn btn-primary btn-small" data-job-action="download-selected" hidden title="Скачать ZIP">${CGIcons.html('folder')}<span>Скачать ZIP</span></button>
                <button class="btn btn-ghost btn-small btn-danger-text" data-job-action="batch-delete" hidden title="Удалить выбранные" aria-label="Удалить выбранные"><span class="ui-code-icon" aria-hidden="true">DEL</span><span>Удалить</span></button>
                <button class="btn btn-ghost btn-small" data-job-action="clear-selection" title="Снять выбор" aria-label="Снять выбор"><span class="ui-code-icon" aria-hidden="true">HIDE</span><span>Снять выбор</span></button>
            </div>
        </div>`;
    },

    toggleSelection(id, selected){
        const key=String(id||'');
        if(!key)return;
        if(selected)this._selectedJobIds.add(key);
        else this._selectedJobIds.delete(key);
        this._refreshSelectionChrome();
    },

    clearSelection(){
        this._selectedJobIds.clear();
        this._refreshSelectionChrome();
    },

    selectAllVisible(){
        const scope=this._activeScope()||document;
        const ids=Array.from(scope.querySelectorAll('[data-job-select]'))
            .map(el=>String(el.dataset.jobId||'')).filter(Boolean);
        ids.forEach(id=>this._selectedJobIds.add(id));
        if(ids.length)this._selectionAnchorId=ids[ids.length-1];
        this._refreshSelectionChrome();
    },

    toggleProjectSelection(pid){
        const key=String(pid||'');
        if(!key)return;
        const safe=(typeof CSS!=='undefined'&&CSS.escape)?CSS.escape(key):key;
        const ids=Array.from(document.querySelectorAll('#dash-group-'+safe+' [data-job-select]'))
            .map(el=>String(el.dataset.jobId||'')).filter(Boolean);
        if(!ids.length)return;
        const allSelected=ids.every(id=>this._selectedJobIds.has(id));
        if(allSelected) ids.forEach(id=>this._selectedJobIds.delete(id));
        else ids.forEach(id=>this._selectedJobIds.add(id));
        this._refreshSelectionChrome();
    },

    _activeScope(){
        if(typeof document==='undefined')return null;
        const route=(typeof Router!=='undefined'&&Router&&Router.current)||'';
        if(route==='home')return document.querySelector('#dash-recent-jobs')||document;
        if(route==='project')return document.querySelector('#project-runs')||document;
        if(route==='task')return document.querySelector('#task-jobs')||document;
        return document;  // JSDOM-тесты, не загружающие router.js
    },

    _jobsByIdMap(){
        const route=(typeof Router!=='undefined'&&Router&&Router.current)||'';
        const taskJobs=(route==='task'&&typeof TaskView!=='undefined'&&Array.isArray(TaskView._jobs))?TaskView._jobs:null;
        const projectJobs=(route==='project'&&typeof ProjectView!=='undefined'&&Array.isArray(ProjectView.allJobs))?ProjectView.allJobs:null;
        const dashJobs=(typeof window!=='undefined'&&window.Dashboard&&Array.isArray(window.Dashboard._jobs))?window.Dashboard._jobs:[];
        const jobs=taskJobs||projectJobs||dashJobs;
        const map={};
        jobs.forEach(j=>{if(j&&j.id!=null)map[String(j.id)]=j;});
        return map;
    },

    _selectionStats(){
        const map=this._jobsByIdMap();
        let running=0,pending=0,paused=0,done=0,error=0;
        this._selectedJobIds.forEach(id=>{
            const s=map[String(id)]?.status||'';
            if(s==='running')running++;
            else if(s==='pending')pending++;
            else if(s==='paused')paused++;
            else if(s==='done')done++;
            else if(s==='error')error++;
        });
        return {total:this._selectedJobIds.size,running,pending,paused,done,error};
    },

    _selectionErrorCount(){
        return this._selectionStats().error;
    },

    _refreshSelectionChrome(){
        if(typeof document==='undefined')return;
        const scope=this._activeScope()||document;
        // Cleanup отсутствующих в активном scope id (удалённые/отфильтрованные запуски)
        const stale=[];
        this._selectedJobIds.forEach(id=>{
            const safe=(typeof CSS!=='undefined'&&CSS.escape)?CSS.escape(String(id)):String(id);
            if(!scope.querySelector('[data-job-id="'+safe+'"]'))stale.push(id);
        });
        stale.forEach(id=>this._selectedJobIds.delete(id));

        const count=this._selectedJobIds.size;
        scope.querySelectorAll('[data-job-select]').forEach(input=>{
            const id=String(input.dataset.jobId||'');
            input.checked=this._selectedJobIds.has(id);
            input.closest('.job-card')?.classList.toggle('job-card-selected', input.checked);
        });
        // 3-state header чекбоксы для проектов
        scope.querySelectorAll('[data-project-select]').forEach(input=>{
            const pid=String(input.dataset.projectId||'');
            const safe=(typeof CSS!=='undefined'&&CSS.escape)?CSS.escape(pid):pid;
            const ids=Array.from(scope.querySelectorAll('#dash-group-'+safe+' [data-job-select]'))
                .map(el=>String(el.dataset.jobId||''));
            const sel=ids.filter(id=>this._selectedJobIds.has(id)).length;
            input.checked=sel>0&&sel===ids.length;
            input.indeterminate=sel>0&&sel<ids.length;
        });
        const inFlight=this._batchInFlight===true;
        const hasSelection=count>0;
        const stats=this._selectionStats();
        const showPause=stats.running>0;
        const showResume=stats.paused>0;
        const showStop=stats.running+stats.pending+stats.paused>0;
        const showRetry=stats.error>0;
        const showDelete=hasSelection&&stats.running===0;
        const showDownload=stats.done>0;
        const visibility={
            'batch-pause':showPause,
            'batch-resume':showResume,
            'batch-stop':showStop,
            'batch-retry':showRetry,
            'batch-delete':showDelete,
            'download-selected':showDownload,
        };
        scope.querySelectorAll('[data-job-download-bar]').forEach(bar=>{
            bar.hidden=!hasSelection;
            const countEl=bar.querySelector('[data-job-download-count]');
            if(countEl)countEl.textContent=String(count);
            Object.keys(visibility).forEach(action=>{
                const btn=bar.querySelector('[data-job-action="'+action+'"]');
                if(!btn)return;
                btn.hidden=!visibility[action];
                btn.disabled=inFlight;
            });
            const selectAll=bar.querySelector('[data-job-action="select-all"]');
            if(selectAll)selectAll.disabled=inFlight;
            const clear=bar.querySelector('[data-job-action="clear-selection"]');
            if(clear)clear.disabled=inFlight;
        });
    },

    openDownloadDialog(){
        if(!this._selectedJobIds.size){Toast.warning('Выберите готовые запуски');return;}
        Modal.show(`<h2>скачать результаты</h2>
            <div class="download-layout-options" role="radiogroup" aria-label="Структура архива">
                <label class="download-layout-option">
                    <input type="radio" name="job-download-layout" value="folders" checked>
                    <span>по папкам</span>
                </label>
                <label class="download-layout-option">
                    <input type="radio" name="job-download-layout" value="flat">
                    <span>без папок</span>
                </label>
            </div>
            <div class="download-modal-actions">
                <button class="btn btn-primary" data-job-action="confirm-download-selected">${CGIcons.html('folder')}<span>скачать ZIP</span></button>
                <button class="btn btn-ghost" data-action="modal-hide">отмена</button>
            </div>`);
    },

    async confirmDownloadSelected(){
        const map=this._jobsByIdMap();
        const ids=Array.from(this._selectedJobIds).filter(id=>map[String(id)]?.status==='done');
        if(!ids.length){Toast.warning('Выберите готовые запуски');return;}
        const checked=document.querySelector('input[name="job-download-layout"]:checked');
        const layout=checked?.value==='flat'?'flat':'folders';
        const headers=new Headers({'Content-Type':'application/json'});
        if(typeof Auth!=='undefined'&&Auth.csrfToken)headers.set('X-CSRF-Token',Auth.csrfToken);

        const btn=document.querySelector('[data-job-action="confirm-download-selected"]');
        const original=btn?btn.innerHTML:'';
        let timer=null;
        if(btn){
            btn.disabled=true;
            btn.setAttribute('aria-busy','true');
            btn.innerHTML='<span class="btn-spinner" aria-hidden="true"></span><span data-zip-status>готовим архив…</span>';
            timer=setTimeout(()=>{
                const s=btn.querySelector('[data-zip-status]');
                if(s)s.textContent='может занять до минуты…';
            },8000);
        }
        this._batchInFlight=true;
        this._refreshSelectionChrome();

        try{
            let response;
            try{
                response=await fetch('/api/jobs/archive/download',{
                    method:'POST',
                    headers,
                    credentials:'same-origin',
                    body:JSON.stringify({job_ids:ids,layout})
                });
            }catch(e){
                Toast.error('Не удалось подключиться к серверу');
                return;
            }
            if(!response.ok){
                let data={};
                try{data=await response.json()}catch(e){}
                if(!data.error){
                    data.error=await this._downloadErrorMessage(response);
                }
                Toast.error(data.error||'Не удалось скачать архив');
                return;
            }
            const blob=await response.blob();
            const url=URL.createObjectURL(blob);
            const link=document.createElement('a');
            link.href=url;
            link.download=this._downloadName(response.headers.get('Content-Disposition'));
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(()=>URL.revokeObjectURL(url),1000);
            Modal.hide();
            Toast.success('Архив скачивается');
        } finally {
            if(timer)clearTimeout(timer);
            if(btn && document.body.contains(btn)){
                btn.disabled=false;
                btn.removeAttribute('aria-busy');
                btn.innerHTML=original;
            }
            this._batchInFlight=false;
            this._refreshSelectionChrome();
        }
    },

    _downloadName(disposition){
        const raw=String(disposition||'');
        const utf=raw.match(/filename\*=UTF-8''([^;]+)/i);
        if(utf){
            try{return decodeURIComponent(utf[1])}catch(e){}
        }
        const quoted=raw.match(/filename="?([^";]+)"?/i);
        return quoted?quoted[1]:'omoibot_results.zip';
    },

    async _downloadErrorMessage(response){
        let detail='';
        try{
            const text=await response.text();
            const title=(text.match(/<title>(.*?)<\/title>/i)||[])[1]||'';
            detail=(title||text.replace(/<[^>]+>/g,' ').replace(/\s+/g,' ').trim()).slice(0,160);
        }catch(e){}
        const status=[response.status,response.statusText].filter(Boolean).join(' ');
        if(detail&&status&&detail.toLowerCase()!==status.toLowerCase())return `${status}: ${detail}`;
        return status||detail||'Не удалось скачать архив';
    },

    _fmtTime(ts){
        if(!ts)return'';
        const d=new Date(ts*1000);
        const now=new Date();
        const pad=n=>String(n).padStart(2,'0');
        const time=pad(d.getHours())+':'+pad(d.getMinutes());
        const sameDay=d.toDateString()===now.toDateString();
        if(sameDay)return'сегодня '+time;
        const yesterday=new Date(now);yesterday.setDate(now.getDate()-1);
        if(d.toDateString()===yesterday.toDateString())return'вчера '+time;
        return pad(d.getDate())+'.'+pad(d.getMonth()+1)+' '+time;
    },

    async showDetail(id){
        Router.go('debugger',{jobId:id});
    },

    async openFolder(id){
        const safeId=encodeURIComponent(String(id));
        const r=await API.post('/api/jobs/'+safeId+'/open_folder');
        if(Array.isArray(r.files)){
            const fallback=r.files.length?r:await this._enrichFolderFallback(id,r);
            this.showFolderFallback(fallback);
            return;
        }
        if(this._isMissingRunDirResponse(r)){
            const fallback=await this._enrichFolderFallback(id,{
                ...r,
                files:[],
                message:'Локальная папка запуска недоступна, а зарегистрированные файлы результата не найдены.'
            });
            this.showFolderFallback(fallback);
            return;
        }
        if(r.ok){
            Toast.success('Папка открыта');
            return;
        }
        Toast.error(r.error||r.message||'Не удалось открыть папку');
    },

    _isMissingRunDirResponse(resp){
        if(!resp||typeof resp!=='object')return false;
        const msg=String(resp.error||resp.message||'').toLowerCase();
        return resp.http_status===404 && (msg.includes('directory not found')||msg.includes('папка')||msg.includes('директор'));
    },

    async _enrichFolderFallback(id, data={}){
        const existing=Array.isArray(data.files)?data.files:[];
        if(existing.length)return data;
        const job=await this._loadJobForFolderFallback(id);
        const files=this._folderFilesFromJob(job);
        if(!files.length)return {...data, files:existing};
        return {
            ...data,
            files,
            folder_name:data.folder_name||job.task_name||job.id||'папка запуска',
            message:'Локальная папка запуска недоступна. Файлы из результатов нод показаны ниже.'
        };
    },

    async _loadJobForFolderFallback(id){
        const jobId=String(id||'');
        if(typeof JobDebugger!=='undefined' && JobDebugger.job && String(JobDebugger.job.id||'')===jobId){
            return JobDebugger.job;
        }
        try{
            const job=await API.get('/api/jobs/'+encodeURIComponent(jobId)+'?repair_legacy=1');
            if(job&&String(job.id||'')===jobId)return job;
        }catch(e){}
        return {};
    },

    _folderFilesFromJob(job){
        const results=(job&&Array.isArray(job.step_results))?job.step_results:[];
        const done=results.filter(sr=>sr&&typeof sr==='object'&&sr.status==='done');
        const marked=done.filter(sr=>sr.client_output||sr.step_type==='client_output');
        const inputTypes=new Set(['batch_input','file_input','file_picker','input_files']);
        const output=done.filter(sr=>!inputTypes.has(String(sr.step_type||sr.type||'')));
        const source=[...marked, ...output.filter(sr=>!marked.includes(sr))];
        const files=[];
        const seen=new Set();
        source.forEach((sr,idx)=>{
            const values=this._folderResultValues(sr);
            values.forEach((entry,valueIdx)=>{
                const url=this._folderResultUrl(entry.value, job);
                if(!url||seen.has(url))return;
                seen.add(url);
                const name=this._folderResultName(entry.value, entry.name||sr.save_as||('result_'+(idx+1)+'_'+(valueIdx+1)), sr.type||sr.step_type||'');
                files.push({name, relative_path:name, size:0, url});
            });
        });
        return files;
    },

    _folderResultValues(sr){
        const out=[];
        const seen=new Set();
        const add=(value,name='')=>{
            if(typeof value!=='string'||!value||seen.has(value))return;
            seen.add(value);
            out.push({value,name});
        };
        const keys=['local_path','content','file_url','local_url','public_url','url','href','path','virtual_path','download_url','output_url','result_url'];
        keys.forEach(key=>add(sr[key]||'', sr.save_as||''));
        (Array.isArray(sr.pieces)?sr.pieces:[]).forEach(v=>add(v));
        (Array.isArray(sr.remote_files)?sr.remote_files:[]).forEach(remote=>{
            if(remote&&typeof remote==='object'){
                const name=remote.name||remote.filename||remote.disk_path||remote.original_name||remote.path||remote.virtual_path||'';
                keys.forEach(key=>add(remote[key]||'', name));
            }else add(remote);
        });
        return out;
    },

    _folderResultUrl(value, job){
        const raw=String(value||'').trim();
        if(!raw)return '';
        try{
            const logical=window.AppURL?window.AppURL.internalPath(raw):raw;
            if(/^(\/(?:uploads|images|videos|f|api\/jobs)\/)/i.test(logical))return window.AppURL?window.AppURL.resolve(raw):raw;
            if(/^https?:\/\//i.test(raw))return window.AppURL?window.AppURL.resolve(raw):raw;
            if(/^data:(?:image|video|audio)\//i.test(raw))return raw;
            const runDir=String(job&&job.run_dir||'').replace(/\/+$/,'');
            if(runDir&&raw.startsWith(runDir+'/')){
                const rel=raw.slice(runDir.length+1).split('/').map(encodeURIComponent).join('/');
                const url='/api/jobs/'+encodeURIComponent(String(job.id||''))+'/files/'+rel;
                return window.AppURL?window.AppURL.resolve(url):url;
            }
        }catch(_e){/* Reject disguised paths without aborting the folder render. */}
        return '';
    },

    _folderResultName(value, fallback, resultType=''){
        const raw=String(value||'').split('?')[0].split('#')[0];
        let name=String(fallback||'result').split('/').pop()||'result';
        const tail=raw.split('/').pop()||'';
        try{name=decodeURIComponent(tail)||name;}catch(e){name=tail||name;}
        if(!/\.[a-z0-9]{2,5}$/i.test(name)){
            const extMap={image:'.jpg',video:'.mp4',audio:'.mp3',text:'.txt'};
            name+=extMap[String(resultType||'').toLowerCase()]||'';
        }
        return name.replace(/[\\/:*?"<>|]+/g,'_').slice(0,96)||'result';
    },

    showFolderFallback(data){
        const files=data.files||[];
        const message=data.message||data.error||'Папку нельзя открыть напрямую из текущего режима.';
        const cards=files.length?files.map(f=>this._folderFileCard(f)).join(''):'<div class="folder-empty">В папке пока нет файлов.</div>';
        Modal.show(`<h2>папка запуска</h2>
            <div class="folder-browser">
                <p class="folder-message">${escHtml(message)}</p>
                <div class="folder-grid">${cards}</div>
                <div class="folder-footer">
                    <button class="btn btn-primary" data-action="modal-hide">готово</button>
                </div>
	            </div>`);
	        requestAnimationFrame(()=>this._hydrateVideoPreviews(document.getElementById('modal-content')||document));
	    },

    showSelectedFoldersFallback(entries){
        const list=Array.isArray(entries)?entries:[];
        const sections=list.map((entry)=>{
            const files=Array.isArray(entry.files)?entry.files:[];
            const title=entry.folder_name||entry.task_name||entry.job_id||'папка запуска';
            const message=entry.message||entry.error||'Папку нельзя открыть напрямую из текущего режима.';
            const cards=files.length?files.map(f=>this._folderFileCard(f)).join(''):'<div class="folder-empty">В папке пока нет файлов.</div>';
            return `<section class="folder-batch-section">
                <div class="folder-batch-title">${escHtml(title)}</div>
                <p class="folder-message">${escHtml(message)}</p>
                <div class="folder-grid">${cards}</div>
            </section>`;
        }).join('') || '<div class="folder-empty">Нет папок для просмотра.</div>';
        Modal.show(`<h2>папки выбранных запусков</h2>
            <div class="folder-browser folder-browser-batch">
                ${sections}
                <div class="folder-footer">
                    <button class="btn btn-primary" data-action="modal-hide">готово</button>
                </div>
            </div>`);
        requestAnimationFrame(()=>this._hydrateVideoPreviews(document.getElementById('modal-content')||document));
    },

    _folderFileCard(file){
        const rel=file.relative_path||file.name||'file';
        const name=file.name||rel.split('/').pop()||'file';
        const url=this._fileUrl(file.url);
        const downloadUrl=this._fileUrl(file.url,true);
        const kind=this._fileKind(rel);
        return `<div class="folder-card">
            ${this._folderPreview(kind,url,rel)}
            <div class="folder-card-body">
                <div class="folder-file-name" title="${escHtml(rel)}">${escHtml(rel)}</div>
                <div class="folder-file-meta">${escHtml(kind.label)} · ${this._fmtBytes(file.size||0)}</div>
                <div class="folder-card-actions">
                    <a class="btn btn-ghost btn-small" href="${escHtml(url)}" target="_blank" rel="noopener">открыть</a>
                    <a class="btn btn-ghost btn-small" href="${escHtml(downloadUrl)}" download="${escHtml(name)}">скачать</a>
                </div>
            </div>
        </div>`;
    },

    _folderPreview(kind,url,name){
        if(kind.type==='image'){
            return `<a class="folder-preview folder-preview-image" href="${escHtml(url)}" target="_blank" rel="noopener">
                <img src="${escHtml(url)}" alt="${escHtml(name)}" loading="lazy" data-folder-preview-image>
                <span class="folder-file-icon">IMG</span>
            </a>`;
	        }
	        if(kind.type==='video'){
	            return `<div class="folder-preview folder-preview-video">
	                <video src="${escHtml(url)}" controls preload="metadata" playsinline></video>
	            </div>`;
	        }
        if(kind.type==='audio'){
            return `<div class="folder-preview folder-preview-audio">
                <span class="folder-file-icon">AUD</span>
                <audio src="${escHtml(url)}" controls preload="metadata"></audio>
            </div>`;
        }
        return `<a class="folder-preview folder-preview-file" href="${escHtml(url)}" target="_blank" rel="noopener">
            <span class="folder-file-icon">${escHtml(kind.badge)}</span>
        </a>`;
    },

	    _fileKind(name){
	        return MediaUtils.fileKind(name);
	    },

	    _fileUrl(url,download=false){
	        return MediaUtils.withAuth(url,download);
	    },

	    _fmtBytes(bytes){
	        const n=Number(bytes)||0;
	        if(n<1024)return n+' Б';
	        if(n<1024*1024)return (n/1024).toFixed(1)+' КБ';
	        if(n<1024*1024*1024)return (n/1024/1024).toFixed(1)+' МБ';
	        return (n/1024/1024/1024).toFixed(1)+' ГБ';
	    },

	    _hydrateVideoPreviews(root=document){
	        MediaUtils.hydrateVideoPreviews(root);
	    },

	    _buildVideoThumb(video){
	        MediaUtils.buildVideoThumb(video);
	    },

	    copyLog(btn){
        const log=btn.parentElement;
        const text=Array.from(log.querySelectorAll('div')).map(d=>d.textContent).join('\n');
        navigator.clipboard.writeText(text).then(()=>{
            btn.textContent='OK Скопировано';
            setTimeout(()=>{btn.innerHTML=CGIcons.html('clipboard')+'<span>Копировать</span>'},1500);
        });
    },

    async retry(id){
        const r=await API.post('/api/jobs/'+encodeURIComponent(String(id))+'/retry');
        if(!r.ok){Toast.error(r.error||'Ошибка перезапуска');return;}
        Toast.success('RETRY запущен');
        const route=(typeof Router!=='undefined'&&Router&&Router.current)||'';
        if(route==='home'&&typeof Dashboard!=='undefined')Dashboard._refreshJobs?.();
        if(route==='project'&&typeof ProjectView!=='undefined')ProjectView.refreshRuns();
        if(route==='task'&&typeof TaskView!=='undefined')TaskView.loadJobs?.();
    },
    async retryFrom(id,step){
        const r=await API.post('/api/jobs/'+encodeURIComponent(String(id))+'/retry',{from_step:step});
        if(!r.ok){Toast.error(r.error||'Ошибка перезапуска');return;}
        if(Router.current==='task')TaskView.loadJobs();
        if(Router.current==='project') ProjectView.refreshRuns();
    },
    async pause(id){
        const r=await API.post('/api/jobs/'+encodeURIComponent(String(id))+'/pause');
        if(r.ok){Toast.info('PAUSE запрошена');}else{Toast.error(r.error||'Ошибка');}
    },
    async cancel(id){
        if(!await Dialogs.confirm('Отменить запуск?'))return;
        const r=await API.post('/api/jobs/'+encodeURIComponent(String(id))+'/cancel');
        if(r.ok){Toast.info('STOP запрошен');}else{Toast.error(r.error||'Ошибка');}
    },
    async resume(id){
        const r=await API.post('/api/jobs/'+encodeURIComponent(String(id))+'/resume');
        if(r.ok){Toast.success('RUN возобновлено');}else{Toast.error(r.error||'Ошибка');}
    },
    async _batchAction(action, ids, label){
        if(this._batchInFlight)return;
        if(!ids.length){Toast.warning('Нет применимых запусков');return;}
        this._batchInFlight=true;
        this._refreshSelectionChrome();
        const progressEl=(this._activeScope?.()||document).querySelector('[data-batch-progress]');
        const setProgress=(k)=>{
            if(progressEl){
                progressEl.hidden=false;
                progressEl.textContent=label+': '+k+' из '+ids.length;
            }
        };
        setProgress(0);
        let ok=0;
        let processed=0;
        const chunkSize=8;
        try{
            for(let i=0;i<ids.length;i+=chunkSize){
                const chunk=ids.slice(i,i+chunkSize);
                const results=await Promise.allSettled(chunk.map(id=>API.post('/api/jobs/'+encodeURIComponent(String(id))+'/'+action)));
                results.forEach(r=>{
                    processed++;
                    if(r.status==='fulfilled' && r.value && r.value.ok)ok++;
                });
                setProgress(processed);
            }
        } finally {
            this._batchInFlight=false;
            if(progressEl){progressEl.hidden=true; progressEl.textContent='';}
        }
        if(ok===ids.length) Toast.success(label+': '+ok+' из '+ids.length);
        else if(ok>0)       Toast.warning(label+': '+ok+' из '+ids.length+' (часть пропущена)');
        else                Toast.error(label+': не удалось');
        this.clearSelection();
    },
    async pauseSelected(){
        const map=this._jobsByIdMap();
        const ids=Array.from(this._selectedJobIds).filter(id=>map[String(id)]?.status==='running');
        return this._batchAction('pause', ids, 'PAUSE');
    },
    async resumeSelected(){
        const map=this._jobsByIdMap();
        const ids=Array.from(this._selectedJobIds).filter(id=>map[String(id)]?.status==='paused');
        return this._batchAction('resume', ids, 'RUN');
    },
    async cancelSelected(){
        const map=this._jobsByIdMap();
        const ids=Array.from(this._selectedJobIds).filter(id=>{
            const s=map[String(id)]?.status;
            return s==='running'||s==='pending'||s==='paused';
        });
        if(!ids.length){Toast.warning('Нет применимых запусков');return;}
        if(!await Dialogs.confirm('Отменить '+ids.length+' запусков?'))return;
        return this._batchAction('cancel', ids, 'STOP');
    },
    async retrySelected(){
        const map=this._jobsByIdMap();
        const ids=Array.from(this._selectedJobIds).filter(id=>map[String(id)]?.status==='error');
        if(!ids.length){Toast.warning('Нет ошибочных запусков');return;}
        return this._batchAction('retry', ids, 'RETRY');
    },
    async deleteSelected(){
        const map=this._jobsByIdMap();
        // Server returns 409 for active jobs; pre-filter only running locally,
        // pending may be claimed and will fail server-side — best-effort.
        const ids=Array.from(this._selectedJobIds).filter(id=>{
            const s=map[String(id)]?.status||'';
            return s!=='running';
        });
        if(!ids.length){Toast.warning('Нет применимых для удаления запусков');return;}
        if(!await Dialogs.confirm('Удалить '+ids.length+' запусков? Действие нельзя отменить.'))return;
        return this._batchDeleteAction(ids);
    },
    async _batchDeleteAction(ids){
        if(this._batchInFlight)return;
        if(!ids.length)return;
        this._batchInFlight=true;
        this._refreshSelectionChrome();
        const progressEl=(this._activeScope?.()||document).querySelector('[data-batch-progress]');
        const setProgress=(k)=>{
            if(progressEl){
                progressEl.hidden=false;
                progressEl.textContent='DEL: '+k+' из '+ids.length;
            }
        };
        setProgress(0);
        let ok=0;
        let processed=0;
        const chunkSize=8;
        try{
            for(let i=0;i<ids.length;i+=chunkSize){
                const chunk=ids.slice(i,i+chunkSize);
                const results=await Promise.allSettled(
                    chunk.map(id=>API.del('/api/jobs/'+encodeURIComponent(String(id))))
                );
                results.forEach(r=>{
                    processed++;
                    if(r.status==='fulfilled' && r.value && r.value.ok)ok++;
                });
                setProgress(processed);
            }
        } finally {
            this._batchInFlight=false;
            if(progressEl){progressEl.hidden=true; progressEl.textContent='';}
        }
        if(ok===ids.length) Toast.success('Удалено: '+ok+' из '+ids.length);
        else if(ok>0)       Toast.warning('Удалено: '+ok+' из '+ids.length+' (некоторые активные пропущены)');
        else                Toast.error('Не удалось удалить');
        this.clearSelection();
        if(typeof Router!=='undefined' && Router.current==='home') Dashboard._refreshJobs?.();
        if(typeof Router!=='undefined' && Router.current==='project') ProjectView.refreshRuns();
        if(typeof Router!=='undefined' && Router.current==='task') TaskView.loadJobs?.();
    },
    async openFolderSelected(){
        if(this._batchInFlight)return;
        const map=this._jobsByIdMap();
        const ids=Array.from(this._selectedJobIds).filter(id=>{
            const s=map[String(id)]?.status||'';
            return s!=='running'&&s!=='pending';
        });
        if(!ids.length){Toast.warning('Нет выбранных запусков');return;}
        this._batchInFlight=true;
        this._refreshSelectionChrome();
        const progressEl=(this._activeScope?.()||document).querySelector('[data-batch-progress]');
        const setProgress=(k)=>{
            if(progressEl){
                progressEl.hidden=false;
                progressEl.textContent='FOLDER: '+k+' из '+ids.length;
            }
        };
        setProgress(0);
        let ok=0,processed=0;
        const fallbacks=[];
        const chunkSize=8;
        try{
            for(let i=0;i<ids.length;i+=chunkSize){
                const chunk=ids.slice(i,i+chunkSize);
                const results=await Promise.allSettled(
                    chunk.map(id=>API.post('/api/jobs/'+encodeURIComponent(String(id))+'/open_folder').then(r=>({...r,job_id:id,task_name:map[String(id)]?.task_name||''})))
                );
                results.forEach(r=>{
                    processed++;
                    if(r.status==='fulfilled' && r.value && r.value.ok)ok++;
                    if(r.status==='fulfilled' && r.value && Array.isArray(r.value.files))fallbacks.push(r.value);
                    else if(r.status==='fulfilled' && this._isMissingRunDirResponse(r.value)){
                        fallbacks.push({
                            ...r.value,
                            files:[],
                            job_id:r.value?.job_id,
                            task_name:r.value?.task_name||map[String(r.value?.job_id||'')]?.task_name||'',
                            message:'Локальная папка запуска недоступна, а зарегистрированные файлы результата не найдены.'
                        });
                    }
                });
                setProgress(processed);
            }
        } finally {
            this._batchInFlight=false;
            if(progressEl){progressEl.hidden=true; progressEl.textContent='';}
        }
        if(fallbacks.length){
            this.showSelectedFoldersFallback(fallbacks);
            if(ok>0&&ok<ids.length) Toast.warning('Папок открыто: '+ok+' из '+ids.length+', остальные показаны ниже');
            else     Toast.info('Папки показаны ниже');
        }else if(ok===ids.length) Toast.success('Папок открыто: '+ok);
        else if(ok>0)       Toast.warning('Папок открыто: '+ok+' из '+ids.length);
        else                Toast.error('Не удалось открыть папки');
    },
    async del(id){
        if(!await Dialogs.confirm('Удалить запуск?'))return;
        const r=await API.del('/api/jobs/'+encodeURIComponent(String(id)));
        if(!r.ok){Toast.error(r.error||'Ошибка удаления');return;}
        Modal.hide();
        if(Router.current==='task')TaskView.loadJobs();
        if(Router.current==='project') ProjectView.refreshRuns();
        if(Router.current==='home')Dashboard.load();
        if(Router.current==='debugger')Router.closeDebugger();
    },

    // Fallback polling — основной реалтайм через SSE
    startPoll(){
        if(typeof Auth!=='undefined'&&!Auth.user)return;
        if(this.pollTimer)clearInterval(this.pollTimer);
        this.pollTimer=setInterval(()=>{
            if(typeof Auth!=='undefined'&&!Auth.user){
                clearInterval(this.pollTimer);
                this.pollTimer=null;
                return;
            }
            if(Router.current==='task')TaskView.loadJobs(false);
            if(Router.current==='project')ProjectView.refreshRuns();
            if(Router.current==='home')Dashboard._refreshJobs();
        },15000);
    }
};

/* web/js/modules.js */
/* ═══ API keys and authorizations ═══ */
const Modules={
    savedModules:[],
    templates:null,
    availablePlugins:null,
    integrations:{},
    _eventsBound:false,

    initEvents(){
        if(this._eventsBound)return;
        this._eventsBound=true;
        document.addEventListener('click',(event)=>{
            const target=event.target instanceof Element ? event.target.closest('[data-module-action]') : null;
            if(!target)return;
            event.preventDefault();
            event.stopPropagation();
            const action=target.dataset.moduleAction;
            const id=target.dataset.moduleId||'';
            const templateKey=target.dataset.templateKey||'';
            const presetKey=target.dataset.presetKey||'';
            const modelIdx=Number(target.dataset.modelIdx||0);
            const runner=window.ActionRunner||{run:(_target,fn)=>fn()};
            const run=(fn)=>runner.run(target,fn);
            if(action==='show-settings')this.showSettings(id);
            else if(action==='show-help')this.showHelp(id);
            else if(action==='open-help-link')this.openHelpLink(target.dataset.helpUrl||'');
            else if(action==='save-settings')runner.run(target,()=>this.saveSettings(id));
            else if(action==='test-yandex-disk')runner.run(target,()=>this.testYandexDisk());
            else if(action==='apply-template')runner.run(target,()=>this.applyTemplate(id,templateKey));
            else if(action==='switch-cat')this._switchCat(id,target.dataset.cat||'');
            else if(action==='add-model-template')this.addModelFromTemplate(id);
            else if(action==='add-model-manual')this.addModelManual(id);
            else if(action==='save-manual-model')run(()=>this.saveManualModel(id));
            else if(action==='edit-model')this.editModel(id,modelIdx);
            else if(action==='save-edit-model')run(()=>this.saveEditModel(id,modelIdx));
            else if(action==='remove-model')run(()=>this.removeModel(id,modelIdx));
            else if(action==='show-add-dialog')this.showAddDialog();
            else if(action==='add-from-preset')this.addFromPreset(presetKey);
            else if(action==='confirm-preset')runner.run(target,()=>this.confirmPreset(presetKey));
        });
    },

    _esc(value){
        return String(value ?? '')
            .replace(/&/g,'&amp;')
            .replace(/</g,'&lt;')
            .replace(/>/g,'&gt;')
            .replace(/"/g,'&quot;')
            .replace(/'/g,'&#39;');
    },
    _js(value){return this._esc(JSON.stringify(String(value??'')));},

    _clone(value){
        return JSON.parse(JSON.stringify(value ?? null));
    },

    _fieldId(key){
        return `ms-cfg-${String(key||'').replace(/[^a-zA-Z0-9_-]/g,'-')}`;
    },

    _renderOption(option, current){
        const isObject=option && typeof option==='object' && !Array.isArray(option);
        const value=isObject ? option.value : option;
        const label=isObject ? `${option.label ?? option.value ?? ''}` : option;
        return `<option value="${this._esc(value)}" ${String(current ?? '')===String(value ?? '')?'selected':''}>${this._esc(label)}</option>`;
    },

    _renderSettingField(setting,cfg,hasApiKey){
        const key=setting?.key||'';
        if(!key)return '';
        const type=setting.type||'text';
        const label=this._esc(setting.label||key);
        const fieldId=this._fieldId(key);
        const rawValue=cfg?.[key] ?? setting.default ?? '';
        const value=this._esc(rawValue);

        if(type==='password'){
            const clearId=`${fieldId}-clear`;
            return `<div class="form-group"><label class="form-label">${label}</label>
                <input class="form-input" id="${fieldId}" type="password" value="" placeholder="${hasApiKey&&key==='api_key'?'Ключ уже сохранён. Введите новый только если хотите заменить.':this._esc(setting.placeholder||'Введите значение')}">
                ${hasApiKey&&key==='api_key' ? '<div data-style="margin-top:6px;font-size:11px;color:var(--text-muted)">Текущий ключ хранится отдельно для вашего профиля.</div><label data-style="display:flex;align-items:center;gap:6px;margin-top:8px;font-size:12px;color:var(--text-secondary)"><input type="checkbox" id="'+clearId+'"> Удалить сохранённый ключ</label>' : ''}
            </div>`;
        }
        if(type==='textarea'){
            return `<div class="form-group"><label class="form-label">${label}</label>
                <textarea class="form-textarea" id="${fieldId}" rows="${this._esc(setting.rows||4)}" placeholder="${this._esc(setting.placeholder||'')}">${value}</textarea></div>`;
        }
        if(type==='select' || type==='icon_select'){
            const options=(setting.options||[]).map(opt=>this._renderOption(opt, rawValue)).join('');
            return `<div class="form-group"><label class="form-label">${label}</label>
                <select class="form-select" id="${fieldId}">${options}</select></div>`;
        }
        return `<div class="form-group"><label class="form-label">${label}</label>
            <input class="form-input" id="${fieldId}" type="${type==='number'?'number':'text'}" value="${value}" placeholder="${this._esc(setting.placeholder||'')}"></div>`;
    },

    async _ensureAvailablePlugins(){
        if(this.availablePlugins) return this.availablePlugins;
        try{
            const plugins=await API.get('/api/available_modules');
            this.availablePlugins=API.expectArray(plugins,'/api/available_modules');
        }catch(_e){
            API._contractError('/api/available_modules','запрос не выполнен');
            this.availablePlugins=[];
        }
        return this.availablePlugins;
    },

    _isServerOnlySetting(setting){
        const key=setting?.key||'';
        return ['app_path','command_template','category'].includes(key);
    },

    _connectionSettings(cfg){
        return (Array.isArray(cfg?.settings)?cfg.settings:[]).filter(setting=>!this._isServerOnlySetting(setting));
    },

    _hasKeySetting(cfg){
        return this._connectionSettings(cfg).some(setting=>setting.key==='api_key'||setting.type==='password');
    },

    _moduleById(id){
        return this.savedModules.find(m=>m.id===id);
    },

    _fieldIsSecret(field){
        return !!(field?.secret || field?.type==='password' || field?.key==='api_key');
    },

    _moduleHasSecret(module, key){
        const cfg=module?.config||{};
        if(key==='api_key') return !!(cfg._has_api_key||cfg._has_api_key===1||cfg.api_key);
        return !!(cfg[`_has_${key}`]||cfg[key]);
    },

    _credentialDefinitions(){
        return [
            {
                id:'kie',
                title:'API Генерации',
                icon:'generate',
                description:'Один ключ для изображений, видео, музыки и Suno.',
                moduleIds:['kieai','suno'],
                chips:['Генерация','Suno'],
                fields:[
                    {key:'api_key',label:'API ключ генерации',type:'password',secret:true,placeholder:'Вставьте ваш API ключ'},
                    {key:'base_url',label:'Base URL',type:'text',adminOnly:true,default:'https://api.kie.ai/api/v1'},
                ],
                help:{
                    title:'Как получить API ключ',
                    steps:[
                        'Откройте панель управления сервисом генерации и войдите в аккаунт.',
                        'Перейдите в раздел API Key, создайте новый ключ для omoibot.',
                        'Если доступна защита по лимитам или IP whitelist, включите ее для этого ключа.',
                        'Скопируйте ключ целиком и вставьте его в поле API ключ генерации.',
                        'Нажмите Сохранить. Этот же ключ используется для моделей генерации и Suno.'
                    ],
                    notes:[
                        'Ключ нельзя отправлять пользователям и публиковать в чатах.',
                        'Если лимит закончился или ключ отозван, генерации начнут падать с ошибкой авторизации.'
                    ],
                    links:[
                        {label:'Получить API ключ',url:'https://kie.ai/api-key'},
                    ],
                },
            },
            {
                id:'openrouter',
                title:'OpenRouter API',
                icon:'link',
                description:'LLM-шлюз: DeepSeek, Llama и другие модели через OpenAI-совместимый API.',
                moduleIds:['openrouter'],
                chips:['LLM','DeepSeek','Fallback'],
                fields:[
                    {key:'api_key',label:'OpenRouter API key',type:'password',secret:true,placeholder:'sk-or-v1-...'},
                    {key:'base_url',label:'Base URL',type:'text',adminOnly:true,default:'https://openrouter.ai/api/v1'},
                ],
                help:{
                    title:'Как получить OpenRouter API key',
                    steps:[
                        'Откройте openrouter.ai и войдите в аккаунт.',
                        'Перейдите в раздел Keys и нажмите Create Key.',
                        'Задайте имя (например omoibot) и при необходимости лимит расходов.',
                        'Скопируйте ключ целиком и вставьте его в поле OpenRouter API key.',
                        'Нажмите Сохранить. Ключ используется для DeepSeek и других моделей через OpenRouter.'
                    ],
                    notes:[
                        'OpenRouter тарифицирует по фактическому потреблению токенов модели.',
                        'Если лимит исчерпан или ключ отозван, бот автоматически переключится на следующую модель из цепочки fallback.'
                    ],
                    links:[
                        {label:'Получить OpenRouter API key',url:'https://openrouter.ai/settings/keys'},
                    ],
                },
            },
            {
                id:'deepseek',
                title:'DeepSeek API',
                icon:'brain',
                description:'Прямое подключение DeepSeek V4 Flash/Pro без OpenRouter.',
                moduleIds:['deepseek'],
                alwaysShow:true,
                chips:['LLM','V4 Flash','V4 Pro'],
                fields:[
                    {key:'api_key',label:'DeepSeek API key',type:'password',secret:true,placeholder:'sk-...'},
                    {key:'base_url',label:'Base URL',type:'text',adminOnly:true,default:'https://api.deepseek.com'},
                ],
                virtualModule:{
                    id:'deepseek',
                    name:'DeepSeek',
                    description:'Прямое подключение к DeepSeek API без OpenRouter',
                    icon:'D',
                    color:'var(--omb-stone)',
                    enabled:1,
                    config:{
                        provider_type:'openai_compatible',
                        category:'llm',
                        connection_type:'api',
                        auth_type:'bearer',
                        base_url:'https://api.deepseek.com',
                        settings:[
                            {key:'api_key',label:'API ключ (DeepSeek)',type:'password'},
                            {key:'base_url',label:'Base URL',type:'text',default:'https://api.deepseek.com'},
                            {key:'auth_type',label:'Авторизация',type:'select',default:'bearer',options:[{value:'bearer',label:'Bearer Token'}]},
                        ],
                        models:[],
                    },
                },
                help:{
                    title:'Как получить DeepSeek API key',
                    steps:[
                        'Откройте платформу DeepSeek и войдите в аккаунт.',
                        'Перейдите в раздел API Keys.',
                        'Создайте новый ключ для omoibot.',
                        'Скопируйте ключ целиком и вставьте его в поле DeepSeek API key.',
                        'Нажмите Сохранить. Агент будет использовать DeepSeek V4 Flash/Pro напрямую.'
                    ],
                    notes:[
                        'Base URL оставьте https://api.deepseek.com.',
                        'OpenRouter для DeepSeek больше не нужен, если здесь сохранён прямой ключ.'
                    ],
                    links:[
                        {label:'Открыть DeepSeek API Keys',url:'https://platform.deepseek.com/api_keys'},
                    ],
                },
            },
            {
                id:'mimo',
                title:'MiMo API',
                icon:'brain',
                description:'Прямое подключение MiMo 2.5 Pro через OpenAI-совместимый API.',
                moduleIds:['mimo'],
                alwaysShow:true,
                chips:['LLM','MiMo 2.5 Pro'],
                fields:[
                    {key:'api_key',label:'MiMo API key',type:'password',secret:true,placeholder:'tp-...'},
                    {key:'base_url',label:'Base URL',type:'text',adminOnly:true,default:'https://token-plan-sgp.xiaomimimo.com/v1'},
                ],
                virtualModule:{
                    id:'mimo',
                    name:'MiMo',
                    description:'Прямое подключение к MiMo API',
                    icon:'M',
                    color:'var(--omb-stone)',
                    enabled:1,
                    config:{
                        provider_type:'openai_compatible',
                        category:'llm',
                        connection_type:'api',
                        auth_type:'bearer',
                        base_url:'https://token-plan-sgp.xiaomimimo.com/v1',
                        settings:[
                            {key:'api_key',label:'API ключ (MiMo)',type:'password'},
                            {key:'base_url',label:'Base URL',type:'text',default:'https://token-plan-sgp.xiaomimimo.com/v1'},
                            {key:'auth_type',label:'Авторизация',type:'select',default:'bearer',options:[{value:'bearer',label:'Bearer Token'}]},
                        ],
                        models:[],
                    },
                },
                help:{
                    title:'Как получить MiMo API key',
                    steps:[
                        'Откройте панель MiMo и войдите в аккаунт.',
                        'Создайте API key для omoibot.',
                        'Скопируйте ключ целиком и вставьте его в поле MiMo API key.',
                        'Нажмите Сохранить. Агент будет использовать MiMo 2.5 Pro напрямую.'
                    ],
                    notes:[
                        'Base URL оставьте https://token-plan-sgp.xiaomimimo.com/v1.',
                        'Если ключ отозван или лимит исчерпан, бот переключится на следующий доступный fallback.'
                    ],
                },
            },
            {
                id:'elevenlabs',
                title:'ElevenLabs API',
                icon:'audio',
                description:'Озвучка, транскрибация и аудио-инструменты.',
                moduleIds:['voice_tools'],
                chips:['TTS','STT'],
                fields:[
                    {key:'api_key',label:'ElevenLabs API key',type:'password',secret:true,placeholder:'xi-...'},
                ],
                help:{
                    title:'Как получить ElevenLabs API key',
                    steps:[
                        'Откройте ElevenLabs и войдите в аккаунт.',
                        'В левом меню откройте Developers, затем вкладку API Keys.',
                        'Нажмите Create API Key, задайте имя вроде omoibot.',
                        'Выберите нужные права для озвучки, транскрибации и аудио-функций.',
                        'Скопируйте ключ сразу после создания и вставьте его в omoibot.'
                    ],
                    notes:[
                        'ElevenLabs показывает полный ключ только при создании, поэтому сохраните его сразу.',
                        'Для безопасности можно поставить лимит расходов/символов на ключ.'
                    ],
                    links:[
                        {label:'Получить ElevenLabs API key',url:'https://elevenlabs.io/app/settings/api-keys'},
                    ],
                },
            },
            {
                id:'heygen',
                title:'HeyGen API',
                icon:'avatar',
                description:'Аватары и говорящие видео.',
                moduleIds:['heygen'],
                chips:['Avatar','Video'],
                fields:[
                    {key:'api_key',label:'HeyGen API key',type:'password',secret:true,placeholder:'Ваш HeyGen ключ'},
                    {key:'base_url',label:'Base URL',type:'text',adminOnly:true,default:'https://api.heygen.com'},
                ],
                help:{
                    title:'Как получить HeyGen API key',
                    steps:[
                        'Откройте HeyGen и войдите в аккаунт.',
                        'Перейдите в API dashboard или настройки Direct API.',
                        'Создайте API key для omoibot и проверьте, что на API-балансе есть кредиты.',
                        'Скопируйте ключ и вставьте его в поле HeyGen API key.',
                        'Base URL оставьте без изменений, если HeyGen не дал другой адрес.'
                    ],
                    notes:[
                        'Для omoibot нужен Direct API key, не OAuth для MCP.',
                        'Если ключ рабочий, но генерация не стартует, чаще всего не хватает API-кредитов.'
                    ],
                    links:[
                        {label:'Получить HeyGen API key',url:'https://app.heygen.com/home?nav=API'},
                    ],
                },
            },
            {
                id:'syncso',
                title:'Sync.so API',
                icon:'avatar',
                description:'Облачный lip-sync по видео и аудио.',
                moduleIds:['syncso'],
                chips:['Lip-sync','Video'],
                fields:[
                    {key:'api_key',label:'Sync.so API key',type:'password',secret:true,placeholder:'sync_...'},
                    {key:'base_url',label:'Base URL',type:'text',adminOnly:true,default:'https://api.sync.so'},
                ],
                help:{
                    title:'Как получить Sync.so API key',
                    steps:[
                        'Откройте Sync.so и войдите в аккаунт.',
                        'Перейдите на страницу API Keys.',
                        'Создайте новый ключ для omoibot и скопируйте его.',
                        'Вставьте ключ в поле Sync.so API key.',
                        'Base URL оставьте https://api.sync.so.'
                    ],
                    notes:[
                        'Sync.so использует API key в заголовке x-api-key, omoibot подставит его сам.',
                        'Если lip-sync возвращает 401, проверьте, что ключ скопирован без пробелов.'
                    ],
                    links:[
                        {label:'Получить Sync.so API key',url:'https://sync.so/settings/api-keys'},
                    ],
                },
            },
            {
                id:'downloads',
                title:'VK и Instagram',
                icon:'link',
                description:'Авторизация для скачивания закрытых или ограниченных публикаций.',
                moduleIds:['url_downloader'],
                chips:['VK','Instagram'],
                optional:true,
                fields:[
                    {key:'vk_token',label:'VK access token',type:'password',secret:true,required:false,placeholder:'vk1.a...'},
                    {key:'instagram_cookies',label:'Instagram cookies.txt',type:'textarea',secret:true,required:false,rows:6,placeholder:'# Netscape HTTP Cookie File'},
                ],
                help:{
                    title:'Как подключить VK и Instagram',
                    steps:[
                        'Для открытых ссылок авторизация может быть не нужна.',
                        'Для VK создайте приложение VK ID или используйте токен сообщества, если скачиваете материалы своего сообщества.',
                        'Выдайте токену права, которые нужны для ваших ссылок: wall, photos, video, docs или доступ к сообществу.',
                        'Скопируйте VK access token в omoibot.',
                        'Для Instagram войдите в свой аккаунт в браузере и экспортируйте cookies в формате Netscape cookies.txt.',
                        'Вставьте весь текст cookies.txt в поле Instagram cookies.txt.'
                    ],
                    notes:[
                        'Cookies Instagram работают от имени вашего аккаунта, поэтому не передавайте их другим людям.',
                        'Если Instagram начал отдавать ошибки, обычно нужно обновить cookies после повторного входа.'
                    ],
                    links:[
                        {label:'Получить VK token',url:'https://dev.vk.com/ru/api/access-token/getting-started'},
                    ],
                },
            },
            {
                id:'yandex_disk',
                title:'Яндекс.Диск',
                icon:'cloud',
                description:'Папка для входящих файлов и выгрузки результатов. Локальные копии живут 24 часа.',
                moduleIds:[],
                customIntegration:'yandex_disk',
                chips:['Storage','Outputs'],
                optional:true,
                fields:[
                    {key:'enabled',label:'Выгружать результаты генерации на Яндекс.Диск',type:'checkbox'},
                    {key:'oauth_token',label:'OAuth токен Яндекс.Диска',type:'password',secret:true,placeholder:'y0_Ag...'},
                    {key:'folder_path',label:'Папка для результатов',type:'text',default:'/omoibot',placeholder:'/omoibot'},
                ],
                help:{
                    title:'Как получить OAuth токен Яндекс.Диска',
                    steps:[
                        'Откройте oauth.yandex.ru и создайте приложение omoibot.',
                        'В Redirect URI добавьте https://oauth.yandex.ru/verification_code.',
                        'Добавьте права cloud_api:disk.read, cloud_api:disk.write, cloud_api:disk.info и cloud_api:disk.app_folder.',
                        'Скопируйте ClientID приложения.',
                        'Откройте ссылку https://oauth.yandex.ru/authorize?response_type=token&client_id=ВАШ_CLIENT_ID.',
                        'Разрешите доступ и скопируйте значение access_token.',
                        'Вставьте токен в omoibot, укажите папку /omoibot, нажмите Проверить и Сохранить.'
                    ],
                    notes:[
                        'Нужен именно OAuth токен вашего Яндекс-аккаунта, не Client secret.',
                        'Файлы результатов доступны в omoibot 24 часа, после этого они удаляются. Подключите Яндекс.Диск, чтобы всегда иметь к ним доступ.'
                    ],
                    links:[
                        {label:'Получить OAuth токен Яндекс.Диска',url:'https://oauth.yandex.ru/'},
                    ],
                },
            },
        ];
    },

    _fallbackCredentialModule(def){
        if(!def?.virtualModule) return null;
        return this._clone(def.virtualModule);
    },

    _canManageModuleCredentials(){
        if(typeof Auth==='undefined') return false;
        if(typeof Auth.canOpenSettings==='function') return !!Auth.canOpenSettings();
        if(typeof Auth.isSuperAdmin==='function') return !!Auth.isSuperAdmin();
        if(typeof Auth.isAdmin==='function') return !!Auth.isAdmin();
        return false;
    },

    _credentialGroups(){
        const canManageModules=this._canManageModuleCredentials();
        return this._credentialDefinitions()
            .filter(def=>def.customIntegration || canManageModules)
            .map(def=>{
                let modules=def.moduleIds.map(id=>this._moduleById(id)).filter(Boolean);
                if(!modules.length && def.alwaysShow){
                    const fallback=this._fallbackCredentialModule(def);
                    if(fallback) modules=[fallback];
                }
                return {...def,modules};
            })
            .filter(group=>group.customIntegration || group.modules.length);
    },

    _groupById(id){
        return this._credentialGroups().find(group=>group.id===id || group.moduleIds.includes(id));
    },

    _primaryModule(group){
        return group.modules.find(m=>m.id===group.moduleIds[0]) || group.modules[0];
    },

    _fieldModules(group, field){
        const ids=field.moduleIds||group.moduleIds;
        return group.modules.filter(m=>ids.includes(m.id));
    },

    _fieldSaved(group, field){
        if(group.customIntegration==='yandex_disk'){
            const cfg=this.integrations.yandex_disk||{};
            if(field.key==='oauth_token') return !!cfg.connected;
            return cfg[field.key]!==undefined && cfg[field.key]!==null && cfg[field.key]!=='';
        }
        return this._fieldModules(group,field).some(m=>this._moduleHasSecret(m,field.key));
    },

    _fieldValue(group, field){
        if(group.customIntegration==='yandex_disk'){
            const cfg=this.integrations.yandex_disk||{};
            if(field.type==='checkbox') return !!cfg[field.key];
            return cfg[field.key] ?? field.default ?? '';
        }
        const module=this._fieldModules(group,field)[0] || this._primaryModule(group);
        const cfg=module?.config||{};
        return cfg[field.key] ?? field.default ?? '';
    },

    _groupStatus(group){
        if(group.customIntegration==='yandex_disk'){
            const cfg=this.integrations.yandex_disk||{};
            if(!cfg.connected) return {label:'нужен токен', className:'connection-status-warn'};
            if(!cfg.enabled) return {label:'подключено, выкл.', className:'connection-status-muted'};
            return {label:'подключено', className:'connection-status-ok'};
        }
        const secretFields=group.fields.filter(field=>this._fieldIsSecret(field));
        const savedCount=secretFields.filter(field=>this._fieldSaved(group,field)).length;
        if(group.optional){
            if(!savedCount) return {label:'без авторизации', className:'connection-status-muted'};
            if(savedCount<secretFields.length) return {label:'частично', className:'connection-status-warn'};
            return {label:'подключено', className:'connection-status-ok'};
        }
        const required=secretFields.filter(field=>field.required!==false);
        const missing=required.filter(field=>!this._fieldSaved(group,field));
        if(missing.length) return {label:'нужен ключ', className:'connection-status-warn'};
        return {label:'подключено', className:'connection-status-ok'};
    },

    _groupChips(group){
        return group.chips || group.modules.map(m=>m.name).filter(Boolean);
    },

    _helpGroup(id){
        return this._groupById(id) || this._credentialDefinitions().find(group=>group.id===id || group.moduleIds?.includes(id));
    },

    showHelp(id){
        const group=this._helpGroup(id);if(!group?.help)return;
        const help=group.help;
        const steps=(help.steps||[]).map(step=>`<li>${this._esc(step)}</li>`).join('');
        const notes=(help.notes||[]).map(note=>`<li>${this._esc(note)}</li>`).join('');
        const links=(help.links||[]).map(link=>{
            const url=(typeof MediaUtils!=='undefined')?MediaUtils.safeUrl(link.url,{allowData:false}):'';
            if(!url)return '';
            return `<button class="credential-help-link btn btn-ghost btn-small" data-module-action="open-help-link" data-help-url="${this._esc(url)}">${this._esc(link.label)}</button>`;
        }).filter(Boolean).join('');
        Modal.show(`<div class="credential-help">
            <h2>${this._esc(group.icon||'?')} ${this._esc(help.title||group.title)}</h2>
            ${steps?`<ol class="credential-help-steps">${steps}</ol>`:''}
            ${notes?`<div class="credential-help-notes"><div class="credential-help-subtitle">важно</div><ul>${notes}</ul></div>`:''}
            ${links?`<div class="credential-help-links">${links}</div>`:''}
            <div class="credential-help-actions">
                <button class="btn btn-primary" data-module-action="show-settings" data-module-id="${this._esc(group.id)}">Настроить</button>
                <button class="btn btn-ghost" data-action="modal-hide">Закрыть</button>
            </div>
        </div>`);
    },

    openHelpLink(url){
        const safe=(typeof MediaUtils!=='undefined')?MediaUtils.safeUrl(url,{allowData:false}):'';
        if(!safe)return;
        window.open(safe,'_blank','noopener');
    },

    _isUserConnection(m){
        const cfg=m?.config||{};
        if(['agent','generator'].includes(m?.id)) return false;
        if(this._hasKeySetting(cfg)) return true;
        if(cfg.connection_type==='api'||cfg.connection_type==='oauth') return true;
        if((cfg.provider_type||'')!=='cli' && (cfg.base_url||cfg.api_url)) return true;
        return false;
    },

    _connectionStatus(m){
        const cfg=m.config||{};
        const enabled=m.enabled===1||m.enabled===true;
        const hasKey=!!(cfg._has_api_key||cfg.api_key);
        const needsKey=this._hasKeySetting(cfg) || cfg.connection_type==='api' || (cfg.provider_type||'')!=='cli';
        if(!enabled) return {label:'выключено', className:'connection-status-muted'};
        if(needsKey && !hasKey) return {label:'нужен ключ', className:'connection-status-warn'};
        return {label:'подключено', className:'connection-status-ok'};
    },

    _connectionChips(m){
        const cfg=m.config||{};
        const chips=[];
        if(cfg.connection_type==='oauth') chips.push('OAuth');
        else if(cfg.connection_type==='api'||this._hasKeySetting(cfg)) chips.push('API key');
        if(cfg.base_url||cfg.api_url) chips.push('URL');
        return chips;
    },

    _renderConnectionCard(group){
        const status=this._groupStatus(group);
        const chips=this._groupChips(group);
        return `<div class="connection-card">
            <div class="connection-card-head">
                <div class="connection-icon">${CGIcons.maybe(group.icon||'tools',{fallback:'tools'})}</div>
                <div class="connection-info">
                    <div class="connection-title">${this._esc(group.title||'API')}</div>
                    <div class="connection-desc">${this._esc(group.description||'')}</div>
                </div>
            </div>
            <span class="connection-status ${status.className}">${this._esc(status.label)}</span>
            ${chips.length?`<div class="connection-meta">${chips.map(chip=>`<span class="connection-chip">${this._esc(chip)}</span>`).join('')}</div>`:''}
            <div class="connection-actions">
                ${group.help?`<button class="btn btn-ghost btn-small connection-help-btn" data-module-action="show-help" data-module-id="${this._esc(group.id)}" title="Как получить API">?</button>`:''}
                <button class="btn btn-primary btn-small" data-module-action="show-settings" data-module-id="${this._esc(group.id)}">Настроить</button>
            </div>
        </div>`;
    },

    async load(){
        const [modules,yandexDisk]=await Promise.all([
            API.get('/api/modules'),
            API.get('/api/integrations/yandex_disk').catch(()=>({})),
        ]);
        this.savedModules=Array.isArray(modules)?modules:[];
        this.integrations.yandex_disk=yandexDisk||{};
        const g=document.getElementById('modules-grid');
        if(!g)return;
        const connections=this._credentialGroups();
        if(!connections.length){
            g.innerHTML='<div class="empty-state"><div class="empty-state-icon">'+CGIcons.html('tools')+'</div><div class="empty-state-text">Нет доступных API и авторизаций</div></div>';
            return;
        }
        g.innerHTML=connections.map(group=>this._renderConnectionCard(group)).join('');
    },

    async toggle(id,state){
        const m=this.savedModules.find(x=>x.id===id);if(!m)return;
        m.enabled=state;await API.post('/api/modules',m);invalidateStepTypes();this.load();
    },

    // ─── Settings ───

    showSettings(id){
        const group=this._groupById(id);if(!group)return;
        const status=this._groupStatus(group);
        const serviceList=this._groupChips(group).map(chip=>`<span class="connection-chip">${this._esc(chip)}</span>`).join('');
        const fieldsHtml=group.fields
            .filter(field=>!field.adminOnly || Auth.isAdmin?.())
            .map(field=>this._renderCredentialField(group,field)).join('');

        Modal.show(`<div class="connection-modal-title">
                <h2>${CGIcons.inline(group.icon||'tools',{fallback:'tools'})} ${this._esc(group.title)}</h2>
                ${group.help?`<button class="btn btn-ghost btn-small connection-help-btn" data-module-action="show-help" data-module-id="${this._esc(group.id)}" title="Как получить API">?</button>`:''}
            </div>
            <div class="connection-modal-status">
                <strong class="connection-status ${status.className}">${this._esc(status.label)}</strong>
                <div class="connection-meta">${serviceList}</div>
            </div>
            ${fieldsHtml}
            <div data-style="display:flex;gap:8px;margin-top:12px">
                <button class="btn btn-primary" data-module-action="save-settings" data-module-id="${this._esc(group.id)}">Сохранить</button>
                ${group.customIntegration==='yandex_disk'?`<button class="btn btn-ghost" data-module-action="test-yandex-disk" type="button">Проверить</button>`:''}
                <button class="btn btn-ghost" data-action="modal-hide">Отмена</button></div>`);
    },

    _renderCredentialField(group, field){
        const key=field.key||'';
        const fieldId=this._fieldId(`${group.id}-${key}`);
        const label=this._esc(field.label||key);
        const hasSaved=this._fieldSaved(group,field);
        const placeholder=this._esc(hasSaved&&this._fieldIsSecret(field)?'Уже сохранено. Введите новое значение только для замены.':(field.placeholder||''));

        if(field.type==='textarea'){
            const clearId=`${fieldId}-clear`;
            return `<div class="form-group"><label class="form-label">${label}</label>
                <textarea class="form-textarea" id="${fieldId}" rows="${this._esc(field.rows||4)}" placeholder="${placeholder}"></textarea>
                ${hasSaved?'<label data-style="display:flex;align-items:center;gap:6px;margin-top:8px;font-size:12px;color:var(--text-secondary)"><input type="checkbox" id="'+clearId+'"> Удалить сохранённое значение</label>':''}
            </div>`;
        }
        if(field.type==='select'){
            const current=this._fieldValue(group,field);
            const options=(field.options||[]).map(opt=>this._renderOption(opt,current)).join('');
            return `<div class="form-group"><label class="form-label">${label}</label>
                <select class="form-select" id="${fieldId}">${options}</select></div>`;
        }
        if(field.type==='checkbox'){
            const checked=this._fieldValue(group,field)?'checked':'';
            return `<div class="form-group">
                <label data-style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-primary)">
                    <input type="checkbox" id="${fieldId}" ${checked}> ${label}
                </label>
            </div>`;
        }
        if(this._fieldIsSecret(field)){
            const clearId=`${fieldId}-clear`;
            return `<div class="form-group"><label class="form-label">${label}</label>
                <input class="form-input" id="${fieldId}" type="password" value="" placeholder="${placeholder}">
                ${hasSaved?'<label data-style="display:flex;align-items:center;gap:6px;margin-top:8px;font-size:12px;color:var(--text-secondary)"><input type="checkbox" id="'+clearId+'"> Удалить сохранённое значение</label>':''}
            </div>`;
        }
        const value=this._esc(this._fieldValue(group,field));
        return `<div class="form-group"><label class="form-label">${label}</label>
            <input class="form-input" id="${fieldId}" type="${field.type==='number'?'number':'text'}" value="${value}" placeholder="${this._esc(field.placeholder||'')}"></div>`;
    },

    async saveSettings(id){
        const group=this._groupById(id);if(!group)return;
        if(group.customIntegration==='yandex_disk'){
            const payload={};
            group.fields.forEach(field=>{
                const key=field.key||'';
                const el=document.getElementById(this._fieldId(`${group.id}-${key}`));
                if(!key || !el) return;
                if(field.type==='checkbox'){
                    payload[key]=!!el.checked;
                    return;
                }
                if(this._fieldIsSecret(field)){
                    const value=(el.value||'').trim();
                    const clearEl=document.getElementById(`${this._fieldId(`${group.id}-${key}`)}-clear`);
                    if(value) payload[key]=value;
                    if(clearEl?.checked && !value) payload.clear_token=true;
                    return;
                }
                payload[key]=el.value;
            });
            const resp=await API.post('/api/integrations/yandex_disk',payload);
            if(resp.error){Toast.error(resp.error);return}
            this.integrations.yandex_disk=resp;
            Modal.hide();this.load();
            Toast.success('Яндекс.Диск сохранён');
            return;
        }
        const requests=group.modules.map(module=>{
            const payload={...module,config:{...(module.config||{})}};
            group.fields.forEach(field=>{
                if(field.adminOnly && !Auth.isAdmin?.()) return;
                if(!this._fieldModules(group,field).some(m=>m.id===module.id)) return;
                const key=field.key||'';
                const el=document.getElementById(this._fieldId(`${group.id}-${key}`));
                if(!key || !el) return;
                if(this._fieldIsSecret(field)){
                    const value=(el.value||'').trim();
                    const clearEl=document.getElementById(`${this._fieldId(`${group.id}-${key}`)}-clear`);
                    if(value) payload.config[key]=value;
                    if(clearEl?.checked && !value) payload.config[`__clear_${key}`]=true;
                    return;
                }
                payload.config[key]=el.value;
            });
            return API.post('/api/modules',payload);
        });
        let responses=[];
        try{
            responses=await Promise.all(requests);
        }catch(e){
            Toast.error(e?.message||'Не удалось сохранить настройки');
            return;
        }
        const failed=responses.find(resp=>resp?.error || resp?.ok===false || (resp?.http_status&&resp.http_status>=400));
        if(failed){
            Toast.error(failed.error||'Не удалось сохранить настройки');
            return;
        }
        invalidateStepTypes();Modal.hide();this.load();
        Toast.success('Настройки сохранены');
    },

    async testYandexDisk(){
        const group=this._groupById('yandex_disk');
        if(!group)return;
        const tokenEl=document.getElementById(this._fieldId('yandex_disk-oauth_token'));
        const folderEl=document.getElementById(this._fieldId('yandex_disk-folder_path'));
        const resp=await API.post('/api/integrations/yandex_disk/test',{
            oauth_token:(tokenEl?.value||'').trim(),
            folder_path:(folderEl?.value||'').trim(),
        });
        if(resp.error){Toast.error(resp.error);return}
        Toast.success(`Яндекс.Диск доступен: ${resp.folder_path||'папка готова'}`);
    },

    startAuthorization(id){
        const m=this.savedModules.find(x=>x.id===id);
        Toast.info(`${m?.name||'Сервис'}: авторизация через браузер пока не настроена`);
    },

    // ─── Add Model from Template ───

    async addModelFromTemplate(moduleId){
        if(!this.templates) this.templates=await API.get('/api/templates');
        const models=this.templates.models||{};
        const m=this.savedModules.find(x=>x.id===moduleId);
        const provType=m?.config?.provider_type||'async_task';
        const existing=new Set((m?.config?.models||[]).map(md=>md.id));

        const relevant=Object.entries(models).filter(([k,v])=>{
            if(v.hidden) return false;
            if(existing.has(v.id)) return false;
            if(provType==='async_task') return !v.endpoint;
            if(provType==='sync_api') return !!v.endpoint;
            return true;
        });

        if(!relevant.length){
            Modal.show(`<h2>Все модели подключены</h2><p>Нет доступных для добавления.</p>
                <button class="btn btn-ghost omb-mt-3" data-module-action="show-settings" data-module-id="${this._esc(moduleId)}"><span class="ui-code-icon" aria-hidden="true">BACK</span><span>Назад</span></button>`);
            return;
        }

        const cats={};
        relevant.forEach(([k,v])=>{const c=v.category||'Другое';if(!cats[c])cats[c]=[];cats[c].push([k,v])});
        const catNames=Object.keys(cats);

        const _inputBadge=(tpl)=>{
            const params=(tpl.params||[]).map(p=>p.key);
            const has=k=>params.includes(k);
            const parts=[];
            if(has('image_urls')||has('image_url')) parts.push(`${CGIcons.inline('image')} картинка`);
            if(has('audio_url')) parts.push(`${CGIcons.inline('audio')} аудио`);
            if(has('video_url')) parts.push(`${CGIcons.inline('video')} видео`);
            if(has('prompt')||has('text')) parts.push(`${CGIcons.inline('text')} текст`);
            const out={image:`// ${CGIcons.inline('image')}`,video:`// ${CGIcons.inline('video')}`,audio:`// ${CGIcons.inline('audio')}`,text:`// ${CGIcons.inline('chat')}`}[tpl.output_type]||'';
            return parts.length?`<div data-style="font-size:9px;color:var(--text-secondary);margin-top:4px">${parts.join(' + ')} ${out}</div>`:'';
        };

        const renderCat=(cat)=>(cats[cat]||[]).map(([key,tpl])=>{
            const credits=Number(tpl.credits||0);
            const creditHint=credits>0?`ориентир: ${credits} тк`:'';
            const priceHint=creditHint||(tpl.price?`ориентир: ${tpl.price}`:'');
            return `<div class="modal-tile" data-module-action="apply-template" data-module-id="${this._esc(moduleId)}" data-template-key="${this._esc(key)}">
                <div class="tile-icon">${CGIcons.maybe(tpl.icon||'generate',{fallback:'generate'})}</div>
                <div class="tile-title omb-fs-12">${this._esc(tpl.name)}</div>
                <div class="tile-desc">${this._esc(priceHint)}</div>
                ${_inputBadge(tpl)}
            </div>`;
        }).join('');

        const tabsHtml=catNames.map((c,i)=>`<button class="btn ${i===0?'btn-primary':'btn-ghost'} btn-small cat-tab" data-module-action="switch-cat" data-module-id="${this._esc(moduleId)}" data-cat="${this._esc(c)}" data-style="margin-right:4px">${this._esc(c)}</button>`).join('');

        Modal.show(`<h2>Каталог моделей</h2>
            <div data-style="margin-bottom:12px;display:flex;flex-wrap:wrap;gap:4px">${tabsHtml}</div>
            <div class="modal-tiles" id="cat-tiles">${renderCat(catNames[0])}</div>
            <button class="btn btn-ghost omb-mt-3" data-module-action="show-settings" data-module-id="${this._esc(moduleId)}"><span class="ui-code-icon" aria-hidden="true">BACK</span><span>Назад</span></button>`);
        this._catData={moduleId,cats,renderCat};
    },

    _switchCat(moduleId, cat){
        if(!this._catData) return;
        document.getElementById('cat-tiles').innerHTML=this._catData.renderCat(cat);
        document.querySelectorAll('.cat-tab').forEach(b=>{
            if(b.dataset.cat===cat){b.classList.add('btn-primary');b.classList.remove('btn-ghost')}
            else{b.classList.remove('btn-primary');b.classList.add('btn-ghost')}
        });
    },

    async applyTemplate(moduleId, templateKey){
        const tpl=this.templates.models[templateKey];if(!tpl)return;
        const m=this.savedModules.find(x=>x.id===moduleId);if(!m)return;
        if(!m.config.models) m.config.models=[];

        // Check for duplicate
        if(m.config.models.some(md=>md.id===tpl.id)){
            Toast.warning(`Модель ${tpl.name} уже добавлена`);return;
        }

        m.config.models.push({...tpl});
        await API.post('/api/modules',m);invalidateStepTypes();
        this.showSettings(moduleId);
    },

    // ─── Add Model Manually ───

    addModelManual(moduleId){
        Modal.show(`<h2>Новая модель</h2>
            <div class="form-group"><label class="form-label">Model ID *</label>
                <input class="form-input" id="nm-id" placeholder="nano-banana-2"></div>
            <div class="form-group"><label class="form-label">Название</label>
                <input class="form-input" id="nm-name" placeholder="Nano Banana 2"></div>
            <div class="form-group"><label class="form-label">Тип выхода</label>
                <select class="form-select" id="nm-otype">
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                    <option value="text">Text</option></select></div>
            <div class="form-group"><label class="form-label">Параметры (JSON)</label>
                <textarea class="form-textarea" id="nm-params" rows="6" placeholder='[{"key":"prompt","label":"Prompt","type":"textarea","required":true}]'>[]</textarea>
                <div data-style="font-size:11px;opacity:0.5;margin-top:4px">type: text, textarea, select, number, variable</div></div>
            <div class="omb-flex omb-gap-2 omb-mt-4">
                <button class="btn btn-primary" data-module-action="save-manual-model" data-module-id="${this._esc(moduleId)}">Добавить</button>
                <button class="btn btn-ghost" data-module-action="show-settings" data-module-id="${this._esc(moduleId)}"><span class="ui-code-icon" aria-hidden="true">BACK</span><span>Назад</span></button></div>`);
    },

    async saveManualModel(moduleId){
        const id=document.getElementById('nm-id')?.value?.trim();
        if(!id){Toast.warning('Model ID обязателен');return}
        let params=[];
        try{params=JSON.parse(document.getElementById('nm-params').value||'[]')}catch(e){Toast.warning('Ошибка JSON: '+e.message);return}

        const m=this.savedModules.find(x=>x.id===moduleId);if(!m)return;
        if(!m.config.models) m.config.models=[];

        m.config.models.push({
            id, name:document.getElementById('nm-name')?.value||id,
            output_type:document.getElementById('nm-otype').value,
            params
        });
        await API.post('/api/modules',m);invalidateStepTypes();
        this.showSettings(moduleId);
    },

    // ─── Edit Model ───

    editModel(moduleId, modelIdx){
        const m=this.savedModules.find(x=>x.id===moduleId);if(!m)return;
        const md=(m.config?.models||[])[modelIdx];if(!md)return;
        const paramsJson=this._esc(JSON.stringify(md.params||[],null,2));
        const moduleArg=this._js(moduleId);

        Modal.show(`<h2>${CGIcons.inline('tools')} ${this._esc(md.name||md.id)}</h2>
            <div class="form-group"><label class="form-label">Model ID</label>
                <input class="form-input" id="em-id" value="${this._esc(md.id||'')}"></div>
            <div class="form-group"><label class="form-label">Название</label>
                <input class="form-input" id="em-name" value="${this._esc(md.name||'')}"></div>
            <div data-style="display:flex;gap:8px">
                <div class="form-group omb-flex-1"><label class="form-label">Тип выхода</label>
                    <select class="form-select" id="em-otype">
                        <option value="image" ${md.output_type==='image'?'selected':''}>Image</option>
                        <option value="video" ${md.output_type==='video'?'selected':''}>Video</option>
                        <option value="audio" ${md.output_type==='audio'?'selected':''}>Audio</option>
                        <option value="text" ${md.output_type==='text'?'selected':''}>Text</option></select></div>
                <div class="form-group omb-flex-1"><label class="form-label">Кредиты</label>
                    <input class="form-input" id="em-credits" type="number" value="${this._esc(md.credits||0)}" placeholder="0"></div>
            </div>
            <div class="form-group"><label class="form-label">Цена (отображение)</label>
                <input class="form-input" id="em-price" value="${this._esc(md.price||'')}" placeholder="30 кр"></div>
            <div class="form-group"><label class="form-label">Параметры (JSON)</label>
                <textarea class="form-textarea" id="em-params" rows="8">${paramsJson}</textarea></div>
            <div class="omb-flex omb-gap-2 omb-mt-4">
                <button class="btn btn-primary" data-module-action="save-edit-model" data-module-id="${this._esc(moduleId)}" data-model-idx="${modelIdx}">Сохранить</button>
                <button class="btn btn-ghost" data-module-action="show-settings" data-module-id="${this._esc(moduleId)}"><span class="ui-code-icon" aria-hidden="true">BACK</span><span>Назад</span></button></div>`);
    },

    async saveEditModel(moduleId, modelIdx){
        const m=this.savedModules.find(x=>x.id===moduleId);if(!m)return;
        let params=[];
        try{params=JSON.parse(document.getElementById('em-params').value||'[]')}catch(e){Toast.warning('Ошибка JSON: '+e.message);return}

        m.config.models[modelIdx]={
            ...m.config.models[modelIdx],
            id:document.getElementById('em-id').value,
            name:document.getElementById('em-name').value,
            output_type:document.getElementById('em-otype').value,
            credits:parseFloat(document.getElementById('em-credits').value)||0,
            price:document.getElementById('em-price').value,
            params
        };
        await API.post('/api/modules',m);invalidateStepTypes();
        this.showSettings(moduleId);
    },

    async removeModel(moduleId, modelIdx){
        if(!await Dialogs.confirm('Удалить модель?'))return;
        const m=this.savedModules.find(x=>x.id===moduleId);if(!m)return;
        m.config.models.splice(modelIdx,1);
        await API.post('/api/modules',m);invalidateStepTypes();
        this.showSettings(moduleId);
    },

    // ─── Remove Module ───

    async remove(id){
        if(!await Dialogs.confirm('Удалить подключение?'))return;
        await API.del('/api/modules/'+id);invalidateStepTypes();this.availablePlugins=null;Modal.hide();this.load();
    },

    // ─── Add Provider ───

    editModels(id){
        const m=this.savedModules.find(x=>x.id===id);if(!m)return;
        const models=m.config?.models||[];
        const typeIcons={image:'image',video:'video',audio:'audio',text:'chat'};

        const modelsHtml=models.length?models.map((md,i)=>{
            const paramCount=(md.params||[]).length;
            return `<div data-style="display:flex;align-items:center;gap:10px;padding:10px 12px;background:var(--bg-card);margin-bottom:1px">
                <span data-style="font-size:18px">${CGIcons.html(typeIcons[md.output_type]||'generate')}</span>
                <div data-style="flex:1;min-width:0">
                    <div data-style="font-size:13px;font-weight:600">${this._esc(md.name||md.id)}</div>
                    <div data-style="font-size:10px;color:var(--text-muted)">${this._esc(md.id)} · ${this._esc(md.output_type||'?')} · ${paramCount} парам.${md.price?' · '+this._esc(md.price):''}</div>
                </div>
                <button class="btn btn-ghost btn-small" data-module-action="edit-model" data-module-id="${this._esc(id)}" data-model-idx="${i}" title="Редактировать"><span class="ui-code-icon" aria-hidden="true">EDIT</span></button>
                <button class="btn btn-ghost btn-small" data-module-action="remove-model" data-module-id="${this._esc(id)}" data-model-idx="${i}" title="Удалить"><span class="ui-code-icon" aria-hidden="true">DEL</span></button>
            </div>`}).join(''):'<div data-style="padding:16px;color:var(--text-muted);font-size:13px">Нет моделей. Добавьте из каталога или вручную.</div>';

        Modal.show(`<h2>${CGIcons.inline(m.icon||'tools',{fallback:'tools'})} ${this._esc(m.name)} — Модели</h2>
            <div data-style="margin-bottom:12px">${modelsHtml}</div>
            <div data-style="display:flex;gap:4px;margin-bottom:16px">
                <button class="btn btn-primary btn-small" data-module-action="add-model-template" data-module-id="${this._esc(id)}">+ Из каталога</button>
                <button class="btn btn-ghost btn-small" data-module-action="add-model-manual" data-module-id="${this._esc(id)}">+ Вручную</button>
            </div>
            <button class="btn btn-ghost" data-action="modal-hide">Закрыть</button>`);
    },

    async showAddDialog(){
        const groupTiles=this._credentialGroups().map(group=>{
            const status=this._groupStatus(group);
            return `<div class="modal-tile" data-module-action="show-settings" data-module-id="${this._esc(group.id)}">
                <div class="tile-icon">${CGIcons.maybe(group.icon||'tools',{fallback:'tools'})}</div>
                <div class="tile-title">${this._esc(group.title)}</div>
                <div class="tile-desc">${this._esc(status.label)}</div>
            </div>`;
        }).join('');

        Modal.show(`<h2>API и авторизации</h2>
            <div class="modal-tiles">
                ${groupTiles}
                ${Auth.isAdmin?.()?`<div class="modal-tile" data-module-action="add-from-preset" data-preset-key="custom"><div class="tile-icon">${CGIcons.html('tools')}</div><div class="tile-title">Свой API</div><div class="tile-desc">Произвольный провайдер</div></div>`:''}
            </div>`);
    },

    async addPlugin(pluginId){
        // Fetch manifest info from available_modules
        const plugins=await this._ensureAvailablePlugins();
        const p=plugins.find(x=>x.id===pluginId);
        if(!p){Modal.hide();return;}
        const data={
            id:p.id,
            name:p.name,
            description:p.description||'',
            icon:p.icon||'dashboard',
            color:p.color||'#666',
            enabled:1,
            config:this._clone(p.default_config)||{}
        };
        await API.post('/api/modules',data);invalidateStepTypes();this.availablePlugins=null;Modal.hide();this.load();
    },

    _pluginAlreadyAdded(pluginId){
        const m=this.savedModules.find(x=>x.id===pluginId);
        if(!m)return;
        Modal.show(`<h2>Сервис уже подключён</h2>
            <p data-style="color:var(--text-secondary)">${this._esc(m.name)} уже есть в настройках.</p>
            <div class="omb-flex omb-gap-2 omb-mt-4">
                <button class="btn btn-primary" data-module-action="show-settings" data-module-id="${this._esc(pluginId)}">Настройки</button>
                <button class="btn btn-ghost" data-action="modal-hide">OK</button></div>`);
    },

    showAddCLI(){
        Modal.show(`<h2>Локальное приложение</h2>
            <p data-style="color:var(--text-secondary);line-height:1.5">Локальные приложения настраиваются на сервере.</p>
            <button class="btn btn-ghost omb-mt-3" data-module-action="show-add-dialog">Назад</button>`);
    },

    _updateCliDynFields(){
        return;
    },

    async confirmCLI(){
        Toast.warning('Локальные приложения настраиваются на сервере');
    },

    async addFromPreset(presetKey){
        if(!this.templates) this.templates=await API.get('/api/templates');
        const preset=this.templates.providers[presetKey];if(!preset)return;

        Modal.show(`<h2>${CGIcons.inline(preset.icon||'tools',{fallback:'tools'})} ${this._esc(preset.name)}</h2>
            <p data-style="opacity:0.6;margin-bottom:16px">${this._esc(preset.description||'')}</p>
            <div class="form-group"><label class="form-label">API ключ *</label>
                <input class="form-input" id="np-key" type="password" placeholder="Ваш API ключ"></div>
            ${presetKey==='custom'?`
            <div class="form-group"><label class="form-label">Название</label>
                <input class="form-input" id="np-name" placeholder="My Provider"></div>
            <div class="form-group"><label class="form-label">Base URL</label>
                <input class="form-input" id="np-url" placeholder="https://api.example.com/v1"></div>`:''}
            <div data-style="display:flex;gap:8px;margin-top:20px">
                <button class="btn btn-primary" data-module-action="confirm-preset" data-preset-key="${this._esc(presetKey)}">Подключить</button>
                <button class="btn btn-ghost" data-module-action="show-add-dialog"><span class="ui-code-icon" aria-hidden="true">BACK</span><span>Назад</span></button></div>`);
    },

    async confirmPreset(presetKey){
        if(!this.templates) this.templates=await API.get('/api/templates');
        const preset=this.templates.providers[presetKey];if(!preset)return;
        const apiKey=document.getElementById('np-key')?.value?.trim();
        if(!apiKey){Toast.warning('API ключ обязателен');return}

        const name=presetKey==='custom'?(document.getElementById('np-name')?.value?.trim()||'Custom API'):preset.name;
        const baseUrl=presetKey==='custom'?(document.getElementById('np-url')?.value?.trim()||''):preset.base_url;
        const id=name.toLowerCase().replace(/\s+/g,'_').replace(/[^\w-]/g,'');

        // No models added by default — user picks from catalog
        const models=[];

        const data={id, name, description:preset.description||'', icon:preset.icon||'generate',
            color:preset.color||'#3e7ff0', enabled:1,
            config:{api_key:apiKey, base_url:baseUrl, auth_type:preset.auth_type||'bearer',
                provider_type:preset.provider_type||'async_task', models}};

        await API.post('/api/modules',data);invalidateStepTypes();this.availablePlugins=null;Modal.hide();this.load();
    },

};

/* web/js/wallet.js */
/* ═══ Wallet page ═══ */
const Wallet={
    _pendingPurchaseKey:'omoibot:wallet:pending-purchase:v1',
    _pendingPurchaseTimer:null,
    state:{
        wallet:null,
        fallbackFeatureEnabled:false,
        transactions:[],
        packages:[],
        filterType:'',
        loading:false,
        purchasing:null,
        checkingPayment:false,
    },
    _eventsBound:false,
    _typeLabels:{
        purchase:'покупка',
        grant:'бонус',
        spend:'списание за шаг',
        refund:'возврат',
        admin_adjust:'корректировка администратора',
    },

    _esc(value){
        return String(value??'').replace(/[&<>"']/g,(ch)=>({
            '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
        }[ch]));
    },
    _safeCheckoutUrl(value){
        const raw=String(value||'').trim();
        if(!raw)return '';
        try{
            const url=new URL(raw,window.location.origin);
            const host=url.hostname.toLowerCase();
            const trusted=host==='yookassa.ru'||host.endsWith('.yookassa.ru')||host==='yoomoney.ru'||host.endsWith('.yoomoney.ru');
            if(url.protocol==='https:'&&trusted)return url.href;
        }catch(_e){}
        return '';
    },
    _fmtCredits(value){
        const n=Number(value||0);
        return Number.isInteger(n)?String(n):n.toFixed(2);
    },
    _fmtMoney(minor,currency='RUB'){
        const amount=(Number(minor||0)/100).toLocaleString('ru-RU',{maximumFractionDigits:0});
        return `${amount} ${String(currency||'RUB').toUpperCase()}`;
    },
    _fmtDate(value){
        if(value==null)return '';
        const ts=Number(value);
        if(!Number.isFinite(ts))return '';
        const d=new Date(ts*1000);
        return d.toLocaleString('ru-RU',{dateStyle:'medium',timeStyle:'short'});
    },

    initEvents(){
        if(this._eventsBound)return;
        this._eventsBound=true;
        document.addEventListener('click',(event)=>{
            const target=event.target instanceof Element?event.target.closest('[data-wallet-action]'):null;
            if(!target)return;
            event.preventDefault();
            event.stopPropagation();
            const action=target.dataset.walletAction;
            const arg=target.dataset.arg||'';
            const runner=window.ActionRunner||{run:(_target,fn)=>fn()};
            if(action==='filter')this._setFilter(arg);
            else if(action==='buy')runner.run(target,()=>this._buyPackage(arg));
            else if(action==='check-payment')runner.run(target,()=>this._checkPendingPayment());
            else if(action==='reload')this.load();
            else if(action==='toggle-fallback')runner.run(target,()=>this._toggleFallback());
        });
    },

    async load(){
        this.initEvents();
        const root=document.getElementById('wallet-root');
        if(!root)return;
        if(this.state.loading)return;
        this.state.loading=true;
        try{
            const [wallet,txs,packages]=await Promise.all([
                API.get('/api/wallet'),
                API.get('/api/wallet/transactions?limit=50'),
                API.get('/api/billing/packages'),
            ]);
            if(!wallet||wallet.error){throw new Error(API._errorMessage(wallet,'Не удалось загрузить кошелёк'));}
            this.state.wallet=wallet.wallet||null;
            this.state.fallbackFeatureEnabled=!!wallet.fallback_feature_enabled;
            this.state.transactions=Array.isArray(txs?.transactions)?txs.transactions:[];
            this.state.packages=Array.isArray(packages?.packages)?packages.packages:[];
            this._render();
            this._watchPendingPurchase();
        }catch(e){
            root.innerHTML=`<div class="wallet-error">
                <div>${this._esc(e.message||'Не удалось загрузить кошелёк')}</div>
                <button class="btn btn-primary btn-small" data-wallet-action="reload" data-style="margin-top:10px">Повторить</button>
            </div>`;
        }finally{
            this.state.loading=false;
        }
    },

    _render(){
        const root=document.getElementById('wallet-root');
        if(!root)return;
        root.innerHTML=this._renderBalance()+this._renderPaymentCheck()+this._renderPackages()+this._renderTransactions();
    },

    _renderBalance(){
        const w=this.state.wallet||{};
        const fallbackOn=!!w.wallet_fallback_enabled;
        const fallbackFeature=this.state.fallbackFeatureEnabled;
        const fallbackToggle=fallbackFeature
            ? `<label class="wallet-toggle">
                <input type="checkbox" ${fallbackOn?'checked':''} data-wallet-action="toggle-fallback">
                разрешить тратить токены, когда нет своего ключа
            </label>`
            : `<div class="wallet-fallback-locked">
                Оплата генераций идёт через токены. Купите пакет ниже, чтобы пополнить баланс.
            </div>`;
        return `<section class="wallet-card wallet-balance-card">
            <div class="wallet-balance-row">
                <div class="wallet-balance-amount">
                    <div class="wallet-balance-label">Баланс</div>
                    <div class="wallet-balance-value">${this._fmtCredits(w.balance_credits)} <span class="wallet-balance-unit">токенов</span></div>
                </div>
                <div class="wallet-balance-meta">
                    <div class="wallet-lifetime">
                        куплено: ${this._fmtCredits(w.lifetime_purchased)} · потрачено: ${this._fmtCredits(w.lifetime_spent)} · бонусом: ${this._fmtCredits(w.lifetime_granted)}
                    </div>
                </div>
            </div>
            <div class="wallet-controls">${fallbackToggle}</div>
        </section>`;
    },

    _renderPaymentCheck(){
        const pending=this._pendingPurchase();
        if(!pending)return '';
        const checking=!!this.state.checkingPayment;
        return `<section class="wallet-card wallet-payment-check-card">
            <div class="wallet-payment-check-row">
                <div class="wallet-payment-check-copy">
                    <div class="wallet-section-title">Ждём подтверждение оплаты</div>
                    <div class="wallet-payment-check-hint">Если вы уже оплатили, сервер проверит платёж в YooKassa и зачислит токены.</div>
                </div>
                <button class="btn btn-primary btn-small" data-wallet-action="check-payment" ${checking?'disabled':''}>
                    ${checking?'Проверяем…':'Я оплатил'}
                </button>
            </div>
        </section>`;
    },

    _renderPackages(){
        const items=this.state.packages||[];
        if(!items.length){
            return `<section class="wallet-card wallet-packages-card">
                <div class="wallet-section-title">Пакеты токенов</div>
                <div class="wallet-empty">Пока нет доступных пакетов.</div>
            </section>`;
        }
        const tiles=items.map((p)=>{
            const buying=this.state.purchasing===p.id;
            const desc=p.description?`<div class="wallet-package-desc">${this._esc(p.description)}</div>`:'';
            return `<div class="wallet-package-tile">
                <div class="wallet-package-name">${this._esc(p.name)}</div>
                <div class="wallet-package-credits">${this._fmtCredits(p.credits)} токенов</div>
                <div class="wallet-package-price">${this._fmtMoney(p.price_minor,p.currency)}</div>
                ${desc}
                <button class="btn btn-primary btn-small" data-wallet-action="buy" data-arg="${this._esc(p.id)}" ${buying?'disabled':''}>
                    ${buying?'Открываем оплату…':'Купить'}
                </button>
            </div>`;
        }).join('');
        return `<section class="wallet-card wallet-packages-card">
            <div class="wallet-section-title">Пакеты токенов</div>
            <div class="wallet-package-grid">${tiles}</div>
        </section>`;
    },

    _renderTransactions(){
        const items=this.state.transactions||[];
        const filter=this.state.filterType||'';
        const types=Object.keys(this._typeLabels);
        const filterTabs=[''].concat(types).map((t)=>{
            const label=t?this._typeLabels[t]:'все';
            const cls=t===filter?'wallet-filter wallet-filter--active':'wallet-filter';
            return `<button class="${cls}" data-wallet-action="filter" data-arg="${this._esc(t)}">${this._esc(label)}</button>`;
        }).join('');
        const visible=filter?items.filter((tx)=>tx.type===filter):items;
        const rows=visible.length
            ? visible.map((tx)=>{
                const amount=Number(tx.amount_credits||0);
                const sign=amount>=0?'+':'';
                const cls=amount>=0?'wallet-tx-amount wallet-tx-amount--in':'wallet-tx-amount wallet-tx-amount--out';
                const typeLabel=this._typeLabels[tx.type]||tx.type;
                const meta=[
                    typeLabel,
                    tx.related_job_id?`запуск ${this._esc(tx.related_job_id)}`:'',
                ].filter(Boolean).join(' · ');
                return `<tr class="wallet-tx-row">
                    <td class="wallet-tx-when">${this._esc(this._fmtDate(tx.created_at))}</td>
                    <td class="wallet-tx-meta">
                        <div class="wallet-tx-meta-line">${meta}</div>
                        ${tx.description?`<div class="wallet-tx-meta-desc">${this._esc(tx.description)}</div>`:''}
                    </td>
                    <td class="${cls}">${sign}${this._fmtCredits(amount)}</td>
                    <td class="wallet-tx-balance">${this._fmtCredits(tx.balance_after)}</td>
                </tr>`;
            }).join('')
            : '<tr><td colspan="4" class="wallet-empty">Транзакций пока нет.</td></tr>';
        return `<section class="wallet-card wallet-transactions-card">
            <div class="wallet-section-title">История транзакций</div>
            <div class="wallet-filters">${filterTabs}</div>
            <div class="wallet-tx-table-wrap">
                <table class="wallet-tx-table">
                    <thead><tr><th>дата</th><th>детали</th><th>сумма</th><th>баланс после</th></tr></thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        </section>`;
    },

    _setFilter(value){
        this.state.filterType=value||'';
        this._render();
    },

    _rememberPendingPurchase(billingPaymentId){
        const id=String(billingPaymentId||'').trim();
        if(!id)return;
        try{
            window.sessionStorage.setItem(this._pendingPurchaseKey,JSON.stringify({
                billing_payment_id:id,
                auto_poll_until:Date.now()+120000,
                expires_at:Date.now()+(24*60*60*1000),
            }));
        }catch(_e){}
    },

    _pendingPurchase(){
        try{
            const raw=window.sessionStorage.getItem(this._pendingPurchaseKey);
            const pending=raw?JSON.parse(raw):null;
            if(!pending||!pending.billing_payment_id||Number(pending.expires_at||0)<=Date.now()){
                window.sessionStorage.removeItem(this._pendingPurchaseKey);
                return null;
            }
            return pending;
        }catch(_e){
            return null;
        }
    },

    _clearPendingPurchase(){
        if(this._pendingPurchaseTimer){
            window.clearTimeout(this._pendingPurchaseTimer);
            this._pendingPurchaseTimer=null;
        }
        try{window.sessionStorage.removeItem(this._pendingPurchaseKey)}catch(_e){}
    },

    _watchPendingPurchase(){
        const pending=this._pendingPurchase();
        if(!pending)return;
        const credited=(this.state.transactions||[]).some((tx)=>
            tx.type==='purchase'&&String(tx.related_payment_id||'')===String(pending.billing_payment_id)
        );
        if(credited){
            this._clearPendingPurchase();
            this._render();
            if(typeof Router!=='undefined'&&Router.refreshBalance)Router.refreshBalance();
            if(typeof Toast!=='undefined')Toast.success('Токены зачислены');
            return;
        }
        const autoPollUntil=Number(pending.auto_poll_until||pending.expires_at||0);
        if(autoPollUntil<=Date.now())return;
        if(this._pendingPurchaseTimer)return;
        this._pendingPurchaseTimer=window.setTimeout(()=>{
            this._pendingPurchaseTimer=null;
            this.load();
        },2500);
    },

    async _checkPendingPayment(){
        const pending=this._pendingPurchase();
        if(!pending||this.state.checkingPayment)return;
        this.state.checkingPayment=true;
        this._render();
        try{
            const paymentId=encodeURIComponent(String(pending.billing_payment_id));
            const resp=await API.post(`/api/billing/payments/${paymentId}/check`,{});
            if(!resp||resp.ok===false||resp.error){
                throw new Error(API._errorMessage(resp,'Не удалось проверить платёж'));
            }
            const status=String(resp.status||'pending').toLowerCase();
            if(resp.confirmed||status==='succeeded'){
                this._clearPendingPurchase();
                if(typeof Toast!=='undefined')Toast.success('Платёж подтверждён, токены зачислены');
                if(typeof Router!=='undefined'&&Router.refreshBalance)Router.refreshBalance();
                await this.load();
                return;
            }
            if(status==='canceled'){
                this._clearPendingPurchase();
                if(typeof Toast!=='undefined')Toast.error('Платёж отменён или не прошёл');
                return;
            }
            if(typeof Toast!=='undefined'){
                const message='Платёж пока не подтверждён. Попробуйте ещё раз через несколько секунд.';
                if(Toast.info)Toast.info(message);
                else Toast.error(message);
            }
        }catch(e){
            if(typeof Toast!=='undefined')Toast.error(e.message||'Ошибка проверки платежа');
        }finally{
            this.state.checkingPayment=false;
            this._render();
        }
    },

    async _toggleFallback(){
        const next=!(this.state.wallet&&this.state.wallet.wallet_fallback_enabled);
        const resp=await API.patch('/api/wallet/settings',{wallet_fallback_enabled:next});
        if(!resp||resp.ok===false||resp.error){
            if(typeof Toast!=='undefined')Toast.error(API._errorMessage(resp,'Не удалось обновить настройку'));
            return;
        }
        this.state.wallet=resp.wallet||this.state.wallet;
        this._render();
    },

    async _buyPackage(packageId){
        if(!packageId)return;
        if(this.state.purchasing)return;
        this.state.purchasing=packageId;
        this._render();
        try{
            const returnUrl=window.location.origin+window.location.pathname+'#/profile';
            const resp=await API.post(`/api/billing/packages/${encodeURIComponent(packageId)}/checkout`,{return_url:returnUrl});
            if(!resp||resp.ok===false||resp.error){
                throw new Error(API._errorMessage(resp,'Не удалось создать платёж'));
            }
            if(resp.confirmation_url){
                const confirmationUrl=this._safeCheckoutUrl(resp.confirmation_url);
                if(!confirmationUrl){
                    if(typeof Toast!=='undefined')Toast.error('Платёж создан, но платёжная ссылка выглядит небезопасно');
                    return;
                }
                this._rememberPendingPurchase(resp.billing_payment_id);
                window.location.href=confirmationUrl;
                return;
            }
            if(typeof Toast!=='undefined')Toast.success('Платёж создан, проверьте YooKassa.');
        }catch(e){
            if(typeof Toast!=='undefined')Toast.error(e.message||'Ошибка покупки');
        }finally{
            this.state.purchasing=null;
            this._render();
        }
    },
};

window.Wallet=Wallet;

/* web/js/chats.js */
/* ═══ Chats — streaming + reasoning + persistent file references ═══ */
const Chats={
    list:[], currentId:null, sending:false,
    pendingFiles:[],
    _eventsBound:false,

    initEvents(){
        if(this._eventsBound)return;
        this._eventsBound=true;
        document.addEventListener('click',(event)=>{
            const target=event.target instanceof Element ? event.target.closest('[data-chat-action]') : null;
            if(!target)return;
            event.preventDefault();
            event.stopPropagation();
            const chatId=target.dataset.chatId||target.closest('[data-chat-id]')?.dataset.chatId||'';
            const action=target.dataset.chatAction;
            const runner=window.ActionRunner||{run:(_target,fn)=>fn()};
            if(action==='open')this.open(chatId);
            else if(action==='mobile-back')this.closeMobileThread();
            else if(action==='chats-load-more')this.loadMore();
            else if(action==='confirm-create')runner.run(target,()=>this.confirmCreate());
            else if(action==='edit')this.editChat(chatId);
            else if(action==='delete')runner.run(target,()=>this.deleteChat(chatId));
            else if(action==='copy-msg')this.copyMsg(target);
            else if(action==='remove-pending-file')this.removePendingFile(Number(target.dataset.fileIndex||0));
            else if(action==='save-edit')runner.run(target,()=>this.saveEdit(chatId));
            else if(action==='clear-history')runner.run(target,()=>this.clearHistory(chatId));
        });
    },

    PAGE_SIZE:50,

    async loadList(){
        this._listDone=false;
        const resp=await API.get('/api/chats?limit='+this.PAGE_SIZE);
        this.list=Array.isArray(resp)?resp:[];
        if(this.list.length<this.PAGE_SIZE)this._listDone=true;
        const el=document.getElementById('chats-list');
        if(!this.currentId)this._setMobileState('list');
        if(!this.list.length){
            el.innerHTML=`<div class="empty-state empty-state-compact">
                <div class="empty-state-icon">${CGIcons.html('chat')}</div>
                <div class="empty-state-title">нет чатов</div>
                <div class="empty-state-text">Создайте первый чат для быстрых промптов и переписок.</div>
                <div class="empty-state-actions">
                    <button class="btn btn-primary btn-small" data-action="chats-create">${CGIcons.html('chat')}<span>новый чат</span></button>
                </div>
            </div>`;
            return;
        }
        el.innerHTML=this._renderChatList();
    },

    // Keyset pagination: the cursor is the (updated_at, id) pair of the last
    // row of the current page — the API answers with the rows strictly older
    // than the cursor, so no chat is skipped or repeated between pages.
    async loadMore(){
        if(this._listDone||this._loadingMore||!this.list.length)return;
        this._loadingMore=true;
        try{
            const last=this.list[this.list.length-1];
            const cursor='&before_updated_at='+encodeURIComponent(String(last.updated_at??''))
                +'&before_id='+encodeURIComponent(String(last.id||''));
            const resp=await API.get('/api/chats?limit='+this.PAGE_SIZE+cursor);
            const page=Array.isArray(resp)?resp:[];
            if(page.length<this.PAGE_SIZE)this._listDone=true;
            const seen=new Set(this.list.map(c=>String(c.id)));
            const fresh=page.filter(c=>!seen.has(String(c.id)));
            this.list=this.list.concat(fresh);
            const el=document.getElementById('chats-list');
            if(el)el.innerHTML=this._renderChatList();
        }finally{
            this._loadingMore=false;
        }
    },

    _renderChatList(){
        const items=this.list.map(c=>{
            const active=c.id===this.currentId?'chat-item-active':'';
            return `<div class="chat-item ${active}" data-chat-id="${this._esc(c.id)}" data-chat-action="open">
                <div class="chat-item-name">${this._esc(c.name||'Чат')}</div>
                <div class="chat-item-meta">${this._esc(this._chatListMeta(c))}</div>
            </div>`;
        }).join('');
        if(this._listDone)return items;
        return items+`<button class="btn btn-ghost btn-small chats-load-more" data-chat-action="chats-load-more">показать ещё</button>`;
    },

    async create(){
        const modelInput = this._isAdmin() ? await this._renderModelSelect() : '';
        Modal.show(`<h2>новый чат</h2>
            <div class="form-group"><label class="form-label">название</label>
                <input class="form-input" id="new-chat-name" placeholder="мой чат" autofocus></div>
            ${modelInput}
            <div class="form-group"><label class="form-label">системный промпт</label>
                <textarea class="form-textarea chat-system-textarea" id="new-chat-system" rows="6" placeholder="инструкции для AI..." data-chat-system-prompt></textarea></div>
            <div class="omb-flex omb-gap-2 omb-mt-4">
                <button class="btn btn-primary" data-chat-action="confirm-create">создать</button>
                <button class="btn btn-ghost" data-action="modal-hide">отмена</button></div>`);
        this._fitSystemPromptTextarea(document.getElementById('new-chat-system'));
        setTimeout(()=>document.getElementById('new-chat-name')?.focus(),100);
    },

    async confirmCreate(){
        const name=document.getElementById('new-chat-name')?.value?.trim()||'Чат';
        const system=document.getElementById('new-chat-system')?.value||'';
        const payload={name,system_prompt:system,messages:[]};
        if(this._isAdmin()) payload.model=document.getElementById('new-chat-model')?.value;
        try{
            const resp=await API.post('/api/chats',payload);
            API._throwIfError(resp,'Не удалось создать чат');
            const id=resp.id;
            invalidateStepTypes();
            Modal.hide();
            await this.loadList();
            this.open(id);
        }catch(e){
            if(typeof Toast!=='undefined')Toast.error(e.message||'Не удалось создать чат');
        }
    },

    async open(chatId){
        this.currentId=chatId;
        this.pendingFiles=[];
        this._renderFilesBar();
        document.getElementById('chat-empty').style.display='none';
        document.getElementById('chat-active').style.display='flex';
        this._setMobileState('thread');
        document.querySelectorAll('.chat-item').forEach(el=>el.classList.remove('chat-item-active'));
        document.querySelectorAll('.chat-item').forEach(el=>{
            if(el.dataset.chatId===String(chatId)) el.classList.add('chat-item-active');
        });
        await this.renderChat();
    },

    async renderChat(){
        if(!this.currentId)return;
        const chatId=this.currentId;
        const chat=await API.get('/api/chats/'+this._id(chatId));
        if(this.currentId!==chatId)return;
        if(!chat||chat.error)return;

        document.getElementById('chat-header').innerHTML=`
            <button class="btn btn-ghost btn-small btn-icon-only chat-mobile-back" data-chat-action="mobile-back" title="К списку чатов" aria-label="К списку чатов">
                <span class="ui-code-icon" aria-hidden="true">BACK</span>
            </button>
            <span class="chat-header-name">${this._esc(chat.name||'Чат')}</span>
            ${this._showModelLabels()&&chat.model?`<span class="chat-header-model">${this._esc(chat.model||'')}</span>`:''}
            <span class="chat-header-actions">
                <span class="chat-header-cost" id="chat-header-cost" hidden></span>
                <button class="btn btn-ghost btn-small" data-chat-action="edit" data-chat-id="${this._esc(chat.id)}" title="Настройки">${CGIcons.html('tools')}</button>
                <button class="btn btn-ghost btn-small btn-danger-text" data-chat-action="delete" data-chat-id="${this._esc(chat.id)}" title="Удалить" aria-label="Удалить чат"><span class="ui-code-icon" aria-hidden="true">DEL</span></button>
            </span>`;
        if(typeof CostEstimator!=='undefined'){
            CostEstimator.loadSummary().then(()=>this._refreshCostBadge());
            if(!this._costListenerWired){
                this._costListenerWired=true;
                CostEstimator.on(()=>this._refreshCostBadge());
            }
        }

        const el=document.getElementById('chat-messages');
        if(!chat.messages||!chat.messages.length){
            el.innerHTML=`<div class="empty-state">
                <div class="empty-state-icon">${CGIcons.html('chat')}</div>
                <div class="empty-state-title">диалог пуст</div>
                <div class="empty-state-text">Напишите первое сообщение или прикрепите файл, чтобы начать работу с моделью.</div>
            </div>`;
        }else{
            el.innerHTML=chat.messages.map(m=>this._renderMsg(m)).join('');
            el.scrollTop=el.scrollHeight;
        }
    },

    /* ── Message rendering ── */
    _renderMsg(m){
        const cls=m.role==='user'?'msg-user':m.role==='assistant'?'msg-assistant':'msg-system';
        const modelTag=this._showModelLabels()&&m.model?`<span class="msg-model">${this._esc(m.model)}</span>`:'';

        // Files / images (stored in the owner-scoped OMOIBOT registry)
        let filesHtml='';
        if(m.files&&m.files.length){
            filesHtml='<div class="msg-files">'+m.files.map(f=>{
                const isImg=f.type&&f.type.startsWith('image/');
                const url=this._safeUrl(f.url||'');
                if(isImg&&url){
                    return `<div class="msg-file msg-file-img">
                        <a href="${this._esc(url)}" target="_blank" rel="noopener">
                            <img src="${this._esc(url)}" alt="${this._esc(f.name)}" class="msg-img-preview">
                        </a>
                        <span class="msg-file-name">${this._esc(f.name)}</span>
                    </div>`;
                }
                return `<div class="msg-file">
                    ${url?`<a href="${this._esc(url)}" target="_blank" rel="noopener">${CGIcons.inline('text')} ${this._esc(f.name)}</a>`:`${CGIcons.inline('text')} ${this._esc(f.name)}`}
                </div>`;
            }).join('')+'</div>';
        }

        // Reasoning / chain of thought
        let reasoningHtml='';
        if(m.reasoning){
            reasoningHtml=`
            <details class="msg-reasoning">
                <summary class="msg-reasoning-toggle">${CGIcons.inline('generate')} цепочка мыслей</summary>
                <div class="msg-reasoning-content">${this._escapeHtml(m.reasoning)}</div>
            </details>`;
        }

        return `<div class="chat-msg ${cls}">
            ${reasoningHtml}
            <div class="msg-content">${this._escapeHtml(m.content)}</div>
            ${filesHtml}${modelTag}
            <button class="msg-copy" data-chat-action="copy-msg" title="Копировать">${CGIcons.html('clipboard')}</button>
        </div>`;
    },

    /* ── File attach ── */
    attachFile(){
        document.getElementById('chat-file-input').click();
    },

    onFilesSelected(input){
        for(const f of input.files) this.pendingFiles.push(f);
        input.value='';
        this._renderFilesBar();
    },

    removePendingFile(idx){
        this.pendingFiles.splice(idx,1);
        this._renderFilesBar();
    },

    _renderFilesBar(){
        const bar=document.getElementById('chat-files-bar');
        if(!this.pendingFiles.length){bar.innerHTML='';return;}
        bar.innerHTML=this.pendingFiles.map((f,i)=>{
            const isImg=f.type&&f.type.startsWith('image/');
            return `<div class="chat-file-tag">${CGIcons.inline(isImg?'image':'text')} ${this._esc(f.name)}
                <button data-chat-action="remove-pending-file" data-file-index="${i}" title="Убрать" aria-label="Убрать файл"><span class="ui-code-icon" aria-hidden="true">DEL</span></button>
            </div>`;
        }).join('');
    },

    /* ── Send ── */
    async send(){
        if(this.sending||!this.currentId)return;
        const chatId=this.currentId;
        const inp=document.getElementById('chat-input');
        const text=inp.value.trim();
        if(!text&&!this.pendingFiles.length)return;

        this.sending=true;
        const btn=document.getElementById('chat-send-btn');
        btn.disabled=true; btn.textContent='...';
        inp.value='';
        this._fitComposerTextarea(inp);

        // Persist files first. The chat JSON carries only opaque file ids;
        // binary data never enters the message/context payload.
        let files=[];
        const localPreviews=[];  // for immediate UI display before server confirms
        let typingId='';
        const el=document.getElementById('chat-messages');
        try{
            if(this.pendingFiles.length){
                for(const f of this.pendingFiles){
                    const uploaded=await API.upload(f,{meta:{timeoutMs:1200000}});
                    API._throwIfError(uploaded,`Не удалось загрузить ${f.name}`);
                    if(!uploaded.file_id)throw new Error(`Сервер не вернул file_id для ${f.name}`);
                    files.push({file_id:uploaded.file_id});
                    localPreviews.push({
                        name:uploaded.original_name||f.name,
                        type:uploaded.mime_type||f.type||'application/octet-stream',
                        url:uploaded.local_url||''
                    });
                }
                this.pendingFiles=[];
                this._renderFilesBar();
            }

            const noMsgs=el.querySelector('.empty-state,[style*="text-align:center"]');
            if(noMsgs)noMsgs.remove();

            const tempMsg={role:'user',content:text,files:localPreviews};
            el.insertAdjacentHTML('beforeend',this._renderMsg(tempMsg));

            typingId='typing-'+Date.now();
            el.insertAdjacentHTML('beforeend',`<div class="chat-msg msg-assistant" id="${typingId}">
                <div class="msg-content typing-dots">●●●</div>
            </div>`);
            el.scrollTop=el.scrollHeight;

            const payload={content:text};
            if(files.length) payload.files=files;

            const resp=await API.post('/api/chats/'+this._id(chatId)+'/send',payload);
            document.getElementById(typingId)?.remove();

            if(resp.ok&&resp.message){
                // Remove optimistic user message and re-render full chat
                // (so files use their persistent owner-scoped URLs)
                if(this.currentId===chatId) await this.renderChat();
            }else if(this.currentId===chatId){
                el.insertAdjacentHTML('beforeend',`<div class="chat-msg msg-system">
                    <div class="msg-content">Ошибка: ${this._esc(resp.error||'unknown')}</div>
                </div>`);
                el.scrollTop=el.scrollHeight;
            }
        }catch(e){
            if(typingId)document.getElementById(typingId)?.remove();
            if(!typingId&&this.currentId===chatId){
                inp.value=text;
                this._fitComposerTextarea(inp);
            }
            if(this.currentId===chatId&&el){
                el.insertAdjacentHTML('beforeend',`<div class="chat-msg msg-system">
                    <div class="msg-content">Ошибка: ${this._esc(e.message)}</div>
                </div>`);
                el.scrollTop=el.scrollHeight;
            }
        }finally{
            this.sending=false;
            btn.disabled=false; btn.innerHTML='<span class="ui-code-icon" aria-hidden="true">SEND</span>';
            this.loadList();
        }
    },

    /* ── Edit chat ── */
    async editChat(chatId){
        const [chat]=await Promise.all([
            API.get('/api/chats/'+this._id(chatId))
        ]);
        try{
            API._throwIfError(chat,'Не удалось открыть настройки чата');
            const modelInput = this._isAdmin() ? await this._collectModelSelect(chat.model) : '';
            Modal.show(`<h2>${CGIcons.inline('tools')} ${this._esc(chat.name||'чат')}</h2>
                <div class="form-group"><label class="form-label">название</label>
                    <input class="form-input" id="ec-name" value="${this._esc(chat.name||'')}"></div>
                ${modelInput}
                <div class="form-group"><label class="form-label">системный промпт</label>
                    <textarea class="form-textarea chat-system-textarea" id="ec-system" rows="6" data-chat-system-prompt>${this._esc(chat.system_prompt||'')}</textarea></div>
                <div class="omb-flex omb-gap-2 omb-mt-4">
                    <button class="btn btn-primary" data-chat-action="save-edit" data-chat-id="${this._esc(chatId)}">сохранить</button>
                    <button class="btn btn-ghost" data-action="modal-hide">отмена</button>
                    <button class="btn btn-ghost btn-small" data-chat-action="clear-history" data-chat-id="${this._esc(chatId)}" data-style="margin-left:auto;color:var(--amber)">очистить историю</button></div>`);
            this._fitSystemPromptTextarea(document.getElementById('ec-system'));
        }catch(e){
            if(typeof Toast!=='undefined')Toast.error(e.message);
        }
    },

    async saveEdit(chatId){
        try{
            const chat=await API.get('/api/chats/'+this._id(chatId));
            API._throwIfError(chat,'Не удалось загрузить чат');
            chat.name=document.getElementById('ec-name').value;
            if(this._isAdmin()){
                chat.model=document.getElementById('ec-model')?.value||chat.model;
            }
            chat.system_prompt=document.getElementById('ec-system').value;
            const saved=await API.post('/api/chats',chat);
            API._throwIfError(saved,'Не удалось сохранить чат');
            Modal.hide(); this.loadList(); this.renderChat();
        }catch(e){
            if(typeof Toast!=='undefined')Toast.error(e.message||'Не удалось сохранить чат');
        }
    },

    async clearHistory(chatId){
        if(!await Dialogs.confirm('Очистить историю?'))return;
        const chat=await API.get('/api/chats/'+this._id(chatId));
        API._throwIfError(chat,'Не удалось загрузить чат');
        chat.messages=[];
        const saved=await API.post('/api/chats',chat);
        API._throwIfError(saved,'Не удалось очистить историю');
        Modal.hide(); this.renderChat(); this.loadList();
    },

    async deleteChat(chatId){
        if(!await Dialogs.confirm('Удалить чат?'))return;
        const deleted=await API.del('/api/chats/'+this._id(chatId));
        API._throwIfError(deleted,'Не удалось удалить чат');
        if(this.currentId===chatId){
            this.currentId=null;
            document.getElementById('chat-active').style.display='none';
            document.getElementById('chat-empty').style.display='flex';
            this._setMobileState('list');
        }
        this.loadList();
    },

    /* ── Collect all available models ── */
    _isAdmin(){
        if(typeof Auth!=='undefined' && typeof Auth.isAdmin==='function')return !!Auth.isAdmin();
        const role=(typeof Auth!=='undefined' && Auth.user && Auth.user.role)||'';
        return role==='admin'||role==='super_admin';
    },

    _showModelLabels(){
        return this._isAdmin();
    },

    _chatListMeta(chat){
        const count=`${chat?.message_count||0} сообщ.`;
        if(this._showModelLabels()&&chat?.model)return `${chat.model} · ${count}`;
        return count;
    },

    async _renderModelSelect(selected=''){
        const models=await this._collectModels();
        const options=models.map(m=>`<option value="${this._esc(m)}" ${m===selected?'selected':''}>${this._esc(m)}</option>`).join('');
        return `<div class="form-group"><label class="form-label">модель</label>
            <select class="form-select" id="new-chat-model">${options}</select></div>`;
    },

    async _collectModelSelect(selected=''){
        const models=await this._collectModels();
        if(selected&&!models.includes(selected))models.unshift(selected);
        const options=models.map(m=>`<option value="${this._esc(m)}" ${m===selected?'selected':''}>${this._esc(m)}</option>`).join('');
        return `<div class="form-group"><label class="form-label">модель</label>
            <select class="form-select" id="ec-model">${options}</select></div>`;
    },

    async _collectModels(){
        let models=[];
        const seen=new Set();
        try{
            const types=await getStepTypes();
            const opts=(types['chat']?.params||[]).find(p=>p.key==='model')?.options||[];
            for(const m of opts){
                const name=typeof m==='object'?(m.label||m.value||''):m;
                if(name&&!seen.has(name)&&name!=='(добавьте модели)'){models.push(name);seen.add(name);}
            }
        }catch(e){}
        try{
            const mods=await API.get('/api/modules');
            for(const mod of mods){
                if(!mod.enabled)continue;
                for(const md of (mod.config?.models||[])){
                    const name=md.name||md.id||'';
                    if(name&&md.url&&!seen.has(name)){models.push(name);seen.add(name);}
                }
            }
        }catch(e){}
        if(!models.length) models=['gpt-5-2'];
        return models;
    },

    copyMsg(btn){
        const msgEl=btn.parentElement.querySelector('.msg-content');
        const text=msgEl?msgEl.textContent:'';
        navigator.clipboard.writeText(text).then(()=>{
            btn.textContent='OK';
            setTimeout(()=>{btn.innerHTML=CGIcons.html('clipboard')},1500);
        });
    },

    _safeUrl(value){
        if(typeof MediaUtils!=='undefined')return MediaUtils.safeUrl(value,{allowData:true});
        return '';
    },
    closeMobileThread(){
        this._setMobileState('list');
        document.getElementById('chat-input')?.blur();
    },
    _setMobileState(state){
        const page=document.getElementById('page-chats');
        if(!page)return;
        page.dataset.mobileState=state==='thread'?'thread':'list';
    },
    _fitComposerTextarea(textarea){
        if(!textarea)return;
        const viewportHeight=window.innerHeight||900;
        const cap=Math.max(220,Math.min(320,Math.round(viewportHeight*0.32)));
        textarea.style.height='auto';
        const scrollHeight=textarea.scrollHeight||40;
        textarea.style.height=Math.max(40,Math.min(scrollHeight,cap))+'px';
        textarea.style.overflowY=scrollHeight>cap?'auto':'hidden';
    },
    _fitSystemPromptTextarea(textarea){
        if(!textarea)return;
        const viewportHeight=window.innerHeight||900;
        const cap=Math.max(260,Math.min(420,Math.round(viewportHeight*0.46)));
        textarea.style.height='auto';
        const scrollHeight=textarea.scrollHeight||132;
        textarea.style.height=Math.max(132,Math.min(scrollHeight,cap))+'px';
        textarea.style.overflowY=scrollHeight>cap?'auto':'hidden';
    },
    _id(value){return encodeURIComponent(String(value??''));},
    _refreshCostBadge(){
        const badge=document.getElementById('chat-header-cost');
        const sendBtn=document.getElementById('chat-send-btn');
        if(!badge && !sendBtn)return;
        if(typeof CostEstimator==='undefined')return;
        const snap=CostEstimator.snapshot();
        const cost=Number(snap.chatCredits||0);
        const balance=snap.balance==null?null:Number(snap.balance);
        if(badge){
            if(cost<=0){
                badge.hidden=true;
                badge.dataset.state='free';
                badge.textContent='';
            }else if(balance!=null && balance<cost){
                badge.hidden=false;
                badge.dataset.state='insufficient';
                badge.textContent=`сообщение ${cost} тк · баланс ${Math.round(balance)} тк`;
            }else{
                badge.hidden=false;
                badge.dataset.state='ok';
                badge.textContent=balance==null?`сообщение ${cost} тк`:`сообщение ${cost} тк · баланс ${Math.round(balance)} тк`;
            }
        }
        if(sendBtn){
            const block=cost>0 && balance!=null && balance<cost;
            sendBtn.classList.toggle('btn-cost-blocked', block);
            if(block){
                sendBtn.setAttribute('aria-disabled','true');
                sendBtn.title=`Не хватает ${Math.max(0,cost-Math.round(balance||0))} тк`;
            }else{
                sendBtn.removeAttribute('aria-disabled');
                if((sendBtn.title||'').startsWith('Не хватает'))sendBtn.removeAttribute('title');
            }
        }
    },

    _js(value){return this._esc(JSON.stringify(String(value??'')));},
    _esc(s){return String(s??'').replace(/[&<>"']/g,ch=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));},
    _escapeHtml(s){return String(s??'').replace(/[&<>"']/g,ch=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch])).replace(/\n/g,'<br>');}
};

// Enter = send, Shift+Enter = newline
document.addEventListener('keydown',e=>{
    if(e.target.id==='chat-input'&&e.key==='Enter'&&!e.shiftKey){e.preventDefault();Chats.send();}
});
// Auto-resize textarea
document.addEventListener('input',e=>{
    if(e.target.id==='chat-input'){
        Chats._fitComposerTextarea(e.target);
    }else if(e.target.matches?.('[data-chat-system-prompt]')){
        Chats._fitSystemPromptTextarea(e.target);
    }
});

/* web/js/batch-jobs.js */
/* Batch actions for task job lists and dashboard error cleanup. */

const BatchJobs = {
    _pageLimit:200,
    _hardCap:5000,

    async _fetchJobs(params={}){
        const all = [];
        let offset = 0;
        while(all.length < this._hardCap){
            const query = new URLSearchParams();
            Object.entries(params).forEach(([key,value]) => {
                if(value !== undefined && value !== null && value !== '') query.set(key, String(value));
            });
            query.set('limit', String(this._pageLimit));
            query.set('offset', String(offset));
            const page = await API.get('/api/jobs?' + query.toString());
            if(!Array.isArray(page)) return page;
            all.push(...page);
            if(page.length < this._pageLimit) break;
            offset += page.length;
        }
        if(all.length >= this._hardCap) Toast.warning('Достигнут лимит пакетного действия: ' + this._hardCap);
        return all;
    },

    renderBar(containerSelector, jobs){
        const container = document.querySelector(containerSelector);
        if(!container) return;

        container.parentElement.querySelector('.batch-bar')?.remove();
        if(!jobs.length) return;

        const errorJobs = jobs.filter(j => j.status === 'error');
        const doneJobs = jobs.filter(j => j.status === 'done');

        const bar = document.createElement('div');
        bar.className = 'batch-bar';

        let btns = '';
        if(errorJobs.length > 0){
            btns += '<button class="btn btn-ghost btn-small" data-action="batch-delete-errors"><span class="ui-code-icon" aria-hidden="true">DEL</span><span>удалить ошибки (' + errorJobs.length + ')</span></button>';
            btns += '<button class="btn btn-ghost btn-small" data-action="batch-retry-errors">' + CGIcons.html('sync') + '<span>перезапустить ошибки (' + errorJobs.length + ')</span></button>';
        }
        if(doneJobs.length > 0){
            btns += '<button class="btn btn-ghost btn-small" data-action="batch-delete-done"><span class="ui-code-icon" aria-hidden="true">DEL</span><span>удалить готовые (' + doneJobs.length + ')</span></button>';
        }
        if(jobs.length > 1){
            btns += '<button class="btn btn-ghost btn-small btn-danger-text" data-action="batch-delete-all" data-style="margin-left:auto">удалить все (' + jobs.length + ')</button>';
        }

        if(!btns) return;

        bar.innerHTML = '<span class="batch-bar-count">' + jobs.length + ' запусков</span>' + btns;
        container.parentElement.insertBefore(bar, container);
    },

    renderDashboardErrorBar(parent, errorCount){
        if(!parent)return;
        const existing = parent.querySelector('.batch-bar');
        if(errorCount > 0){
            const html = '<span class="batch-bar-count">' + errorCount + ' с ошибками</span>'
                + '<button class="btn btn-ghost btn-small" data-action="batch-delete-all-errors"><span class="ui-code-icon" aria-hidden="true">DEL</span><span>удалить все ошибки</span></button>';
            if(existing){
                existing.innerHTML = html;
            }else{
                const bar = document.createElement('div');
                bar.className = 'batch-bar';
                bar.innerHTML = html;
                const recentEl=document.getElementById('dash-recent-jobs');
                parent.insertBefore(bar, recentEl||parent.firstChild);
            }
        }else if(existing){
            existing.remove();
        }
    },

    async deleteByStatus(status){
        const label = status === 'error' ? 'ошибочные' : 'готовые';
        if(!await Dialogs.confirm('Удалить все ' + label + ' запуски?')) return;

        const taskId = TaskView.taskId;
        const targets = await this._fetchJobs({ task_id: taskId, status });
        if(!Array.isArray(targets)){Toast.error(targets?.error||'Не удалось получить список запусков');return;}
        
        let deleted = 0;
        for(const j of targets){
            try{
                const resp = await API.del('/api/jobs/' + encodeURIComponent(String(j.id)));
                if(resp?.ok) deleted++;
            }catch(e){}
        }
        Toast.success('Удалено ' + deleted + ' запусков');
        TaskView.loadJobs();
    },

    async retryAll(){
        if(!await Dialogs.confirm('Перезапустить все ошибочные запуски?')) return;

        const taskId = TaskView.taskId;
        const targets = await this._fetchJobs({ task_id: taskId, status: 'error' });
        if(!Array.isArray(targets)){Toast.error(targets?.error||'Не удалось получить список запусков');return;}

        let retried = 0;
        for(const j of targets){
            try{ await API.post('/api/jobs/' + encodeURIComponent(String(j.id)) + '/retry'); retried++; }catch(e){}
        }
        Toast.success('Перезапущено ' + retried + ' запусков');
        TaskView.loadJobs();
    },

    async deleteAll(){
        if(!await Dialogs.confirm('Удалить ВСЕ запуски этой задачи? Это нельзя отменить.')) return;

        const taskId = TaskView.taskId;
        const targets = await this._fetchJobs({ task_id: taskId });
        if(!Array.isArray(targets)){Toast.error(targets?.error||'Не удалось получить список запусков');return;}

        let deleted = 0;
        for(const j of targets){
            try{ await API.del('/api/jobs/' + encodeURIComponent(String(j.id))); deleted++; }catch(e){}
        }
        Toast.success('Удалено ' + deleted + ' запусков');
        TaskView.loadJobs();
    },

    async deleteAllErrors(){
        if(!await Dialogs.confirm('Удалить ВСЕ ошибочные запуски?')) return;
        const targets = await this._fetchJobs({ status: 'error' });
        if(!Array.isArray(targets)){Toast.error(targets?.error||'Не удалось получить список запусков');return;}
        let deleted = 0;
        for(const j of targets){
            try{ await API.del('/api/jobs/' + encodeURIComponent(String(j.id))); deleted++; }catch(e){}
        }
        Toast.success('Удалено ' + deleted + ' ошибочных запусков');
        Dashboard.load();
    }
};

/* web/js/sse.js */
/* SSE real-time job updates with polling fallback. */

var SSE = window.SSE = {
    _source: null,
    _retryDelay: 1000,
    _maxRetry: 30000,
    _connected: false,
    _bootTs: Date.now() / 1000,
    _lastEventId: 0,
    _reconnectTimer: null,
    _refreshTimers: {},
    _refreshFirstQueued: {},

    _canRun() {
        return typeof Auth !== 'undefined' && !!Auth.user;
    },

    _taskEditorHasCanvasRun(jobId) {
        if (Router.current !== 'task' || typeof TaskEditor === 'undefined') return false;
        if(window.WorkboardV2Session?.isV2(TaskEditor))return false;
        if (typeof TaskEditor._hasActiveCanvasRun === 'function') {
            return TaskEditor._hasActiveCanvasRun(jobId);
        }
        return !!(
            TaskEditor._activeCanvasRun
            && String(TaskEditor._activeCanvasRun.job_id) === String(jobId)
        );
    },

    _setStatus(state, label) {
        const el = document.getElementById('connection-status');
        if (!el) return;
        el.dataset.state = state;
        el.textContent = label || '';
    },

    init() {
        if (!this._canRun()) {
            this.disconnect();
            if (typeof Jobs !== 'undefined' && Jobs.pollTimer) {
                clearInterval(Jobs.pollTimer);
                Jobs.pollTimer = null;
            }
            return;
        }
        this.connect();
        if (Jobs.pollTimer) clearInterval(Jobs.pollTimer);
        Jobs.pollTimer = setInterval(() => {
            if (!this._canRun()) {
                this.disconnect();
                if (Jobs.pollTimer) {
                    clearInterval(Jobs.pollTimer);
                    Jobs.pollTimer = null;
                }
                return;
            }
            if (!this._connected) {
                if (Router.current === 'task') TaskView.loadJobs();
                if (Router.current === 'project') ProjectView.refreshRuns();
                if (Router.current === 'home') Dashboard.load();
            }
        }, 10000);
    },

    connect() {
        if (!this._canRun()) {
            this.disconnect();
            return;
        }
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        if (this._source) this._source.close();

        const eventsUrl=this._lastEventId?('/api/events?after_id='+encodeURIComponent(this._lastEventId)):'/api/events';
        const publicEventsUrl=window.AppURL?window.AppURL.resolve(eventsUrl):eventsUrl;
        this._source = new EventSource(Auth.authUrl(publicEventsUrl));

        this._source.addEventListener('open',() => {
            this._connected = true;
            this._retryDelay = 1000;
            this._setStatus('connected', 'онлайн');
            console.log('[SSE] Connected');
        });

        this._source.addEventListener('message',(e) => {
            try {
                const msg = JSON.parse(e.data);
                if(e.lastEventId)this._lastEventId=parseInt(e.lastEventId,10)||this._lastEventId;
                this._handleEvent(msg);
            } catch (err) {
                console.warn('[SSE] Invalid event payload', err);
            }
        });

        this._source.addEventListener('error',() => {
            this._connected = false;
            if (this._source) this._source.close();
            this._source = null;
            if (!this._canRun()) return;
            this._setStatus('reconnecting', 'подключение');
            console.log('[SSE] Reconnecting in ' + (this._retryDelay / 1000) + 's');
            if (!this._reconnectTimer) {
                const delay = this._retryDelay;
                this._reconnectTimer = setTimeout(() => {
                    this._reconnectTimer = null;
                    this.connect();
                }, delay);
            }
            this._retryDelay = Math.min(this._retryDelay * 2, this._maxRetry);
        });
    },

    disconnect() {
        this._connected = false;
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        if (this._source) this._source.close();
        this._source = null;
        const canRun = this._canRun();
        this._setStatus(canRun ? 'offline' : 'idle', canRun ? 'нет связи' : '');
    },

    _handleEvent(msg) {
        const type = msg.type;
        const data = msg.data || {};

        if (type === 'keepalive' || type === 'connected') return;
        window.WorkboardV2Session?.handleEvent?.(msg);

        if (type === 'job_status') this._onJobStatus(data, msg);
        else if (type === 'step_progress') this._onStepProgress(data);
        else if (type === 'job_progress') this._onJobProgress(data);
        else if (type === 'batch_start') this._onBatchStart(data);
        else if (type === 'step_partial_result') this._onStepPartialResult(data);
        else if (type === 'batch_end') this._onBatchEnd(data);
    },

    /** Clamp helper paired with Jobs._progress(). Protects from 35/34 / 110% if backend
     *  sends a step beyond total. Returns {percent, stepLabel, totalSuffix}.
     *
     *  Stage 4: when ``opts`` carries ``done_steps``/``run_scope`` (the
     *  modern shape), they describe "nodes done in this run / nodes
     *  this run executes" — the actual user-facing counter. The legacy
     *  ``rawStep``/``rawTotal`` args remain the fallback for older payloads. */
    _clampProgress(rawStep, rawTotal, opts){
        const done = opts && Number(opts.done_steps);
        const scope = opts && Number(opts.run_scope);
        const taskTotal = opts && Number(opts.total_steps);
        const status = opts && String(opts.status || '');
        // SSE progress event without explicit status means worker is actively
        // stepping — treat as running so the counter is shown.
        const showForStatus = status === 'running' || status === 'paused'
            || status === 'cancelled' || status === 'error' || !status;
        if (Number.isFinite(done) && Number.isFinite(scope) && scope > 0) {
            const clamped = Math.max(0, Math.min(done, scope));
            const showCounter = showForStatus;
            const percent = showCounter
                ? Math.min(100, Math.max(0, Math.round((clamped / scope) * 100)))
                : 0;
            const stepLabel = showCounter ? (clamped + '/' + scope) : '';
            const totalSuffix = showCounter
                ? ((Number.isFinite(taskTotal) && taskTotal > scope) ? (' · всего в задаче ' + taskTotal) : '')
                : ((Number.isFinite(taskTotal) && taskTotal > 0) ? ('всего в задаче ' + taskTotal) : '');
            return { percent, stepLabel, totalSuffix };
        }
        const total = Number(rawTotal) || 0;
        const stepNum = Number(rawStep);
        if (!Number.isFinite(stepNum)) {
            return { percent: 0, stepLabel: '', totalSuffix: total > 0 ? ('всего в задаче ' + total) : '' };
        }
        const showCounter = showForStatus;
        const displayCurrent = total > 0
            ? Math.min(total, Math.max(0, stepNum + 1))
            : Math.max(0, stepNum + 1);
        const percent = showCounter && total > 0
            ? Math.min(100, Math.max(0, Math.round((displayCurrent / total) * 100)))
            : 0;
        const stepLabel = showCounter ? (displayCurrent + '/' + (total > 0 ? total : '?')) : '';
        const totalSuffix = showCounter ? '' : (total > 0 ? ('всего в задаче ' + total) : '');
        return { percent, stepLabel, totalSuffix };
    },

    _onJobProgress(data) {
        const { job_id, current_step, total_steps, step_type } = data;
        if (!job_id) return;
        const {percent, stepLabel, totalSuffix} = this._clampProgress(current_step, total_steps, data);
        this._patchJobCard(job_id, {
            percent, stepLabel, totalSuffix,
            stepType: data.current_node_type || step_type,
            nodeLabel: data.current_node_label,
        });
    },

    _patchJobCard(jobId, patch){
        if(!jobId || !patch || typeof Jobs==='undefined') return;
        const safe=(window.CSS && CSS.escape)?CSS.escape(String(jobId)):String(jobId);
        const scope=Jobs._activeScope?.()||document;
        const card=scope.querySelector?.('.job-card[data-job-id="'+safe+'"]');
        if(!card){
            // Conservative fallback: only refresh if jobId is genuinely outside Dashboard._jobs
            // (i.e. dropped past limit=100). If the job IS in memory but not in DOM,
            // it's just filtered — ignore the progress event without refresh.
            const route=(typeof Router!=='undefined' && Router && Router.current)||'';
            if(route==='home'){
                const inMemory=(typeof Dashboard!=='undefined' && Array.isArray(Dashboard._jobs))
                    ? Dashboard._jobs.some(j=>j && String(j.id)===String(jobId))
                    : false;
                if(!inMemory){
                    this._schedule('home-jobs', () => Dashboard._refreshJobs?.(), 900);
                }
            }
            if(route==='project'){
                this._schedule('project-jobs', () => ProjectView.refreshRuns(), 900);
            }
            return;
        }
        if(patch.percent!=null){
            const bar=card.querySelector('[data-progress-bar]');
            if(bar) bar.style.width=Math.max(0,Math.min(100,patch.percent))+'%';
        }
        if(patch.stepLabel!=null){
            const labelEl=card.querySelector('[data-step-label]');
            if(labelEl){
                const raw=String(patch.stepLabel||'');
                const suffix=String(patch.totalSuffix||'');
                // Stage 5 wording: "X/Y выполнено · в задаче Z" (no "шаг").
                // When counter is suppressed (idle + done=0), render suffix
                // alone so "всего в задаче N" stays visible.
                labelEl.textContent=raw?(raw+' выполнено'+suffix):(suffix||'');
            }
        }
        if(patch.stepType!=null || patch.nodeLabel!=null){
            const typeEl=card.querySelector('[data-step-type]');
            if(typeEl){
                const human=patch.stepType
                    ?(Jobs._stepTypeLabel?.(patch.stepType)||String(patch.stepType||''))
                    :'';
                const node=String(patch.nodeLabel||'').trim();
                const display=node
                    ?(node+(human?(' · '+human):''))
                    :human;
                typeEl.textContent=display;
                typeEl.hidden=!display;
            }
        }
        if(patch.preview!=null){
            const prevEl=card.querySelector('[data-step-preview]');
            if(prevEl){
                if(patch.previewType==='text'){
                    prevEl.textContent=String(patch.preview||'').slice(0,140);
                    prevEl.hidden=!prevEl.textContent;
                }else{
                    prevEl.textContent='';
                    prevEl.hidden=true;
                }
            }
        }
        if(patch.status){
            Jobs.applyStatusToCard?.(card, {status:patch.status});
        }
    },

    _onBatchStart(data) {
        const { job_id, indices, run_epoch } = data;
        if (!job_id) return;
        if(window.WorkboardV2Session?.isV2(window.JobDebugger)&&String(window.JobDebugger.job?.id)===String(job_id))return;
        const isDebuggerActive = Router.current === 'debugger'
            && typeof JobDebugger !== 'undefined'
            && JobDebugger.job && String(JobDebugger.job.id) === String(job_id);
        const isCanvasActive = this._taskEditorHasCanvasRun(job_id);
        if (isDebuggerActive) {
            try {
                JobDebugger.applyBatchEvent?.({
                    type: 'start',
                    jobId: job_id,
                    indices,
                    runEpoch: Number(run_epoch),
                });
            } catch (_) {}
        } else if (isCanvasActive) {
            TaskEditor._handleCanvasRunEvent?.('batch_start', data);
        }
    },

    _onStepPartialResult(data) {
        const { job_id, step_idx, save_as, type, content_preview, local_path, run_epoch } = data;
        if (!job_id) return;
        if(window.WorkboardV2Session?.isV2(window.JobDebugger)&&String(window.JobDebugger.job?.id)===String(job_id))return;
        const isDebuggerActive = Router.current === 'debugger'
            && typeof JobDebugger !== 'undefined'
            && JobDebugger.job && String(JobDebugger.job.id) === String(job_id);
        const isCanvasActive = this._taskEditorHasCanvasRun(job_id);
        if (isDebuggerActive) {
            try {
                JobDebugger.applyPartialResult?.({
                    jobId: job_id,
                    stepIdx: Number(step_idx),
                    save_as,
                    type,
                    content_preview,
                    local_path,
                    runEpoch: Number(run_epoch),
                });
            } catch (_) {}
        } else if (isCanvasActive) {
            TaskEditor._handleCanvasRunEvent?.('step_partial_result', data);
        }
    },

    _onBatchEnd(data) {
        const { job_id, indices, run_epoch } = data;
        if (!job_id) return;
        if(window.WorkboardV2Session?.isV2(window.JobDebugger)&&String(window.JobDebugger.job?.id)===String(job_id))return;
        const isDebuggerActive = Router.current === 'debugger'
            && typeof JobDebugger !== 'undefined'
            && JobDebugger.job && String(JobDebugger.job.id) === String(job_id);
        const isCanvasActive = this._taskEditorHasCanvasRun(job_id);
        if (isCanvasActive) {
            TaskEditor._handleCanvasRunEvent?.('batch_end', data);
            return;
        }
        if (!isDebuggerActive) return;
        try {
            JobDebugger.applyBatchEvent?.({
                type: 'end',
                jobId: job_id,
                indices,
                runEpoch: Number(run_epoch),
            });
        } catch (_) {}
        // Force a full reload so persisted step_results take over from
        // _partial overlays. Without skipIfLocalEditing — batch_end is
        // an explicit reconciliation point; preserveView keeps camera/zoom.
        setTimeout(() => {
            if (Router.current !== 'debugger') return;
            if (!JobDebugger.job || String(JobDebugger.job.id) !== String(job_id)) return;
            try { JobDebugger.refresh?.(job_id, { preserveView: true }); }
            catch (_) {}
        }, 400);
    },

    _onJobStatus(data, msg={}) {
        const { job_id, status, is_canvas_run } = data;
        if (!job_id) return;
        const isReplay = msg.ts && msg.ts < this._bootTs - 2;

        // Canvas-run dispatch — sink event into TaskEditor and suppress
        // generic toasts/refresh. Two paths:
        //  - active match: forward to TaskEditor for state updates;
        //  - is_canvas_run flag without active state (fast-job race
        //    before _activeCanvasRun was set): silent suppression,
        //    reconcile GET in runNodeFromCanvas will pick it up.
        const activeCanvasMatch = this._taskEditorHasCanvasRun(job_id);
        if (activeCanvasMatch) {
            TaskEditor._handleCanvasRunEvent?.('job_status', data);
            return;
        }
        if (is_canvas_run === true) return;

        if (!isReplay && status === 'done' && typeof Toast !== 'undefined') {
            Toast.success('OK ' + (data.task_name || 'Задача') + ' завершена');
        }
        if (!isReplay && status === 'error' && typeof Toast !== 'undefined') {
            Toast.error('ERR Ошибка: ' + (data.error || '').substring(0, 80));
        }
        if (!isReplay && status === 'paused' && typeof Toast !== 'undefined') {
            Toast.info('PAUSE ' + (data.task_name || 'Задача') + ' на паузе');
        }
        if (!isReplay && status === 'cancelled' && typeof Toast !== 'undefined') {
            Toast.info('STOP ' + (data.task_name || 'Задача') + ' отменена');
        }

        this._refresh();

        if (Router.current === 'debugger' && typeof JobDebugger !== 'undefined'
            && JobDebugger.job && JobDebugger.job.id === job_id) {
            this._refreshDebugger(job_id, data, 700);
        }
    },

    _onStepProgress(data) {
        const { job_id, step, total, preview, type, is_canvas_run } = data;
        if (!job_id) return;

        // Canvas-run suppression (same model as _onJobStatus).
        const activeCanvasMatch = this._taskEditorHasCanvasRun(job_id);
        if (activeCanvasMatch) {
            TaskEditor._handleCanvasRunEvent?.('step_progress', data);
            return;
        }
        if (is_canvas_run === true) return;

        const {percent, stepLabel, totalSuffix} = this._clampProgress(step, total, data);
        this._patchJobCard(job_id, {
            percent, stepLabel, totalSuffix, preview, previewType: type,
            stepType: data.current_node_type || type,
            nodeLabel: data.current_node_label,
        });

        if (Router.current === 'debugger' && typeof JobDebugger !== 'undefined'
            && JobDebugger.job && JobDebugger.job.id === job_id) {
            this._refreshDebugger(job_id, null, 1500);
        }
    },

    _refresh() {
        if (Router.current === 'task') {
            this._schedule('task-jobs', () => TaskView.loadJobs(), 900);
        }
        if (Router.current === 'project') {
            this._schedule('project-jobs', () => ProjectView.refreshRuns(), 900);
        }
        if (Router.current === 'home') {
            this._schedule('home-jobs', () => Dashboard._refreshJobs(), 900);
        }
    },

    _refreshDebugger(jobId, snapshot=null, delay=1200) {
        if (!jobId) return;
        clearTimeout(JobDebugger._sseRefreshTimer);
        JobDebugger._sseRefreshTimer = setTimeout(() => {
            if (Router.current !== 'debugger' || !JobDebugger.job || JobDebugger.job.id !== jobId) return;
            JobDebugger.refreshLive
                ? JobDebugger.refreshLive(jobId, snapshot)
                : JobDebugger.refresh?.(jobId, { preserveView: true });
        }, delay);
    },

    _schedule(key, fn, delay, maxWait = 2000) {
        const now = Date.now();
        if (!this._refreshFirstQueued[key]) {
            this._refreshFirstQueued[key] = now;
        }
        clearTimeout(this._refreshTimers[key]);

        const elapsed = now - this._refreshFirstQueued[key];
        const effectiveDelay = elapsed >= maxWait ? 0 : Math.min(delay, maxWait - elapsed);

        this._refreshTimers[key] = setTimeout(async () => {
            delete this._refreshTimers[key];
            delete this._refreshFirstQueued[key];
            if (!this._canRun()) return;
            try { await fn(); } catch(e) {
                console.warn('[SSE] Refresh callback failed', e);
            }
        }, effectiveDelay);
    }
};

/* web/js/onboarding.js */
/* Onboarding tour */
const OnboardingTour = {
    _eventsBound:false,
    _layer:null,
    _bubble:null,
    _spotlight:null,
    _arrow:null,
    _mascot:null,
    _mascotPose:'idle',
    _poseList:['idle','run','wave','point','cheer','point-left','point-right','point-down','soft','cheer-static','wave-static'],
    _titleEl:null,
    _bodyEl:null,
    _counterEl:null,
    _progressFillEl:null,
    _prevBtn:null,
    _nextBtn:null,
    _stepIdx:0,
    _filteredSteps:[],
    _activeAnchor:null,
    _resizeRAF:0,
    _scrollTimer:0,
    _running:false,
    _reason:'',
    _afterHideHook:null,
    _runTransitionTimer:0,
    _bubbleShowTimer:0,

    _mascotSize:{w:112, h:112},
    _finishedKey:'omb_onboarding_v1_completed',
    _skippedKey:'omb_onboarding_v1_skipped',
    _seenAtKey:'omb_onboarding_v1_seen_at',

    _allSteps:[
        {id:'sidebar',  page:null,        anchor:'#sidebar',
         title:'Помощник доски',
         body:'Привет. Я помогу быстро сориентироваться в рабочей доске. Слева — доступные разделы: главная, проекты, шаблоны и инструменты тарифа. Покажу, как собрать первый запуск.',
         requiredFeature:null, placement:'right', pose:'wave', animate:true,
         beforeShow:()=>document.body.classList.add('tour-pin-sidebar'),
         afterHide:()=>document.body.classList.remove('tour-pin-sidebar')},

        {id:'home',     page:'home',      anchor:'#page-home',
         title:'Главная',
         body:'Дашборд: что в работе, последние запуски и быстрый доступ к шаблонам. Сюда возвращаются, когда нужно посмотреть, как идёт смена.',
         requiredFeature:null, placement:'top', pose:'soft', animate:false},

        {id:'projects', page:'projects',  anchor:'[data-action="projects-create"]',
         title:'Проекты',
         body:'Проекты собирают шаблоны и файлы по теме. Кнопка «новый проект» создаёт рабочее пространство; внутри него будут шаблоны, файлы и запуски.',
         requiredFeature:'tasks.create', placement:'bottom', pose:'point', animate:false},

        {id:'templates', page:'templates', anchor:'.nav-item[data-page="templates"]',
         title:'Готовые шаблоны',
         body:'Если не хочется собирать пайплайн с нуля — открой «Шаблоны». Это маркетплейс: клонируй понравившийся в свой проект и подправь под себя.',
         requiredFeature:'tasks.create', placement:'right', pose:'point', animate:false},

        {id:'chats',    page:'chats',     anchor:'#page-chats',
         title:'Чаты',
         body:'Чаты — это и тесты промптов, и массовый прогон: один и тот же запрос можно отправить разным моделям, получить разные результаты и сравнить, какая лучше для сценария.',
         requiredFeature:'llm.chat', placement:'top', pose:'point', animate:false},

        {id:'workboard', page:'projects', anchor:'.nav-item[data-page="projects"]',
         title:'Рабочая доска',
         body:'Когда стартует запуск, открывается рабочая доска: видно каждый шаг, можно перезапустить любой узел или подправить параметры. Открывается внутри проекта.',
         requiredFeature:null, placement:'right', pose:'point', animate:false},

        {id:'tpl-nodes', narrative:true,
         title:'Открой шаблон — там канвас',
         body:'Нажми на любой шаблон в проекте — откроется канвас редактора. В верхней панели кнопка «+ Шаг»: каждый клик добавляет узел. У каждого узла слева IN (вход), справа OUT (выход), внутри — поле «промпт» или параметры.',
         diagram:'nodes',
         requiredFeature:null, pose:'soft', animate:false},

        {id:'tpl-edges', narrative:true,
         title:'Свяжи узлы мышью',
         body:'Чтобы соединить два узла — зажми мышь на OUT первого и протяни до IN второго. Появится стрелка-связь. А чтобы внутри второго использовать результат первого — впиши в поле {{node1.text}} (имя узла + поле).',
         diagram:'edges',
         requiredFeature:null, pose:'point-right', animate:false},

        {id:'tpl-run', narrative:true,
         title:'Сохрани и запусти',
         body:'Когда схема собрана — нажми «Сохранить», потом «Запустить». Откроется рабочая доска: серый узел ждёт, синий работает, зелёный готов, красный с ошибкой — его можно перезапустить отдельно. В панели управления канвасом после сохранения активируется кнопка «Агент» — попроси его достроить пайплайн или объяснить, почему упал узел.',
         diagram:'run',
         requiredFeature:null, pose:'cheer-static', animate:false},

        {id:'profile',  page:null,        anchor:'.sidebar-footer',
         title:'Профиль и настройки',
         body:'Аватар, тариф, баланс токенов и справка по интерфейсу — всё в подвале меню. Я раскрыл панель, чтобы было видно.',
         requiredFeature:null, placement:'right', pose:'soft', animate:false,
         beforeShow:()=>document.body.classList.add('tour-pin-sidebar'),
         afterHide:()=>document.body.classList.remove('tour-pin-sidebar')},

        {id:'finale',   page:'projects',  anchor:'[data-action="projects-create"]',
         title:'Поехали',
         body:'Готовы попробовать? Создадим первый проект — соберём шаблон и запустим.',
         requiredFeature:'tasks.create', placement:'bottom', pose:'cheer', animate:true,
         isFinale:true}
    ],

    initEvents(){
        if(this._eventsBound)return;
        this._eventsBound=true;
        window.addEventListener('resize',()=>this._onResize(),{passive:true});
        window.addEventListener('scroll',()=>this._onScroll(),{passive:true,capture:true});
        document.addEventListener('keydown',(event)=>this._onKeydown(event));
        const mark=new Image();
        mark.src='/assets/omoibot-mark.svg';
    },

    _setMascotPose(pose, {flip=false, animate=true, glitch=false, dir=null}={}){
        if(!this._mascot)return;
        let valid=this._poseList.includes(pose)?pose:'idle';
        if(valid==='point' && dir){
            const dirMap={left:'point-left', right:'point-right', down:'point-down', up:'wave-static', self:'soft'};
            const variant=dirMap[dir];
            if(variant && this._poseList.includes(variant)) valid=variant;
        }
        const isStaticPose = ['point-left','point-right','point-down','soft','cheer-static','wave-static'].includes(valid);
        const wantStatic = !animate && !isStaticPose;
        const currentFlip=this._mascot.classList.contains('flip-x');
        const currentStatic=this._mascot.classList.contains('is-static');
        if(this._mascotPose===valid && currentFlip===!!flip && currentStatic===wantStatic && !glitch)return;
        if(glitch)this._glitch(360);
        for(const p of this._poseList){
            this._mascot.classList.toggle('pose-'+p, p===valid);
        }
        this._mascot.classList.toggle('flip-x', !!flip);
        this._mascot.classList.toggle('is-static', wantStatic);
        this._mascotPose=valid;
    },

    _glitch(duration=350){
        if(!this._mascot)return;
        clearTimeout(this._glitchTimer);
        this._mascot.classList.remove('is-glitching');
        void this._mascot.offsetWidth;
        this._mascot.classList.add('is-glitching');
        this._glitchTimer=setTimeout(()=>{
            this._mascot?.classList.remove('is-glitching');
        }, duration);
    },

    _onKeydown(event){
        if(!this._running)return;
        if(event.key==='Escape'){event.preventDefault();this.skip();}
        else if(event.key==='Enter'||event.key==='ArrowRight'){event.preventDefault();this.next();}
        else if(event.key==='ArrowLeft'){event.preventDefault();this.prev();}
    },

    _onResize(){
        if(!this._running||!this._activeAnchor)return;
        cancelAnimationFrame(this._resizeRAF);
        this._resizeRAF=requestAnimationFrame(()=>{
            if(!this._activeAnchor||!document.contains(this._activeAnchor))return;
            this._highlightAnchor(this._activeAnchor);
            this._placeMascotAndBubble(this._activeAnchor,this._filteredSteps[this._stepIdx]?.placement,{instant:true});
        });
    },

    _onScroll(){
        if(!this._running||this._scrollTimer)return;
        this._scrollTimer=setTimeout(()=>{
            this._scrollTimer=0;
            if(!this._activeAnchor||!document.contains(this._activeAnchor))return;
            this._highlightAnchor(this._activeAnchor);
            this._placeMascotAndBubble(this._activeAnchor,this._filteredSteps[this._stepIdx]?.placement,{instant:true});
        },16);
    },

    _renderLayer(){
        if(this._layer)return;
        const layer=document.createElement('div');
        layer.className='omb-tour-layer';
        layer.setAttribute('role','dialog');
        layer.setAttribute('aria-modal','true');
        layer.setAttribute('aria-labelledby','omb-tour-title');

        const spotlight=document.createElement('div');
        spotlight.className='omb-tour-spotlight';
        spotlight.setAttribute('aria-hidden','true');

        const mascot=document.createElement('div');
        mascot.className='omb-tour-mascot pose-idle';
        mascot.setAttribute('aria-hidden','true');

        const bubble=document.createElement('div');
        bubble.className='omb-tour-bubble';

        const arrow=document.createElement('div');
        arrow.className='omb-tour-bubble-arrow';
        arrow.setAttribute('aria-hidden','true');

        const content=document.createElement('div');
        content.className='omb-tour-content';

        const title=document.createElement('div');
        title.className='omb-tour-title';
        title.id='omb-tour-title';

        const body=document.createElement('div');
        body.className='omb-tour-body';

        const diagram=document.createElement('div');
        diagram.className='omb-tour-diagram';
        diagram.hidden=true;

        const progress=document.createElement('div');
        progress.className='omb-tour-progress';
        const counter=document.createElement('span');
        counter.className='omb-tour-counter';
        const bar=document.createElement('span');
        bar.className='omb-tour-progress-bar';
        const fill=document.createElement('span');
        fill.className='omb-tour-progress-fill';
        bar.appendChild(fill);
        progress.appendChild(counter);
        progress.appendChild(bar);

        const actions=document.createElement('div');
        actions.className='omb-tour-actions';

        const skipBtn=document.createElement('button');
        skipBtn.type='button';
        skipBtn.className='btn btn-ghost btn-small omb-tour-skip';
        skipBtn.dataset.action='onboarding-skip';
        skipBtn.textContent='Пропустить';

        const prevBtn=document.createElement('button');
        prevBtn.type='button';
        prevBtn.className='btn btn-ghost btn-small omb-tour-prev';
        prevBtn.dataset.action='onboarding-prev';
        prevBtn.textContent='Назад';

        const nextBtn=document.createElement('button');
        nextBtn.type='button';
        nextBtn.className='btn btn-primary btn-small omb-tour-next';
        nextBtn.dataset.action='onboarding-next';
        nextBtn.textContent='Дальше';

        actions.appendChild(skipBtn);
        actions.appendChild(prevBtn);
        actions.appendChild(nextBtn);

        content.appendChild(title);
        content.appendChild(body);
        content.appendChild(diagram);
        content.appendChild(progress);
        content.appendChild(actions);

        bubble.appendChild(arrow);
        bubble.appendChild(content);

        layer.appendChild(spotlight);
        layer.appendChild(mascot);
        layer.appendChild(bubble);
        document.body.appendChild(layer);

        this._layer=layer;
        this._spotlight=spotlight;
        this._mascot=mascot;
        this._bubble=bubble;
        this._arrow=arrow;
        this._titleEl=title;
        this._bodyEl=body;
        this._diagramEl=diagram;
        this._counterEl=counter;
        this._progressFillEl=fill;
        this._prevBtn=prevBtn;
        this._nextBtn=nextBtn;
    },

    _filterSteps(){
        const hasFeature=(key)=>{
            if(typeof Auth==='undefined')return true;
            if(Auth.isSuperAdmin&&Auth.isSuperAdmin())return true;
            if(Array.isArray(key))return Auth.hasAnyFeature?Auth.hasAnyFeature(key):key.some(k=>Auth.hasFeature?.(k));
            return Auth.hasFeature?Auth.hasFeature(key):true;
        };
        return this._allSteps.filter(step=>!step.requiredFeature||hasFeature(step.requiredFeature));
    },

    _isVisible(el){
        if(!el)return false;
        if(el.hidden)return false;
        const rect=el.getBoundingClientRect();
        if(rect.width<=0||rect.height<=0)return false;
        const style=window.getComputedStyle(el);
        if(style.display==='none'||style.visibility==='hidden')return false;
        return true;
    },

    _waitForAnchor(selector,{timeout=2000}={}){
        return new Promise((resolve)=>{
            const existing=document.querySelector(selector);
            if(existing&&this._isVisible(existing))return resolve(existing);
            let resolved=false;
            const obs=new MutationObserver(()=>{
                const el=document.querySelector(selector);
                if(el&&this._isVisible(el)){
                    resolved=true;
                    obs.disconnect();
                    clearTimeout(timer);
                    resolve(el);
                }
            });
            obs.observe(document.body,{childList:true,subtree:true,attributes:true,attributeFilter:['hidden','style','class']});
            const timer=setTimeout(()=>{
                if(resolved)return;
                obs.disconnect();
                resolve(document.querySelector(selector)||null);
            },timeout);
        });
    },

    _highlightAnchor(el){
        if(!this._spotlight)return;
        if(!el){
            this._spotlight.style.display='none';
            return;
        }
        const rect=el.getBoundingClientRect();
        const pad=8;
        this._spotlight.style.display='block';
        this._spotlight.style.top=Math.max(0,rect.top-pad)+'px';
        this._spotlight.style.left=Math.max(0,rect.left-pad)+'px';
        this._spotlight.style.width=(rect.width+pad*2)+'px';
        this._spotlight.style.height=(rect.height+pad*2)+'px';
    },

    _pickMascotPos(anchorRect, preferred){
        const vw=window.innerWidth, vh=window.innerHeight;
        const mw=this._mascotSize.w, mh=this._mascotSize.h;
        const gap=20;
        const order = preferred==='top'    ? ['top','bottom','right','left']
                   : preferred==='left'   ? ['left','right','top','bottom']
                   : preferred==='right'  ? ['right','left','top','bottom']
                   :                         ['bottom','top','right','left'];
        for(const side of order){
            if(side==='right' && anchorRect.right+gap+mw <= vw-12){
                return {x:anchorRect.right+gap, y:Math.min(Math.max(12,anchorRect.top+anchorRect.height/2-mh/2), vh-mh-12), side};
            }
            if(side==='left' && anchorRect.left-gap-mw >= 12){
                return {x:anchorRect.left-gap-mw, y:Math.min(Math.max(12,anchorRect.top+anchorRect.height/2-mh/2), vh-mh-12), side};
            }
            if(side==='bottom' && anchorRect.bottom+gap+mh <= vh-12){
                return {x:Math.min(Math.max(12,anchorRect.left+anchorRect.width/2-mw/2), vw-mw-12), y:anchorRect.bottom+gap, side};
            }
            if(side==='top' && anchorRect.top-gap-mh >= 12){
                return {x:Math.min(Math.max(12,anchorRect.left+anchorRect.width/2-mw/2), vw-mw-12), y:anchorRect.top-gap-mh, side};
            }
        }
        return {x:Math.max(12,Math.min(anchorRect.left,vw-mw-12)), y:Math.max(12,Math.min(anchorRect.bottom+gap,vh-mh-12)), side:'bottom'};
    },

    _pickBubblePos(mascotX, mascotY, mascotSide){
        const vw=window.innerWidth, vh=window.innerHeight;
        const bb=this._bubble.getBoundingClientRect();
        const mb=this._mascot.getBoundingClientRect();
        const bw=bb.width||340, bh=bb.height||200;
        const mw=mb.width||170, mh=mb.height||this._mascotSize.h;
        const gap=18, pad=12;
        const order = mascotSide==='right' ? ['right','top','bottom','left']
                    : mascotSide==='left'  ? ['left','top','bottom','right']
                    : mascotSide==='top'   ? ['top','right','left','bottom']
                    :                         ['bottom','right','left','top'];
        for(const side of order){
            if(side==='right' && mascotX+mw+gap+bw <= vw-pad){
                return {x:mascotX+mw+gap, y:Math.min(Math.max(pad, mascotY+mh/2-bh/2), vh-bh-pad), side:'left'};
            }
            if(side==='left' && mascotX-gap-bw >= pad){
                return {x:mascotX-gap-bw, y:Math.min(Math.max(pad, mascotY+mh/2-bh/2), vh-bh-pad), side:'right'};
            }
            if(side==='top' && mascotY-gap-bh >= pad){
                return {x:Math.min(Math.max(pad, mascotX+mw/2-bw/2), vw-bw-pad), y:mascotY-gap-bh, side:'bottom'};
            }
            if(side==='bottom' && mascotY+mh+gap+bh <= vh-pad){
                return {x:Math.min(Math.max(pad, mascotX+mw/2-bw/2), vw-bw-pad), y:mascotY+mh+gap, side:'top'};
            }
        }
        return {x:Math.min(Math.max(pad, mascotX), vw-bw-pad), y:Math.min(Math.max(pad, mascotY+mh+gap), vh-bh-pad), side:'top'};
    },

    _placeMascotAndBubble(anchor, preferred='bottom', {instant=false}={}){
        if(!this._mascot||!this._bubble)return;
        const ar=anchor.getBoundingClientRect();
        const mascotPos=this._pickMascotPos(ar, preferred);

        if(instant){
            const prevTransition=this._mascot.style.transition;
            this._mascot.style.transition='none';
            this._mascot.style.left=mascotPos.x+'px';
            this._mascot.style.top=mascotPos.y+'px';
            void this._mascot.offsetWidth;
            this._mascot.style.transition=prevTransition;
        }else{
            this._mascot.style.left=mascotPos.x+'px';
            this._mascot.style.top=mascotPos.y+'px';
        }
        this._mascot.dataset.x=mascotPos.x;
        this._mascot.dataset.y=mascotPos.y;
        this._mascot.dataset.side=mascotPos.side;

        const bubblePos=this._pickBubblePos(mascotPos.x, mascotPos.y, mascotPos.side);
        this._bubble.classList.remove('place-top','place-bottom','place-left','place-right');
        this._bubble.classList.add('place-'+bubblePos.side);
        this._bubble.style.left=bubblePos.x+'px';
        this._bubble.style.top=bubblePos.y+'px';

        const arrow=this._arrow;
        const bb=this._bubble.getBoundingClientRect();
        const mascotCenterX=mascotPos.x+this._mascotSize.w/2;
        const mascotCenterY=mascotPos.y+this._mascotSize.h/2;
        if(bubblePos.side==='top'||bubblePos.side==='bottom'){
            const arrowX=Math.min(Math.max(16, mascotCenterX-bubblePos.x), bb.width-16);
            arrow.style.left=arrowX+'px';
            arrow.style.top='';
        }else{
            const arrowY=Math.min(Math.max(16, mascotCenterY-bubblePos.y), bb.height-16);
            arrow.style.top=arrowY+'px';
            arrow.style.left='';
        }

    },

    _flushPendingPose(){
        if(!this._pendingPose)return;
        clearTimeout(this._runTransitionTimer);
        this._runTransitionTimer=null;
        const p=this._pendingPose;
        this._pendingPose=null;
        this._setMascotPose(p.pose, p.opts);
    },

    _runMascotTo(anchor, preferred, targetPose, targetAnimate){
        if(!this._mascot)return;
        this._flushPendingPose();
        const ar=anchor.getBoundingClientRect();
        const target=this._pickMascotPos(ar, preferred);
        const prevX=this._mascot.dataset.x!==undefined?parseFloat(this._mascot.dataset.x):null;
        const prevY=this._mascot.dataset.y!==undefined?parseFloat(this._mascot.dataset.y):null;
        const hasPrev=prevX!==null&&prevY!==null;
        const dx=hasPrev?target.x-prevX:0;
        const dy=hasPrev?target.y-prevY:0;
        const moving=hasPrev&&(Math.abs(dx)>4||Math.abs(dy)>4);

        const dirMap={right:'left', left:'right', top:'down', bottom:'up'};
        const pointDir=dirMap[target.side]||null;

        if(!moving){
            this._setMascotPose(targetPose||'idle', {flip:false, animate:targetAnimate, glitch:hasPrev, dir:pointDir});
            return;
        }
        clearTimeout(this._runTransitionTimer);
        this._setMascotPose('run', {flip: dx<0, animate:true, glitch:true});
        const finalOpts={flip:false, animate:targetAnimate, glitch:true, dir:pointDir};
        this._pendingPose={pose:targetPose||'idle', opts:finalOpts};
        this._runTransitionTimer=setTimeout(()=>{
            this._pendingPose=null;
            this._runTransitionTimer=null;
            if(!this._running||!this._mascot)return;
            this._setMascotPose(targetPose||'idle', finalOpts);
        }, 1100);
    },

    _poseFlipForSide(pose, mascotSide){
        if(pose==='wave'||pose==='cheer')return false;
        if(pose==='point'){
            return mascotSide==='right';
        }
        if(pose==='idle')return mascotSide==='right';
        return false;
    },

    _populateBubble(step,idx,total){
        if(!this._bubble)return;
        this._titleEl.textContent=step.title||'';
        this._bodyEl.textContent=step.body||'';
        this._counterEl.textContent=`${idx+1} / ${total}`;
        const pct=Math.round(((idx+1)/total)*100);
        this._progressFillEl.style.width=pct+'%';
        this._renderDiagram(step.diagram||null);

        this._bubble.classList.remove('is-first','is-last','is-finale','is-narrative');
        if(idx===0)this._bubble.classList.add('is-first');
        if(idx===total-1)this._bubble.classList.add('is-last');
        if(step.narrative)this._bubble.classList.add('is-narrative');

        if(step.isFinale){
            this._bubble.classList.add('is-finale');
            this._nextBtn.textContent='Создать проект';
            this._nextBtn.dataset.action='onboarding-finale-cta';
        }else{
            this._nextBtn.textContent=(idx===total-1)?'Готово':'Дальше';
            this._nextBtn.dataset.action='onboarding-next';
        }
    },

    _renderDiagram(kind){
        if(!this._diagramEl)return;
        const el=this._diagramEl;
        while(el.firstChild)el.removeChild(el.firstChild);
        if(!kind){ el.hidden=true; return; }
        el.hidden=false;
        el.className='omb-tour-diagram diagram-'+kind;
        const node=(label,cls='')=>{
            const n=document.createElement('div');
            n.className='diag-node '+cls;
            n.textContent=label;
            return n;
        };
        const ports=(n)=>{
            const i=document.createElement('span'); i.className='diag-port diag-port-in'; i.textContent='IN';
            const o=document.createElement('span'); o.className='diag-port diag-port-out'; o.textContent='OUT';
            n.appendChild(i); n.appendChild(o);
            return n;
        };
        const arrow=()=>{
            const a=document.createElement('div');
            a.className='diag-arrow';
            return a;
        };
        if(kind==='nodes'){
            el.appendChild(ports(node('Промпт','diag-node-llm')));
            el.appendChild(ports(node('API','diag-node-api')));
            el.appendChild(ports(node('Файл','diag-node-file')));
        }else if(kind==='edges'){
            el.appendChild(ports(node('A','diag-node-llm')));
            el.appendChild(arrow());
            el.appendChild(ports(node('B','diag-node-api')));
            const tag=document.createElement('div');
            tag.className='diag-tag';
            tag.textContent='{{A.text}}';
            el.appendChild(tag);
        }else if(kind==='run'){
            el.appendChild(node('✓','diag-node-ok'));
            el.appendChild(arrow());
            el.appendChild(node('✓','diag-node-ok'));
            el.appendChild(arrow());
            el.appendChild(node('●','diag-node-busy'));
            el.appendChild(arrow());
            el.appendChild(node('!','diag-node-err'));
        }
    },

    async _showStep(idx){
        if(!this._running)return;
        const total=this._filteredSteps.length;
        if(idx<0||idx>=total){
            return this.finish();
        }
        const step=this._filteredSteps[idx];
        this._stepIdx=idx;

        if(this._afterHideHook){
            try{this._afterHideHook();}catch(_e){}
            this._afterHideHook=null;
        }
        const hadBeforeShow=typeof step.beforeShow==='function';
        if(hadBeforeShow){
            try{step.beforeShow();}catch(_e){}
        }
        if(typeof step.afterHide==='function'){
            this._afterHideHook=step.afterHide;
        }

        if(step.page&&typeof Router!=='undefined'&&Router.current!==step.page){
            try{Router.go(step.page);}catch(_e){}
        }

        // Дать CSS-transition'у развернуть скрытые элементы (например auth-userbox)
        if(hadBeforeShow){
            await new Promise(r=>setTimeout(r,180));
            if(!this._running||this._stepIdx!==idx)return;
        }

        clearTimeout(this._bubbleShowTimer);
        this._bubble.classList.add('is-hidden');

        if(step.narrative){
            this._activeAnchor=null;
            if(this._spotlight)this._spotlight.style.display='none';
            await new Promise(r=>setTimeout(r,80));
            if(!this._running||this._stepIdx!==idx)return;
            this._populateBubble(step,idx,total);
            this._placeNarrative(step);
            this._bubbleShowTimer=setTimeout(()=>{
                if(this._running && this._stepIdx===idx){
                    this._bubble.classList.remove('is-hidden');
                }
            }, 350);
            return;
        }

        const anchor=await this._waitForAnchor(step.anchor,{timeout:2000});
        if(!this._running)return;

        if(!anchor){
            if(typeof Toast!=='undefined'&&Toast.warning){
                Toast.warning(`Шаг «${step.title}» сейчас недоступен`);
            }
            return this._showStep(idx+1);
        }

        this._activeAnchor=anchor;
        try{anchor.scrollIntoView({block:'center',inline:'center',behavior:'smooth'});}catch(_e){}

        await new Promise(r=>setTimeout(r,80));
        if(!this._running||this._stepIdx!==idx)return;

        this._populateBubble(step,idx,total);
        this._highlightAnchor(anchor);
        this._runMascotTo(anchor, step.placement, step.pose||'idle', step.animate!==false);
        this._placeMascotAndBubble(anchor, step.placement);

        this._bubbleShowTimer=setTimeout(()=>{
            if(this._running && this._stepIdx===idx){
                this._bubble.classList.remove('is-hidden');
            }
        }, 350);
    },

    _placeNarrative(step){
        if(!this._mascot||!this._bubble)return;
        this._flushPendingPose();
        const vw=window.innerWidth, vh=window.innerHeight;
        const mw=this._mascotSize.w, mh=this._mascotSize.h;
        const bb=this._bubble.getBoundingClientRect();
        const bw=bb.width||360, bh=bb.height||260;
        const gap=24;

        const totalW=mw+gap+bw;
        const startX=Math.max(20, (vw-totalW)/2);
        const mascotX=startX;
        const mascotY=Math.max(20, (vh-mh)/2);
        const bubbleX=mascotX+mw+gap;
        const bubbleY=Math.max(20, (vh-bh)/2);

        const prevX=this._mascot.dataset.x!==undefined?parseFloat(this._mascot.dataset.x):null;
        const hasPrev=prevX!==null;
        const dx=hasPrev?mascotX-prevX:0;
        const moving=hasPrev&&Math.abs(dx)>4;

        this._mascot.style.left=mascotX+'px';
        this._mascot.style.top=mascotY+'px';
        this._mascot.dataset.x=mascotX;
        this._mascot.dataset.y=mascotY;

        if(moving){
            clearTimeout(this._runTransitionTimer);
            this._setMascotPose('run',{flip:dx<0, animate:true, glitch:true});
            const finalOpts={flip:false, animate:step.animate!==false, glitch:true};
            this._pendingPose={pose:step.pose||'idle', opts:finalOpts};
            this._runTransitionTimer=setTimeout(()=>{
                this._pendingPose=null;
                this._runTransitionTimer=null;
                if(!this._running||!this._mascot)return;
                this._setMascotPose(step.pose||'idle', finalOpts);
            },1100);
        }else{
            this._setMascotPose(step.pose||'idle',{flip:false, animate:step.animate!==false, glitch:hasPrev});
        }

        this._bubble.classList.remove('place-top','place-bottom','place-left','place-right');
        this._bubble.classList.add('place-left');
        this._bubble.style.left=bubbleX+'px';
        this._bubble.style.top=bubbleY+'px';
        const arrow=this._arrow;
        if(arrow){
            const arrowY=Math.min(Math.max(16, mascotY+mh/2-bubbleY), bh-16);
            arrow.style.top=arrowY+'px';
            arrow.style.left='';
        }
    },

    start(reason='manual'){
        if(this._running)return;
        if(typeof Modal!=='undefined'){
            const overlay=document.getElementById('modal-overlay');
            if(overlay&&overlay.hidden===false){
                try{Modal.hide();}catch(_e){}
            }
        }
        this._reason=reason;
        this._filteredSteps=this._filterSteps();
        if(!this._filteredSteps.length)return;
        this._renderLayer();
        this._layer.style.display='block';
        this._running=true;
        this._stepIdx=0;
        if(this._mascot){
            delete this._mascot.dataset.x;
            delete this._mascot.dataset.y;
            this._mascot.classList.remove('flip-x');
            this._setMascotPose('idle');
        }
        this._showStep(0);
    },

    next(){
        if(!this._running)return;
        const total=this._filteredSteps.length;
        if(this._stepIdx>=total-1){
            this.finish();
            return;
        }
        this._showStep(this._stepIdx+1);
    },

    prev(){
        if(!this._running)return;
        if(this._stepIdx<=0)return;
        this._showStep(this._stepIdx-1);
    },

    skip(){
        if(!this._running)return;
        this._writeDone({skipped:true});
        this._teardown();
    },

    finish(){
        if(!this._running)return;
        this._writeDone({completed:true});
        this._teardown();
    },

    _teardown(){
        this._running=false;
        clearTimeout(this._runTransitionTimer);
        clearTimeout(this._bubbleShowTimer);
        if(this._afterHideHook){
            try{this._afterHideHook();}catch(_e){}
            this._afterHideHook=null;
        }
        if(this._mascot)this._glitch(420);
        if(this._bubble)this._bubble.classList.add('is-hidden');
        if(this._layer){
            this._layer.classList.add('is-vanishing');
            setTimeout(()=>{
                if(this._layer){
                    this._layer.style.display='none';
                    this._layer.classList.remove('is-vanishing');
                }
            }, 420);
        }
        this._activeAnchor=null;
    },

    _writeDone(state){
        try{
            if(state&&state.completed)localStorage.setItem(this._finishedKey,'1');
            if(state&&state.skipped)localStorage.setItem(this._skippedKey,'1');
            localStorage.setItem(this._seenAtKey,String(Date.now()));
        }catch(_e){}
    },

    _readDone(){
        try{
            return {
                completed:localStorage.getItem(this._finishedKey)==='1',
                skipped:localStorage.getItem(this._skippedKey)==='1',
                seenAt:Number(localStorage.getItem(this._seenAtKey)||0)
            };
        }catch(_e){
            return {completed:false,skipped:false,seenAt:0};
        }
    }
};

window.OnboardingTour=OnboardingTour;

/* web/js/landing-effects.js */
/* omoibot - Landing live effects (reveal only) */
const LandingEffects = {
    _eventsBound:false,
    _initialized:false,
    _observer:null,

    initEvents(){
        if(this._eventsBound)return;
        this._eventsBound=true;
    },

    init(){
        if(this._initialized)return;
        this._initialized=true;
        this._initReveal();
    },

    // The landing is detached from the DOM on login (Auth.setState) and
    // re-cloned on logout. Reset the flag and drop the observer so the next
    // init() re-collects targets — otherwise fresh [data-reveal] nodes would
    // stay at opacity:0 and the landing would render empty.
    destroy(){
        try{ this._observer?.disconnect(); }catch(_e){}
        this._observer=null;
        this._initialized=false;
    },

    _reduceMotion(){
        try{return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);}
        catch(_e){return false;}
    },

    _initReveal(){
        const targets=document.querySelectorAll('[data-reveal]');
        if(!targets.length)return;
        const reduce=this._reduceMotion();
        if(!('IntersectionObserver' in window) || reduce){
            targets.forEach((el)=>el.classList.add('is-revealed'));
            return;
        }
        const obs=new IntersectionObserver((entries)=>{
            entries.forEach((entry)=>{
                if(!entry.isIntersecting)return;
                entry.target.classList.add('is-revealed');
                obs.unobserve(entry.target);
            });
        }, {threshold:0.18, rootMargin:'-40px 0px -40px 0px'});
        targets.forEach((el)=>obs.observe(el));
        this._observer=obs;
    }
};
window.LandingEffects=LandingEffects;

/* web/js/app.js */
/* ═══════════════════════════════════════════
   omoibot — Init
   ═══════════════════════════════════════════ */

const AppEvents = {
    initializedChunks:new Set(),
    actions:{
        'projects-create':()=>Projects.create(),
        'project-edit-name':()=>ProjectView.editName(),
        'project-create-task':()=>ProjectView.createTask(),
        'project-remove':()=>ProjectView.remove(),
        'task-close':()=>{if(typeof TaskView!=='undefined')TaskView.close()},
        'task-edit-name':()=>{if(typeof TaskView!=='undefined')TaskView.editName()},
        'task-run':()=>{if(typeof TaskView!=='undefined')TaskView.run()},
        'task-editor-add-node':()=>{if(typeof TaskEditor!=='undefined')TaskEditor.addNodeDialog()},
        'task-editor-auto-layout':()=>{if(typeof TaskEditor!=='undefined')TaskEditor.autoLayout()},
        'task-editor-zoom-fit':()=>{if(typeof TaskEditor!=='undefined')TaskEditor.zoomToFit()},
        'task-editor-toggle-snap':()=>{if(typeof TaskEditor!=='undefined')TaskEditor.toggleSnap()},
        'task-editor-save':()=>{if(typeof TaskEditor!=='undefined')TaskEditor.save()},
        'chats-create':()=>Chats.create(),
        'chats-attach-file':()=>Chats.attachFile(),
        'chats-send':()=>Chats.send(),
        'debugger-close':()=>{if(typeof JobDebugger!=='undefined')JobDebugger.close()},
        'debugger-sync-task':()=>{if(typeof JobDebugger!=='undefined')JobDebugger.syncToTask()},
        'debugger-restart-selected':()=>{if(typeof JobDebugger!=='undefined')JobDebugger.restartFromSelected()},
        'debugger-add-node':()=>{if(typeof JobDebugger!=='undefined')JobDebugger.addNodeDialog()},
        'debugger-auto-layout':()=>{if(typeof JobDebugger!=='undefined')JobDebugger.autoLayout()},
        'debugger-zoom-fit':()=>{if(typeof JobDebugger!=='undefined')JobDebugger.zoomToFit()},
        'canvas-agent-open':(el)=>{if(typeof CanvasAgent!=='undefined')CanvasAgent.open(el?.dataset?.mode||'template')},
        'canvas-agent-close':()=>{if(typeof CanvasAgent!=='undefined')CanvasAgent.close()},
        'canvas-agent-send':()=>{if(typeof CanvasAgent!=='undefined')CanvasAgent.send()},
        'canvas-agent-stop':()=>{if(typeof CanvasAgent!=='undefined')CanvasAgent.stop()},
        'canvas-agent-clear':()=>{if(typeof CanvasAgent!=='undefined')CanvasAgent.clear()},
        'modules-add':()=>Modules.showAddDialog(),
        'modal-hide':(_el,event)=>{
            // If click came from inside the bottom-sheet, close it as a
            // cancel path (no preservePendingConnect — pending should clear).
            // Otherwise fall through to Modal.hide which itself delegates to
            // MobileSheet for modal-proxy kinds.
            const fromSheet=event?.target?.closest?.('#mobile-sheet');
            if(fromSheet&&typeof MobileSheet!=='undefined'&&MobileSheet.active){
                MobileSheet.close();
            }else{
                Modal.hide();
            }
        },
        'mobile-add-node':()=>{
            const page=document.body.dataset.currentPage;
            if(page==='task'){
                // Guard: only when editor tab is active (FAB is hidden
                // outside it, but action-handler confirms against stale clicks).
                if(document.body.dataset.taskTab==='tv-editor'&&typeof TaskEditor!=='undefined'){
                    TaskEditor.addNodeDialog();
                }
            }else if(page==='debugger'&&typeof JobDebugger!=='undefined'){
                JobDebugger.addNodeDialog();
            }
        },
        'editor-undo':()=>{
            if(typeof UndoStack==='undefined'||typeof TaskEditor==='undefined')return;
            const snap=UndoStack.undo();
            if(snap){
                TaskEditor.nodes=snap.nodes;
                TaskEditor.edges=snap.edges;
                TaskEditor.render();
                if(typeof Toast!=='undefined')Toast.info('Отменено');
                if(typeof AutoSave!=='undefined')AutoSave.markDirty();
            }
        },
        'editor-redo':()=>{
            if(typeof UndoStack==='undefined'||typeof TaskEditor==='undefined')return;
            const snap=UndoStack.redo();
            if(snap){
                TaskEditor.nodes=snap.nodes;
                TaskEditor.edges=snap.edges;
                TaskEditor.render();
                if(typeof Toast!=='undefined')Toast.info('Повторено');
                if(typeof AutoSave!=='undefined')AutoSave.markDirty();
            }
        },
        'debugger-undo':()=>{
            if(typeof JobDebugger!=='undefined')JobDebugger._undo?.();
        },
        'debugger-redo':()=>{
            if(typeof JobDebugger!=='undefined')JobDebugger._redo?.();
        },
        'mobile-peek-more':(el)=>{
            if(typeof CanvasMobileShell==='undefined')return;
            const mode=document.body.dataset.currentPage==='debugger'?'debugger':'editor';
            CanvasMobileShell.openActionSheet(mode, el?.dataset?.nodeId||'');
        },
        'mobile-peek-edit':(el)=>{
            const nodeId=el?.dataset?.nodeId||'';
            if(typeof TaskEditor!=='undefined'&&TaskEditor.editNode)TaskEditor.editNode(nodeId);
        },
        'auth-copy-invite':()=>Auth.copyInviteCode(),
        'profile-save':()=>Profile.save(),
        'profile-reset':()=>Profile.load(true),
        'profile-change-password':()=>Profile.changePassword(),
        'mcp-copy-url':()=>Profile.copyMcpUrl(),
        'mcp-revoke-connection':(el)=>Profile.revokeMcpConnection(el?.dataset?.clientId||''),
        'mcp-revoke-grant':(el)=>Profile.revokeGrant(el?.dataset?.grantId||''),
        'mcp-edit-policy':(el)=>Profile.editGrantPolicy?.(el?.dataset?.grantId||'',el?.dataset?.updatedAt||''),
        'referral-copy':()=>Profile.copyReferralLink(),
        'referral-withdraw':()=>Profile.withdraw(),
        'landing-open-auth':(el)=>Auth.openFromLanding(el?.dataset?.authMode||'register'),
        'mobile-account-toggle':()=>Auth.toggleMobileAccountMenu(),
        'theme-toggle':(el)=>{
            if(typeof Theme!=='undefined')Theme.toggle();
            if(el?.closest?.('#mobile-account-menu'))Auth.closeMobileAccountMenu();
        },
        'auth-submit':()=>Auth.submit(),
        'auth-toggle':()=>Auth.toggleMode(),
        'auth-retry':()=>Auth.retrySessionCheck(),
        'batch-delete-errors':()=>BatchJobs.deleteByStatus('error'),
        'batch-retry-errors':()=>BatchJobs.retryAll(),
        'batch-delete-done':()=>BatchJobs.deleteByStatus('done'),
        'batch-delete-all':()=>BatchJobs.deleteAll(),
        'batch-delete-all-errors':()=>BatchJobs.deleteAllErrors(),
        'task-confirm-run':()=>{if(typeof TaskView!=='undefined')TaskView.confirmRun()},
        'onboarding-next':()=>OnboardingTour.next(),
        'onboarding-prev':()=>OnboardingTour.prev(),
        'onboarding-skip':()=>OnboardingTour.skip(),
        'onboarding-start':()=>OnboardingTour.start('manual'),
        'onboarding-finale-cta':()=>{OnboardingTour.finish();Projects.create();},
        'gallery-apply':()=>{if(typeof Gallery!=='undefined')Gallery.applyFilters();},
        'gallery-reset':()=>{if(typeof Gallery!=='undefined')Gallery.resetFilters();},
        'gallery-refresh':()=>{if(typeof Gallery!=='undefined')Gallery.load(true);},
        'gallery-load-more':()=>{if(typeof Gallery!=='undefined')Gallery.loadMore();}
    },
    busyActions:new Set([
        'project-remove',
        'task-editor-save',
        'chats-send',
        'debugger-sync-task',
        'debugger-restart-selected',
        'canvas-agent-send',
        'profile-save',
        'profile-change-password',
        'mcp-revoke-grant',
        'referral-withdraw',
        'auth-submit',
        'batch-delete-errors',
        'batch-retry-errors',
        'batch-delete-done',
        'batch-delete-all',
        'batch-delete-all-errors',
        'task-confirm-run',
    ]),

    initChunk(name){
        name=String(name||'');
        if(this.initializedChunks.has(name))return false;
        if(name==='core'){
            Jobs.initEvents?.();
            Dashboard.initEvents?.();
            Chats.initEvents?.();
            Projects.initEvents?.();
            ProjectView.initEvents?.();
            if(typeof Gallery!=='undefined')Gallery.initEvents?.();
            Modules.initEvents?.();
            if(typeof OnboardingTour!=='undefined')OnboardingTour.initEvents?.();
            if(typeof LandingEffects!=='undefined')LandingEffects.initEvents?.();
        }else if(name==='canvas'){
            if(typeof JobsLasso!=='undefined')JobsLasso.init?.();
            if(typeof TaskEditor!=='undefined')TaskEditor.initEvents?.();
            if(typeof JobDebugger!=='undefined')JobDebugger.initEvents?.();
            if(window.ToolsDock)window.ToolsDock.init?.();
            if(window.DebuggerToolsDock)window.DebuggerToolsDock.init?.();
            if(typeof CanvasAgent!=='undefined')CanvasAgent.init?.();
            if(typeof CanvasMobileShell!=='undefined')CanvasMobileShell.init?.();
        }else if(name==='bots'){
            if(typeof BotSettings!=='undefined')BotSettings.initEvents?.();
        }else if(name==='admin'){
            if(typeof AdminPanel!=='undefined')AdminPanel.initEvents?.();
        }else{
            return false;
        }
        this.initializedChunks.add(name);
        return true;
    },

    init(){
        document.addEventListener('click',(event)=>this.onClick(event));
        document.addEventListener('keydown',(event)=>this.onKeydown(event));
        document.addEventListener('change',(event)=>this.onChange(event));
        document.addEventListener('input',(event)=>this.onInput(event));
        if(typeof MobileSheet!=='undefined')MobileSheet.init();
        if(typeof Router!=='undefined'&&Router.registerLeaveHook&&!this._routeTeardownBound){
            this._routeTeardownBound=true;
            Router.registerLeaveHook('task',()=>{
                if(typeof TaskView!=='undefined')TaskView._teardownJobsScroll?.();
                if(typeof TaskEditor!=='undefined'){
                    TaskEditor._disconnectNodeGeometryObserver?.();
                    TaskEditor._cancelScheduledRender?.();
                }
                if(typeof CanvasZoom!=='undefined')CanvasZoom.destroy?.('editor');
            });
            Router.registerLeaveHook('debugger',()=>{
                if(typeof JobDebugger!=='undefined')JobDebugger._cancelScheduledRender?.();
                if(typeof CanvasZoom!=='undefined')CanvasZoom.destroy?.('debugger');
            });
        }
    },

    closest(target,selector){
        return target instanceof Element ? target.closest(selector) : null;
    },

    setActive(btn){
        btn.parentElement?.querySelectorAll('.filter-btn').forEach(b=>b.classList.remove('active'));
        btn.classList.add('active');
    },

    onClick(event){
        const route=this.closest(event.target,'[data-route]');
        if(route){
            event.preventDefault();
            Router.go(route.dataset.route);
            return;
        }

        const zoom=this.closest(event.target,'[data-zoom-scope][data-zoom-action]');
        if(zoom){
            event.preventDefault();
            const scope=zoom.dataset.zoomScope;
            const action=zoom.dataset.zoomAction;
            if(action==='in')CanvasZoom.zoomIn(scope);
            else if(action==='out')CanvasZoom.zoomOut(scope);
            else CanvasZoom.zoomReset(scope);
            return;
        }

        const tab=this.closest(event.target,'.tab-btn[data-tab]');
        if(tab){
            event.preventDefault();
            if(typeof TaskView!=='undefined')TaskView.switchTab(tab.dataset.tab);
            return;
        }

        const sort=this.closest(event.target,'.filter-btn[data-sort]');
        if(sort){
            event.preventDefault();
            this.setActive(sort);
            ProjectView.setSort(sort.dataset.sort);
            return;
        }

        const filter=this.closest(event.target,'.filter-btn[data-filter]');
        if(filter){
            event.preventDefault();
            this.setActive(filter);
            if(Router.current==='task'&&typeof TaskView!=='undefined')TaskView.loadJobs();
            return;
        }

        const action=this.closest(event.target,'[data-action]');
        if(action){
            // Buttons that the cost preview marked as cost-blocked must
            // not fire — backend would still 402 them, but the UX is
            // sharper if we short-circuit on the click and show the
            // top-up modal directly. Detected via the `btn-cost-blocked`
            // class set by TaskEditor / Chats / Agent when balance < cost.
            const blocked=action.classList.contains('btn-cost-blocked')||action.getAttribute('aria-disabled')==='true';
            if(blocked){
                event.preventDefault();
                event.stopPropagation();
                if(typeof CostEstimator!=='undefined' && typeof InsufficientFunds!=='undefined'){
                    const snap=CostEstimator.snapshot();
                    const isChat=action.id==='chat-send-btn';
                    const isAgent=action.id==='canvas-agent-send-btn';
                    const explicitNeeded=Number(action.dataset.costNeeded||0);
                    const hasExplicitBalance=action.dataset.costBalance!==undefined;
                    const needed=explicitNeeded>0?explicitNeeded
                        :isChat?snap.chatCredits
                        :isAgent?snap.agentCredits
                        :Number(snap.total||snap.perRun||0);
                    const balance=hasExplicitBalance?Number(action.dataset.costBalance||0):Number(snap.balance||0);
                    InsufficientFunds.show({
                        needed:Number(needed||0),
                        balance,
                        message:'Не хватает токенов для этого действия. Купите пакет токенов в кошельке.',
                    });
                }
                return;
            }
            const handler=this.actions[action.dataset.action];
            if(handler){
                event.preventDefault();
                const runner=window.ActionRunner||{run:(_target,fn)=>fn()};
                if(this.busyActions.has(action.dataset.action))runner.run(action,()=>handler(action,event));
                else handler(action,event);
            }
        }
    },

    onKeydown(event){
        const tab=this.closest(event.target,'.tab-btn[data-tab]');
        if(!tab)return;
        const key=event.key;
        if(!['ArrowRight','ArrowDown','ArrowLeft','ArrowUp','Home','End'].includes(key))return;
        if(typeof TaskView==='undefined')return;
        const tablist=tab.closest('[role="tablist"]')||tab.parentElement;
        const tabs=[...(tablist?.querySelectorAll?.('.tab-btn[data-tab]')||[])];
        if(!tabs.length)return;
        const current=tabs.indexOf(tab);
        if(current<0)return;
        event.preventDefault();
        let next=current;
        if(key==='Home')next=0;
        else if(key==='End')next=tabs.length-1;
        else if(key==='ArrowRight'||key==='ArrowDown')next=(current+1)%tabs.length;
        else next=(current-1+tabs.length)%tabs.length;
        const nextTab=tabs[next];
        TaskView.switchTab(nextTab.dataset.tab);
        nextTab.focus?.();
    },

    onChange(event){
        const action=event.target?.dataset?.action;
        if(action==='dashboard-filter-project')Dashboard.filterByProject();
        else if(action==='chats-files-selected')Chats.onFilesSelected(event.target);
    },

    onInput(event){
        const target=event.target instanceof Element?event.target:null;
        if(target?.matches?.(Auth._authFieldSelector))Auth._clearFieldError(target);
        if(target?.dataset?.action==='profile-preview')Profile.preview();
    }
};

window.AppEvents=AppEvents;

const bootstrapApp=async()=>{
    AppEvents.init();
    AppEvents.initChunk('core');
    const ready=await Auth.init();
    if(ready){
        Auth.booted=true;
        Router.init();
    }
};

if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',bootstrapApp,{once:true});
}else{
    bootstrapApp();
}
